import { updateResource as updateResource$1, findExistingCoCMFlag, createResource as createResource$1, getActivityTasks, getCarePlanServiceRequests, readResource as readResource$1, resolveActivityOwner, enrichCoverageAreasWithLocations, submitBundle, buildCarePlanTasksMap, resolveOwnerOverridesForPlan, getCarePlanByType, updateCarePlan as updateCarePlan$1, getProgramSelectionObservation, findExistingMinorFlag, getActiveCarePlans, deleteResource, findBenchmarkPractitionerRole, fetchExistingActiveFlags, loadEncounterFormDataContext, fetchBenchmarkPractitionerRole, resolveIntakeCallCareTimeLink, flattenResponseItems, evaluateQuestionnaireCompletion, filterScorableResponses } from './services.mjs';
import { globalEventEmitter as globalEventEmitter$1, DomainEventNames as DomainEventNames$1 } from './events.mjs';
import { FLAG_LOCAL_CODE_SYSTEM as FLAG_LOCAL_CODE_SYSTEM$1, GP_ROLE_TYPE_EXTENSION, GP_ROLE_TYPES, COCM_CONSENT_EXTENSION_URL, getAllCarePlanActivityOptions as getAllCarePlanActivityOptions$1, getActivitySlaDurationHours, TASK_DRAFT_FORM_STATE_EXTENSION_URL, TASK_OUTPUT_DOCUMENTED_ENCOUNTER_CODE, TASK_OUTPUT_TYPE_SYSTEM, CARE_PLAN_CATEGORY_SYSTEM as CARE_PLAN_CATEGORY_SYSTEM$1, getFlexibleActivitiesForType, getCarePlanActivityDescription, getCarePlanActivityDisplay, getSequentialActivitiesForType, COCM_CONSENT_DATE_EXTENSION_URL as COCM_CONSENT_DATE_EXTENSION_URL$1, isValidGASScore, PAUSE_FLAG_REASON_EXTENSION_URL, ACTIVITY_PROGRESS_ARCHETYPE_MAP, PROGRESS_LOG_CATEGORIES, buildMediumCoding, PROGRESS_LOG_OUTCOME_SYSTEM, PROGRESS_LOG_CATEGORY_SYSTEM, isAdministrativeActivityCode, FINDING_TO_CHARACTERISTIC_MAP, isActivityFlexible, CARE_PLAN_ACTIVITY_SYSTEM as CARE_PLAN_ACTIVITY_SYSTEM$1, TERMINAL_TASK_STATUSES, VALUE_SET, isIntakeEncounterType, CLINICAL_INTAKE_ENCOUNTER_TYPE_CODE as CLINICAL_INTAKE_ENCOUNTER_TYPE_CODE$1, COCM_INTAKE_ENCOUNTER_TYPE_CODE as COCM_INTAKE_ENCOUNTER_TYPE_CODE$1, INTAKE_CHARGE_CPT_90791, CODE_SYSTEM as CODE_SYSTEM$1, INTAKE_CHARGE_ITEM_IDENTIFIER_SYSTEM, PATIENT_ENGAGEMENT_STATUS_SYSTEM as PATIENT_ENGAGEMENT_STATUS_SYSTEM$1, LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM as LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM$1, migratePatientEngagementStatusCode, PATIENT_ENGAGEMENT_STATUS_OPTIONS as PATIENT_ENGAGEMENT_STATUS_OPTIONS$1, isTerminatingEngagementStatus, LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS as LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS$1, INTAKE_BILLING_FLAG_OPTIONS as INTAKE_BILLING_FLAG_OPTIONS$1, PATIENT_INTAKE_ELIGIBILITY_SYSTEM, getFlagCodingByCode, MAX_LIBRARY_MARKDOWN_BYTES, STANDARD_QUESTIONNAIRES } from './constants.mjs';
import { getPatientDisplayName as getPatientDisplayName$1, getCarePlanType, mapTaskStatusToActivityStatus, areAllActivitiesCompleted, getPractitionerDisplayName as getPractitionerDisplayName$1 } from './resolvers.mjs';
import { buildPatientReference, buildReferenceWithDisplay, goalFromForm, goalProgressFromForm, patientFromForm, buildPractitionerRoleReference, patientUpdateFromForm, appointmentFromForm, clinicalImpressionFromForm, coverageFromForm, encounterToForm, flagFromForm, libraryFromForm, observationFromForm, questionnaireFromForm, questionnaireResponseFromForm, taskFromForm } from './adapters.mjs';
import { toPatientId } from './types.mjs';
import { updateCarePlanActivityStatus, buildCarePlanActivityTask, buildBatchUpdateHealthcareServicesBundle, updateActivitiesForCancellation, buildCancelledTaskEntries, buildCarePlanBundle, buildGoalTransactionBundle, buildCreatePractitionerBundle, buildLibraryResource, buildAvailabilityUpdateBundle, buildProgramSelectionObservation, buildClinicalImpression, buildUpsertCommunicationBundle, getQuarantinedAssessmentStateFingerprint, QuestionnairePersistenceError, assertAdaptiveScreenerFinalization, buildUpsertEncounterBundle, hasAssessmentStatePayload, buildUpsertHealthcareServiceBundle, buildObservationFromScore } from './builders.mjs';
import { OperationOutcomeError, isOperationOutcome, getStatus, formatHumanName, getReferenceString } from '@medplum/core';
import { toLogSafeError as toLogSafeError$1, toDateInputValue, extractResourcesFromTransactionResult as extractResourcesFromTransactionResult$1, extractSingleResourceFromTransactionResult as extractSingleResourceFromTransactionResult$1, localCalendarDateTodayISO, dateOnlyToISO, parseFhirInstantForDisplay, calculateAge, executeTransaction as executeTransaction$1, getMarkdownByteLength } from './utils.mjs';
import { calculateQuestionnaireScore } from './scoring.mjs';

/**
 * Activate a care plan
 *
 * Changes a care plan from "draft" to "active" status.
 *
 * @deprecated Care plans are now created in active status. This action is only for edge cases.
 * Use createCarePlan action instead, which creates care plans in active status.
 *
 * @param medplum - MedplumClient instance
 * @param carePlan - Care plan to activate
 * @returns Updated CarePlan
 * @throws Error if care plan is not in draft status
 * @agentic @benchmark/core
 */ async function activateCarePlan(medplum, carePlan) {
    if (!carePlan.id) {
        throw new Error("Care plan ID is required to activate");
    }
    if (carePlan.status !== "draft") {
        throw new Error(`Care plan must be in draft status to activate. Current status: ${carePlan.status}`);
    }
    const patientId = carePlan.subject?.reference?.replace("Patient/", "") || "";
    const updatedCarePlan = await updateResource$1(medplum, {
        ...carePlan,
        id: carePlan.id,
        status: "active",
        period: {
            ...carePlan.period,
            start: carePlan.period?.start || new Date().toISOString()
        }
    });
    // Emit event
    if (!updatedCarePlan.id) {
        throw new Error("Care plan ID is required after update");
    }
    const event = {
        carePlanId: updatedCarePlan.id,
        carePlan: updatedCarePlan,
        patientId
    };
    globalEventEmitter$1.publish(DomainEventNames$1.CarePlanActivated, event);
    return updatedCarePlan;
}

/**
 * Activate CoCM flag for patient
 *
 * Creates a new "cocm-enrolled" flag if one doesn't exist, or activates an existing
 * inactive flag. Validates no duplicate active flags exist.
 *
 * @param medplum - MedplumClient instance
 * @param input - CoCM flag activation data
 * @returns Created or updated flag resource
 * @throws Error if validation or operation fails
 * @agentic @benchmark/core
 */ async function activateCoCMFlag(medplum, input) {
    const { patient } = input;
    // Validate input
    if (!patient) {
        throw new Error("Patient is required");
    }
    if (!patient.id) {
        throw new Error("Patient must have an id");
    }
    const patientId = toPatientId(patient.id);
    // Check for existing CoCM flag
    const existingFlag = await findExistingCoCMFlag(medplum, patientId);
    // If flag exists and is already active, return it
    if (existingFlag && existingFlag.status === "active") {
        return existingFlag;
    }
    // Build flag resource
    const flag = existingFlag ? {
        ...existingFlag,
        status: "active",
        subject: buildPatientReference(patientId, getPatientDisplayName$1(patient))
    } : {
        resourceType: "Flag",
        status: "active",
        code: {
            coding: [
                {
                    system: FLAG_LOCAL_CODE_SYSTEM$1,
                    code: "cocm-enrolled",
                    display: "Enrolled In CoCM"
                }
            ],
            text: "Enrolled In CoCM"
        },
        category: [
            {
                coding: [
                    {
                        system: "http://terminology.hl7.org/CodeSystem/flag-category",
                        code: "admin",
                        display: "Administrative"
                    }
                ],
                text: "Administrative"
            }
        ],
        subject: buildPatientReference(patientId, getPatientDisplayName$1(patient))
    };
    // Create or update flag
    let result;
    if (flag.id) {
        result = await updateResource$1(medplum, flag);
        // Emit event
        if (!result.id || !patient.id) {
            throw new Error("Missing required IDs for event");
        }
        const event = {
            flagId: result.id,
            flag: result,
            patientId: patient.id
        };
        globalEventEmitter$1.publish(DomainEventNames$1.FlagUpdated, event);
    } else {
        result = await createResource$1(medplum, flag);
        // Emit event
        if (!result.id || !patient.id) {
            throw new Error("Missing required IDs for event");
        }
        const event = {
            flagId: result.id,
            flag: result,
            patientId: patient.id
        };
        globalEventEmitter$1.publish(DomainEventNames$1.FlagCreated, event);
    }
    return result;
}

/**
 * Clinical Notification Task constants and predicates.
 *
 * Clinical notifications are a distinct domain backed by FHIR `Task`, kept
 * strictly separate from care-plan and operational Tasks. These constants
 * define the discriminating `code.system` and the per-recipient read/unread
 * `businessStatus`, plus predicates used to slice notification Tasks from
 * operational Tasks in both directions.
 *
 * @agentic @benchmark/core
 */ /**
 * Canonical system URL for clinical notification Task types. A Task carrying a
 * `code.coding` in this system is a clinical notification, never an operational
 * or care-plan Task.
 * @agentic @benchmark/core
 */ const NOTIFICATION_TASK_TYPE_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/notification-task-type";
/**
 * The centralized clinical notification dictionary. Keyed by a stable SCREAMING
 * key; every entry is validated against {@link NotificationTypeDefinition}. This
 * is the one place to register, inspect, or reason about notification types.
 * @agentic @benchmark/core
 */ const NOTIFICATION_TYPE_DEFINITIONS = {
    ACUTE_RISK_ESCALATION: {
        code: "acute-risk-escalation",
        display: "Acute Risk Escalation",
        system: NOTIFICATION_TASK_TYPE_SYSTEM,
        description: "A member survey response crossed an acute clinical-risk threshold and needs prompt clinician review.",
        category: "clinical-risk",
        defaultPriority: "urgent",
        prominenceTier: "urgent",
        triggerSummary: "PHQ-9 / PHQ-9A item 9 >= 1, C-SSRS >= 3, or a high-acuity questionnaire risk band.",
        userConfigurable: false,
        alwaysOnFloor: true
    },
    SERVICE_APPROVAL: {
        code: "service-approval",
        display: "Service approval",
        system: NOTIFICATION_TASK_TYPE_SYSTEM,
        description: "A member's selected services are ready for the advocate to approve or deny.",
        category: "service-approval",
        defaultPriority: "routine",
        prominenceTier: "routine",
        triggerSummary: "The member's approve-selected-services task transitioned to ready (actionable).",
        userConfigurable: false,
        alwaysOnFloor: false
    },
    APPOINTMENT_SCHEDULED: {
        code: "appointment-scheduled",
        display: "Appointment scheduled",
        system: NOTIFICATION_TASK_TYPE_SYSTEM,
        description: "A booked appointment with a future start was newly recorded for the advocate's member.",
        category: "appointment",
        defaultPriority: "routine",
        prominenceTier: "routine",
        triggerSummary: "An Appointment was created with status booked and a start in the future.",
        userConfigurable: false,
        alwaysOnFloor: false
    }
};
/**
 * All registered notification type definitions.
 * @agentic @benchmark/core
 */ function listNotificationTypeDefinitions() {
    return Object.values(NOTIFICATION_TYPE_DEFINITIONS);
}
/**
 * Look up a notification type definition by its code. Returns undefined for an
 * unknown code.
 * @agentic @benchmark/core
 */ function getNotificationTypeDefinition(code) {
    return listNotificationTypeDefinitions().find((def)=>def.code === code);
}
/**
 * Canonical system URL for the per-recipient read/unread state carried on
 * `Task.businessStatus`. Kept distinct from `Task.status` so "read" is a
 * business fact, not a workflow-lifecycle transition (read !== work done).
 * @agentic @benchmark/core
 */ const NOTIFICATION_READ_STATUS_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/notification-read-status";
/**
 * Read/unread codes stored on `Task.businessStatus.coding`.
 * @agentic @benchmark/core
 */ const NOTIFICATION_READ_STATUS = {
    UNREAD: {
        code: "unread",
        display: "Unread"},
    READ: {
        code: "read",
        display: "Read"}
};
/**
 * FHIR token that matches any notification Task by system (system-only token,
 * trailing pipe). Use with the `code` search param for the notification slice,
 * or with `code:not` to exclude notifications from operational searches.
 * @agentic @benchmark/core
 */ const NOTIFICATION_TASK_CODE_TOKEN = `${NOTIFICATION_TASK_TYPE_SYSTEM}|`;
/**
 * True when the Task is a clinical notification (carries a coding in the
 * notification code system).
 * @agentic @benchmark/core
 */ function isNotificationTask(task) {
    return (task.code?.coding ?? []).some((coding)=>coding.system === NOTIFICATION_TASK_TYPE_SYSTEM);
}
/**
 * True when the notification Task carries the given notification code.
 * @agentic @benchmark/core
 */ function hasNotificationTaskCode(task, code) {
    return (task.code?.coding ?? []).some((coding)=>coding.system === NOTIFICATION_TASK_TYPE_SYSTEM && coding.code === code);
}
/**
 * Reads the per-recipient read/unread state from `Task.businessStatus`.
 * Returns undefined when absent.
 * @agentic @benchmark/core
 */ function getNotificationReadStatus(task) {
    const coding = task.businessStatus?.coding?.find((entry)=>entry.system === NOTIFICATION_READ_STATUS_SYSTEM);
    return coding?.code;
}

/**
 * FHIR Helper Functions
 *
 * Pure functions for constructing common FHIR data structures.
 * These are shared utilities used by builders across apps.
 */ /**
 * Create a FHIR Reference with required display value
 *
 * @param resourceType - The FHIR resource type
 * @param id - The resource ID (can be a real ID or urn:uuid for bundles)
 * @param display - Human-readable display name (required)
 * @returns Reference with display
 */ const buildReference = (resourceType, id, display)=>{
    if (!display || display.trim().length === 0) {
        throw new Error(`${resourceType} reference requires display name. ID: ${id}`);
    }
    // Support both real IDs and urn:uuid references for transaction bundles
    const reference = id.startsWith("urn:uuid:") ? id : `${resourceType}/${id}`;
    return {
        reference,
        display: display.trim()
    };
};

/**
 * Build an unread clinical notification Task. Display and default priority come
 * from the centralized {@link NOTIFICATION_TYPE_DEFINITIONS} registry, so a new
 * type needs no builder change.
 * @agentic @benchmark/core
 */ const buildNotificationTask = (input)=>{
    const code = input.code ?? NOTIFICATION_TYPE_DEFINITIONS.ACUTE_RISK_ESCALATION.code;
    const definition = getNotificationTypeDefinition(code);
    const display = definition?.display ?? code;
    const task = {
        resourceType: "Task",
        status: "ready",
        intent: "order",
        priority: input.priority ?? definition?.defaultPriority ?? "routine",
        code: {
            coding: [
                {
                    system: NOTIFICATION_TASK_TYPE_SYSTEM,
                    code,
                    display
                }
            ],
            text: display
        },
        businessStatus: {
            coding: [
                {
                    system: NOTIFICATION_READ_STATUS_SYSTEM,
                    code: NOTIFICATION_READ_STATUS.UNREAD.code,
                    display: NOTIFICATION_READ_STATUS.UNREAD.display
                }
            ],
            text: NOTIFICATION_READ_STATUS.UNREAD.display
        },
        description: input.description,
        for: buildReference("Patient", input.patientId, input.patientDisplay),
        authoredOn: input.authoredOn ?? new Date().toISOString()
    };
    if (input.focus) {
        task.focus = input.focus;
    }
    if (input.reason) {
        task.reasonReference = input.reason;
    }
    if (input.owner) {
        task.owner = input.owner;
    }
    return task;
};

/**
 * Get display name for a patient.
 * When a preferred name (FHIR nickname) exists, formats as "Legal Name (Preferred Name)".
 */ const getPatientDisplayName = (patient)=>{
    const officialName = patient.name?.find((n)=>n.use === "official") || patient.name?.[0];
    if (!officialName) {
        return `Patient ${patient.id}`;
    }
    const parts = [];
    if (officialName.given?.length) {
        parts.push(...officialName.given);
    }
    if (officialName.family) {
        parts.push(officialName.family);
    }
    if (parts.length === 0) {
        return `Patient ${patient.id}`;
    }
    const legalName = parts.join(" ");
    const nickname = patient.name?.find((n)=>n.use === "nickname" && n !== officialName);
    const preferredName = nickname?.given?.[0];
    if (preferredName) {
        return `${legalName} (${preferredName})`;
    }
    return legalName;
};
/**
 * The patient's name for a persisted FHIR reference `display`, or `undefined`
 * when they have no recorded name — `getPatientDisplayName`'s `Patient {id}`
 * placeholder reads as a UI fallback and is worse than no display at all on a
 * stored reference.
 */ const getPatientReferenceDisplay = (patient)=>{
    const display = getPatientDisplayName(patient);
    return display === `Patient ${patient.id}` ? undefined : display;
};
/**
 * Returns true when a gp-role-type code represents the Benchmark primary advocate.
 * Supports both current and legacy value codes.
 */ const isBenchmarkPrimaryAdvocateRoleCode = (valueCode)=>valueCode === GP_ROLE_TYPES.BENCHMARK_PRIMARY_ADVOCATE || valueCode === GP_ROLE_TYPES.BENCHMARK_LCSW;
/**
 * Get Benchmark primary advocate reference from patient's generalPractitioner array.
 * Function name preserved for backward compatibility.
 */ const getPatientBenchmarkLCSW = (patient)=>{
    return patient.generalPractitioner?.find((ref)=>ref.extension?.some((ext)=>ext.url === GP_ROLE_TYPE_EXTENSION && isBenchmarkPrimaryAdvocateRoleCode(ext.valueCode))) ?? null;
};
/**
 * Extracts CoCM consent (verified flag + date) from the Patient resource's
 * `cocm-consent` complex extension — populated via the patient edit form's
 * "CoCM Consent Verified" checkbox and "CoCM Consent Date" field
 * (`ReferralInformationSection`). This is distinct from the CoCM CarePlan's
 * `cocm-consent-date` extension (captured automatically at enrollment); see
 * `resolveCoCMConsent` in `cocm-eligibility.service` for how the two sources
 * are reconciled for billing/eligibility display.
 * @agentic @benchmark/core
 */ const getCoCMConsentFromPatient = (patient)=>{
    const cocmExtension = patient.extension?.find((ext)=>ext.url === COCM_CONSENT_EXTENSION_URL);
    if (!cocmExtension?.extension) return undefined;
    const verified = Boolean(cocmExtension.extension.find((sub)=>sub.url === "verified")?.valueBoolean);
    const date = cocmExtension.extension.find((sub)=>sub.url === "date")?.valueDate;
    return {
        verified,
        date
    };
};

/**
 * Extra wait added to Medplum's server hint so retries do not wake exactly on
 * the limiter boundary.
 * @agentic @benchmark/core
 */ const RATE_LIMIT_BUFFER_MS = 500;
/** Wait used for a throttled batch entry whose diagnostics are unparseable. */ const BATCH_RATE_LIMIT_DEFAULT_MS = 30_000;
/** Sleep utility. @agentic @benchmark/core */ const sleep = (ms)=>new Promise((resolve)=>setTimeout(resolve, ms));
/** Agreed default rate-limit retry policy. @agentic @benchmark/core */ const DEFAULT_RATE_LIMIT_POLICY = {
    maxRetries: 5,
    bufferMs: RATE_LIMIT_BUFFER_MS,
    baseDelayMs: 1000,
    maxDelayMs: 60_000,
    jitter: true,
    sleep
};
/** Extract `_msBeforeNext` (ms) from an OperationOutcome issue `diagnostics` JSON. */ const serverHintFromDiagnostics = (diagnostics)=>{
    if (!diagnostics) return undefined;
    try {
        const parsed = JSON.parse(diagnostics);
        return typeof parsed._msBeforeNext === "number" && parsed._msBeforeNext > 0 ? parsed._msBeforeNext : undefined;
    } catch  {
        return undefined;
    }
};
/** True when an OperationOutcome represents Medplum throttling. */ const isTooManyRequestsOutcome = (outcome)=>{
    if (outcome.id === "too-many-requests") return true;
    try {
        return getStatus(outcome) === 429;
    } catch  {
        return false;
    }
};
const errorMessage = (error)=>error instanceof Error ? error.message : String(error ?? "");
/**
 * Extract the first balanced `{...}` JSON object substring from a message
 * without regex backtracking (avoids ReDoS on adversarial input). Returns the
 * embedded `_msBeforeNext` if the substring parses, else undefined.
 */ const serverHintFromMessage = (message)=>{
    const start = message.indexOf("{");
    const end = message.lastIndexOf("}");
    if (start === -1 || end <= start) return undefined;
    return serverHintFromDiagnostics(message.slice(start, end + 1));
};
/** Detect a thrown rate-limit error. */ const detectErrorRateLimit = (error)=>{
    // 1. OperationOutcomeError / raw OperationOutcome (thrown ops + transactions).
    const outcome = error instanceof OperationOutcomeError ? error.outcome : isOperationOutcome(error) ? error : undefined;
    if (outcome && isTooManyRequestsOutcome(outcome)) {
        return {
            serverHintMs: serverHintFromDiagnostics(outcome.issue?.[0]?.diagnostics),
            fallbackMs: 0
        };
    }
    // 2. Defensive: a direct numeric `_msBeforeNext` property on the error.
    if (typeof error === "object" && error !== null) {
        const direct = error._msBeforeNext;
        if (typeof direct === "number" && direct > 0) {
            return {
                serverHintMs: direct,
                fallbackMs: 0
            };
        }
    }
    // 3. Fallback: message substring with optionally embedded JSON.
    const message = errorMessage(error);
    if (!message.includes("Too Many Requests")) return undefined;
    return {
        serverHintMs: serverHintFromMessage(message),
        fallbackMs: 0
    };
};
/** Detect a throttled entry inside a batch-response bundle. */ const detectBundleRateLimit = (bundle)=>{
    for (const entry of bundle.entry ?? []){
        const outcome = entry.response?.outcome;
        const status = entry.response?.status;
        const throttled = outcome && outcome.id === "too-many-requests" || typeof status === "string" && status.startsWith("429");
        if (throttled) {
            return {
                serverHintMs: serverHintFromDiagnostics(outcome?.issue?.[0]?.diagnostics),
                fallbackMs: BATCH_RATE_LIMIT_DEFAULT_MS
            };
        }
    }
    return undefined;
};
/**
 * Resolve the actual wait. A server hint is honored exactly (plus the policy
 * buffer, capped). Without a hint, wait at least the detection's fallback floor
 * (e.g. the 30s batch default) and at least the exponential backoff for the
 * attempt — whichever is larger — capped at `maxDelayMs`.
 */ const computeWaitMs = (detection, attempt, policy)=>{
    if (detection.serverHintMs !== undefined) {
        return Math.min(detection.serverHintMs + policy.bufferMs, policy.maxDelayMs);
    }
    let backoff = policy.baseDelayMs * 2 ** attempt;
    if (policy.jitter) backoff += Math.random() * policy.baseDelayMs;
    return Math.min(Math.max(detection.fallbackMs, Math.round(backoff)), policy.maxDelayMs);
};
const resolvePolicy = (policy)=>({
        ...DEFAULT_RATE_LIMIT_POLICY,
        ...policy
    });
/**
 * Run an async operation, retrying only on a thrown Medplum 429. Use for
 * single-resource ops and transaction bundles. Non-429 errors propagate.
 * @agentic @benchmark/core
 */ const withRateLimitRetry = async (fn, policy = {})=>{
    const resolved = resolvePolicy(policy);
    let attempt = 0;
    for(;;){
        try {
            return await fn();
        } catch (error) {
            const hint = detectErrorRateLimit(error);
            if (!hint || attempt >= resolved.maxRetries) throw error;
            const waitMs = computeWaitMs(hint, attempt, resolved);
            attempt += 1;
            resolved.onRetry?.({
                waitMs,
                attempt,
                maxRetries: resolved.maxRetries
            });
            await resolved.sleep(waitMs);
        }
    }
};
/**
 * Run a batch operation, retrying on a thrown 429 *and* on per-entry 429s in the
 * returned bundle. When retries are exhausted the last bundle is returned so the
 * caller still sees the throttled entries.
 * @agentic @benchmark/core
 */ const withBundleRateLimitRetry = async (fn, policy = {})=>{
    const resolved = resolvePolicy(policy);
    let attempt = 0;
    for(;;){
        let result;
        try {
            result = await fn();
        } catch (error) {
            const hint = detectErrorRateLimit(error);
            if (!hint || attempt >= resolved.maxRetries) throw error;
            const waitMs = computeWaitMs(hint, attempt, resolved);
            attempt += 1;
            resolved.onRetry?.({
                waitMs,
                attempt,
                maxRetries: resolved.maxRetries
            });
            await resolved.sleep(waitMs);
            continue;
        }
        const bundleHint = detectBundleRateLimit(result);
        if (!bundleHint || attempt >= resolved.maxRetries) return result;
        const waitMs = computeWaitMs(bundleHint, attempt, resolved);
        attempt += 1;
        resolved.onRetry?.({
            waitMs,
            attempt,
            maxRetries: resolved.maxRetries
        });
        await resolved.sleep(waitMs);
    }
};

/**
 * Collect distinct FHIR resource types referenced by bundle entries so
 * callers can invalidate Medplum search caches after a transaction.
 * @agentic @benchmark/core
 */ const collectBundleResourceTypes = (bundle)=>{
    const types = new Set();
    for (const entry of bundle.entry ?? []){
        if (entry.resource?.resourceType) {
            types.add(entry.resource.resourceType);
        }
        const requestUrl = entry.request?.url;
        if (requestUrl) {
            const resourceType = requestUrl.split(/[/?]/)[0];
            if (resourceType && /^[A-Z]/.test(resourceType)) {
                types.add(resourceType);
            }
        }
    }
    return [
        ...types
    ];
};
/**
 * Invalidate Medplum search caches for every resource type touched by a bundle.
 * `executeBatch` does not do this automatically (unlike `createResource`), so
 * event-driven refetches would otherwise read stale search results.
 * @agentic @benchmark/core
 */ const invalidateBundleSearchCaches = (medplum, bundle)=>{
    for (const resourceType of collectBundleResourceTypes(bundle)){
        medplum.invalidateSearches(resourceType);
    }
};
/**
 * Execute a FHIR batch transaction and invalidate search caches for touched types.
 * Retries automatically on a thrown 429 (transaction bundles) and on per-entry
 * 429s (non-atomic batch bundles) via the centralized rate-limit handler.
 * @agentic @benchmark/core
 */ const executeBatchWithCacheInvalidation = async (medplum, bundle, rateLimitPolicy = {})=>{
    const result = await withBundleRateLimitRetry(()=>medplum.executeBatch(bundle), rateLimitPolicy);
    invalidateBundleSearchCaches(medplum, bundle);
    invalidateBundleSearchCaches(medplum, result);
    return result;
};

/**
 * Creates a FHIR resource. Retries automatically on a Medplum 429 (safe even
 * for this POST: the limiter rejects before the write is processed).
 * @agentic @benchmark/core
 */ async function createResource(medplum, resource, rateLimitPolicy = {}) {
    return await withRateLimitRetry(()=>medplum.createResource(resource), rateLimitPolicy);
}
/**
 * Atomically creates a FHIR resource only if no existing resource matches the
 * given search `query` (FHIR conditional create / `If-None-Exist`). Returns the
 * existing resource on a match, avoiding check-then-create races and duplicate
 * writes on retries. Retries on a Medplum 429.
 * @agentic @benchmark/core
 */ async function createResourceIfNoneExist(medplum, resource, query, rateLimitPolicy = {}) {
    return await withRateLimitRetry(()=>medplum.createResourceIfNoneExist(resource, query), rateLimitPolicy);
}
/** Updates a FHIR resource, retrying on a Medplum 429. @agentic @benchmark/core */ async function updateResource(medplum, resource, rateLimitPolicy = {}) {
    return await withRateLimitRetry(()=>medplum.updateResource(resource), rateLimitPolicy);
}
/** Reads a FHIR resource by type and ID, retrying on a Medplum 429. @agentic @benchmark/core */ async function readResource(medplum, resourceType, id, rateLimitPolicy = {}) {
    return await withRateLimitRetry(()=>medplum.readResource(resourceType, id), rateLimitPolicy);
}
/** Searches for FHIR resources by type and query, retrying on a Medplum 429. @agentic @benchmark/core */ async function searchResources(medplum, resourceType, query, rateLimitPolicy = {}) {
    const result = await withRateLimitRetry(()=>medplum.search(resourceType, query), rateLimitPolicy);
    return result;
}

/**
 * Typed event emitter for domain and application-level events.
 * @agentic @benchmark/core
 */ /**
 * Type-safe event emitter for publishing and subscribing to domain events.
 * @agentic @benchmark/core
 */ class EventEmitter {
    /**
   * Register a handler called whenever a listener throws during publish.
   *
   * @param handler - Receives the error, event name, and payload
   */ onError(handler) {
        this.errorHandler = handler;
    }
    /**
   * Register a handler called after every successful publish.
   *
   * Useful for analytics / observability integrations (e.g. Datadog RUM).
   *
   * @param handler - Receives the event name and payload
   */ onPublish(handler) {
        this.publishHandler = handler;
    }
    /**
   * Subscribe to an event
   *
   * @param eventName - Name of the event to subscribe to
   * @param listener - Callback function to invoke when event is published
   * @returns Unsubscribe function
   */ subscribe(eventName, listener) {
        if (!this.listeners.has(eventName)) {
            this.listeners.set(eventName, new Set());
        }
        const listeners = this.listeners.get(eventName);
        listeners.add(listener);
        // Return unsubscribe function
        return ()=>{
            this.unsubscribe(eventName, listener);
        };
    }
    /**
   * Unsubscribe from an event
   *
   * @param eventName - Name of the event to unsubscribe from
   * @param listener - Callback function to remove
   */ unsubscribe(eventName, listener) {
        const listeners = this.listeners.get(eventName);
        if (listeners) {
            listeners.delete(listener);
            if (listeners.size === 0) {
                this.listeners.delete(eventName);
            }
        }
    }
    /**
   * Publish an event to all subscribers
   *
   * @param eventName - Name of the event to publish
   * @param payload - Event payload data
   */ publish(eventName, payload) {
        const listeners = this.listeners.get(eventName);
        if (listeners) {
            listeners.forEach((listener)=>{
                try {
                    listener(payload);
                } catch (error) {
                    // Log error but don't throw - allow other listeners to execute
                    console.error(`Error in event listener for ${eventName}:`, error);
                    // Forward to error handler (e.g. Datadog RUM) if registered
                    if (this.errorHandler) {
                        try {
                            this.errorHandler(error, eventName, payload);
                        } catch  {
                        // Swallow handler errors to prevent cascading failures
                        }
                    }
                }
            });
        }
        // Notify publish handler (e.g. Datadog RUM custom actions) if registered
        if (this.publishHandler) {
            try {
                this.publishHandler(eventName, payload);
            } catch  {
            // Swallow handler errors — observability should never break the app
            }
        }
    }
    /**
   * Remove all listeners for a specific event
   *
   * @param eventName - Name of the event (optional, removes all if not provided)
   */ removeAllListeners(eventName) {
        if (eventName) {
            this.listeners.delete(eventName);
        } else {
            this.listeners.clear();
        }
    }
    /**
   * Get the number of listeners for an event
   *
   * @param eventName - Name of the event
   * @returns Number of listeners
   */ listenerCount(eventName) {
        return this.listeners.get(eventName)?.size || 0;
    }
    constructor(){
        this.listeners = new Map();
    }
}
/**
 * Realm-global key under which the singleton emitter is stashed.
 *
 * The in-process event bus is only useful if there is exactly ONE emitter per
 * runtime: writes publish and views subscribe through the same instance. The
 * `@benchmark/core` build (bunchee) emits a separate copy of this module into
 * each package entrypoint (`./events`, `./actions`, `./hooks`, …), so a plain
 * `new EventEmitter()` module singleton is duplicated — writes published via
 * `@benchmark/core/actions` would never reach subscribers registered via
 * `@benchmark/core/events`, silently breaking every event-driven refetch in the
 * bundled app (source-level tests still pass, since they import one copy). We
 * anchor the instance on the realm's global registry via `Symbol.for` so every
 * duplicated module copy resolves to the same underlying emitter.
 */ const GLOBAL_EVENT_EMITTER_KEY = Symbol.for("@benchmark/core/globalEventEmitter");
const eventEmitterGlobal = globalThis;
/**
 * Global event emitter instance for application-wide domain events.
 *
 * Guaranteed to be a single instance per JS realm even when this module is
 * duplicated across bundle entrypoints (see {@link GLOBAL_EVENT_EMITTER_KEY}).
 * @agentic @benchmark/core
 */ const globalEventEmitter = eventEmitterGlobal[GLOBAL_EVENT_EMITTER_KEY] ?? (eventEmitterGlobal[GLOBAL_EVENT_EMITTER_KEY] = new EventEmitter());

/**
 * Domain event type definitions for application-wide event payloads.
 * @agentic @benchmark/core
 */ /**
 * Domain event name constants for type-safe publishing/subscribing.
 * @agentic @benchmark/core
 */ const DomainEventNames = {
    CareTimeCreated: "care-time.created",
    CareTimeDeleted: "care-time.deleted",
    CareTimeUpdated: "care-time.updated",
    CoCMBillingSummaryCreated: "cocm-billing-summary.created",
    CoCMBillingSummaryUpdated: "cocm-billing-summary.updated",
    CoCMBillingPacketSubmitted: "cocm-billing-packet.submitted",
    CoCMBillingPacketReopened: "cocm-billing-packet.reopened",
    NotificationTaskCreated: "notification-task.created",
    NotificationTaskUpdated: "notification-task.updated"};

const extractPatientId$1 = (patient)=>{
    if (!patient.id) {
        throw new Error("Patient must have an id to create a notification Task");
    }
    return patient.id;
};
/**
 * Build the conditional-create (`If-None-Exist`) search query keyed on the
 * triggering `focus` (the risk Observation) plus the notification `code`.
 *
 * Only `code` and `focus` are used — both are standard R4 Task search
 * parameters. The `for` parameter is intentionally omitted: Medplum does not
 * register `for` as a Task search parameter (it errors with "Unknown search
 * parameter: for"), which previously threw inside the conditional create and
 * failed every acute-risk notification. Patient scoping is unnecessary here
 * anyway — a `focus` Observation belongs to exactly one patient, so
 * `code + focus` is a globally unique, sufficient idempotency key. Without a
 * focus there is no stable dedupe key.
 */ const buildIdempotencyQuery = (code, focus)=>{
    const focusRef = focus?.reference;
    if (!focusRef) {
        return undefined;
    }
    const params = new URLSearchParams({
        code: `${NOTIFICATION_TASK_TYPE_SYSTEM}|${code}`,
        focus: focusRef
    });
    return params.toString();
};
/**
 * Idempotently create an unread clinical notification Task.
 * @agentic @benchmark/core
 */ async function createNotificationTask(medplum, input) {
    const patientId = extractPatientId$1(input.patient);
    const code = input.code ?? NOTIFICATION_TYPE_DEFINITIONS.ACUTE_RISK_ESCALATION.code;
    const task = buildNotificationTask({
        code,
        patientId,
        patientDisplay: getPatientDisplayName(input.patient) || "Patient",
        description: input.description,
        focus: input.focus,
        reason: input.reason,
        owner: input.owner,
        priority: input.priority
    });
    // Atomic, idempotent create when we have a stable focus key; otherwise an
    // unconditional create (no dedupe key available).
    const idempotencyQuery = buildIdempotencyQuery(code, input.focus);
    const result = idempotencyQuery ? await createResourceIfNoneExist(medplum, task, idempotencyQuery) : await createResource(medplum, task);
    if (result.id) {
        const event = {
            taskId: result.id,
            task: result,
            patientId
        };
        globalEventEmitter.publish(DomainEventNames.NotificationTaskCreated, event);
    }
    return result;
}

/** Lookup table for canonical activity order by v2 activity code. */ new Map(getAllCarePlanActivityOptions$1().map((activity)=>[
        activity.code,
        activity.order
    ]));
/**
 * The exact top-level clause labels that completion flows emit (single source of
 * truth for both the clause split and keyed detection below). A clause is only
 * `keyed`, and the top-level split only fires, when a clause begins with one of
 * these labels followed by `": "`. This is what keeps free text intact: a note
 * such as `Notes: Discussed options. Recommendation: PHP` has no allow-listed
 * label after its internal `". "`, so it is not split, and a standalone
 * `Summary: …` note is not mistaken for a keyed segment.
 */ const STATUS_REASON_LABELS = [
    "Selected services",
    "Services",
    "Notes",
    "Approved",
    "Denied",
    "Needs more information",
    "Outcome",
    "Follow-up",
    "Member accepted",
    "Member declined",
    "Member wants changes",
    "Appointments scheduled",
    "CoCM care plan built with activities"
];
new Set(STATUS_REASON_LABELS);
/**
 * Splits a status-reason string into top-level clauses. Completion flows join
 * clauses with `". "` and every non-leading clause begins with one of
 * {@link STATUS_REASON_LABELS} + `": "`. Breaking only before an allow-listed
 * label keeps sentence-ending periods inside a clause value from fragmenting it
 * — e.g. the `Dr. Jane Doe` in a consult string, or a `". Recommendation: "`
 * inside a free-text note, stays within its clause.
 *
 * Built from the constant allow-list above (labels are escaped); a bounded
 * alternation of literals with a single lookahead — no nested quantifiers, so it
 * stays linear on adversarial input (CodeQL `js/polynomial-redos`).
 */ new RegExp(`\\. +(?=(?:${STATUS_REASON_LABELS.map((label)=>label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")}): )`);

/**
 * Returns true when a ServiceRequest has a selected HealthcareService performer.
 * @agentic @benchmark/core
 */ const hasSelectedService = (sr)=>Boolean(sr.performer?.some((p)=>p.reference?.startsWith("HealthcareService/")));

/** Activity code for the Approve Selected Services task. @agentic @benchmark/core */ const APPROVE_SELECTED_SERVICES_CODE = "approve-selected-services";
/** True when the Task is a ready (actionable) approve-selected-services task. */ const isReadyApprovalTask = (task)=>task.status === "ready" && (task.code?.coding ?? []).some((coding)=>coding.code === APPROVE_SELECTED_SERVICES_CODE);
/** PHI-minimal notice body: the ask and the count only (name rides on `for`). */ const buildDescription$1 = (selectedCount)=>{
    const noun = selectedCount === 1 ? "service" : "services";
    return `Special Ops selected ${selectedCount} ${noun} that need your approval.`;
};
/**
 * Create the service-approval notification for a plan's ready approval task.
 * Returns the Task, or null when there is no ready approval task or the patient
 * cannot be resolved.
 * @agentic @benchmark/core
 */ async function createServiceApprovalNotificationTask(medplum, input) {
    const { carePlan } = input;
    if (!carePlan.id) {
        return null;
    }
    let approvalTask = input.approvalTask;
    if (!approvalTask || !isReadyApprovalTask(approvalTask)) {
        const tasks = await getActivityTasks(medplum, carePlan.id);
        approvalTask = tasks.find(isReadyApprovalTask);
    }
    if (!approvalTask?.id) {
        return null;
    }
    const patientId = carePlan.subject?.reference?.replace("Patient/", "");
    if (!patientId) {
        return null;
    }
    // D1: the notice is delivered only to the task's current owner. The approval
    // task is owned at creation (primary-advocate), so a missing owner is a
    // pathological state; do NOT broadcast a PHI-bearing notice to the team queue
    // — skip it rather than deliver more widely than D1 permits.
    const owner = approvalTask.owner;
    if (!owner) {
        return null;
    }
    let patient;
    try {
        patient = await medplum.readResource("Patient", patientId);
    } catch  {
        return null;
    }
    const serviceRequests = await getCarePlanServiceRequests(medplum, carePlan.id);
    const selectedCount = serviceRequests.filter(hasSelectedService).length;
    return createNotificationTask(medplum, {
        code: NOTIFICATION_TYPE_DEFINITIONS.SERVICE_APPROVAL.code,
        patient,
        description: buildDescription$1(selectedCount),
        // Idempotency key + navigation target: the approval Task itself.
        focus: {
            reference: `Task/${approvalTask.id}`,
            display: "Service approval task"
        },
        owner
    });
}

/**
 * The registry. Keyed by care-plan activity code. A `Map` (not a plain object) so
 * the lookup by an arbitrary activity-code string is not a dynamic object access.
 * Add a "notify when task X becomes ready" type here.
 */ const READY_ACTIVITY_NOTIFICATION_BUILDERS = new Map([
    [
        APPROVE_SELECTED_SERVICES_CODE,
        (medplum, { carePlan, task })=>createServiceApprovalNotificationTask(medplum, {
                carePlan,
                approvalTask: task
            })
    ]
]);
/**
 * Raise the registered notification (if any) for an activity that just became
 * `ready`. Best-effort: a notification failure is logged and never propagates, so
 * it can never affect the status transition that triggered it.
 * @agentic @benchmark/core
 */ async function dispatchReadyActivityNotification(medplum, args) {
    const build = READY_ACTIVITY_NOTIFICATION_BUILDERS.get(args.activityCode);
    if (!build) return;
    try {
        await build(medplum, {
            carePlan: args.carePlan,
            task: args.task
        });
    } catch (error) {
        console.error("ready-activity notification dispatch failed:", toLogSafeError$1(error));
    }
}

/**
 * Map unified activity status values to FHIR Task.status.
 * @agentic @benchmark/core
 */ const ACTIVITY_STATUS_TO_TASK_STATUS = {
    draft: "draft",
    ready: "ready",
    completed: "completed",
    cancelled: "cancelled",
    "on-hold": "on-hold"
};
/**
 * Update a CarePlan activity status
 *
 * Updates the status of a specific activity within a care plan.
 *
 * @param medplum - MedplumClient instance
 * @param input - Activity update data
 * @returns Updated CarePlan
 * @throws Error if validation fails
 * @agentic @benchmark/core
 */ async function updateCarePlanActivity(medplum, input) {
    const { carePlan, activityCode, taskId, newStatus, statusReason, practitionerRole, dueDateOverride, ownerOverride, documentedEncounter } = input;
    if (!carePlan.id) {
        throw new Error("Care plan ID is required to update activity");
    }
    // Exact resolution by Task reference takes precedence. This disambiguates
    // multi-occurrence activities (e.g. 6/12/18-week cadenced followups) where
    // several activity entries share the same code; a code-only lookup would
    // always resolve to the first occurrence.
    const targetTaskRef = taskId ? taskId.startsWith("Task/") ? taskId : `Task/${taskId}` : undefined;
    let activity = targetTaskRef ? carePlan.activity?.find((a)=>a.reference?.reference === targetTaskRef) : undefined;
    // Fall back to detail.code (legacy) when no exact Task reference match.
    if (!activity) {
        activity = carePlan.activity?.find((a)=>a.detail?.code?.coding?.[0]?.code === activityCode);
    }
    let taskRef = activity?.reference?.reference;
    // If still not found, search by Task reference and match by Task.code.
    // Collect every matching occurrence so multi-occurrence activities (repeated
    // CoCM followups, cadenced followups) can be disambiguated when no explicit
    // taskId was threaded.
    if (!activity) {
        const matches = [];
        for (const a of carePlan.activity || []){
            const ref = a.reference?.reference;
            if (!ref?.startsWith("Task/")) continue;
            const tId = ref.replace("Task/", "");
            const t = await readResource$1(medplum, "Task", tId);
            if (t.code?.coding?.[0]?.code === activityCode) {
                matches.push({
                    activity: a,
                    ref,
                    status: t.status
                });
            }
        }
        // When completing without an explicit taskId, target the first still-
        // actionable occurrence rather than the first overall — the earliest
        // occurrence is often already completed, and re-completing it leaves the
        // active task untouched (and its successor stranded in draft).
        const chosen = (newStatus === "completed" ? matches.find((m)=>m.status !== "completed" && m.status !== "cancelled") : undefined) ?? matches[0];
        if (chosen) {
            activity = chosen.activity;
            taskRef = chosen.ref;
        }
    }
    if (!activity) {
        throw new Error(`Activity with code "${activityCode}" not found in care plan`);
    }
    // The just-updated Task, captured so a ready-transition can hand it straight to
    // the notification dispatch without a refetch.
    let updatedTask;
    // For Task-backed activities, update Task.status as primary operation.
    // Skip detail mutation (no detail exists per cpl-3).
    if (taskRef?.startsWith("Task/")) {
        const taskId = taskRef.replace("Task/", "");
        const existingTask = await readResource$1(medplum, "Task", taskId);
        const taskStatus = ACTIVITY_STATUS_TO_TASK_STATUS[newStatus];
        // When resetting a previously-actioned task, append a note preserving
        // the prior status and the reason for the reset. This creates an
        // append-only audit trail on Task.note that survives status reuse.
        const isReset = existingTask.status !== "draft" && (newStatus === "ready" || newStatus === "draft") && statusReason;
        const resetNote = isReset ? [
            ...existingTask.note ?? [],
            {
                time: new Date().toISOString(),
                text: `Status reset (${existingTask.status} → ${newStatus}): ${statusReason}`
            }
        ] : existingTask.note;
        const taskUpdate = {
            ...existingTask,
            id: taskId,
            resourceType: "Task",
            status: taskStatus ?? existingTask.status,
            lastModified: new Date().toISOString(),
            ...statusReason && {
                statusReason: {
                    text: statusReason
                }
            },
            ...resetNote && {
                note: resetNote
            }
        };
        // Set due date when task becomes actionable (SLA or end-of-day default).
        // When dueDateOverride is explicitly provided (e.g. rejection restart), always apply it.
        if (newStatus === "ready" && (dueDateOverride || !existingTask.restriction?.period?.end)) {
            let dueDateIso;
            if (dueDateOverride) {
                dueDateIso = dueDateOverride;
            } else {
                const slaHours = getActivitySlaDurationHours(activityCode);
                if (slaHours) {
                    const dueDate = new Date();
                    dueDate.setTime(dueDate.getTime() + slaHours * 60 * 60 * 1000);
                    dueDateIso = dueDate.toISOString();
                } else {
                    dueDateIso = toDateInputValue(new Date());
                }
            }
            taskUpdate.restriction = {
                ...existingTask.restriction,
                period: {
                    end: dueDateIso
                }
            };
        }
        if (ownerOverride) {
            taskUpdate.owner = buildReferenceWithDisplay("PractitionerRole", ownerOverride.ref, ownerOverride.display);
        }
        // On completion, record the actual completer as Task.owner for audit trail.
        // This ensures clinical traceability when someone other than the assigned
        // owner completes the task.
        if (newStatus === "completed" && practitionerRole?.id) {
            const practitionerDisplay = practitionerRole.practitioner?.display || "Unknown Practitioner";
            taskUpdate.owner = buildReferenceWithDisplay("PractitionerRole", practitionerRole.id, practitionerDisplay);
        }
        if (newStatus === "completed") {
            taskUpdate.executionPeriod = {
                ...existingTask.executionPeriod,
                end: new Date().toISOString()
            };
            const extension = existingTask.extension?.filter((ext)=>ext.url !== TASK_DRAFT_FORM_STATE_EXTENSION_URL);
            taskUpdate.extension = extension?.length ? extension : undefined;
        }
        // Durable Task → Encounter link (D4): record the documenting Encounter as
        // a typed Task.output. Rides the same update as the status change, so the
        // link is atomic with completion. Idempotent: a retry after a partial
        // failure (Task updated, CarePlan write failed → user retries the submit)
        // must not append a duplicate link.
        const alreadyLinked = existingTask.output?.some((entry)=>entry.valueReference?.reference === `Encounter/${documentedEncounter?.id}`);
        if (newStatus === "completed" && documentedEncounter && !alreadyLinked) {
            taskUpdate.output = [
                ...existingTask.output ?? [],
                {
                    type: {
                        coding: [
                            {
                                system: TASK_OUTPUT_TYPE_SYSTEM,
                                code: TASK_OUTPUT_DOCUMENTED_ENCOUNTER_CODE,
                                display: "Documented Encounter"
                            }
                        ]
                    },
                    valueReference: {
                        reference: `Encounter/${documentedEncounter.id}`,
                        display: documentedEncounter.display
                    }
                }
            ];
        }
        updatedTask = await updateResource$1(medplum, taskUpdate);
    }
    // For legacy activities (detail-based), update detail.status.
    // For Task-backed, this is a no-op (no detail to mutate).
    const updatedCarePlan = updateCarePlanActivityStatus(carePlan, activityCode, newStatus, statusReason);
    // Add contributor if provided, otherwise use author as contributor
    const contributorRef = practitionerRole ? buildReferenceWithDisplay("PractitionerRole", practitionerRole.id, practitionerRole.practitioner?.display || "Unknown Practitioner") : carePlan.author;
    if (contributorRef) {
        updatedCarePlan.contributor = updatedCarePlan.contributor || [];
        const contributorReference = contributorRef.reference;
        const isAlreadyContributor = updatedCarePlan.contributor.some((c)=>c.reference === contributorReference);
        if (!isAlreadyContributor) {
            updatedCarePlan.contributor.push(contributorRef);
        }
    }
    // Save to server
    const savedCarePlan = await updateResource$1(medplum, {
        ...updatedCarePlan,
        id: carePlan.id
    });
    const patientId = savedCarePlan.subject?.reference?.replace("Patient/", "") || "";
    // Emit event
    if (!savedCarePlan.id) {
        throw new Error("Care plan ID is required after update");
    }
    const event = {
        carePlanId: savedCarePlan.id,
        carePlan: savedCarePlan,
        patientId
    };
    globalEventEmitter$1.publish(DomainEventNames$1.CarePlanUpdated, event);
    // An activity becoming actionable ("ready") may raise a clinical notification
    // (e.g. service approval). Dispatched centrally here — the one primitive every
    // ready-transition funnels through — keyed on the now-ready activity, so it
    // fires no matter which path advanced it. Best-effort: never affects the update.
    if (newStatus === "ready") {
        await dispatchReadyActivityNotification(medplum, {
            carePlan: savedCarePlan,
            activityCode,
            task: updatedTask
        });
    }
    return savedCarePlan;
}

/**
 * Resolve the linked Task for a CarePlan activity entry from a lookup map.
 * @agentic @benchmark/core
 */ const resolveActivityTask = (activity, tasksById)=>{
    const ref = activity.reference?.reference;
    if (!ref?.startsWith("Task/")) return undefined;
    return tasksById.get(ref.slice(5));
};
/**
 * Reconcile the ready/draft state of a CoCM CarePlan so exactly one open
 * activity — the first in array order — is `ready`.
 *
 * Writes only when a status actually needs to change (promote a leading `draft`
 * to `ready`, demote a trailing `ready`/`requested` to `draft`); an already-valid
 * plan is returned unchanged with no writes and no events. In-progress activities
 * are never demoted (they represent work a clinician has already begun).
 *
 * @param medplum - MedplumClient instance
 * @param carePlan - CarePlan to reconcile
 * @returns The reconciled CarePlan (or the input when no change was needed)
 * @agentic @benchmark/core
 */ async function reconcileCarePlanReadyState(medplum, carePlan) {
    if (getCarePlanType(carePlan) !== "cocm") {
        return carePlan;
    }
    const activities = carePlan.activity ?? [];
    if (activities.length === 0 || !carePlan.id) {
        return carePlan;
    }
    const tasks = await getActivityTasks(medplum, carePlan.id) ?? [];
    const tasksById = new Map(tasks.filter((task)=>task.id).map((task)=>[
            task.id,
            task
        ]));
    let current = carePlan;
    let firstOpenSeen = false;
    for (const activity of activities){
        const task = resolveActivityTask(activity, tasksById);
        if (!task) continue;
        const displayStatus = mapTaskStatusToActivityStatus(task.status);
        // Skip terminal (completed/cancelled) and paused (on-hold) activities — they
        // are neither the current step nor eligible to become one.
        if (displayStatus !== "ready" && displayStatus !== "draft") continue;
        const activityCode = task.code?.coding?.[0]?.code;
        const taskId = activity.reference?.reference;
        if (!activityCode || !taskId) continue;
        if (!firstOpenSeen) {
            firstOpenSeen = true;
            // First open activity must be actionable.
            if (task.status === "draft") {
                current = await updateCarePlanActivity(medplum, {
                    carePlan: current,
                    activityCode,
                    taskId,
                    newStatus: "ready"
                });
            }
        } else if (task.status === "ready" || task.status === "requested") {
            // Every later open activity must wait its turn.
            current = await updateCarePlanActivity(medplum, {
                carePlan: current,
                activityCode,
                taskId,
                newStatus: "draft"
            });
        }
    }
    return current;
}

/**
 * Add an activity to a CarePlan and create its backing Task
 *
 * Creates the Task first, then adds activity with reference ONLY (no detail)
 * to satisfy FHIR cpl-3. Flexible activities are appended to the activity list.
 *
 * @param medplum - MedplumClient instance
 * @param input - Activity addition data
 * @returns Updated CarePlan with activity.reference set
 * @throws Error if validation fails
 * @agentic @benchmark/core
 */ async function addCarePlanActivity(medplum, input) {
    const { carePlan, activityCode, targetDate, description, ownerOverride } = input;
    const targetDateValue = targetDate || undefined;
    if (!carePlan.id) {
        throw new Error("Care plan ID is required to add activity");
    }
    // Derive care plan type from category and validate activity is flexible for that type
    const carePlanType = carePlan.category?.[0]?.coding?.find((c)=>c.system === CARE_PLAN_CATEGORY_SYSTEM$1)?.code ?? null;
    if (!carePlanType) {
        throw new Error("Care plan must have a category with care-plan-type coding");
    }
    const flexibleActivities = getFlexibleActivitiesForType(carePlanType);
    const activityOption = flexibleActivities.find((activity)=>activity.code === activityCode);
    if (!activityOption) {
        throw new Error(`Activity code "${activityCode}" is not a valid flexible activity for care plan type "${carePlanType}"`);
    }
    const patientRef = carePlan.subject?.reference?.replace(/^Patient\//, "") || "";
    const patientDisplay = carePlan.subject?.display || carePlan.subject?.reference || "Patient";
    const authorRef = (carePlan.author?.reference ?? "").replace(/^PractitionerRole\//, "");
    const authorDisplay = carePlan.author?.display || "Unknown";
    const carePlanDisplay = carePlan.title || "Care Plan";
    // Resolve owner: explicit override > waterfall resolution > care plan author
    let ownerRef = authorRef || undefined;
    let ownerDisplay = authorDisplay;
    if (ownerOverride) {
        ownerRef = ownerOverride.ref;
        ownerDisplay = ownerOverride.display;
    } else if (patientRef) {
        const patient = await readResource$1(medplum, "Patient", patientRef);
        const resolvedOwner = await resolveActivityOwner(medplum, {
            activityCode,
            patient
        });
        if (resolvedOwner) {
            ownerRef = resolvedOwner.ref;
            ownerDisplay = resolvedOwner.display;
        }
    }
    // New activities are added at the end as draft - they will become ready
    // when their turn comes in the workflow progression
    const existingActivities = carePlan.activity || [];
    // Create the new Task as "draft" (will be activated when prior activities complete)
    const task = buildCarePlanActivityTask({
        activityCode,
        activityDisplay: getCarePlanActivityDisplay(activityCode),
        activityDescription: description?.trim() || getCarePlanActivityDescription(activityCode),
        carePlanRef: carePlan.id,
        carePlanDisplay,
        patientRef,
        patientDisplay,
        ownerRef,
        ownerDisplay,
        requesterRef: authorRef || undefined,
        requesterDisplay: authorDisplay,
        initialStatus: "draft",
        targetDate: targetDateValue
    });
    const createdTask = await createResource$1(medplum, task);
    if (!createdTask.id) {
        throw new Error("Task was created but has no id");
    }
    const newActivity = {
        reference: {
            reference: `Task/${createdTask.id}`,
            display: activityOption.display
        }
    };
    // Append to the end of the activity list
    const updatedActivities = [
        ...existingActivities,
        newActivity
    ];
    const updatedCarePlan = {
        ...carePlan,
        activity: updatedActivities
    };
    const savedCarePlan = await updateResource$1(medplum, {
        ...updatedCarePlan,
        id: carePlan.id
    });
    const patientId = savedCarePlan.subject?.reference?.replace("Patient/", "") || "";
    if (!savedCarePlan.id) {
        throw new Error("Care plan ID is required after update");
    }
    const event = {
        carePlanId: savedCarePlan.id,
        carePlan: savedCarePlan,
        patientId
    };
    globalEventEmitter$1.publish(DomainEventNames$1.CarePlanUpdated, event);
    // CoCM plans hold a flexible bag of activities added in `draft`. Enforce the
    // step-based invariant so the first open activity is always `ready` — this is
    // what prevents a plan with remaining work from reporting "no active tasks".
    // Intake/triage rely on strict sequential advancement and are left untouched.
    if (carePlanType === "cocm") {
        return reconcileCarePlanReadyState(medplum, savedCarePlan);
    }
    return savedCarePlan;
}

/**
 * Search params that exclude clinical notification Tasks from an operational
 * `Task` search. Spread into every broad operational Task query so
 * notifications never leak into task lists/counts/hooks.
 * @agentic @benchmark/core
 */ function buildOperationalTaskExclusionParams() {
    return {
        "code:not": NOTIFICATION_TASK_CODE_TOKEN
    };
}

/**
 * Adjust task dates for pause
 *
 * Finds all open tasks for a patient and extends their due dates by the pause duration.
 * Task status is preserved (not changed to "on-hold") to prevent tasks from being lost
 * if pause expires without manual unpause. UI will indicate pause state visually.
 *
 * @param medplum - MedplumClient instance
 * @param patient - Patient whose tasks should be adjusted
 * @param durationDays - Number of days to extend task due dates
 * @agentic @benchmark/core
 */ async function adjustTaskDatesForPause(medplum, patient, durationDays) {
    // Get all open tasks for this patient
    // Search by both 'for' and 'subject' to catch all cases
    // Some FHIR servers may index tasks differently
    // Include all "open" statuses: requested, received, accepted, ready, in-progress, on-hold
    const patientRef = `Patient/${patient.id}`;
    const openStatuses = "requested,received,accepted,ready,in-progress,on-hold";
    const excludeNotifications = buildOperationalTaskExclusionParams();
    // `subject` is the FHIR R4 Task search parameter for `Task.for`
    // (Medplum rejects a literal `for` param as unknown).
    const tasksBySubjectResult = await medplum.searchResources("Task", {
        subject: patientRef,
        status: openStatuses,
        ...excludeNotifications
    }).catch(()=>[]);
    // Deduplicate tasks by ID
    const taskMap = new Map();
    tasksBySubjectResult.forEach((task)=>{
        if (task?.resourceType === "Task" && task.id) {
            taskMap.set(task.id, task);
        }
    });
    const openTasks = Array.from(taskMap.values());
    if (openTasks.length === 0) {
        return;
    }
    // Adjust due dates only (preserve original task status)
    // This prevents tasks from being lost if pause expires without manual unpause
    const updatePromises = openTasks.filter((task)=>task.restriction?.period?.end).map(async (task)=>{
        const currentDueDate = new Date(task.restriction.period.end);
        const newDueDate = new Date(currentDueDate);
        // Use UTC methods to avoid timezone shifts
        newDueDate.setUTCDate(newDueDate.getUTCDate() + durationDays);
        return updateResource$1(medplum, {
            ...task,
            id: task.id,
            restriction: {
                ...task.restriction,
                period: {
                    end: newDueDate.toISOString()
                }
            }
        });
    });
    await Promise.all(updatePromises);
}

/**
 * Archive a task by marking it as cancelled and adding an archive reason note.
 *
 * @param medplum - MedplumClient instance
 * @param input - Archive task input containing task, reason, and author ID
 * @returns Promise resolving to archived task
 * @throws Error if update fails
 * @agentic @benchmark/core
 */ async function archiveTask(medplum, input) {
    const { task, reason, authorId } = input;
    const archivedTask = {
        ...task,
        id: task.id,
        status: "cancelled",
        lastModified: new Date().toISOString(),
        note: [
            ...task.note || [],
            {
                authorString: authorId,
                time: new Date().toISOString(),
                text: `Archived: ${reason}`
            }
        ]
    };
    return await updateResource$1(medplum, archivedTask);
}

/**
 * Batch update healthcare services with shared field values.
 * @agentic @benchmark/core
 */ async function batchUpdateHealthcareServices(medplum, input) {
    const { formData, primaryService, siblingServices } = input;
    // Resolve canonical state-coverage Location resources before building
    // the bundle. No-op when the form has no coverage areas selected.
    const enrichedFormData = await enrichCoverageAreasWithLocations(medplum, formData);
    // Build transaction bundle
    const { bundle, newLocationTempUrls, updatedServiceCount } = buildBatchUpdateHealthcareServicesBundle({
        formData: enrichedFormData,
        primaryService,
        siblingServices
    });
    // Submit bundle atomically
    const result = await submitBundle(medplum, bundle);
    // Extract updated services
    const healthcareServices = extractResourcesFromTransactionResult$1(result, "HealthcareService");
    // Emit Location events for newly created locations
    if (newLocationTempUrls.size > 0) {
        const createdLocations = extractResourcesFromTransactionResult$1(result, "Location");
        createdLocations.forEach((location)=>{
            if (location.id) {
                const event = {
                    locationId: location.id,
                    location,
                    healthcareServiceId: primaryService.id
                };
                globalEventEmitter$1.publish(DomainEventNames$1.LocationCreated, event);
            }
        });
    }
    // Emit update events for all services
    healthcareServices.forEach((service)=>{
        if (!service.id) return;
        const event = {
            healthcareServiceId: service.id,
            healthcareService: service
        };
        globalEventEmitter$1.publish(DomainEventNames$1.HealthcareServiceUpdated, event);
    });
    return {
        healthcareServices,
        updatedCount: updatedServiceCount
    };
}

/**
 * Build a cancelled care plan resource
 *
 * Pure function that constructs a cancelled CarePlan resource with updated activity statuses.
 * Does not save to server - returns the built resource.
 *
 * @param carePlan - Care plan to cancel (must have current activity statuses)
 * @param reason - Optional cancellation reason
 * @returns Cancelled CarePlan resource (not saved)
 * @agentic @benchmark/core
 */ function buildCancelledCarePlan(carePlan, reason) {
    // Update activity statuses for cancellation before updating care plan status
    const carePlanWithUpdatedActivities = updateActivitiesForCancellation(carePlan);
    const updatedCarePlan = {
        ...carePlanWithUpdatedActivities,
        id: carePlan.id,
        status: "revoked",
        period: {
            ...carePlan.period,
            end: new Date().toISOString()
        },
        // Explicitly preserve updated activities from carePlanWithUpdatedActivities
        activity: carePlanWithUpdatedActivities.activity
    };
    // Add cancellation reason as a note if provided
    if (reason) {
        updatedCarePlan.note = [
            ...carePlan.note || [],
            {
                text: `Cancelled: ${reason}`,
                time: new Date().toISOString()
            }
        ];
    }
    return updatedCarePlan;
}
/**
 * Cancel activity Tasks linked to a Task-backed CarePlan.
 *
 * For CarePlans with activity.reference (Task-backed), fetches all linked Tasks
 * via based-on search and cancels any that are not already completed/cancelled.
 *
 * @param medplum - MedplumClient instance
 * @param carePlanId - CarePlan ID to find linked Tasks
 * @param reason - Optional cancellation reason
 * @returns Number of Tasks cancelled
 */ async function cancelActivityTasks(medplum, carePlanId, reason) {
    const activityTasks = await getActivityTasks(medplum, carePlanId);
    const openTasks = activityTasks.filter((t)=>t.status !== "completed" && t.status !== "cancelled" && t.id);
    await Promise.all(openTasks.map((task)=>medplum.updateResource({
            ...task,
            status: "cancelled",
            lastModified: new Date().toISOString(),
            statusReason: {
                text: reason || "Care plan cancelled"
            }
        })));
    return openTasks.length;
}
/**
 * Cancel a care plan
 *
 * Atomically cancels a care plan and its linked activity Tasks in a single
 * FHIR transaction Bundle. This is the generic cancellation used when a
 * type-specific workflow (intake/triage/CoCM) is not needed.
 *
 * @param medplum - MedplumClient instance
 * @param input - CarePlan cancellation data
 * @returns Updated CarePlan
 * @throws Error if validation fails
 * @agentic @benchmark/core
 */ async function cancelCarePlan(medplum, input) {
    const { carePlan, reason } = input;
    if (!carePlan.id) {
        throw new Error("Care plan ID is required to cancel");
    }
    // Read the latest care plan from server to ensure we have current activity statuses
    const latestCarePlan = await medplum.readResource("CarePlan", carePlan.id);
    if (latestCarePlan.status === "completed") {
        throw new Error("Cannot cancel a completed care plan");
    }
    if (latestCarePlan.status === "revoked") {
        throw new Error("Care plan is already cancelled");
    }
    const patientId = latestCarePlan.subject?.reference?.replace("Patient/", "") || "";
    // Build cancelled care plan resource
    const cancelledCarePlanResource = buildCancelledCarePlan(latestCarePlan, reason);
    // Fetch activity Tasks for atomic cancellation
    let activityTasks = [];
    try {
        const result = await getActivityTasks(medplum, carePlan.id);
        if (result) activityTasks = result;
    } catch  {
    // Continue without activity tasks
    }
    const openActivityTasks = activityTasks.filter((t)=>!!t.id && t.status !== "completed" && t.status !== "cancelled");
    // Build atomic transaction Bundle
    const entries = [
        {
            resource: cancelledCarePlanResource,
            method: "PUT",
            url: `CarePlan/${cancelledCarePlanResource.id}`
        },
        ...buildCancelledTaskEntries(openActivityTasks, reason || "Care plan cancelled")
    ];
    const bundle = {
        resourceType: "Bundle",
        type: "transaction",
        entry: entries.map((entry)=>({
                ...entry.fullUrl && {
                    fullUrl: entry.fullUrl
                },
                request: {
                    method: entry.method || "POST",
                    url: entry.url || entry.resource.resourceType
                },
                resource: entry.resource
            }))
    };
    const result = await submitBundle(medplum, bundle);
    const cancelledCarePlan = extractSingleResourceFromTransactionResult$1(result, "CarePlan");
    if (!cancelledCarePlan?.id) {
        throw new Error("Care plan not found in transaction response");
    }
    // Emit event
    const event = {
        carePlanId: cancelledCarePlan.id,
        carePlan: cancelledCarePlan,
        patientId
    };
    globalEventEmitter$1.publish(DomainEventNames$1.CarePlanCancelled, event);
    return cancelledCarePlan;
}

/**
 * Cancel all active tasks for a patient.
 * @agentic @benchmark/core
 */ async function cancelTasks(medplum, input) {
    // Search for active tasks
    // Search by both 'for' and 'subject' to catch all cases
    // Some FHIR servers may index tasks differently
    const patientRef = `Patient/${input.patientId}`;
    // All open task statuses per FHIR Task status value set
    // Includes: requested, received, accepted, ready, in-progress, on-hold
    // Excludes: completed, cancelled, entered-in-error, draft
    const activeStatuses = "requested,received,accepted,ready,in-progress,on-hold";
    // Never cancel clinical notifications on patient disengagement (D4).
    const excludeNotifications = buildOperationalTaskExclusionParams();
    // `subject` is the FHIR R4 Task search parameter for `Task.for`
    // (Medplum rejects a literal `for` param as unknown).
    const tasksBySubjectResult = await medplum.searchResources("Task", {
        subject: patientRef,
        status: activeStatuses,
        ...excludeNotifications
    }).catch(()=>[]);
    // Deduplicate tasks by ID
    const taskMap = new Map();
    tasksBySubjectResult.forEach((task)=>{
        if (task?.resourceType === "Task" && task.id) {
            taskMap.set(task.id, task);
        }
    });
    const activeTasks = Array.from(taskMap.values());
    // Update each task to cancelled with reason
    const tasks = [];
    for (const task of activeTasks){
        if (!task.id) {
            continue; // Skip tasks without IDs (shouldn't happen due to filter above)
        }
        const updatedTask = {
            ...task,
            id: task.id,
            status: "cancelled",
            statusReason: {
                text: input.reason
            }
        };
        const updated = await updateResource$1(medplum, updatedTask);
        tasks.push(updated);
    }
    return tasks;
}

/**
 * Claim Task Action
 *
 * Race-safe atomic claim using optimistic locking (meta.versionId test op).
 * If Task.owner is already set, throws an error — the task was claimed by another user.
 * @agentic @benchmark/core
 */ /**
 * Atomically claim an unassigned task for the given PractitionerRole.
 *
 * Uses JSON Patch with a meta.versionId test op for optimistic locking.
 * If the task already has an owner, throws before attempting the patch.
 * If a concurrent claim changes the versionId, the patch will fail server-side.
 * @agentic @benchmark/core
 */ async function claimTask(medplum, input) {
    const task = await medplum.readResource("Task", input.taskId);
    if (task.owner) {
        throw new Error(`Task ${input.taskId} is already assigned to ${task.owner.display ?? task.owner.reference}`);
    }
    const ops = [];
    if (task.meta?.versionId) {
        ops.push({
            op: "test",
            path: "/meta/versionId",
            value: task.meta.versionId
        });
    }
    ops.push({
        op: "add",
        path: "/owner",
        value: {
            reference: `PractitionerRole/${input.claimantRef}`,
            display: input.claimantDisplay
        }
    });
    return medplum.patchResource("Task", input.taskId, ops);
}

/**
 * Close a Goal by marking it as completed
 *
 * Updates a Goal's lifecycle status to "completed" and sets the achievement
 * status. If statusDate is not provided, uses the current date.
 *
 * @param medplum - MedplumClient instance
 * @param input - Goal closure data
 * @returns Updated Goal resource
 * @throws Error if validation or update fails
 * @agentic @benchmark/core
 */ async function closeGoal(medplum, input) {
    const { goal, achievementStatus, statusDate, patient } = input;
    // Validate goal has ID
    if (!goal.id) {
        throw new Error("Goal must have an id for closure");
    }
    // Use provided statusDate or current date
    const finalStatusDate = statusDate || localCalendarDateTodayISO();
    // Build updated Goal with closure information
    const closedGoal = {
        ...goal,
        lifecycleStatus: "completed",
        achievementStatus: {
            coding: [
                {
                    code: achievementStatus,
                    display: achievementStatus
                }
            ]
        },
        statusDate: finalStatusDate
    };
    // Save to server
    const savedGoal = await updateResource$1(medplum, closedGoal);
    // Emit event
    if (!savedGoal.id || !patient.id) {
        throw new Error("Missing required IDs for event");
    }
    const event = {
        goalId: savedGoal.id,
        goal: savedGoal,
        achievementStatus,
        patientId: patient.id
    };
    globalEventEmitter$1.publish(DomainEventNames$1.GoalClosed, event);
    return savedGoal;
}

/**
 * Build a completed care plan resource
 *
 * Pure function that constructs a completed CarePlan resource.
 * Does not save to server - returns the built resource.
 *
 * @param carePlan - Care plan to complete
 * @param reason - Optional completion reason
 * @returns Completed CarePlan resource (not saved)
 * @agentic @benchmark/core
 */ function buildCompletedCarePlan(carePlan, reason) {
    const updatedCarePlan = {
        ...carePlan,
        id: carePlan.id,
        status: "completed",
        period: {
            ...carePlan.period,
            end: new Date().toISOString()
        }
    };
    // Add completion reason as a note if provided
    if (reason) {
        updatedCarePlan.note = [
            ...carePlan.note || [],
            {
                text: `Completed: ${reason}`,
                time: new Date().toISOString()
            }
        ];
    }
    return updatedCarePlan;
}
/**
 * Complete a care plan
 *
 * Marks a care plan as completed. This should only be called when all activities
 * are completed or the care plan is being closed prematurely.
 *
 * @param medplum - MedplumClient instance
 * @param input - CarePlan completion data
 * @returns Updated CarePlan
 * @throws Error if validation fails
 * @agentic @benchmark/core
 */ async function completeCarePlan(medplum, input) {
    const { carePlan, force = false, reason } = input;
    if (!carePlan.id) {
        throw new Error("Care plan ID is required to complete");
    }
    if (carePlan.status === "completed") {
        throw new Error("Care plan is already completed");
    }
    const tasksById = force ? undefined : await buildCarePlanTasksMap(medplum, carePlan);
    if (!force && !areAllActivitiesCompleted(carePlan, tasksById)) {
        throw new Error("Cannot complete care plan: not all activities are completed. Use force=true to complete anyway.");
    }
    const patientId = carePlan.subject?.reference?.replace("Patient/", "") || "";
    // Build completed care plan resource
    const completedCarePlanResource = buildCompletedCarePlan(carePlan, reason);
    const completedCarePlan = await updateResource$1(medplum, completedCarePlanResource);
    // Emit event
    if (!completedCarePlan.id) {
        throw new Error("Care plan ID is required after update");
    }
    const event = {
        carePlanId: completedCarePlan.id,
        carePlan: completedCarePlan,
        patientId
    };
    globalEventEmitter$1.publish(DomainEventNames$1.CarePlanCompleted, event);
    return completedCarePlan;
}

/**
 * Create a new CarePlan from template with Task-backed activities
 *
 * Creates CarePlan + activity Tasks atomically in a transaction. Tasks are created
 * with basedOn → CarePlan and CarePlan.activity.reference → each Task. Activity.detail
 * is preserved for backwards compatibility.
 *
 * @param medplum - MedplumClient instance
 * @param input - CarePlan creation data
 * @returns Created CarePlan resource (in active status) with activity.reference populated
 * @throws Error if validation or creation fails
 * @agentic @benchmark/core
 */ async function createCarePlan(medplum, input) {
    const { patient, type, practitionerRole, careTeamId, consentDate, periodStart, notes, replacesCarePlans, serviceRequestDetails, includeApproval, excludeCadencedFollowup } = input;
    if (!patient.id) {
        throw new Error("Patient must have an id for care plan creation");
    }
    // Auto-fetch CareTeam if not provided
    let resolvedCareTeamId = careTeamId;
    if (!resolvedCareTeamId) {
        const careTeams = await medplum.searchResources("CareTeam", {
            subject: `Patient/${patient.id}`,
            status: "active"
        });
        const benchmarkCareTeam = careTeams.find((ct)=>ct.name === "Benchmark Care Team" || ct.category?.[0]?.coding?.[0]?.code === "LA28865-6");
        resolvedCareTeamId = benchmarkCareTeam?.id;
    }
    const authorDisplay = practitionerRole.practitioner?.display || "Unknown Practitioner";
    // Resolve ServiceRequest for intake care plans
    let serviceRequestRef;
    let serviceRequestDisplay;
    if (type === "intake") {
        const serviceRequests = await medplum.searchResources("ServiceRequest", {
            subject: `Patient/${patient.id}`,
            code: "1003472007",
            status: "active"
        });
        if (serviceRequests.length > 0) {
            const referralServiceRequest = serviceRequests[0];
            if (referralServiceRequest?.id) {
                const refWithDisplay = buildReferenceWithDisplay("ServiceRequest", referralServiceRequest.id, referralServiceRequest.requester?.display || "Initial Referral");
                serviceRequestRef = referralServiceRequest.id;
                serviceRequestDisplay = refWithDisplay.display;
            }
        }
    }
    // Recent assessments for supportingInfo
    let supportingInfo;
    const recentAssessments = await medplum.searchResources("QuestionnaireResponse", {
        subject: `Patient/${patient.id}`,
        _sort: "-authored",
        _count: 3
    });
    if ((recentAssessments?.length ?? 0) > 0) {
        supportingInfo = recentAssessments.map((qr)=>({
                reference: `QuestionnaireResponse/${qr.id}`,
                display: qr.questionnaire?.split("/").pop() || "Assessment"
            }));
    }
    const periodStartDateTime = periodStart ? /^\d{4}-\d{2}-\d{2}$/.test(periodStart) ? `${periodStart}T12:00:00Z` : periodStart : undefined;
    let extension;
    if (consentDate && type === "cocm") {
        extension = [
            {
                url: COCM_CONSENT_DATE_EXTENSION_URL$1,
                valueDate: consentDate
            }
        ];
    }
    // Resolve per-activity owner overrides via centralized waterfall resolver
    const baseActivityCodes = getSequentialActivitiesForType(type).map((a)=>a.code);
    const activityCodesToResolve = [
        ...baseActivityCodes
    ];
    if (includeApproval) {
        activityCodesToResolve.push("approve-selected-services");
    }
    const taskOwnerOverrides = await resolveOwnerOverridesForPlan(medplum, {
        activityCodes: activityCodesToResolve,
        patient,
        currentUserRole: practitionerRole
    });
    const excludeActivityCodes = [];
    if (type === "triage" && excludeCadencedFollowup) {
        excludeActivityCodes.push("complete-cadenced-followup");
    }
    const bundle = buildCarePlanBundle({
        type,
        patientRef: patient.id,
        patientDisplay: getPatientDisplayName$1(patient),
        authorRef: practitionerRole.id,
        authorDisplay,
        careTeamId: resolvedCareTeamId,
        serviceRequestRef,
        serviceRequestDisplay,
        notes,
        replaces: replacesCarePlans?.map((plan)=>({
                reference: `CarePlan/${plan.id}`,
                display: plan.display
            })),
        supportingInfo,
        periodStart: periodStartDateTime,
        extension,
        includeApproval,
        initialStatus: "active",
        startFirstActivity: true
    }, undefined, serviceRequestDetails, Object.keys(taskOwnerOverrides).length > 0 ? taskOwnerOverrides : undefined, excludeActivityCodes.length > 0 ? {
        excludeActivityCodes
    } : undefined);
    const result = await submitBundle(medplum, bundle);
    // Extract CarePlan from transaction response.
    // Medplum transaction responses don't always include entry.resource,
    // so fall back to parsing entry.response.location and reading from the server.
    let createdCarePlan = (result.entry ?? []).map((e)=>e.resource).find((r)=>r?.resourceType === "CarePlan");
    if (!createdCarePlan) {
        const locationEntry = result.entry?.find((e)=>e.response?.location?.startsWith("CarePlan/"));
        if (locationEntry?.response?.location) {
            const carePlanId = locationEntry.response.location.split("/")[1];
            createdCarePlan = await medplum.readResource("CarePlan", carePlanId);
        }
    }
    if (!createdCarePlan) {
        throw new Error("Failed to create CarePlan from transaction bundle");
    }
    if (!createdCarePlan.id || !patient.id) {
        throw new Error("Missing required IDs for event");
    }
    const createdEvent = {
        carePlanId: createdCarePlan.id,
        carePlan: createdCarePlan,
        patientId: patient.id
    };
    globalEventEmitter$1.publish(DomainEventNames$1.CarePlanCreated, createdEvent);
    return createdCarePlan;
}

/**
 * Create a new Goal with baseline Observation atomically
 *
 * Creates a Goal resource and a baseline Observation (GAS score 0) in a single
 * atomic transaction. Both resources are created together or not at all.
 *
 * @param medplum - MedplumClient instance
 * @param input - Goal creation data
 * @returns Created Goal resource
 * @throws Error if validation or creation fails
 * @agentic @benchmark/core
 */ async function createGoal(medplum, input) {
    const { formData, patient, practitionerRole } = input;
    // Validate patient has ID
    if (!patient.id) {
        throw new Error("Patient must have an id for goal creation");
    }
    // Goals can now be created before official CoCM enrollment.
    // If an active CoCM plan already exists, link the goal to that plan.
    const cocmCarePlan = await getCarePlanByType(medplum, patient.id, "cocm");
    const activeCarePlan = cocmCarePlan?.status === "active" ? cocmCarePlan : undefined;
    // Build Goal resource from form data
    const goal = goalFromForm(formData, patient, practitionerRole, activeCarePlan, undefined);
    // Build baseline Observation (GAS score 0) for the goal
    // Use goal's startDate or current date for effectiveDateTime
    // Use noon UTC to avoid timezone shifts with date-only values
    const dateOnlyValue = formData.startDate || localCalendarDateTodayISO();
    const effectiveDateTime = dateOnlyToISO(dateOnlyValue);
    // Create temporary goal ID for baseline observation reference
    // This will be replaced with actual goal ID after transaction
    const tempGoalId = "temp-goal-id";
    const tempGoal = {
        ...goal,
        id: tempGoalId
    };
    const baselineProgressFormData = {
        goalId: tempGoalId,
        effectiveDateTime,
        gasScore: 0,
        note: undefined
    };
    const baselineObservation = goalProgressFromForm(baselineProgressFormData, tempGoal, patient, practitionerRole, undefined);
    // Build transaction bundle
    const bundle = buildGoalTransactionBundle(goal, baselineObservation);
    // Execute transaction atomically using bundle
    const result = await submitBundle(medplum, bundle);
    // Extract created Goal from transaction result
    const createdGoal = extractSingleResourceFromTransactionResult$1(result, "Goal");
    // Update baseline observation's focus reference with actual goal ID
    if (createdGoal.id) {
        const createdObservations = result.entry?.map((e)=>e.resource).filter((r)=>r?.resourceType === "Observation") || [];
        if (createdObservations.length > 0) {
            const baselineObs = createdObservations[0];
            if (baselineObs.focus?.[0] && baselineObs.focus[0].reference === `Goal/${tempGoalId}`) {
                baselineObs.focus[0].reference = `Goal/${createdGoal.id}`;
                // Update observation with correct reference
                await medplum.updateResource(baselineObs);
            }
        }
    }
    // Update CarePlan to add goal reference
    if (createdGoal.id && activeCarePlan?.id) {
        const goalDisplay = formData.description || "Goal";
        const updatedCarePlan = {
            ...activeCarePlan,
            id: activeCarePlan.id,
            goal: [
                ...activeCarePlan.goal || [],
                buildReferenceWithDisplay("Goal", createdGoal.id, goalDisplay)
            ]
        };
        await updateCarePlan$1(medplum, updatedCarePlan);
    }
    // Emit event
    if (!createdGoal.id || !patient.id) {
        throw new Error("Missing required IDs for event");
    }
    const event = {
        goalId: createdGoal.id,
        goal: createdGoal,
        patientId: patient.id,
        practitionerRoleId: practitionerRole?.id
    };
    globalEventEmitter$1.publish(DomainEventNames$1.GoalCreated, event);
    return createdGoal;
}

/**
 * Create a new Observation for goal progress tracking
 *
 * Creates an Observation resource with GAS score to track progress
 * toward a goal. The observation is linked to the goal via the focus reference.
 *
 * @param medplum - MedplumClient instance
 * @param input - Goal progress creation data
 * @returns Created Observation resource
 * @throws Error if validation or creation fails
 * @agentic @benchmark/core
 */ async function createGoalProgress(medplum, input) {
    const { formData, patient, practitionerRole } = input;
    // Validate GAS score range
    if (!isValidGASScore(formData.gasScore)) {
        throw new Error("GAS score must be between -2 and +2");
    }
    // Fetch Goal resource
    let goal;
    try {
        goal = await medplum.readResource("Goal", formData.goalId);
    } catch (error) {
        throw new Error(`Failed to load Goal ${formData.goalId}: ${error instanceof Error ? error.message : "Unknown error"}`, {
            cause: error
        });
    }
    // Build Observation resource from form data
    const observation = goalProgressFromForm(formData, goal, patient, practitionerRole, undefined);
    // Save to server
    const createdObservation = await createResource$1(medplum, observation);
    // Emit event
    if (!createdObservation.id || !goal.id || !patient.id) {
        throw new Error("Missing required IDs for event");
    }
    const event = {
        goalId: goal.id,
        observationId: createdObservation.id,
        observation: createdObservation,
        gasScore: formData.gasScore,
        patientId: patient.id
    };
    globalEventEmitter$1.publish(DomainEventNames$1.GoalProgressRecorded, event);
    return createdObservation;
}

/**
 * Validate patient form data.
 * @agentic @benchmark/core
 */ function validatePatientFormData(formData) {
    if (!formData["first-name"] || !formData["last-name"]) {
        throw new Error("First name and last name are required");
    }
}
/**
 * Create a patient resource
 *
 * This is a discrete action that creates only the Patient resource.
 * For comprehensive patient creation with all related resources (Coverage + RelatedPerson + CareTeam + ServiceRequest + Task),
 * use PatientFacade.create() which uses createPatientWithRelatedResources workflow.
 *
 * @param medplum - MedplumClient instance
 * @param formData - Patient form data
 * @param authorReference - Optional PractitionerRole reference for summary extension author
 * @returns Created patient resource
 * @throws Error if validation or creation fails
 * @agentic @benchmark/core
 */ async function createPatient(medplum, formData, authorReference) {
    // Validate input
    validatePatientFormData(formData);
    // Build patient resource using adapter
    const patient = patientFromForm(formData, authorReference);
    // Create resource
    const result = await createResource$1(medplum, patient);
    // Emit event
    if (!result.id) {
        throw new Error("Patient ID is required after creation");
    }
    const event = {
        patientId: result.id,
        patient: result
    };
    globalEventEmitter$1.publish(DomainEventNames$1.PatientCreated, event);
    return result;
}

/**
 * Create patient ServiceRequest for onboarding referral
 *
 * Creates a ServiceRequest if primary care provider is specified.
 * Only creates for new patients during onboarding.
 *
 * @param medplum - MedplumClient instance
 * @param patient - Patient resource
 * @param formData - Patient form data containing primary care provider information
 * @throws Error if operation fails
 * @agentic @benchmark/core
 */ async function createPatientReferralServiceRequest(medplum, patient, formData) {
    // Only create ServiceRequest if primary care provider is specified
    if (!formData["primary-care-provider"] || !formData["primary-care-provider-display"]) {
        console.warn("Skipping ServiceRequest creation: primary care provider or display name missing");
        return;
    }
    if (!patient.id) {
        throw new Error("Patient must have an id");
    }
    const referralDate = formData["referral-date"] ? `${formData["referral-date"]}T12:00:00Z` // Noon UTC avoids date shifts
     : `${localCalendarDateTodayISO()}T12:00:00Z`;
    const serviceRequestData = {
        resourceType: "ServiceRequest",
        status: "active",
        intent: "order",
        category: [
            {
                coding: [
                    {
                        system: "http://snomed.info/sct",
                        code: "305932003",
                        display: "Referral by own general practitioner"
                    }
                ],
                text: "Referral by own general practitioner"
            }
        ],
        code: {
            coding: [
                {
                    system: "http://snomed.info/sct",
                    code: "1003472007",
                    display: "Referral to patient navigation service"
                }
            ],
            text: "Referral to patient navigation service"
        },
        subject: buildReferenceWithDisplay("Patient", patient.id, getPatientDisplayName$1(patient)),
        authoredOn: referralDate,
        requester: buildReferenceWithDisplay("PractitionerRole", formData["primary-care-provider"], formData["primary-care-provider-display"]),
        note: [
            {
                text: "ServiceRequest created automatically during patient intake process"
            }
        ]
    };
    // Create new ServiceRequest
    await createResource$1(medplum, serviceRequestData);
}

/**
 * Create a pause flag for a patient
 *
 * Creates a Flag resource with the pause reason and duration.
 * The flag is created in "active" status with a period indicating
 * the planned pause duration.
 *
 * @param medplum - MedplumClient instance
 * @param input - Pause flag creation data
 * @returns Created pause flag
 * @throws Error if patient has no id or flag creation fails
 * @agentic @benchmark/core
 */ async function createPauseFlag(medplum, input) {
    const { patient, reason, durationDays } = input;
    // Validate patient has ID
    if (!patient.id) {
        throw new Error("Patient must have an id to create pause flag");
    }
    // Calculate period dates
    const now = new Date();
    const endDate = new Date(now);
    endDate.setUTCDate(endDate.getUTCDate() + durationDays);
    // Build patient display name
    const displayName = patient.name?.[0] ? `${patient.name[0].given?.[0] || ""} ${patient.name[0].family || ""}`.trim() || undefined : undefined;
    // Create pause flag
    const pauseFlag = {
        resourceType: "Flag",
        status: "active",
        code: {
            coding: [
                {
                    system: FLAG_LOCAL_CODE_SYSTEM$1,
                    code: "patient-paused",
                    display: "Paused"
                }
            ],
            text: "Paused"
        },
        category: [
            {
                coding: [
                    {
                        system: "http://terminology.hl7.org/CodeSystem/flag-category",
                        code: "admin",
                        display: "Administrative"
                    }
                ],
                text: "Administrative"
            }
        ],
        subject: {
            reference: `Patient/${patient.id}`,
            display: displayName
        },
        period: {
            start: now.toISOString(),
            end: endDate.toISOString()
        },
        extension: [
            {
                url: PAUSE_FLAG_REASON_EXTENSION_URL,
                valueString: reason
            }
        ]
    };
    return await createResource$1(medplum, pauseFlag);
}

const SERVICE_APPROVAL_CODE = NOTIFICATION_TYPE_DEFINITIONS.SERVICE_APPROVAL.code;
/** Normalize a raw id or `Task/{id}` to the `Task/{id}` reference form. */ const toTaskReference = (taskIdOrRef)=>taskIdOrRef.startsWith("Task/") ? taskIdOrRef : `Task/${taskIdOrRef}`;
const patientIdOf = (task)=>{
    const ref = task.for?.reference;
    return ref?.startsWith("Patient/") ? ref.slice("Patient/".length) : "";
};
/**
 * Find the live (non-terminal) service-approval notices whose `focus` is the
 * given approval task. Returns at most one in practice (one-per-task), but
 * defends against duplicates.
 */ const findLiveServiceApprovalNotices = async (medplum, approvalTaskRef)=>{
    const notices = await medplum.searchResources("Task", {
        code: NOTIFICATION_TASK_CODE_TOKEN,
        focus: approvalTaskRef
    });
    return notices.filter((notice)=>hasNotificationTaskCode(notice, SERVICE_APPROVAL_CODE) && notice.status !== "cancelled" && notice.status !== "completed");
};
const publishUpdated = (task)=>{
    const event = {
        taskId: task.id ?? "",
        task,
        patientId: patientIdOf(task)
    };
    globalEventEmitter.publish(DomainEventNames.NotificationTaskUpdated, event);
};
/**
 * Clear (cancel) the service-approval notice for an approval task once the work
 * is done or the member is paused (D8). Idempotent: a no-op when no live notice
 * exists. Returns the cleared Task, or null.
 * @agentic @benchmark/core
 */ async function clearServiceApprovalNotificationForTask(medplum, approvalTaskIdOrRef) {
    const ref = toTaskReference(approvalTaskIdOrRef);
    const live = await findLiveServiceApprovalNotices(medplum, ref);
    let cleared = null;
    const now = new Date().toISOString();
    for (const notice of live){
        const id = notice.id;
        if (!id) continue;
        const updated = await updateResource(medplum, {
            ...notice,
            id,
            status: "cancelled",
            executionPeriod: {
                ...notice.executionPeriod,
                end: now
            }
        });
        publishUpdated(updated);
        cleared = updated;
    }
    return cleared;
}
/**
 * Move the service-approval notice to a new owner when its approval task is
 * reassigned (D9/B7). Idempotent no-op when no live notice exists. Returns the
 * updated Task, or null.
 * @agentic @benchmark/core
 */ async function reassignServiceApprovalNotificationForTask(medplum, input) {
    const ref = toTaskReference(input.approvalTaskIdOrRef);
    const ownerReference = {
        reference: input.newOwnerRef.startsWith("PractitionerRole/") ? input.newOwnerRef : `PractitionerRole/${input.newOwnerRef}`,
        ...input.newOwnerDisplay ? {
            display: input.newOwnerDisplay
        } : {}
    };
    const live = await findLiveServiceApprovalNotices(medplum, ref);
    let updatedNotice = null;
    for (const notice of live){
        const id = notice.id;
        if (!id) continue;
        const updated = await updateResource(medplum, {
            ...notice,
            id,
            owner: ownerReference
        });
        publishUpdated(updated);
        updatedNotice = updated;
    }
    return updatedNotice;
}

/**
 * Validate practitioner creation input.
 * @agentic @benchmark/core
 */ function validatePractitionerInput(input) {
    const { formData } = input;
    if (!formData["practitioner-first-name"] || !formData["practitioner-last-name"]) {
        throw new Error("First name and last name are required");
    }
    if (!formData["practitioner-specialty"]) {
        throw new Error("Specialty is required");
    }
}
/**
 * Create a practitioner and practitionerRole
 *
 * @param medplum - MedplumClient instance
 * @param input - Practitioner creation data
 * @returns Created practitioner and practitionerRole resources
 * @throws Error if validation or creation fails
 * @agentic @benchmark/core
 */ async function createPractitioner(medplum, input) {
    // Validate input
    validatePractitionerInput(input);
    // Build bundle
    const bundle = buildCreatePractitionerBundle(input);
    // Submit bundle
    const result = await submitBundle(medplum, bundle);
    // Extract practitioner and practitionerRole from result
    const practitioner = extractSingleResourceFromTransactionResult$1(result, "Practitioner");
    let practitionerRole = extractSingleResourceFromTransactionResult$1(result, "PractitionerRole");
    // Emit events
    if (!practitioner.id) {
        throw new Error("Practitioner ID is required after creation");
    }
    if (!practitionerRole.id) {
        throw new Error("PractitionerRole ID is required after creation");
    }
    // CRITICAL FIX: Ensure PractitionerRole reference uses actual Practitioner ID
    // FHIR servers should resolve temporary UUIDs automatically, but if the reference
    // still contains a temporary UUID, update it to the actual Practitioner ID
    const practitionerReference = practitionerRole.practitioner?.reference;
    const expectedPractitionerReference = `Practitioner/${practitioner.id}`;
    if (practitionerReference && practitionerReference !== expectedPractitionerReference && (practitionerReference.startsWith("urn:uuid:") || !practitionerReference.startsWith("Practitioner/"))) {
        // Update the PractitionerRole reference to use the actual Practitioner ID
        const displayName = practitionerRole.practitioner?.display || `${practitioner.name?.[0]?.given?.[0] || ""} ${practitioner.name?.[0]?.family || ""}`.trim();
        practitionerRole = {
            ...practitionerRole,
            practitioner: {
                reference: expectedPractitionerReference,
                display: displayName
            }
        };
        // Update the PractitionerRole resource to persist the correct reference
        const updatedPractitionerRole = await updateResource$1(medplum, practitionerRole);
        if (!updatedPractitionerRole.id) {
            throw new Error("PractitionerRole ID is required after update");
        }
        practitionerRole = updatedPractitionerRole;
    }
    // Ensure IDs are present (should already be validated above, but TypeScript needs this)
    if (!practitionerRole.id) {
        throw new Error("PractitionerRole ID is required");
    }
    const practitionerEvent = {
        practitionerId: practitioner.id,
        practitioner,
        practitionerRoleId: practitionerRole.id,
        practitionerRole
    };
    globalEventEmitter$1.publish(DomainEventNames$1.PractitionerCreated, practitionerEvent);
    const practitionerRoleEvent = {
        practitionerRoleId: practitionerRole.id,
        practitionerRole,
        practitionerId: practitioner.id
    };
    globalEventEmitter$1.publish(DomainEventNames$1.PractitionerRoleCreated, practitionerRoleEvent);
    return {
        practitioner,
        practitionerRole
    };
}

/**
 * Deactivate CoCM flag for patient
 *
 * Finds and deactivates the "cocm-enrolled" flag. If no flag exists or flag is
 * already inactive, returns null (graceful handling).
 *
 * @param medplum - MedplumClient instance
 * @param input - CoCM flag deactivation data
 * @returns Updated flag resource or null if no flag found or already inactive
 * @throws Error if update operation fails
 * @agentic @benchmark/core
 */ async function deactivateCoCMFlag(medplum, input) {
    const { patient } = input;
    // Validate input
    if (!patient) {
        throw new Error("Patient is required");
    }
    if (!patient.id) {
        throw new Error("Patient must have an id");
    }
    // Find existing CoCM flag
    const existingFlag = await findExistingCoCMFlag(medplum, patient.id);
    // If no flag exists or already inactive, return null (graceful handling)
    if (!existingFlag || existingFlag.status !== "active") {
        return null;
    }
    // Deactivate flag
    if (!existingFlag.id) {
        throw new Error("Flag ID is required for deactivation");
    }
    const deactivatedFlag = await updateResource$1(medplum, {
        ...existingFlag,
        id: existingFlag.id,
        status: "inactive"
    });
    // Emit event
    if (!deactivatedFlag.id || !patient.id) {
        throw new Error("Missing required IDs for event");
    }
    const event = {
        flagId: deactivatedFlag.id,
        flag: deactivatedFlag,
        patientId: patient.id
    };
    globalEventEmitter$1.publish(DomainEventNames$1.FlagUpdated, event);
    return deactivatedFlag;
}

/**
 * Deactivate a pause flag
 *
 * Updates the flag status to "inactive" and sets the period end
 * to the current time, effectively ending the pause period.
 *
 * @param medplum - MedplumClient instance
 * @param pauseFlag - Pause flag to deactivate
 * @returns Deactivated pause flag
 * @throws Error if flag has no id or update fails
 * @agentic @benchmark/core
 */ async function deactivatePauseFlag(medplum, pauseFlag) {
    if (!pauseFlag.id) {
        throw new Error("Pause flag must have an id to deactivate");
    }
    const now = new Date();
    const deactivatedFlag = {
        ...pauseFlag,
        id: pauseFlag.id,
        status: "inactive",
        period: {
            ...pauseFlag.period,
            end: now.toISOString()
        }
    };
    return await updateResource$1(medplum, deactivatedFlag);
}

/**
 * Endorse (publish) a draft Library document.
 * @throws Error if the actor/library is invalid or the document is not a draft
 * @agentic @benchmark/core
 */ async function endorseLibrary(medplum, input) {
    const { library, actor } = input;
    if (!actor?.name) {
        throw new Error("A signed-in endorser is required");
    }
    if (!library.id) {
        throw new Error("Library id is required to endorse");
    }
    if (library.status !== "draft") {
        throw new Error("Only a draft document can be endorsed");
    }
    const updated = buildLibraryResource({
        action: "endorse",
        actor,
        existing: library
    });
    const result = await updateResource$1(medplum, updated);
    if (!result.id) {
        throw new Error("Missing required ID for event");
    }
    const event = {
        libraryId: result.id,
        library: result
    };
    globalEventEmitter$1.publish(DomainEventNames$1.LibraryEndorsed, event);
    return result;
}

/**
 * Put a care plan on hold
 *
 * Temporarily suspends a care plan. This is useful when patient engagement
 * is paused or there are other temporary holds.
 *
 * @param medplum - MedplumClient instance
 * @param input - CarePlan hold data
 * @returns Updated CarePlan
 * @throws Error if validation fails
 * @agentic @benchmark/core
 */ async function holdCarePlan(medplum, input) {
    const { carePlan, reason } = input;
    if (!carePlan.id) {
        throw new Error("Care plan ID is required to put on hold");
    }
    if (carePlan.status !== "active") {
        throw new Error("Only active care plans can be put on hold");
    }
    const patientId = carePlan.subject?.reference?.replace("Patient/", "") || "";
    const updatedCarePlan = {
        ...carePlan,
        id: carePlan.id,
        status: "on-hold"
    };
    // Add hold reason as a note if provided
    if (reason) {
        updatedCarePlan.note = [
            ...carePlan.note || [],
            {
                text: `On Hold: ${reason}`,
                time: new Date().toISOString()
            }
        ];
    }
    const heldCarePlan = await updateResource$1(medplum, updatedCarePlan);
    // Emit event
    if (!heldCarePlan.id) {
        throw new Error("Care plan ID is required after update");
    }
    const event = {
        carePlanId: heldCarePlan.id,
        carePlan: heldCarePlan,
        patientId
    };
    globalEventEmitter$1.publish(DomainEventNames$1.CarePlanHeld, event);
    return heldCarePlan;
}

/**
 * FHIR-related URLs used throughout the application
 */ // HL7 FHIR Base URLs
const HL7_TERMINOLOGY_BASE = "http://terminology.hl7.org";
// Organization Base URLs
const ORGANIZATION_BASE = "http://benchmarkhealth.com/fhir";
/**
 * Base URL for Benchmark Health custom FHIR extensions
 *
 * All custom StructureDefinition URLs should be derived from this base.
 * Use this constant to ensure consistency across the codebase.
 *
 * @example
 * ```ts
 * const myExtension = `${EXTENSION_BASE}/my-extension-name`;
 * // => "http://benchmarkhealth.com/fhir/StructureDefinition/my-extension-name"
 * ```
 */ const EXTENSION_BASE = `${ORGANIZATION_BASE}/StructureDefinition`;
// CodeSystem URLs
const CODE_SYSTEM = {
    // External
    CPT: "http://www.ama-assn.org/go/cpt"};

/**
 * Current observation LOINC codes for assessment scores
 * Used for creating new Observation resources from questionnaire responses
 *
 * IMPORTANT: These codes should ONLY be used for Observation resources that contain
 * calculated total scores. For questionnaire definitions, use QUESTIONNAIRE_LOINC_CODES
 * from questionnaire.ts which contains the panel codes.
 *
 * Validation: ✅ All codes verified against official LOINC database (2025-01-08)
 * See: packages/medplum/LOINC_CODE_MAPPINGS.md for detailed mapping documentation
 */ const OBSERVATION_LOINC_CODES = {
    /** ASRS Adult ADHD Self-Report Scale Total Score - Organizational code (no official LOINC) */ ASRS_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/asrs-total-score",
    /** AUDIT-C Total Score - LOINC 75626-2 ✅ VALIDATED */ AUDIT_C_TOTAL_SCORE: "75626-2",
    /** C-SSRS Columbia Suicide Severity Rating Scale Risk Level - Organizational code (no official LOINC) */ C_SSRS_SEVERITY: "https://benchmarkhealth.org/fhir/CodeSystem/c-ssrs-severity",
    /** GAD-7 Total Score - LOINC 70274-6 ✅ VALIDATED */ GAD7_TOTAL_SCORE: "70274-6",
    /** GAD-2 Total Score - Organizational code (the legacy LOINC 89204-7 is deprecated; the adaptive short check uses an org code) */ GAD2_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/gad-2-total-score",
    /** UCLA-3 Three-Item Loneliness Scale Total Score - Organizational code (no official LOINC) */ UCLA3_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/ucla-3-total-score",
    /** WSAS Work and Social Adjustment Scale Total Score - Organizational code (no official LOINC) */ WSAS_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/wsas-total-score",
    /** OCI-4 Obsessive-Compulsive Inventory Total Score - Organizational code (no official LOINC) */ OCI_4_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/oci-4-total-score",
    /** PC-PTSD-5 Total Score - LOINC 102017-1 ✅ VALIDATED */ PC_PTSD_5_TOTAL_SCORE: "102017-1",
    /** PSC-17 Pediatric Symptom Checklist Total Score - Organizational code (no official LOINC) */ PSC_17_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/psc-17-total-score",
    /** PHQ-9 Total Score - LOINC 44261-6 ✅ VALIDATED */ PHQ9_TOTAL_SCORE: "44261-6",
    /** PHQ-9A (Adolescent) Total Score - LOINC 89204-2 ✅ VALIDATED */ PHQ9A_TOTAL_SCORE: "89204-2",
    /** PHQ-4 Total Score - LOINC 70272-0 ✅ VALIDATED */ PHQ4_TOTAL_SCORE: "70272-0",
    /** SBIRT-ED Eating Disorder Screening Score - Organizational code (no official LOINC) */ SBIRT_ED_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/sbirt-ed-score",
    /** TAPS-1 Substance Use Screening Score - Organizational code (no official LOINC) */ TAPS_1_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/taps-1-score",
    /** TAPS-2 Substance Use Assessment Score - Organizational code (no official LOINC) */ TAPS_2_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/taps-2-score",
    /** WPAI Work Productivity and Activity Impairment Score - Organizational code (no official LOINC) */ WPAI_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/wpai-shp-score",
    /** WHO-5 Well-Being Index Total Score - Organizational code (no official LOINC) */ WHO5_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/who-5-total-score",
    /** Health Leads Social Needs Screening Toolkit Score - Organizational code (no official LOINC) */ SDOH_TOTAL_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/sdoh-health-leads-score",
    /** Mirah Complexity composite score - Mirah-proprietary (no official LOINC) */ MIRAH_COMPLEXITY_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-complexity-score",
    /** Mirah Severity composite score - Mirah-proprietary (no official LOINC) */ MIRAH_SEVERITY_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-severity-score",
    /** NPS 0–10 likelihood score — organizational code (no official LOINC) */ NPS_SCORE: "https://benchmarkhealth.org/fhir/CodeSystem/nps-score"
};
/**
 * Current observation types for assessment scores
 * FHIR R4-compliant observation types from LOINC
 * http://hl7.org/fhir/ValueSet/observation-codes
 * These are TOTAL SCORE codes for questionnaire observations
 * This is the UNIFIED source that replaces the previous duplicated constants
 */ const OBSERVATION_TYPES = [
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/asrs-total-score",
        display: "ASRS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "asrs",
        description: "Adult ADHD Self-Report Scale",
        minValue: 0,
        maxValue: 24
    },
    {
        code: "75626-2",
        display: "AUDIT-C",
        system: "http://loinc.org",
        fieldKey: "audit-c",
        description: "Alcohol Use Disorders Identification Test - Consumption",
        minValue: 0,
        maxValue: 12
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/gad-2-total-score",
        display: "GAD-2",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "gad-2",
        description: "Generalized Anxiety Disorder 2-item short check",
        minValue: 0,
        maxValue: 6
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/ucla-3-total-score",
        display: "UCLA-3",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "ucla-3",
        description: "Three-Item Loneliness Scale (UCLA)",
        minValue: 3,
        maxValue: 9
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/wsas-total-score",
        display: "WSAS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "wsas",
        description: "Work and Social Adjustment Scale",
        minValue: 0,
        maxValue: 40
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/c-ssrs-severity",
        display: "C-SSRS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "c-ssrs",
        description: "Columbia Suicide Severity Rating Scale",
        minValue: 0,
        maxValue: 5
    },
    {
        code: "70274-6",
        display: "GAD-7",
        system: "http://loinc.org",
        fieldKey: "gad-7",
        description: "Generalized Anxiety Disorder-7",
        minValue: 0,
        maxValue: 21
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/oci-4-total-score",
        display: "OCI-4",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "oci-4",
        description: "Obsessive-Compulsive Inventory-4",
        minValue: 0,
        maxValue: 16
    },
    {
        code: "102017-1",
        display: "PC-PTSD-5",
        system: "http://loinc.org",
        fieldKey: "pc-ptsd-5",
        description: "Primary Care PTSD Screen for DSM-5",
        minValue: 0,
        maxValue: 5
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/psc-17-total-score",
        display: "PSC-17",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "psc-17",
        description: "Pediatric Symptom Checklist-17",
        minValue: 0,
        maxValue: 34
    },
    {
        code: "44261-6",
        display: "PHQ-9",
        system: "http://loinc.org",
        fieldKey: "phq-9",
        description: "Patient Health Questionnaire-9",
        minValue: 0,
        maxValue: 27
    },
    {
        code: "89204-2",
        display: "PHQ-9A",
        system: "http://loinc.org",
        fieldKey: "phq-9a",
        description: "Patient Health Questionnaire-9 Modified for Teens",
        minValue: 0,
        maxValue: 27
    },
    {
        code: "70272-0",
        display: "PHQ-4",
        system: "http://loinc.org",
        fieldKey: "phq-4",
        description: "Patient Health Questionnaire-4",
        minValue: 0,
        maxValue: 12
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/prapare-risk-score",
        display: "PRAPARE",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "prapare",
        description: "Protocol for Responding to and Assessing Patients' Assets, Risks, and Experiences",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/sbirt-ed-score",
        display: "SBIRT-ED",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "sbirt-ed",
        description: "SBIRT Eating Disorder Screener",
        minValue: 0,
        maxValue: 5
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/taps-1-score",
        display: "TAPS-1",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "taps-1",
        description: "TAPS Screener (Part 1)",
        minValue: 0,
        maxValue: 16
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/taps-2-score",
        display: "TAPS-2",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "taps-2",
        description: "TAPS Assessment (Part 2)",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/wpai-impairment-score",
        display: "WPAI",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "wpai",
        description: "Work Productivity and Activity Impairment",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/who-5-total-score",
        display: "WHO-5",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "who-5",
        description: "World Health Organization Well-Being Index",
        minValue: 0,
        maxValue: 25
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/sdoh-health-leads-score",
        display: "SDOH",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "sdoh",
        description: "Social Needs Screening Toolkit (Health Leads)",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-complexity-score",
        display: "Mirah Complexity",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "mirah-complexity",
        description: "Mirah Complexity Composite Score",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/mirah-severity-score",
        display: "Mirah Severity",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "mirah-severity",
        description: "Mirah Severity Composite Score",
        minValue: 0,
        maxValue: 100
    },
    {
        code: "https://benchmarkhealth.org/fhir/CodeSystem/nps-score",
        display: "NPS",
        system: "https://benchmarkhealth.org/fhir/CodeSystem",
        fieldKey: "nps",
        description: "Net Promoter Score (0–10)",
        minValue: 0,
        maxValue: 10
    }
];
// Select options for forms
OBSERVATION_TYPES.map((type)=>({
        value: type.code,
        label: type.display
    }));
/**
 * Get the complete observation coding for a given LOINC code
 * Returns undefined if the code is not valid for observations
 */ function getObservationCoding(code) {
    const observationType = OBSERVATION_TYPES.find((type)=>type.code === code);
    return observationType ? {
        system: observationType.system,
        code: observationType.code,
        display: observationType.display
    } : undefined;
}

/**
 * FHIR extension URL marking a care-time entry as excluded from CoCM billing.
 * Absent or `valueBoolean: false` means the entry is billable (backward
 * compatible — no historical data carries the extension). See the care-time
 * non-billable toggle plan (2026-07-23).
 * @agentic @benchmark/core
 */ const CARE_TIME_NON_BILLABLE_EXTENSION_URL = `${EXTENSION_BASE}/care-time-non-billable`;
/**
 * True only when the entry carries the non-billable extension set to `true`.
 * Any other shape (missing extension, `false`, absent `valueBoolean`) is
 * treated as billable.
 * @agentic @benchmark/core
 */ const isCareTimeNonBillable = (observation)=>(observation.extension ?? []).some((ext)=>ext.url === CARE_TIME_NON_BILLABLE_EXTENSION_URL && ext.valueBoolean === true);
/**
 * Care-time observation code system (Benchmark-minted).
 * Holds the single `care-management-minutes` code (D3).
 * @agentic @benchmark/core
 */ const CARE_TIME_CODE_SYSTEM = "https://benchmarkhealth.org/fhir/CodeSystem/care-time-type";
/**
 * Benchmark observation category system used as a sub-classification
 * alongside the HL7 `activity` category.
 * @agentic @benchmark/core
 */ const CARE_TIME_CATEGORY_SYSTEM = "https://benchmarkhealth.org/fhir/CodeSystem/observation-category";
/**
 * Care-time method system encoding the source surface that produced
 * the entry (`task-completion`, `progress-update`, `call`, ...).
 * @agentic @benchmark/core
 */ const CARE_TIME_METHOD_SYSTEM = "https://benchmarkhealth.org/fhir/CodeSystem/care-time-method";
/**
 * UCUM system URL — used on `valueQuantity.system` for the `min` unit.
 * @agentic @benchmark/core
 */ const UCUM_SYSTEM = "http://unitsofmeasure.org";
/**
 * HL7 observation-category code used as the primary care-time category.
 * Pairs with the Benchmark `care-time` sub-category coding.
 * @agentic @benchmark/core
 */ const ACTIVITY_OBSERVATION_CATEGORY = {
    coding: [
        {
            system: "http://terminology.hl7.org/CodeSystem/observation-category",
            code: "activity",
            display: "Activity"
        }
    ]
};
/**
 * Benchmark `care-time` sub-category coding paired with the HL7
 * `activity` category on every care-time Observation.
 * @agentic @benchmark/core
 */ const CARE_TIME_OBSERVATION_CATEGORY = {
    coding: [
        {
            system: CARE_TIME_CATEGORY_SYSTEM,
            code: "care-time",
            display: "Care Time"
        }
    ]
};
/**
 * Token search expression for FHIR queries that target the Benchmark
 * `care-time` category (e.g. `category={CARE_TIME_CATEGORY_TOKEN}`).
 * @agentic @benchmark/core
 */ const CARE_TIME_CATEGORY_TOKEN = `${CARE_TIME_CATEGORY_SYSTEM}|care-time`;
/**
 * Single observation code for all care-time entries.
 * @agentic @benchmark/core
 */ const CARE_TIME_OBSERVATION_CODE_VALUE = "care-management-minutes";
/**
 * `Observation.code` payload for every care-time entry (D3 LOCKED).
 * @agentic @benchmark/core
 */ const CARE_TIME_OBSERVATION_CODE = {
    coding: [
        {
            system: CARE_TIME_CODE_SYSTEM,
            code: CARE_TIME_OBSERVATION_CODE_VALUE,
            display: "Care management minutes"
        }
    ],
    text: "Care management minutes"
};
/**
 * Source-surface method options carried on `Observation.method` (D3 LOCKED).
 * @agentic @benchmark/core
 */ const CARE_TIME_METHOD_OPTIONS = [
    {
        code: "task-completion",
        display: "Task Completion"
    },
    {
        code: "progress-update",
        display: "Progress Update"
    },
    {
        code: "action-list-quick-add",
        display: "Action List Quick Add"
    },
    {
        code: "patient-profile-quick-add",
        display: "Patient Profile Quick Add"
    },
    {
        code: "intake-call",
        display: "Intake Call"
    },
    {
        code: "intake-documentation",
        display: "Intake Documentation"
    },
    {
        code: "cocm-followup",
        display: "CoCM Follow-Up"
    },
    {
        code: "general-interaction",
        display: "General Interaction"
    },
    {
        code: "call",
        display: "Call"
    },
    {
        code: "sms",
        display: "SMS"
    }
];
/**
 * Set of all valid care-time methods for fast membership checks.
 * @agentic @benchmark/core
 */ new Set(CARE_TIME_METHOD_OPTIONS.map((o)=>o.code));
/**
 * Build the FHIR CodeableConcept payload for an `Observation.method` value.
 * @agentic @benchmark/core
 */ const getCareTimeMethodCoding = (method)=>{
    const option = CARE_TIME_METHOD_OPTIONS.find((o)=>o.code === method);
    const display = option?.display ?? method;
    return {
        coding: [
            {
                system: CARE_TIME_METHOD_SYSTEM,
                code: method,
                display
            }
        ],
        text: display
    };
};
/**
 * UCUM `min` quantity coding used on every care-time Observation's
 * `valueQuantity`. Helper centralizes the unit shape.
 * @agentic @benchmark/core
 */ const buildCareTimeMinutesQuantity = (value)=>({
        value,
        unit: "min",
        system: UCUM_SYSTEM,
        code: "min"
    });
/**
 * Complete roster of every assessment instrument that can enter a patient's
 * chart, mapped to the code its score Observation carries. This is the ONE
 * place that enumerates all instruments; billing eligibility is derived from it
 * as a denylist (see {@link BILLING_INELIGIBLE_ASSESSMENTS}) so a newly added
 * instrument is never silently billable — it must be classified here.
 *
 * Observations are clinical chart data first; billing eligibility is a separate
 * decision this module owns. A `code: ""` entry is a categorical instrument
 * (PGI-S, PGI-C, Safety Plan, Drug Use) that emits NO numeric score
 * Observation; every such instrument is billing-ineligible and must appear in
 * {@link BILLING_INELIGIBLE_ASSESSMENTS}.
 *
 * This list is source-agnostic: eligibility reads Observations by code, so a
 * matching score unlocks billing whether it was written by the in-app
 * clinician encounter, a fixed remote survey link, or the adaptive screener.
 *
 * Two eligibility nuances the code-based list CANNOT enforce on its own:
 *   - AUDIT-C: only a FULLY administered AUDIT-C is billing-valid (2026-09-30).
 *     A gated "Never" first item is a genuine complete 0/12 and counts; an
 *     abandoned/partial administration must NOT emit a score Observation. Both
 *     share the one AUDIT-C score code, so completeness has to be enforced
 *     where the Observation is written, not here. Follow-up tracked on the
 *     billing-parity ticket.
 *   - Legacy PHQ-2 (deprecated LOINC 55758-7) is never billing-valid. It is not
 *     an administered instrument today, so it is intentionally absent from this
 *     roster; the lock test guards that its code never appears in the eligible
 *     list.
 * @agentic @benchmark/core
 */ const ALL_ASSESSMENTS = {
    PHQ4: OBSERVATION_LOINC_CODES.PHQ4_TOTAL_SCORE,
    PHQ9: OBSERVATION_LOINC_CODES.PHQ9_TOTAL_SCORE,
    PHQ9A: OBSERVATION_LOINC_CODES.PHQ9A_TOTAL_SCORE,
    GAD7: OBSERVATION_LOINC_CODES.GAD7_TOTAL_SCORE,
    GAD2: OBSERVATION_LOINC_CODES.GAD2_TOTAL_SCORE,
    PC_PTSD_5: OBSERVATION_LOINC_CODES.PC_PTSD_5_TOTAL_SCORE,
    PSC_17: OBSERVATION_LOINC_CODES.PSC_17_TOTAL_SCORE,
    C_SSRS: OBSERVATION_LOINC_CODES.C_SSRS_SEVERITY,
    ASRS: OBSERVATION_LOINC_CODES.ASRS_TOTAL_SCORE,
    OCI_4: OBSERVATION_LOINC_CODES.OCI_4_TOTAL_SCORE,
    TAPS_1: OBSERVATION_LOINC_CODES.TAPS_1_TOTAL_SCORE,
    TAPS_2: OBSERVATION_LOINC_CODES.TAPS_2_TOTAL_SCORE,
    SBIRT_ED: OBSERVATION_LOINC_CODES.SBIRT_ED_TOTAL_SCORE,
    AUDIT_C: OBSERVATION_LOINC_CODES.AUDIT_C_TOTAL_SCORE,
    UCLA3: OBSERVATION_LOINC_CODES.UCLA3_TOTAL_SCORE,
    WSAS: OBSERVATION_LOINC_CODES.WSAS_TOTAL_SCORE,
    WPAI: OBSERVATION_LOINC_CODES.WPAI_TOTAL_SCORE,
    WHO5: OBSERVATION_LOINC_CODES.WHO5_TOTAL_SCORE,
    SDOH: OBSERVATION_LOINC_CODES.SDOH_TOTAL_SCORE,
    MIRAH_COMPLEXITY: OBSERVATION_LOINC_CODES.MIRAH_COMPLEXITY_SCORE,
    MIRAH_SEVERITY: OBSERVATION_LOINC_CODES.MIRAH_SEVERITY_SCORE,
    NPS: OBSERVATION_LOINC_CODES.NPS_SCORE,
    PGI_S: "",
    PGI_C: "",
    SAFETY_PLAN: "",
    DRUG_USE: ""
};
/**
 * Instruments that are administered but are NOT billing-valid for CoCM
 * eligibility (D18). This is the denylist over {@link ALL_ASSESSMENTS}: every
 * key here is subtracted to produce {@link BILLING_ELIGIBLE_ASSESSMENTS}. Each
 * entry names WHY it is excluded so the decision stays auditable. Confirmed
 * with clinical/compliance (2026-06-22, refined 2026-09-30).
 *
 * Note: an instrument absent from BOTH this denylist and the eligible set can
 * only happen if a new key is added to ALL_ASSESSMENTS without classifying it;
 * the exact-set lock test in `care-time.constants.test.ts` fails loudly in that
 * case rather than letting an unreviewed instrument become billable.
 * @agentic @benchmark/core
 */ const BILLING_INELIGIBLE_ASSESSMENTS = {
    PHQ4: "Ultra-brief combined screener; not a standalone severity measure.",
    GAD2: "Two-item anxiety short check; not billing-valid (opens GAD-7, which is).",
    UCLA3: "Loneliness scale; outside the billing-valid clinical set.",
    WSAS: "Everyday-functioning scale; not a clinical severity measure.",
    WPAI: "Work productivity/activity; functional, not clinical severity.",
    WHO5: "Well-being index; not a billing-valid severity measure.",
    SDOH: "Social-needs screen; not a clinical severity measure.",
    MIRAH_COMPLEXITY: "Vendor composite score; not a billing-valid instrument.",
    MIRAH_SEVERITY: "Vendor composite score; not a billing-valid instrument.",
    NPS: "Member-feedback likelihood score; not clinical.",
    PGI_S: "Categorical patient self-rating; no numeric score Observation.",
    PGI_C: "Categorical patient self-rating; no numeric score Observation.",
    SAFETY_PLAN: "Care-planning artifact; no score Observation.",
    DRUG_USE: "House-modified categorical drug screen; not billing-valid (2026-09-30) and no numeric score Observation."
};
// Eligible-by-policy = every instrument NOT on the denylist. Confirmed valid
// (2026-09-30): PHQ-9, PHQ-9A, GAD-7, PC-PTSD-5, PSC-17, C-SSRS, ASRS, OCI-4,
// TAPS-1, TAPS-2, SBIRT-ED, and AUDIT-C (full administration only). Every one
// carries a real score code; the `.filter` below is a guard, and the lock test
// asserts no eligible-by-policy instrument is categorical (`code: ""`) — a
// categorical instrument must be denied above, never silently dropped here.
const BILLING_ELIGIBLE_POLICY_KEYS = Object.keys(ALL_ASSESSMENTS).filter((key)=>!(key in BILLING_INELIGIBLE_ASSESSMENTS));
/**
 * The single billing-eligible assessment list, honored regardless of which
 * entry surface (in-app encounter, remote survey, adaptive screener) wrote the
 * score. Derived as {@link ALL_ASSESSMENTS} minus
 * {@link BILLING_INELIGIBLE_ASSESSMENTS}, then narrowed to instruments that
 * actually emit a score Observation (non-empty code). Each entry's system is
 * resolved the same way `buildObservationFromScore` stamps the score
 * Observation (`getObservationCoding(code)?.system ?? "http://loinc.org"`), so
 * the eligibility search always matches what questionnaire completion writes.
 * Within the 6-week freshness window these unlock 99492 / 99493 / 99494.
 * @agentic @benchmark/core
 */ const BILLING_ELIGIBLE_ASSESSMENTS = BILLING_ELIGIBLE_POLICY_KEYS.map((key)=>ALL_ASSESSMENTS[key]).filter((code)=>code !== "").map((code)=>({
        system: getObservationCoding(code)?.system ?? "http://loinc.org",
        code
    }));
/**
 * Codes-only view of {@link BILLING_ELIGIBLE_ASSESSMENTS} for value comparisons
 * and tests.
 * @agentic @benchmark/core
 */ BILLING_ELIGIBLE_ASSESSMENTS.map((e)=>e.code);
/**
 * System-aware comma-delimited token search expression for the eligible
 * assessments (FHIR uses comma for OR within a single search parameter, and
 * `system|code` for the system-qualified token). Supports mixed LOINC and
 * organizational systems. Used by the eligibility service and the registry hook.
 * @agentic @benchmark/core
 */ const COCM_ELIGIBLE_ASSESSMENT_CODES_TOKEN = BILLING_ELIGIBLE_ASSESSMENTS.map((e)=>`${e.system}|${e.code}`).join(",");
/**
 * Assessment freshness window for CoCM billing eligibility (D18 — 6 weeks).
 * Applied uniformly to every rule that requires an assessment.
 * @agentic @benchmark/core
 */ const COCM_ASSESSMENT_FRESHNESS_WEEKS = 6;

/**
 * Build the FHIR Observation resource for a care-time entry. Pure mapping.
 *
 * If `existingObservation` is provided, the function preserves its `id`
 * and `meta` and overlays the new fields — used by the edit flow in the
 * activity-log drawer and by the intake-modal "update auto-created
 * Communication entry" path (D4 no-double-write).
 * @agentic @benchmark/core
 */ const buildCareTimeObservation = (form, existingObservation)=>{
    if (!Number.isFinite(form.minutes) || form.minutes <= 0) {
        throw new Error("Care-time minutes must be a positive number");
    }
    if (!form.patientId) {
        throw new Error("Care-time entry requires a patientId");
    }
    if (!form.performerRoleId) {
        throw new Error("Care-time entry requires a performerRoleId (performer)");
    }
    const observation = existingObservation ? {
        ...existingObservation
    } : {
        resourceType: "Observation",
        status: "final",
        code: {
            ...CARE_TIME_OBSERVATION_CODE
        }
    };
    // Preserve existing id for updates; status stays as-is unless we're
    // creating, in which case ensure status=final.
    if (!existingObservation) {
        observation.status = "final";
    }
    observation.category = [
        {
            ...ACTIVITY_OBSERVATION_CATEGORY
        },
        {
            ...CARE_TIME_OBSERVATION_CATEGORY
        }
    ];
    observation.code = {
        ...CARE_TIME_OBSERVATION_CODE
    };
    observation.method = getCareTimeMethodCoding(form.method);
    observation.subject = buildReference("Patient", form.patientId, form.patientDisplay);
    observation.performer = [
        buildReference("PractitionerRole", form.performerRoleId, form.performerDisplay)
    ];
    observation.valueQuantity = buildCareTimeMinutesQuantity(form.minutes);
    if (form.effectivePeriodStart && form.effectivePeriodEnd) {
        observation.effectivePeriod = {
            start: form.effectivePeriodStart,
            end: form.effectivePeriodEnd
        };
        delete observation.effectiveDateTime;
    } else {
        observation.effectiveDateTime = form.effectiveDate;
        delete observation.effectivePeriod;
    }
    observation.issued = new Date().toISOString();
    if (form.taskId) {
        observation.focus = [
            buildReference("Task", form.taskId, form.taskDisplay ?? "Task")
        ];
    } else if (!existingObservation) {
        delete observation.focus;
    }
    if (form.carePlanId) {
        observation.basedOn = [
            buildReference("CarePlan", form.carePlanId, form.carePlanDisplay ?? "Care Plan")
        ];
    } else if (!existingObservation) {
        delete observation.basedOn;
    }
    if (form.encounterId) {
        observation.encounter = buildReference("Encounter", form.encounterId, form.encounterDisplay ?? `Encounter ${form.encounterId}`);
    } else if (!existingObservation) {
        delete observation.encounter;
    }
    if (form.communicationId) {
        observation.derivedFrom = [
            buildReference("Communication", form.communicationId, `Communication ${form.communicationId}`)
        ];
    } else if (!existingObservation) {
        delete observation.derivedFrom;
    }
    if (form.notes && form.notes.trim().length > 0) {
        observation.note = [
            {
                text: form.notes.trim()
            }
        ];
    } else if (!existingObservation) {
        delete observation.note;
    } else {
        observation.note = undefined;
    }
    // Upsert the non-billable flag, tri-state on `form.nonBillable` and never
    // clobbering other extensions the Observation may already carry:
    //   true      → stamp the marker (excluded from CoCM billing)
    //   false     → remove the marker (explicitly billable)
    //   undefined → leave the entry's existing flag untouched, so update paths
    //               that don't manage this flag (e.g. encounter re-saves that
    //               rebuild the intake entries) can't silently strip it. An
    //               omitted flag must not be read as "make it billable".
    if (form.nonBillable !== undefined) {
        const otherExtensions = (observation.extension ?? []).filter((ext)=>ext.url !== CARE_TIME_NON_BILLABLE_EXTENSION_URL);
        if (form.nonBillable) {
            observation.extension = [
                ...otherExtensions,
                {
                    url: CARE_TIME_NON_BILLABLE_EXTENSION_URL,
                    valueBoolean: true
                }
            ];
        } else if (otherExtensions.length > 0) {
            observation.extension = otherExtensions;
        } else {
            delete observation.extension;
        }
    }
    return observation;
};

/**
 * Convert care-time form data into a FHIR Observation. Pure.
 * @agentic @benchmark/core
 */ const careTimeFromForm = (form, existingObservation)=>buildCareTimeObservation(form, existingObservation);

const validate = (input)=>{
    const { formData } = input;
    if (!formData) throw new Error("formData is required");
    if (!formData.patientId) throw new Error("patientId is required");
    if (!formData.performerRoleId) {
        throw new Error("performerRoleId (performer) is required");
    }
    if (!Number.isFinite(formData.minutes) || formData.minutes <= 0) {
        throw new Error("minutes must be a positive number");
    }
};
/**
 * Create or update a care-time Observation.
 * @agentic @benchmark/core
 */ const upsertCareTime = async (medplum, input)=>{
    validate(input);
    const { formData, existingObservation } = input;
    const observation = careTimeFromForm(formData, existingObservation);
    let result;
    let isUpdate;
    if (existingObservation?.id) {
        result = await updateResource(medplum, observation);
        isUpdate = true;
        if (!result.id) {
            throw new Error("Updated care-time Observation is missing an id");
        }
        const event = {
            observationId: result.id,
            observation: result,
            patientId: formData.patientId
        };
        globalEventEmitter.publish(DomainEventNames.CareTimeUpdated, event);
    } else {
        result = await createResource(medplum, observation);
        isUpdate = false;
        if (!result.id) {
            throw new Error("Created care-time Observation is missing an id");
        }
        const event = {
            observationId: result.id,
            observation: result,
            patientId: formData.patientId,
            method: formData.method
        };
        globalEventEmitter.publish(DomainEventNames.CareTimeCreated, event);
    }
    return {
        observation: result,
        isUpdate
    };
};

/**
 * Log activity progress by creating a Communication and optionally snoozing the Task.
 * @agentic @benchmark/core
 */ async function logActivityProgress(medplum, input) {
    const { task, progressData, patient, practitioner, practitionerRole } = input;
    if (!task.id) {
        throw new Error("Task ID is required for progress logging");
    }
    if (!patient.id) {
        throw new Error("Patient ID is required for progress logging");
    }
    if (!practitionerRole.id) {
        throw new Error("PractitionerRole ID is required for progress logging");
    }
    const activityCode = task.code?.coding?.[0]?.code;
    const archetype = activityCode ? ACTIVITY_PROGRESS_ARCHETYPE_MAP[activityCode] : undefined;
    const categoryEntry = archetype ? PROGRESS_LOG_CATEGORIES.find((c)=>c.code === archetype) : PROGRESS_LOG_CATEGORIES[0];
    const patientRef = buildPatientReference(toPatientId(patient.id), getPatientDisplayName$1(patient));
    const practitionerRoleRef = buildPractitionerRoleReference(practitionerRole.id, getPractitionerDisplayName$1(practitioner));
    // The patient is the recipient only for member-facing outreach. For
    // provider-outreach the recipient is the provider/facility (not modeled in
    // this action's input), so recipient is omitted rather than incorrectly
    // asserting the patient received the outreach. The patient remains `subject`.
    const isProviderOutreach = categoryEntry?.code === "provider-outreach";
    const communication = {
        resourceType: "Communication",
        status: "completed",
        basedOn: [
            {
                reference: `Task/${task.id}`,
                display: task.description
            }
        ],
        subject: patientRef,
        sender: practitionerRoleRef,
        ...isProviderOutreach ? {} : {
            recipient: [
                patientRef
            ]
        },
        sent: new Date().toISOString(),
        category: categoryEntry ? [
            {
                coding: [
                    {
                        system: PROGRESS_LOG_CATEGORY_SYSTEM,
                        code: categoryEntry.code,
                        display: categoryEntry.display
                    }
                ]
            }
        ] : undefined,
        reasonCode: [
            {
                coding: [
                    {
                        system: PROGRESS_LOG_OUTCOME_SYSTEM,
                        code: progressData.outcome,
                        display: progressData.outcomeDisplay
                    }
                ]
            }
        ],
        ...progressData.contactMethod && {
            medium: buildMediumCoding(progressData.contactMethod)
        },
        ...progressData.notes && {
            payload: [
                {
                    contentString: progressData.notes
                }
            ]
        }
    };
    // Administrative activities (select-services, approve-selected-services,
    // finalize-care-plan-enrollment, build-cocm-care-plan) record an internal
    // status note, not a real member/provider interaction, so they do not write
    // a Communication. Their progress is captured as a Task note + optional
    // care-time Observation below.
    //
    // Historical admin-note Communications (fabricated "interactions") are
    // retired by `connections-cli communication backfill admin-progress-notes`,
    // which preserves content-bearing notes as Task.notes before marking the
    // Communication entered-in-error (convert-then-retire).
    const isAdministrative = isAdministrativeActivityCode(activityCode);
    let createdCommunication;
    if (!isAdministrative) {
        createdCommunication = await medplum.createResource(communication);
        const commEvent = {
            communicationId: createdCommunication.id,
            communication: createdCommunication,
            patientId: patient.id
        };
        globalEventEmitter$1.publish(DomainEventNames$1.CommunicationCreated, commEvent);
    }
    let taskSnoozed = false;
    let newDueDate;
    const { snoozeBehavior, followUpDate } = progressData;
    if (snoozeBehavior.type === "auto") {
        const dueDate = new Date();
        dueDate.setTime(dueDate.getTime() + snoozeBehavior.hours * 60 * 60 * 1000);
        newDueDate = dueDate.toISOString();
        taskSnoozed = true;
    } else if (snoozeBehavior.type === "prompt" && followUpDate) {
        newDueDate = followUpDate;
        taskSnoozed = true;
    }
    const noteSummary = `${categoryEntry?.display ?? "Progress"}: ${progressData.outcomeDisplay}${taskSnoozed ? ` — snoozed to ${parseFhirInstantForDisplay(newDueDate).toLocaleDateString()}` : ""}`;
    const existingNotes = task.note ?? [];
    const updatedTask = {
        ...task,
        note: [
            ...existingNotes,
            {
                text: noteSummary,
                time: new Date().toISOString(),
                authorReference: practitionerRoleRef
            }
        ],
        lastModified: new Date().toISOString(),
        ...taskSnoozed && newDueDate && {
            restriction: {
                ...task.restriction,
                period: {
                    end: newDueDate
                }
            }
        }
    };
    const savedTask = await medplum.updateResource(updatedTask);
    const taskEvent = {
        taskId: savedTask.id,
        task: savedTask,
        patientId: patient.id
    };
    globalEventEmitter$1.publish(DomainEventNames$1.TaskUpdated, taskEvent);
    // Tee a care-time Observation when the progress form captured minutes.
    // Best-effort: a write failure is logged but does not roll back the
    // progress log; the caller can retry via the patient profile widget.
    if (progressData.minutesLogged && progressData.minutesLogged > 0) {
        try {
            const carePlanRef = task.basedOn?.find((b)=>b.reference?.startsWith("CarePlan/"));
            const carePlanId = carePlanRef?.reference?.startsWith("CarePlan/") ? carePlanRef.reference.slice("CarePlan/".length) : undefined;
            await upsertCareTime(medplum, {
                formData: {
                    minutes: progressData.minutesLogged,
                    method: progressData.careTimeMethod ?? "progress-update",
                    notes: progressData.careTimeNotes?.trim() || progressData.notes?.trim() || undefined,
                    effectiveDate: new Date().toISOString(),
                    patientId: patient.id,
                    patientDisplay: getPatientDisplayName$1(patient),
                    performerRoleId: practitionerRole.id,
                    performerDisplay: getPractitionerDisplayName$1(practitioner),
                    taskId: task.id,
                    taskDisplay: task.description ?? task.code?.coding?.[0]?.display,
                    carePlanId,
                    carePlanDisplay: carePlanRef?.display,
                    // Link the tee'd Observation to its progress Communication so the
                    // unified progress + care-time history can dedup the two (the
                    // care-task minutes fold into the Communication row). Administrative
                    // activities have no Communication, so this stays undefined and the
                    // Observation renders as a standalone history row.
                    communicationId: createdCommunication?.id
                }
            });
        } catch (err) {
            // Best-effort side effect — log redacted metadata only, never the raw
            // error object (Medplum errors can embed patient-linked context).
            console.error("Care-time tee failed for progress log:", {
                taskId: task.id,
                ...toLogSafeError$1(err)
            });
        }
    }
    return {
        communication: createdCommunication,
        taskSnoozed,
        newDueDate
    };
}

/**
 * Logs an availability verification and optionally updates the HealthcareService characteristic.
 * @agentic @benchmark/core
 */ const logAvailabilityUpdate = async (medplum, input)=>{
    const { formData, healthcareService, sender, senderDisplay } = input;
    const bundle = buildAvailabilityUpdateBundle({
        formData,
        healthcareService,
        sender,
        senderDisplay
    });
    const result = await submitBundle(medplum, bundle);
    const communications = extractResourcesFromTransactionResult$1(result, "Communication");
    const createdCommunication = communications[0];
    const healthcareServices = extractResourcesFromTransactionResult$1(result, "HealthcareService");
    const updatedHs = healthcareServices[0];
    if (createdCommunication?.id) {
        const event = {
            communicationId: createdCommunication.id,
            communication: createdCommunication,
            healthcareServiceId: formData.healthcareServiceId,
            finding: formData.finding
        };
        globalEventEmitter$1.publish(DomainEventNames$1.AvailabilityUpdateLogged, event);
    }
    const characteristicValue = FINDING_TO_CHARACTERISTIC_MAP[formData.finding];
    if (characteristicValue !== null && updatedHs?.id) {
        const hsEvent = {
            healthcareServiceId: updatedHs.id,
            healthcareService: updatedHs
        };
        globalEventEmitter$1.publish(DomainEventNames$1.HealthcareServiceUpdated, hsEvent);
    }
    return {
        communication: createdCommunication,
        healthcareService: updatedHs
    };
};

const extractPatientId = (task)=>{
    const ref = task.for?.reference;
    return ref?.startsWith("Patient/") ? ref.slice("Patient/".length) : "";
};
/**
 * Mark a clinical notification Task as read.
 * @agentic @benchmark/core
 */ async function markNotificationTaskRead(medplum, input) {
    const { task } = input;
    if (!isNotificationTask(task)) {
        throw new Error("markNotificationTaskRead requires a clinical notification Task");
    }
    // No-op on a dismissed/terminal notification.
    if (task.status === "cancelled" || task.status === "completed") {
        return task;
    }
    // Idempotent: already read.
    if (getNotificationReadStatus(task) === NOTIFICATION_READ_STATUS.READ.code) {
        return task;
    }
    const now = new Date().toISOString();
    const noteText = input.readByDisplay ? `Read by ${input.readByDisplay} at ${now}` : `Read at ${now}`;
    const updated = {
        ...task,
        businessStatus: {
            coding: [
                {
                    system: NOTIFICATION_READ_STATUS_SYSTEM,
                    code: NOTIFICATION_READ_STATUS.READ.code,
                    display: NOTIFICATION_READ_STATUS.READ.display
                }
            ],
            text: NOTIFICATION_READ_STATUS.READ.display
        },
        executionPeriod: {
            ...task.executionPeriod,
            end: now
        },
        note: [
            ...task.note ?? [],
            {
                text: noteText,
                time: now,
                ...input.readByReference ? {
                    authorReference: {
                        reference: input.readByReference,
                        display: input.readByDisplay
                    }
                } : {}
            }
        ]
    };
    const result = await updateResource(medplum, updated);
    const event = {
        taskId: result.id ?? task.id,
        task: result,
        patientId: extractPatientId(result)
    };
    globalEventEmitter.publish(DomainEventNames.NotificationTaskUpdated, event);
    return result;
}

/**
 * Reassign Task Action
 *
 * Version-safe reassignment of Task.owner to a different PractitionerRole.
 * Uses optimistic locking via meta.versionId to prevent stale writes.
 * @agentic @benchmark/core
 */ /**
 * Reassign a task to a new PractitionerRole owner.
 *
 * Uses JSON Patch with meta.versionId test for optimistic locking.
 * Uses "replace" if the task already has an owner, "add" if it doesn't.
 * @agentic @benchmark/core
 */ async function reassignTask(medplum, input) {
    const task = await medplum.readResource("Task", input.taskId);
    const ops = [];
    if (task.meta?.versionId) {
        ops.push({
            op: "test",
            path: "/meta/versionId",
            value: task.meta.versionId
        });
    }
    const ownerValue = {
        reference: `PractitionerRole/${input.newOwnerRef}`,
        display: input.newOwnerDisplay
    };
    ops.push(task.owner ? {
        op: "replace",
        path: "/owner",
        value: ownerValue
    } : {
        op: "add",
        path: "/owner",
        value: ownerValue
    });
    return medplum.patchResource("Task", input.taskId, ops);
}

/**
 * Recalculate task dates after unpause
 *
 * Adjusts task due dates based on actual vs planned pause duration.
 * When unpaused, subtracts the remaining pause days (unusedDays) from current due dates.
 * This correctly restores task dates to account for the actual pause duration.
 * Task status is preserved (not changed to "ready") since we no longer change
 * status to "on-hold" during pause.
 *
 * @param medplum - MedplumClient instance
 * @param patient - Patient whose tasks should be recalculated
 * @param plannedDurationDays - Planned pause duration in days
 * @param actualDurationDays - Actual pause duration in days
 * @agentic @benchmark/core
 */ async function recalculateTaskDatesAfterUnpause(medplum, patient, plannedDurationDays, actualDurationDays) {
    // Get all tasks for this patient that need recalculation
    const patientRef = `Patient/${patient.id}`;
    const relevantStatuses = "requested,received,accepted,ready,in-progress,on-hold";
    const excludeNotifications = buildOperationalTaskExclusionParams();
    // `subject` is the FHIR R4 Task search parameter for `Task.for`
    // (Medplum rejects a literal `for` param as unknown).
    const tasksBySubject = await medplum.searchResources("Task", {
        subject: patientRef,
        status: relevantStatuses,
        ...excludeNotifications
    }).catch(()=>[]);
    // Deduplicate tasks by ID
    const taskMap = new Map();
    tasksBySubject.forEach((task)=>{
        if (task.id) {
            taskMap.set(task.id, task);
        }
    });
    const tasks = Array.from(taskMap.values());
    // Calculate remaining pause days (unusedDays = planned - actual)
    // This represents how many days were left in the pause period when unpaused
    const remainingPauseDays = plannedDurationDays - actualDurationDays;
    // Adjust task dates only (preserve original status since we don't change it during pause)
    const updatePromises = tasks.filter((task)=>{
        return Boolean(task.id && task.restriction?.period?.end);
    }).map(async (task)=>{
        const currentDueDate = new Date(task.restriction.period.end);
        // Calculate the adjusted due date by subtracting remaining pause days
        // This restores the task date to account for the actual pause duration
        const adjustedDueDate = new Date(currentDueDate);
        adjustedDueDate.setUTCDate(adjustedDueDate.getUTCDate() - remainingPauseDays);
        // Always update to the adjusted date (which accounts for remaining pause days)
        // This ensures tasks reflect the correct due date based on actual pause duration
        return updateResource$1(medplum, {
            ...task,
            id: task.id,
            restriction: {
                ...task.restriction,
                period: {
                    end: adjustedDueDate.toISOString()
                }
            }
        });
    });
    await Promise.all(updatePromises);
}

/**
 * Extracts the activity code from a Task resource.
 * @agentic @benchmark/core
 */ const getTaskActivityCode = (task)=>task.code?.coding?.find((c)=>c.system === CARE_PLAN_ACTIVITY_SYSTEM$1)?.code ?? task.code?.coding?.[0]?.code;
/**
 * Reorders flexible draft activities in a CarePlan.
 * Sequential and non-draft activities keep their original positions.
 * @agentic @benchmark/core
 */ async function reorderCarePlanActivities(medplum, input) {
    const { carePlanId, orderedFlexibleDraftTaskIds } = input;
    const carePlan = await readResource$1(medplum, "CarePlan", carePlanId);
    const activities = carePlan.activity || [];
    if (activities.length === 0) return carePlan;
    // CoCM is a step-based flexible bag: the current `ready` step is reorderable
    // alongside the upcoming drafts, and statuses follow the new order. Triage and
    // intake are strictly sequential — only flexible *draft* activities move, and
    // the sequential/ready steps keep their positions.
    const isCoCM = getCarePlanType(carePlan) === "cocm";
    const reorderableStatuses = isCoCM ? new Set([
        "draft",
        "ready"
    ]) : new Set([
        "draft"
    ]);
    const taskCache = new Map();
    for (const activity of activities){
        const ref = activity.reference?.reference;
        if (ref?.startsWith("Task/")) {
            const taskId = ref.replace("Task/", "");
            const task = await readResource$1(medplum, "Task", taskId);
            taskCache.set(taskId, task);
        }
    }
    const flexDraftIndices = [];
    const flexDraftActivities = [];
    for(let i = 0; i < activities.length; i++){
        const activity = activities[i];
        const ref = activity.reference?.reference;
        const taskId = ref?.startsWith("Task/") ? ref.replace("Task/", "") : undefined;
        const task = taskId ? taskCache.get(taskId) : undefined;
        if (!task || !reorderableStatuses.has(task.status)) continue;
        const code = getTaskActivityCode(task);
        if (!code || !isActivityFlexible(code)) continue;
        flexDraftIndices.push(i);
        flexDraftActivities.push(activity);
    }
    if (flexDraftIndices.length === 0) return carePlan;
    const reordered = orderedFlexibleDraftTaskIds.map((taskId)=>flexDraftActivities.find((a)=>a.reference?.reference === `Task/${taskId}`)).filter(Boolean);
    const remaining = flexDraftActivities.filter((a)=>!orderedFlexibleDraftTaskIds.includes(a.reference?.reference?.replace("Task/", "") || ""));
    reordered.push(...remaining);
    const newActivities = [
        ...activities
    ];
    for(let i = 0; i < flexDraftIndices.length; i++){
        if (reordered[i]) {
            newActivities[flexDraftIndices[i]] = reordered[i];
        }
    }
    if (!carePlan.id) {
        throw new Error("CarePlan ID is required to reorder activities");
    }
    const updatedCarePlan = await updateResource$1(medplum, {
        ...carePlan,
        activity: newActivities,
        id: carePlan.id
    });
    if (updatedCarePlan.id) {
        const event = {
            carePlanId: updatedCarePlan.id,
            carePlan: updatedCarePlan,
            patientId: updatedCarePlan.subject?.reference?.replace("Patient/", "") || ""
        };
        globalEventEmitter$1.publish(DomainEventNames$1.CarePlanUpdated, event);
    }
    // For CoCM, the new order determines which flexible step is current: reconcile
    // so the first open activity becomes `ready` and the rest `draft`.
    if (isCoCM) {
        return reconcileCarePlanReadyState(medplum, updatedCarePlan);
    }
    return updatedCarePlan;
}

/**
 * Resume a care plan from hold
 *
 * Reactivates a care plan that was on hold.
 *
 * @param medplum - MedplumClient instance
 * @param carePlan - Care plan to resume
 * @returns Updated CarePlan
 * @throws Error if validation fails
 * @agentic @benchmark/core
 */ async function resumeCarePlan(medplum, carePlan) {
    if (!carePlan.id) {
        throw new Error("Care plan ID is required to resume");
    }
    if (carePlan.status !== "on-hold") {
        throw new Error("Only care plans on hold can be resumed");
    }
    const patientId = carePlan.subject?.reference?.replace("Patient/", "") || "";
    const resumedCarePlan = await updateResource$1(medplum, {
        ...carePlan,
        id: carePlan.id,
        status: "active"
    });
    // Emit event
    if (!resumedCarePlan.id) {
        throw new Error("Care plan ID is required after update");
    }
    const event = {
        carePlanId: resumedCarePlan.id,
        carePlan: resumedCarePlan,
        patientId
    };
    globalEventEmitter$1.publish(DomainEventNames$1.CarePlanResumed, event);
    return resumedCarePlan;
}

/**
 * Save or clear the draft form state on a Task. Saving moves an actionable
 * (`ready`/`requested`) Task to `in-progress` — still open/to-do everywhere —
 * and never touches other statuses (`draft` means blocked). A terminal Task
 * refuses a new draft; clearing one is still allowed.
 * @agentic @benchmark/core
 */ async function saveTaskDraft(medplum, input) {
    const { taskId, patientId, formState } = input;
    const existingTask = await readResource$1(medplum, "Task", taskId);
    if (formState && TERMINAL_TASK_STATUSES.has(existingTask.status)) {
        throw new Error("This task is already closed");
    }
    const now = new Date().toISOString();
    const extension = [
        ...(existingTask.extension ?? []).filter((ext)=>ext.url !== TASK_DRAFT_FORM_STATE_EXTENSION_URL),
        ...formState ? [
            {
                url: TASK_DRAFT_FORM_STATE_EXTENSION_URL,
                valueString: JSON.stringify({
                    ...formState,
                    savedAt: now
                })
            }
        ] : []
    ];
    const status = formState && (existingTask.status === "ready" || existingTask.status === "requested") ? "in-progress" : existingTask.status;
    const savedTask = await updateResource$1(medplum, {
        ...existingTask,
        id: taskId,
        status,
        extension: extension.length > 0 ? extension : undefined,
        lastModified: now
    });
    const event = {
        taskId,
        task: savedTask,
        patientId
    };
    globalEventEmitter$1.publish(DomainEventNames$1.TaskUpdated, event);
    return savedTask;
}

/** Benchmark `billing-summary` category coding (distinct from `care-time`). @agentic @benchmark/core */ const COCM_BILLING_SUMMARY_CATEGORY = {
    coding: [
        {
            system: CARE_TIME_CATEGORY_SYSTEM,
            code: "billing-summary",
            display: "Billing Summary"
        }
    ]
};
/** `Observation.code` value for a CoCM monthly billing summary. @agentic @benchmark/core */ const COCM_BILLING_SUMMARY_CODE_VALUE = "cocm-monthly-billing-summary";
/** `Observation.code` payload. @agentic @benchmark/core */ const COCM_BILLING_SUMMARY_CODE = {
    coding: [
        {
            system: CARE_TIME_CODE_SYSTEM,
            code: COCM_BILLING_SUMMARY_CODE_VALUE,
            display: "CoCM Monthly Billing Summary"
        }
    ],
    text: "CoCM Monthly Billing Summary"
};
/** Identifier system for the `{patientId}|{YYYY-MM}` composite key. @agentic @benchmark/core */ const COCM_BILLING_SUMMARY_IDENTIFIER_SYSTEM = `${ORGANIZATION_BASE}/identifier-system/cocm-billing-summary`;
/** Root complex-extension URL carrying submission + reopen audit. @agentic @benchmark/core */ const COCM_BILLING_SUMMARY_EXTENSION_URL = `${EXTENSION_BASE}/cocm-billing-summary`;
/**
 * Relative sub-extension keys nested under
 * {@link COCM_BILLING_SUMMARY_EXTENSION_URL}.
 * @agentic @benchmark/core
 */ const COCM_BILLING_SUMMARY_EXT = {
    billingMonth: "billingMonth",
    submitted: "submitted",
    submittedAt: "submittedAt",
    submittedBy: "submittedBy",
    reopen: "reopen",
    reopenAt: "at",
    reopenReason: "reason",
    reopenBy: "by"
};
/**
 * `Observation.status` values that model the month lock:
 *  - `preliminary` = open (live, editable)
 *  - `final`       = closed / locked
 *  - `amended`     = reopened for correction
 * @agentic @benchmark/core
 */ const COCM_BILLING_SUMMARY_STATUS = {
    open: "preliminary",
    closed: "final",
    reopened: "amended"
};
/** Build the composite identifier value for a patient-month. @agentic @benchmark/core */ const buildCoCMBillingSummaryIdentifierValue = (patientId, billingMonth)=>`${patientId}|${billingMonth}`;
/** `system|value` token for an identifier search. @agentic @benchmark/core */ const buildCoCMBillingSummaryIdentifierToken = (patientId, billingMonth)=>`${COCM_BILLING_SUMMARY_IDENTIFIER_SYSTEM}|${buildCoCMBillingSummaryIdentifierValue(patientId, billingMonth)}`;

const STATUS_MAP = {
    open: COCM_BILLING_SUMMARY_STATUS.open,
    closed: COCM_BILLING_SUMMARY_STATUS.closed,
    reopened: COCM_BILLING_SUMMARY_STATUS.reopened
};
/**
 * Attested minutes become a persisted billing fact (`valueQuantity.value`),
 * so a corrupted upstream sum must never be frozen into the record. Clamp
 * negatives to 0 and fail non-finite input (NaN / ±Infinity) CLOSED to 0 at
 * this single write chokepoint. Mirrors the resolver's `normalizeMinutes`
 * guard (#1318), which covered the on-read estimate path only — the
 * attestation/build write path was previously unguarded, so a negative or
 * NaN sum could be persisted as attested minutes.
 */ const normalizeAttestedMinutes = (minutes)=>Number.isFinite(minutes) ? Math.max(0, minutes) : 0;
const monthBounds$1 = (billingMonth)=>{
    const [year, month] = billingMonth.split("-").map(Number);
    return {
        start: new Date(Date.UTC(year, month - 1, 1, 0, 0, 0, 0)).toISOString(),
        end: new Date(Date.UTC(year, month, 0, 23, 59, 59, 999)).toISOString()
    };
};
const roleRef = (roleId, display)=>roleId ? {
        reference: `PractitionerRole/${roleId}`,
        ...display ? {
            display
        } : {}
    } : undefined;
/**
 * Build (or rebuild) a CoCM monthly billing summary Observation.
 * @agentic @benchmark/core
 */ const buildCoCMBillingSummary = (input)=>{
    const { snapshot, submission, lockStatus = "open" } = input;
    const bounds = monthBounds$1(snapshot.billingMonth);
    const sub = [
        {
            url: COCM_BILLING_SUMMARY_EXT.billingMonth,
            valueString: snapshot.billingMonth
        },
        {
            url: COCM_BILLING_SUMMARY_EXT.submitted,
            valueBoolean: submission?.submitted ?? false
        }
    ];
    if (submission?.submitted) {
        if (submission.submittedAt) {
            sub.push({
                url: COCM_BILLING_SUMMARY_EXT.submittedAt,
                valueString: submission.submittedAt
            });
        }
        const by = roleRef(submission.submittedBy, submission.submittedByDisplay);
        if (by) {
            sub.push({
                url: COCM_BILLING_SUMMARY_EXT.submittedBy,
                valueReference: by
            });
        }
    }
    for (const entry of input.reopenHistory ?? []){
        const nested = [
            {
                url: COCM_BILLING_SUMMARY_EXT.reopenAt,
                valueString: entry.at
            }
        ];
        if (entry.reason) {
            nested.push({
                url: COCM_BILLING_SUMMARY_EXT.reopenReason,
                valueString: entry.reason
            });
        }
        const by = roleRef(entry.by, entry.byDisplay);
        if (by) nested.push({
            url: COCM_BILLING_SUMMARY_EXT.reopenBy,
            valueReference: by
        });
        sub.push({
            url: COCM_BILLING_SUMMARY_EXT.reopen,
            extension: nested
        });
    }
    const observation = {
        resourceType: "Observation",
        ...input.id ? {
            id: input.id
        } : {},
        status: STATUS_MAP[lockStatus],
        identifier: [
            {
                system: COCM_BILLING_SUMMARY_IDENTIFIER_SYSTEM,
                value: buildCoCMBillingSummaryIdentifierValue(snapshot.patientId, snapshot.billingMonth)
            }
        ],
        category: [
            {
                ...COCM_BILLING_SUMMARY_CATEGORY
            }
        ],
        code: {
            ...COCM_BILLING_SUMMARY_CODE
        },
        subject: {
            reference: `Patient/${snapshot.patientId}`,
            ...snapshot.patientDisplay ? {
                display: snapshot.patientDisplay
            } : {}
        },
        effectivePeriod: {
            start: bounds.start,
            end: bounds.end
        },
        valueQuantity: {
            value: normalizeAttestedMinutes(snapshot.attestedMinutes),
            unit: "min",
            system: UCUM_SYSTEM,
            code: "min"
        },
        issued: input.issued ?? new Date().toISOString(),
        extension: [
            {
                url: COCM_BILLING_SUMMARY_EXTENSION_URL,
                extension: sub
            }
        ]
    };
    const derivedFrom = (snapshot.contributingObservationIds ?? []).map((id)=>({
            reference: `Observation/${id}`
        }));
    if (derivedFrom.length > 0) {
        observation.derivedFrom = derivedFrom;
    }
    if (snapshot.clinicalSummary?.trim()) {
        observation.note = [
            {
                text: snapshot.clinicalSummary
            }
        ];
    }
    // `performer` is the care manager who recorded the month — known from the
    // first preliminary save, well before submission. Fall back to the submitter
    // so a submitted record built without an explicit performer stays attributed.
    const performerRef = roleRef(snapshot.performerRoleId, snapshot.performerDisplay) ?? (submission?.submitted ? roleRef(submission.submittedBy, submission.submittedByDisplay) : undefined);
    if (performerRef) {
        observation.performer = [
            performerRef
        ];
    }
    return observation;
};

const idFromRef = (ref)=>ref?.split("/").pop();
const rootExtensions = (obs)=>obs.extension?.find((e)=>e.url === COCM_BILLING_SUMMARY_EXTENSION_URL)?.extension ?? [];
const first = (exts, url)=>exts.find((e)=>e.url === url);
const lockStatusFromObservation = (obs)=>{
    if (obs.status === COCM_BILLING_SUMMARY_STATUS.closed) return "closed";
    if (obs.status === COCM_BILLING_SUMMARY_STATUS.reopened) return "reopened";
    return "open";
};
/**
 * Parse a persisted billing-summary Observation into a view model.
 * @agentic @benchmark/core
 */ const cocmBillingSummaryToView = (obs)=>{
    const exts = rootExtensions(obs);
    const identifier = obs.identifier?.find((i)=>i.system === COCM_BILLING_SUMMARY_IDENTIFIER_SYSTEM)?.value ?? "";
    const reopenHistory = exts.filter((e)=>e.url === COCM_BILLING_SUMMARY_EXT.reopen).map((entry)=>{
        const nested = entry.extension ?? [];
        const reopenByRef = first(nested, COCM_BILLING_SUMMARY_EXT.reopenBy)?.valueReference;
        return {
            at: first(nested, COCM_BILLING_SUMMARY_EXT.reopenAt)?.valueString ?? "",
            reason: first(nested, COCM_BILLING_SUMMARY_EXT.reopenReason)?.valueString,
            by: idFromRef(reopenByRef?.reference),
            byDisplay: reopenByRef?.display
        };
    });
    const lockStatus = lockStatusFromObservation(obs);
    const submittedByRef = first(exts, COCM_BILLING_SUMMARY_EXT.submittedBy)?.valueReference;
    const performerRef = obs.performer?.[0];
    return {
        id: obs.id,
        identifier,
        patientId: idFromRef(obs.subject?.reference) ?? "",
        patientDisplay: obs.subject?.display,
        performerRoleId: idFromRef(performerRef?.reference),
        performerDisplay: performerRef?.display,
        billingMonth: first(exts, COCM_BILLING_SUMMARY_EXT.billingMonth)?.valueString ?? "",
        attestedMinutes: obs.valueQuantity?.value ?? 0,
        clinicalSummary: obs.note?.[0]?.text,
        contributingObservationIds: (obs.derivedFrom ?? []).map((r)=>idFromRef(r.reference)).filter((id)=>Boolean(id)),
        submitted: first(exts, COCM_BILLING_SUMMARY_EXT.submitted)?.valueBoolean ?? false,
        submittedAt: first(exts, COCM_BILLING_SUMMARY_EXT.submittedAt)?.valueString,
        submittedBy: idFromRef(submittedByRef?.reference),
        submittedByDisplay: submittedByRef?.display,
        lockStatus,
        closed: lockStatus === "closed",
        reopenHistory
    };
};

/**
 * CoCM billing rules — versioned, frozen table.
 *
 * Implements the brief's "Billing Rules Table" verbatim:
 *  - Initial month, assessments present (D14 + D18)
 *  - Subsequent month, assessments present (D13 cascade applies)
 *  - Any month, no eligible assessment (G2214-only)
 *
 * Bounds use the brief's "16 lower bound uniformly" convention (see
 * Boundary precision flag). If the user later confirms 15 for
 * subsequent months, only the two cells in `SUBSEQUENT_WITH_ASSESSMENT`
 * and `NO_ASSESSMENT` change.
 *
 * @agentic @benchmark/core
 */ /** Version stamp included on any copied billing report (D11). @agentic @benchmark/core */ const COCM_BILLING_RULES_VERSION = "COCM_BILLING_RULES_V1";
/**
 * Initial month with eligible assessment (D14).
 * 99492-based ladder.
 * @agentic @benchmark/core
 */ const INITIAL_WITH_ASSESSMENT = [
    {
        minMinutes: 0,
        maxMinutes: 15,
        codes: [],
        packageDisplay: ""
    },
    {
        minMinutes: 16,
        maxMinutes: 35,
        codes: [
            "G2214"
        ],
        packageDisplay: "G2214"
    },
    {
        minMinutes: 36,
        maxMinutes: 85,
        codes: [
            "99492"
        ],
        packageDisplay: "99492"
    },
    {
        minMinutes: 86,
        maxMinutes: 115,
        codes: [
            "99492",
            "99494"
        ],
        packageDisplay: "99492 + 99494"
    },
    {
        minMinutes: 116,
        maxMinutes: 145,
        codes: [
            "99492",
            "99494",
            "99494"
        ],
        packageDisplay: "99492 + 99494×2"
    },
    {
        minMinutes: 146,
        maxMinutes: 175,
        codes: [
            "99492",
            "99494",
            "99494",
            "99494"
        ],
        packageDisplay: "99492 + 99494×3"
    },
    {
        minMinutes: 176,
        maxMinutes: null,
        codes: [
            "99492",
            "99494",
            "99494",
            "99494",
            "99494"
        ],
        packageDisplay: "99492 + 99494×4"
    }
];
/**
 * Subsequent month with eligible assessment.
 * 99493-based ladder. Also used as the cascade ladder per D13 when the
 * episode's first bill was G2214.
 * @agentic @benchmark/core
 */ const SUBSEQUENT_WITH_ASSESSMENT = [
    {
        minMinutes: 0,
        maxMinutes: 15,
        codes: [],
        packageDisplay: ""
    },
    {
        minMinutes: 16,
        maxMinutes: 30,
        codes: [
            "G2214"
        ],
        packageDisplay: "G2214"
    },
    {
        minMinutes: 31,
        maxMinutes: 75,
        codes: [
            "99493"
        ],
        packageDisplay: "99493"
    },
    {
        minMinutes: 76,
        maxMinutes: 105,
        codes: [
            "99493",
            "99494"
        ],
        packageDisplay: "99493 + 99494"
    },
    {
        minMinutes: 106,
        maxMinutes: 135,
        codes: [
            "99493",
            "99494",
            "99494"
        ],
        packageDisplay: "99493 + 99494×2"
    },
    {
        minMinutes: 136,
        maxMinutes: 165,
        codes: [
            "99493",
            "99494",
            "99494",
            "99494"
        ],
        packageDisplay: "99493 + 99494×3"
    },
    {
        minMinutes: 166,
        maxMinutes: null,
        codes: [
            "99493",
            "99494",
            "99494",
            "99494",
            "99494"
        ],
        packageDisplay: "99493 + 99494×4"
    }
];
/**
 * Any month, no eligible assessment in window (D18).
 * Only G2214 may fire without an assessment.
 * @agentic @benchmark/core
 */ const NO_ASSESSMENT = [
    {
        minMinutes: 0,
        maxMinutes: 15,
        codes: [],
        packageDisplay: ""
    },
    {
        minMinutes: 16,
        maxMinutes: null,
        codes: [
            "G2214"
        ],
        packageDisplay: "G2214"
    }
];
/**
 * Care units per billed code — the single TypeScript source of truth, aligned
 * with the dbt `billing_codes` seed (D24): 99492 / 99493 = 1.0 (base month),
 * 99494 = 0.5 (each 30-min add-on), G2214 = 0.25 (GBHI). Summing these over a
 * code package yields the packet's `careUnits`.
 *
 * NOTE (D24): the value for G2214 is 0.25, matching `billing_codes.csv` and both
 * `fct_*_cocm_billing` models. Two documentation strings historically said 0.5;
 * those are the stale ones, not this.
 * @agentic @benchmark/core
 */ const CARE_UNITS_BY_CODE = {
    "99492": 1.0,
    "99493": 1.0,
    "99494": 0.5,
    G2214: 0.25
};
/**
 * Sum the care units for a billed code package. Empty package = 0.
 * @agentic @benchmark/core
 */ const careUnitsForCodes = (codes)=>codes.reduce((sum, code)=>sum + (CARE_UNITS_BY_CODE[code] ?? 0), 0);

/**
 * v1 assessments-used list. `computeCoCMEligibility` surfaces the freshness
 * signal + the latest eligible assessment date; the fuller per-instrument list
 * is a D26 fast-follow. When an eligible assessment exists we record the latest
 * one as the criterion that satisfied the 99492/99493 requirement.
 */ const assessmentsUsedFromSummary = (summary)=>summary.hasEligibleAssessment && summary.latestAssessmentDate ? [
        {
            code: "cocm-eligible",
            date: summary.latestAssessmentDate
        }
    ] : [];
/**
 * Assemble the versioned billing-code packet for a patient-month. Pure.
 * @agentic @benchmark/core
 */ const buildBillingCodePacket = (input)=>{
    const { patientId, billingMonth, summary, attribution } = input;
    const codes = [
        ...summary.billingResult.codes
    ];
    const enrollmentWindow = summary.cocmPeriodStart || summary.cocmPeriodEnd || summary.cocmCarePlanId ? {
        start: summary.cocmPeriodStart,
        end: summary.cocmPeriodEnd,
        carePlanId: summary.cocmCarePlanId
    } : undefined;
    return {
        packetVersion: 1,
        rulesVersion: COCM_BILLING_RULES_VERSION,
        patientId,
        billingMonth,
        codes,
        packageDisplay: summary.billingResult.packageDisplay,
        careUnits: careUnitsForCodes(codes),
        billableMinutes: summary.billableMinutes,
        totalMinutes: summary.totalMinutes,
        isInitialMonth: summary.isInitialMonth,
        episodeFirstBillWasG2214: summary.episodeFirstBillWasG2214,
        assessmentsUsed: assessmentsUsedFromSummary(summary),
        hasEligibleAssessment: summary.hasEligibleAssessment,
        ...enrollmentWindow ? {
            enrollmentWindow
        } : {},
        ...attribution ? {
            attribution
        } : {},
        generatedAt: input.generatedAt ?? new Date().toISOString()
    };
};

/**
 * Returns display name for practitioner from formatted name, parts, or ID fallback.
 * @agentic @benchmark/core
 */ const getPractitionerDisplayName = (practitioner)=>{
    // Try formatted name first
    const formattedName = formatHumanName(practitioner.name?.[0]);
    if (formattedName) return formattedName;
    // Try individual name parts
    const name = practitioner.name?.[0];
    if (name) {
        const parts = [];
        if (name.prefix?.length) parts.push(...name.prefix);
        if (name.given?.length) parts.push(...name.given);
        if (name.family) parts.push(name.family);
        if (parts.length > 0) return parts.join(" ");
    }
    // Fallback to ID-based display
    return `Practitioner ${practitioner.id}`;
};

/**
 * OCPE Phase 1: Taxonomy Compatibility Mapping
 *
 * Central mapping module for legacy-to-v2 activity/task taxonomy translation.
 * Supports display preference (v2 labels wherever possible) and deprecation checks.
 */ /**
 * Legacy activity code → v2 activity code mapping
 */ const ACTIVITY_CODE_MAP = {
    "initial-contact": "schedule-intake",
    "intake-assessment": "complete-intake",
    "care-plan-selection": "finalize-care-plan-enrollment",
    "enroll-in-care-plan": "finalize-care-plan-enrollment",
    "resource-offering": "offer-services",
    "appointment-scheduling": "schedule-services",
    "appointment-completion": "follow-up-on-services",
    "mirah-enrollment": "enroll-in-mirah",
    "cocm-intake": "complete-intake",
    "patient-check-in": "follow-up-on-cocm",
    "psychiatric-consultation": "consult-psych",
    "pcp-consultation": "consult-pcp",
    discharge: "discharge-member",
    // Old v2 codes → new v2 codes (safety net for staging/branch data)
    "select-services-for-referral": "select-services",
    "offer-referral": "offer-services",
    "confirm-referral-services": "confirm-services",
    "initiate-referral": "schedule-services",
    "follow-up-on-referral": "follow-up-on-services"
};
/**
 * Deprecated activity codes (no v2 equivalent, should not be created)
 * Display overrides for legacy data rendering
 */ /**
 * Deprecated plan-backed ad-hoc activity; use standalone custom tasks instead.
 * @agentic @benchmark/core
 */ const DEPRECATED_CUSTOM_CARE_PLAN_ACTIVITY_CODE = "custom-activity";
const DEPRECATED_ACTIVITY_DISPLAYS = {
    [DEPRECATED_CUSTOM_CARE_PLAN_ACTIVITY_CODE]: "Custom Activity (deprecated)",
    "resource-acceptance": "Resource Acceptance",
    "needs-evaluation": "Needs Evaluation",
    "ehr-documentation": "EHR Documentation",
    "time-logging": "Time Logging",
    "chart-review": "Chart Review"
};
/**
 * Standalone task type migration
 * - deprecated: Stop creating; maps to plan-backed activities or is superseded
 * - no-change: Remains standalone (custom, fax workflow tasks)
 */ const STANDALONE_TASK_MIGRATION_MAP = {
    "new-member-intake": {
        action: "deprecated",
        display: "Complete New Member Intake"
    },
    "offer-member-resources": {
        action: "deprecated",
        display: "Offer Resources to Member"
    },
    "schedule-member-appointment": {
        action: "deprecated",
        display: "Schedule Appointment for Member"
    },
    "member-check-in": {
        action: "deprecated",
        newActivityCode: "check-in-on-member"
    },
    custom: {
        action: "no-change",
        display: "Custom Task"
    },
    "review-new-referral": {
        action: "no-change",
        display: "🔔 NEW REFERRAL"
    },
    "potential-duplicate-review": {
        action: "no-change",
        display: "Review New Referral: Potential Duplicate"
    },
    "manual-review-fax": {
        action: "no-change",
        display: "Review New Referral: Complete Insurance & Verification"
    },
    "review-unmatched-fax": {
        action: "no-change",
        display: "Review Unmatched Fax: No Patient Found"
    },
    "post-care-followup": {
        action: "deprecated",
        display: "Complete Post-Care Followup"
    },
    "cadenced-followup": {
        action: "deprecated",
        display: "Complete Cadenced Followup"
    },
    "send-nps-survey": {
        action: "deprecated",
        display: "Send NPS Survey"
    }
};
/**
 * V2 activity code → display label
 * Canonical display strings for all v2 activity codes
 */ const V2_ACTIVITY_DISPLAYS = {
    // Intake
    "schedule-intake": "Schedule Intake",
    "complete-intake": "Complete Intake",
    "finalize-care-plan-enrollment": "Finalize Care Plan Enrollment",
    "check-in-on-member": "Check In on Member",
    // Triage
    "select-services": "Select Services",
    "approve-selected-services": "Approve Selected Services",
    "request-approval": "Approve Selected Services",
    "offer-services": "Offer Services to Member",
    "confirm-services": "Confirm Service Selections",
    "schedule-services": "Schedule Services",
    "follow-up-on-services": "Follow Up on Services",
    "complete-cadenced-followup": "Complete Cadenced Followup",
    // CoCM
    "enroll-in-mirah": "Confirm Mirah Enrollment",
    "build-cocm-care-plan": "Build CoCM Care Plan",
    "follow-up-on-cocm": "Follow Up on CoCM Progress",
    "consult-psych": "Consult Psychiatrist",
    "consult-pcp": "Consult PCP",
    "discharge-member": "Discharge Member"
};
/**
 * Resolve legacy or standalone task code to v2 activity code
 */ const resolveActivityCode = (code)=>ACTIVITY_CODE_MAP[code] ?? code;
/**
 * Resolve any code (activity or standalone task type) to v2 display label.
 * Prefer v2 labels wherever possible for consistent UI rendering.
 */ const resolveActivityDisplay = (code)=>{
    // Standalone task type with explicit newActivityCode → use v2 activity display
    const standaloneEntry = STANDALONE_TASK_MIGRATION_MAP[code];
    if (standaloneEntry?.newActivityCode) {
        return V2_ACTIVITY_DISPLAYS[standaloneEntry.newActivityCode] ?? code;
    }
    // Standalone task type with display override (no-change or deprecated fallback)
    if (standaloneEntry?.display) {
        return standaloneEntry.display;
    }
    // Deprecated activity codes: use legacy display for rendering
    if (DEPRECATED_ACTIVITY_DISPLAYS[code]) {
        return DEPRECATED_ACTIVITY_DISPLAYS[code];
    }
    // Activity code: resolve legacy → v2, then get display
    const v2Code = resolveActivityCode(code);
    return V2_ACTIVITY_DISPLAYS[v2Code] ?? code;
};
/**
 * Check if a standalone task type is deprecated (should not be created)
 */ const isDeprecatedStandaloneTaskType = (code)=>STANDALONE_TASK_MIGRATION_MAP[code]?.action === "deprecated";

/**
 * ActivityDefinition Type System
 * Custom system for activity definition codes
 */ const ACTIVITY_DEFINITION_TYPE_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/activity-type";
/**
 * ActivityDefinition Options (v2 canonical taxonomy)
 * Defines all available activity definitions for NEW CarePlans.
 * Legacy codes remain in taxonomy-mapping.ts for backwards-compatible display.
 */ const ACTIVITY_DEFINITION_OPTIONS = [
    // Intake Protocol Activities
    {
        code: "schedule-intake",
        display: "Schedule Intake",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Make initial contact with the member to schedule an intake session.",
        canonical: "ActivityDefinition/schedule-intake",
        protocol: "intake"
    },
    {
        code: "complete-intake",
        display: "Complete Intake",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Complete the initial clinical intake session.",
        canonical: "ActivityDefinition/complete-intake",
        protocol: "intake"
    },
    {
        code: "finalize-care-plan-enrollment",
        display: "Finalize Care Plan Enrollment",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Finalize care plan enrollment after clinical intake session is completed.",
        canonical: "ActivityDefinition/finalize-care-plan-enrollment",
        protocol: "intake"
    },
    {
        code: "check-in-on-member",
        display: "Check In on Member",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Check in on member to assess progress or needs.",
        canonical: "ActivityDefinition/check-in-on-member",
        protocol: "intake"
    },
    // Triage Protocol Activities
    {
        code: "select-services",
        display: "Select Services",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Select external services that match the member's documented service needs.",
        canonical: "ActivityDefinition/select-services",
        protocol: "triage"
    },
    {
        code: "approve-selected-services",
        display: "Approve Selected Services",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Review the services selected by SpecOps for your member.",
        canonical: "ActivityDefinition/approve-selected-services",
        protocol: "triage"
    },
    {
        code: "offer-services",
        display: "Offer Services to Member",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Present the selected services to the member and discuss options.",
        canonical: "ActivityDefinition/offer-services",
        protocol: "triage"
    },
    {
        code: "confirm-services",
        display: "Confirm Service Selections",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Confirm the member's acceptance of offered services before scheduling.",
        canonical: "ActivityDefinition/confirm-services",
        protocol: "triage"
    },
    {
        code: "schedule-services",
        display: "Schedule Services",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Schedule appointment(s) with external service(s) on behalf of the member.",
        canonical: "ActivityDefinition/schedule-services",
        protocol: "triage"
    },
    {
        code: "follow-up-on-services",
        display: "Follow Up on Services",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Reach out to the member to gauge appointment completion and satisfaction.",
        canonical: "ActivityDefinition/follow-up-on-services",
        protocol: "triage"
    },
    {
        code: "complete-cadenced-followup",
        display: "Complete Cadenced Followup",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Check-in on member at 6 week intervals to assess ongoing progress or needs.",
        canonical: "ActivityDefinition/complete-cadenced-followup",
        protocol: "triage"
    },
    // CoCM Protocol Activities
    // Note: `enroll-in-mirah` was retired when the Benchmark/Mirah partnership
    // ended. The display label is still resolvable via `taxonomy-mapping.ts`
    // (`V2_ACTIVITY_DISPLAYS`) so existing tasks render correctly. New CoCM
    // plans no longer include this activity.
    {
        code: "build-cocm-care-plan",
        display: "Build CoCM Care Plan",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Select and schedule the CoCM activities for this member's care plan.",
        canonical: "ActivityDefinition/build-cocm-care-plan",
        protocol: "cocm"
    },
    {
        code: "follow-up-on-cocm",
        display: "Follow Up on CoCM Progress",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Check in on member to assess ongoing CoCM progress or needs.",
        canonical: "ActivityDefinition/follow-up-on-cocm",
        protocol: "cocm"
    },
    {
        code: "consult-psych",
        display: "Consult Psychiatrist",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Schedule a consultation with a psychiatric provider to assess the member's mental health needs.",
        canonical: "ActivityDefinition/consult-psych",
        protocol: "cocm"
    },
    {
        code: "consult-pcp",
        display: "Consult PCP",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Schedule a consultation with a primary care provider to assess the member's physical health needs.",
        canonical: "ActivityDefinition/consult-pcp",
        protocol: "cocm"
    },
    {
        code: "discharge-member",
        display: "Discharge Member",
        system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
        description: "Discharge the member from the CoCM program.",
        canonical: "ActivityDefinition/discharge-member",
        protocol: "cocm"
    }
];
/**
 * Helper Functions
 * getActivityDefinitionDisplay uses taxonomy mapping for legacy code support
 */ const getActivityDefinitionDisplay = (code)=>resolveActivityDisplay(code);
/**
 * Returns activity definitions for a protocol.
 * @agentic @benchmark/core
 */ const getActivityDefinitionsByProtocol = (protocol)=>{
    return ACTIVITY_DEFINITION_OPTIONS.filter((o)=>o.protocol === protocol);
};
/**
 * UI-ready select options
 */ ACTIVITY_DEFINITION_OPTIONS.map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description
    }));
ACTIVITY_DEFINITION_OPTIONS.filter((o)=>o.protocol === "intake").map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description
    }));
ACTIVITY_DEFINITION_OPTIONS.filter((o)=>o.protocol === "triage").map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description
    }));
ACTIVITY_DEFINITION_OPTIONS.filter((o)=>o.protocol === "cocm").map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description
    }));

/**
 * FHIR-Compliant Constants for Appointment Domain
 *
 * This file contains all constants used in Appointment forms, utils, and related components.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 */ // FHIR R4-compliant specialty options from Practice Setting Code Value Set
// http://hl7.org/fhir/ValueSet/c80-practice-codes
// Based on SNOMED CT International edition
const APPOINTMENT_SPECIALTY_OPTIONS = [
    {
        code: "394576009",
        display: "Accident & emergency",
        system: "http://snomed.info/sct"
    },
    {
        code: "408467006",
        display: "Adult mental illness",
        system: "http://snomed.info/sct"
    },
    {
        code: "394577000",
        display: "Anesthetics",
        system: "http://snomed.info/sct"
    },
    {
        code: "394579002",
        display: "Cardiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394588006",
        display: "Child and adolescent psychiatry",
        system: "http://snomed.info/sct"
    },
    {
        code: "394803006",
        display: "Clinical hematology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408454008",
        display: "Clinical microbiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408478003",
        display: "Critical care medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394582007",
        display: "Dermatology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394583002",
        display: "Endocrinology",
        system: "http://snomed.info/sct"
    },
    {
        code: "419772000",
        display: "Family practice",
        system: "http://snomed.info/sct"
    },
    {
        code: "394584008",
        display: "Gastroenterology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408444009",
        display: "General dental practice",
        system: "http://snomed.info/sct"
    },
    {
        code: "394802001",
        display: "General medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394915009",
        display: "General pathology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394814009",
        display: "General practice",
        system: "http://snomed.info/sct"
    },
    {
        code: "394811001",
        display: "Geriatric medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394586005",
        display: "Gynecology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394597005",
        display: "Histopathology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394807007",
        display: "Infectious diseases",
        system: "http://snomed.info/sct"
    },
    {
        code: "419192003",
        display: "Internal medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394593009",
        display: "Medical oncology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394733009",
        display: "Medical specialty - Other",
        system: "http://snomed.info/sct"
    },
    {
        code: "394589003",
        display: "Nephrology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394591006",
        display: "Neurology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394649004",
        display: "Nuclear medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394585009",
        display: "Obstetrics and gynecology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408470005",
        display: "Obstetrics",
        system: "http://snomed.info/sct"
    },
    {
        code: "394821009",
        display: "Occupational medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394594003",
        display: "Ophthalmology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394605001",
        display: "Oral surgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "394608004",
        display: "Orthodontics",
        system: "http://snomed.info/sct"
    },
    {
        code: "394882004",
        display: "Pain management",
        system: "http://snomed.info/sct"
    },
    {
        code: "394806003",
        display: "Palliative medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "408459003",
        display: "Pediatric cardiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394607009",
        display: "Pediatric dentistry",
        system: "http://snomed.info/sct"
    },
    {
        code: "419610006",
        display: "Pediatric endocrinology",
        system: "http://snomed.info/sct"
    },
    {
        code: "418058008",
        display: "Pediatric gastroenterology",
        system: "http://snomed.info/sct"
    },
    {
        code: "419365004",
        display: "Pediatric nephrology",
        system: "http://snomed.info/sct"
    },
    {
        code: "418002000",
        display: "Pediatric oncology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394539006",
        display: "Pediatric surgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "409968004",
        display: "Preventive medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394587001",
        display: "Psychiatry",
        system: "http://snomed.info/sct"
    },
    {
        code: "394913002",
        display: "Psychotherapy",
        system: "http://snomed.info/sct"
    },
    {
        code: "408440000",
        display: "Public health medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "419815003",
        display: "Radiation oncology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394914008",
        display: "Radiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408455009",
        display: "Radiology - Interventional radiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394602003",
        display: "Rehabilitation",
        system: "http://snomed.info/sct"
    },
    {
        code: "394810000",
        display: "Rheumatology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408466002",
        display: "Surgery - Cardiac surgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "394609007",
        display: "Surgery - General",
        system: "http://snomed.info/sct"
    },
    {
        code: "394610002",
        display: "Surgery - Neurosurgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "394611003",
        display: "Surgery - Plastic surgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "394801008",
        display: "Surgery - Trauma and orthopedics",
        system: "http://snomed.info/sct"
    },
    {
        code: "408463005",
        display: "Surgery - Vascular",
        system: "http://snomed.info/sct"
    },
    {
        code: "394732004",
        display: "Surgical specialty - Other",
        system: "http://snomed.info/sct"
    },
    {
        code: "394590007",
        display: "Thoracic medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394612005",
        display: "Urology",
        system: "http://snomed.info/sct"
    }
];
// FHIR R4-compliant appointment status options
// http://hl7.org/fhir/R4/valueset-appointmentstatus.html
const APPOINTMENT_STATUS_OPTIONS = [
    {
        code: "pending",
        display: "Pending",
        system: "http://hl7.org/fhir/appointmentstatus",
        description: "Some or all of the participant(s) have not/did not appear for the appointment (usually the patient)."
    },
    {
        code: "booked",
        display: "Booked",
        system: "http://hl7.org/fhir/appointmentstatus",
        description: "All participant(s) have been considered and the appointment is confirmed to go ahead at the date/times specified."
    },
    {
        code: "fulfilled",
        display: "Fulfilled",
        system: "http://hl7.org/fhir/appointmentstatus",
        description: "The appointment has been completed and resulted in a clinical encounter."
    },
    {
        code: "cancelled",
        display: "Cancelled",
        system: "http://hl7.org/fhir/appointmentstatus",
        description: "The appointment has been cancelled."
    },
    {
        code: "noshow",
        display: "No Show",
        system: "http://hl7.org/fhir/appointmentstatus",
        description: "Some or all of the participant(s) have not/did not appear for the appointment (usually the patient)."
    },
    {
        code: "waitlist",
        display: "Waitlisted",
        system: "http://hl7.org/fhir/appointmentstatus",
        description: "The appointment has been placed on a waitlist, to be scheduled/confirmed in the future when a slot/service is available."
    }
];
// UI-specific mappings for Select components
APPOINTMENT_SPECIALTY_OPTIONS.map((specialty)=>({
        value: specialty.code,
        label: specialty.display
    }));
APPOINTMENT_STATUS_OPTIONS.map((status)=>({
        value: status.code,
        label: status.display
    }));

/**
 * CarePlan Category System
 * Custom Benchmark Health system for care plan categorization
 */ const CARE_PLAN_CATEGORY_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/care-plan-type";
/**
 * CarePlan Category Options
 * Defines the types of care plans available
 */ const CARE_PLAN_CATEGORY_OPTIONS = [
    {
        code: "intake",
        display: "Intake",
        system: CARE_PLAN_CATEGORY_SYSTEM,
        description: "Initial Member Intake & Onboarding"
    },
    {
        code: "triage",
        display: "Triage",
        system: CARE_PLAN_CATEGORY_SYSTEM,
        description: "Benchmark Care Triage & Navigation"
    },
    {
        code: "cocm",
        display: "CoCM",
        system: CARE_PLAN_CATEGORY_SYSTEM,
        description: "Benchmark CoCM"
    }
];
/**
 * CarePlan Activity Code System
 * Custom system for activity phases within care plans
 * Note: Activity definitions come from ActivityDefinition constants.
 * This system is used for CarePlan.activity.code codings.
 */ const CARE_PLAN_ACTIVITY_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/care-plan-activity";
/**
 * Metadata mapping for all v2 activities
 * Legacy codes resolve via taxonomy-mapping for backwards compatibility
 */ const ACTIVITY_METADATA = {
    // Intake
    "schedule-intake": {
        order: 1,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    },
    "complete-intake": {
        order: 2,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    },
    "finalize-care-plan-enrollment": {
        order: 3,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    },
    "check-in-on-member": {
        order: 4,
        category: "flexible",
        isFlexible: true,
        assignmentMode: "current-user"
    },
    // Triage — tasks start unassigned in the shared queue
    "select-services": {
        order: 1,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "unassigned-queue"
    },
    "approve-selected-services": {
        order: 2,
        category: "as-needed",
        isFlexible: true,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    },
    "offer-services": {
        order: 3,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "unassigned-queue"
    },
    "confirm-services": {
        order: 4,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "unassigned-queue"
    },
    "schedule-services": {
        order: 5,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "unassigned-queue"
    },
    "follow-up-on-services": {
        order: 6,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "unassigned-queue"
    },
    "complete-cadenced-followup": {
        order: 7,
        category: "sequential",
        isFlexible: false,
        maxOccurrences: 3,
        slaDurationHours: 1008,
        assignmentMode: "unassigned-queue"
    },
    // CoCM — all tasks assigned to patient's primary advocate (benchmark-advocate extension)
    // Note: `enroll-in-mirah` (order 1) was retired with the Benchmark/Mirah
    // partnership wind-down. `build-cocm-care-plan` is now the first CoCM
    // sequential activity. Historical task displays still resolve via taxonomy
    // mapping.
    "build-cocm-care-plan": {
        order: 1,
        category: "sequential",
        isFlexible: false,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    },
    "follow-up-on-cocm": {
        order: 3,
        category: "flexible",
        isFlexible: true,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    },
    "consult-psych": {
        order: 4,
        category: "flexible",
        isFlexible: true,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    },
    "consult-pcp": {
        order: 5,
        category: "flexible",
        isFlexible: true,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    },
    "discharge-member": {
        order: 50,
        category: "flexible",
        isFlexible: true,
        assignmentMode: "primary-advocate",
        assignmentGeneralPractitionerIndex: 1
    }
};
/**
 * Get activity metadata for a given activity code.
 * Resolves legacy codes to v2 for backwards compatibility.
 */ const getActivityMetadata = (code)=>{
    const v2Code = resolveActivityCode(code);
    return ACTIVITY_METADATA[v2Code];
};
/**
 * Create CarePlan activity option from ActivityDefinition code
 * Combines ActivityDefinition data with CarePlan metadata
 */ const createCarePlanActivityOption = (code)=>{
    const activityDef = ACTIVITY_DEFINITION_OPTIONS.find((a)=>a.code === code);
    const metadata = getActivityMetadata(code);
    if (!activityDef || !metadata) {
        return undefined;
    }
    return {
        code: activityDef.code,
        display: activityDef.display,
        system: CARE_PLAN_ACTIVITY_SYSTEM,
        description: activityDef.description,
        order: metadata.order,
        isFlexible: metadata.isFlexible,
        category: metadata.category
    };
};
/**
 * Get activities for a care plan type
 * Returns CarePlan activity options with metadata
 */ const getActivitiesForCarePlanType = (type)=>{
    const protocol = type === "cocm" ? "cocm" : type;
    const activityDefs = getActivityDefinitionsByProtocol(protocol);
    return activityDefs.map((def)=>createCarePlanActivityOption(def.code)).filter((option)=>option !== undefined);
};
/**
 * Get all CarePlan activity options
 * Useful for lookups and validation
 */ const getAllCarePlanActivityOptions = ()=>{
    return ACTIVITY_DEFINITION_OPTIONS.map((def)=>createCarePlanActivityOption(def.code)).filter((option)=>option !== undefined);
};
/**
 * Backwards-compatible exports for activity options
 * These now derive from ActivityDefinition constants with metadata
 */ getActivitiesForCarePlanType("intake");
const TRIAGE_ACTIVITY_OPTIONS = getActivitiesForCarePlanType("triage");
/**
 * Set of all triage activity codes for efficient lookup.
 * @agentic @benchmark/core
 */ new Set(TRIAGE_ACTIVITY_OPTIONS.map((opt)=>opt.code));
getActivitiesForCarePlanType("cocm");
/**
 * UI-ready select options
 */ CARE_PLAN_CATEGORY_OPTIONS.map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description
    }));
getActivitiesForCarePlanType("intake").map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description,
        isFlexible: option.isFlexible
    }));
getActivitiesForCarePlanType("triage").map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description,
        isFlexible: option.isFlexible
    }));
getActivitiesForCarePlanType("cocm").map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description,
        isFlexible: option.isFlexible
    }));
/**
 * CoCM Consent Date Extension URL
 * FHIR extension for storing the date when a patient consented to receive CoCM services
 */ const COCM_CONSENT_DATE_EXTENSION_URL = "http://benchmarkhealth.com/fhir/StructureDefinition/cocm-consent-date";

/** HCPCS Level II system (G-codes such as G2214). @agentic @benchmark/core */ const HCPCS_SYSTEM = "https://bluebutton.cms.gov/resources/codesystem/hcpcs";
/**
 * The code system for a billed CoCM code. Numeric CPT codes (99492/99493/99494)
 * use the AMA CPT system; G-codes (G2214) are HCPCS Level II.
 * @agentic @benchmark/core
 */ const billingCodeSystem = (code)=>code.startsWith("G") ? HCPCS_SYSTEM : CODE_SYSTEM.CPT;
/** `Composition.type` value for a CoCM monthly billing packet. @agentic @benchmark/core */ const COCM_BILLING_PACKET_CODE_SYSTEM = `${ORGANIZATION_BASE}/CodeSystem/cocm-billing-packet`;
/** `Composition.type` value token. @agentic @benchmark/core */ const COCM_BILLING_PACKET_TYPE_CODE = "cocm-monthly-billing-packet";
/** `Composition.type` payload. @agentic @benchmark/core */ const COCM_BILLING_PACKET_TYPE = {
    coding: [
        {
            system: COCM_BILLING_PACKET_CODE_SYSTEM,
            code: COCM_BILLING_PACKET_TYPE_CODE,
            display: "CoCM Monthly Billing Packet"
        }
    ],
    text: "CoCM Monthly Billing Packet"
};
/** `Composition.category` payload (billing). @agentic @benchmark/core */ const COCM_BILLING_PACKET_CATEGORY = {
    coding: [
        {
            system: COCM_BILLING_PACKET_CODE_SYSTEM,
            code: "billing",
            display: "Billing"
        }
    ]
};
/** Identifier system for the `{patientId}|{YYYY-MM}` composite key. @agentic @benchmark/core */ const COCM_BILLING_PACKET_IDENTIFIER_SYSTEM = `${ORGANIZATION_BASE}/identifier-system/cocm-billing-packet`;
/** Identifier system for the ChargeItem `{patientId}|{YYYY-MM}|{code}|{n}` key. @agentic @benchmark/core */ const COCM_CHARGE_ITEM_IDENTIFIER_SYSTEM = `${ORGANIZATION_BASE}/identifier-system/cocm-charge-item`;
/**
 * Extension URL carrying the packet + rules version on each ChargeItem, so a
 * line-item is self-describing (which rules produced it) for audit + dbt.
 * @agentic @benchmark/core
 */ const COCM_BILLING_PACKET_VERSION_EXTENSION_URL = `${EXTENSION_BASE}/cocm-billing-packet-version`;
/**
 * Extension URL carrying the FULL serialized `BillingCodePacket` (JSON) on the
 * attested Composition, so every criterion that produced the codes — assessments
 * used, enrollment window, attribution, episode flags, versions, timestamp — is
 * reconstructible from the persisted resource (D19). Human-readable sections +
 * ChargeItems remain for querying; this is the complete audit snapshot.
 * @agentic @benchmark/core
 */ const COCM_BILLING_PACKET_SNAPSHOT_EXTENSION_URL = `${EXTENSION_BASE}/cocm-billing-packet-snapshot`;
/**
 * `Composition.status` values that model the packet lock (mirrors the summary
 * Observation semantics): open→closed→reopened.
 *  - `preliminary` = open (draft, not yet locked)
 *  - `final`       = closed / locked (submitted)
 *  - `amended`     = reopened for correction
 * @agentic @benchmark/core
 */ const COCM_BILLING_PACKET_STATUS = {
    closed: "final",
    reopened: "amended"
};
/** Build the composite identifier value for a patient-month packet. @agentic @benchmark/core */ const buildCoCMBillingPacketIdentifierValue = (patientId, billingMonth)=>`${patientId}|${billingMonth}`;
/** `system|value` token for a packet identifier search. @agentic @benchmark/core */ const buildCoCMBillingPacketIdentifierToken = (patientId, billingMonth)=>`${COCM_BILLING_PACKET_IDENTIFIER_SYSTEM}|${buildCoCMBillingPacketIdentifierValue(patientId, billingMonth)}`;
/**
 * Build the identifier value for one billed-code ChargeItem occurrence
 * (`{patientId}|{YYYY-MM}|{code}|{n}`). Shared by the packet builder and the
 * submit/reopen reconciliation so both agree on line-item identity.
 * @agentic @benchmark/core
 */ const buildCoCMChargeItemIdentifierValue = (patientId, billingMonth, code, index)=>`${buildCoCMBillingPacketIdentifierValue(patientId, billingMonth)}|${code}|${index}`;
/**
 * Identifier-value prefix (`{patientId}|{YYYY-MM}|`) shared by every ChargeItem
 * of a patient-month, used to scope a reconciliation search to that month.
 * @agentic @benchmark/core
 */ const cocmChargeItemMonthPrefix = (patientId, billingMonth)=>`${buildCoCMBillingPacketIdentifierValue(patientId, billingMonth)}|`;

/**
 * FHIR-Compliant Constants for ClinicalImpression Domain
 *
 * This file contains all constants used in ClinicalImpression forms, utils, and related components.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 */ // FHIR-compliant clinical impression type options
const CLINICAL_IMPRESSION_TYPE_OPTIONS = [
    {
        code: "assessmentPlan",
        display: "Assessment and Plan",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "caseManagementSummary",
        display: "Case Management Summary",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "currentOutpatientTreaters",
        display: "Current Outpatient Treaters",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "chiefComplaint",
        display: "Chief Complaint",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "familyHistory",
        display: "Family History",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "generalNote",
        display: "General Note",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "hpiCurrentSymptoms",
        display: "HPI Current Symptoms",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "medicalHistory",
        display: "Medical History",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalHealthHistory",
        display: "Mental Health History",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalStatusAppearance",
        display: "Mental Status - Appearance",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalStatusBehavior",
        display: "Mental Status - Behavior",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalStatusSpeech",
        display: "Mental Status - Speech",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalStatusMoodAffect",
        display: "Mental Status - Mood/Affect",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalStatusThoughtProcessContent",
        display: "Mental Status - Thought Process/Content",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalStatusCognition",
        display: "Mental Status - Cognition",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalStatusPerception",
        display: "Mental Status - Perception",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "mentalStatusInsightJudgment",
        display: "Mental Status - Insight/Judgment",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "socialHistory",
        display: "Social History",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "substanceUseHistory",
        display: "Substance Use History",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    },
    {
        code: "traumaHistory",
        display: "Trauma History",
        system: "http://benchmarkhealth.com/fhir/ValueSet/clinical-impression-types"
    }
];
// FHIR-compliant clinical impression status options
// http://hl7.org/fhir/R4/valueset-clinicalimpression-status.html
const CLINICAL_IMPRESSION_STATUS_OPTIONS = [
    {
        code: "in-progress",
        display: "In Progress",
        system: "http://hl7.org/fhir/clinicalimpression-status"
    },
    {
        code: "completed",
        display: "Completed",
        system: "http://hl7.org/fhir/clinicalimpression-status"
    },
    {
        code: "entered-in-error",
        display: "Entered in Error",
        system: "http://hl7.org/fhir/clinicalimpression-status"
    }
];
// UI-specific mappings for Select components
CLINICAL_IMPRESSION_TYPE_OPTIONS.map((type)=>({
        value: type.code,
        label: type.display
    }));
CLINICAL_IMPRESSION_STATUS_OPTIONS.map((status)=>({
        value: status.code,
        label: status.display
    }));
// Backward compatibility: Create a lookup object for existing code
CLINICAL_IMPRESSION_TYPE_OPTIONS.reduce((acc, type)=>{
    acc[type.code] = type.display;
    return acc;
}, {});

/**
 * FHIR-Compliant Constants for Communication Domain
 *
 * This file contains all constants used in Communication forms, utils, and related components.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 *
 * ── Sacred Communication model (ratified 2026-06-08, Decision R1: multi-system) ──
 *
 * Every creation path and every backfill must satisfy this single contract.
 * Channel and interaction type are deliberately separate axes; the category
 * systems are purpose-specific and are NOT collapsed into one enum.
 *
 *   medium    → CHANNEL ("how"). Always HL7 `v3-ParticipationMode`
 *               (`COMMUNICATION_MEDIUM_SYSTEM`): phone | text | email |
 *               secure-message | fax | portal. Set whenever a real channel
 *               exists; omitted only for genuinely channel-less records.
 *   category  → TYPE/PURPOSE ("what"). One coding per cohesive question, each on
 *               its own canonical Benchmark host:
 *                 • `COMMUNICATION_TYPE_SYSTEM`        patient-message |
 *                                                      manual-outreach | nps-4-week |
 *                                                      nps-onboarding | nps-ongoing-care
 *                 • `CALL_STATE_SYSTEM` (calls only)   completed | missed | voicemail
 *                 • `PROGRESS_LOG_CATEGORY_SYSTEM`     outreach-attempt |
 *                                                      provider-outreach |
 *                                                      administrative-note
 *                 • `AVAILABILITY_UPDATE_CATEGORY_SYSTEM`  availability-update
 *   reasonCode → OUTCOME/FINDING (progress-log-outcome, availability-finding).
 *   statusReason → NPS only (`NPS_STATUS_REASON_SYSTEM`).
 *   sender/recipient/subject → `ResourceType/id` WITH `display`, semantically
 *               correct per archetype (no patient-as-recipient on provider
 *               outreach).
 *   status    → per-archetype policy. Hardcoded `completed` on the staff/log
 *               paths (interaction occurred); NPS is the only path that varies
 *               it; SMS/call lifecycle status is deferred (Decision D7).
 *
 * Deprecated, read-only, target = 0 rows after backfill (legacy detection +
 * read-side fallback ONLY): `LEGACY_COMMUNICATION_CHANNEL_SYSTEM`,
 * `LEGACY_CALL_STATE_SYSTEM`. Channel now lives exclusively in `medium`.
 *
 * Conscious exception: call extensions (`call-duration-ms`, `call-direction`)
 * still use the `benchmark.health/fhir/StructureDefinition/...` host. They are
 * extension URLs, not category systems, so they are out of the canonical-host
 * migration (Decision D3) scope.
 */ /** HL7 v3-ParticipationMode system URL for communication medium codings. */ const COMMUNICATION_MEDIUM_SYSTEM = "http://terminology.hl7.org/CodeSystem/v3-ParticipationMode";
/** All communication medium options (member + provider facing). */ const COMMUNICATION_MEDIUM_OPTIONS = [
    {
        code: "phone",
        display: "Phone",
        system: COMMUNICATION_MEDIUM_SYSTEM
    },
    {
        code: "text",
        display: "Text",
        system: COMMUNICATION_MEDIUM_SYSTEM
    },
    {
        code: "email",
        display: "Email",
        system: COMMUNICATION_MEDIUM_SYSTEM
    },
    {
        code: "secure-message",
        display: "Secure Message",
        system: COMMUNICATION_MEDIUM_SYSTEM
    },
    {
        code: "fax",
        display: "Fax",
        system: COMMUNICATION_MEDIUM_SYSTEM
    },
    {
        code: "portal",
        display: "Portal",
        system: COMMUNICATION_MEDIUM_SYSTEM
    }
];
/** Member-facing medium select options (phone, text, email, secure-message). */ COMMUNICATION_MEDIUM_OPTIONS.filter((o)=>o.code !== "fax" && o.code !== "portal").map((option)=>({
        value: option.code,
        label: option.display
    }));
/** Provider-facing medium select options (phone, fax, email, portal). */ COMMUNICATION_MEDIUM_OPTIONS.filter((o)=>o.code === "phone" || o.code === "fax" || o.code === "email" || o.code === "portal").map((option)=>({
        value: option.code,
        label: option.display
    }));

/**
 * FHIR-Compliant Constants for Coverage Domain
 *
 * This file contains all constants used in Coverage forms, utils, and related components.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 */ // FHIR R4-compliant coverage type options
// http://hl7.org/fhir/R4/valueset-coverage-type.html
const COVERAGE_TYPE_OPTIONS = [
    {
        code: "EHCPOL",
        display: "Extended Healthcare",
        system: "http://terminology.hl7.org/CodeSystem/v3-ActCode"
    },
    {
        code: "PUBLICPOL",
        display: "Public Healthcare",
        system: "http://terminology.hl7.org/CodeSystem/v3-ActCode"
    },
    {
        code: "DENTPRG",
        display: "Dental Program",
        system: "http://terminology.hl7.org/CodeSystem/v3-ActCode"
    },
    {
        code: "MENTPRG",
        display: "Mental Health Program",
        system: "http://terminology.hl7.org/CodeSystem/v3-ActCode"
    },
    {
        code: "SUBPRG",
        display: "Substance Use Program",
        system: "http://terminology.hl7.org/CodeSystem/v3-ActCode"
    },
    {
        code: "VISPOL",
        display: "Vision Care Policy",
        system: "http://terminology.hl7.org/CodeSystem/v3-ActCode"
    }
];
// FHIR R4-compliant coverage status options
// http://hl7.org/fhir/R4/valueset-fm-status.html
const COVERAGE_STATUS_OPTIONS = [
    {
        code: "active",
        display: "Active",
        system: "http://hl7.org/fhir/fm-status",
        description: "The coverage is currently in-force and providing benefits."
    },
    {
        code: "cancelled",
        display: "Cancelled",
        system: "http://hl7.org/fhir/fm-status",
        description: "The coverage has been cancelled and is no longer in effect."
    },
    {
        code: "draft",
        display: "Draft",
        system: "http://hl7.org/fhir/fm-status",
        description: "The coverage is being set up but not yet active."
    },
    {
        code: "entered-in-error",
        display: "Entered in Error",
        system: "http://hl7.org/fhir/fm-status",
        description: "The coverage was entered in error and should be ignored."
    }
];
// FHIR R4-compliant relationship options
// http://hl7.org/fhir/R4/valueset-subscriber-relationship.html
const COVERAGE_RELATIONSHIP_OPTIONS = [
    {
        code: "self",
        display: "Self",
        system: "http://terminology.hl7.org/CodeSystem/subscriber-relationship",
        description: "The beneficiary is the subscriber."
    },
    {
        code: "spouse",
        display: "Spouse",
        system: "http://terminology.hl7.org/CodeSystem/subscriber-relationship",
        description: "The beneficiary is the spouse or equivalent of the subscriber."
    },
    {
        code: "child",
        display: "Child",
        system: "http://terminology.hl7.org/CodeSystem/subscriber-relationship",
        description: "The beneficiary is a child of the subscriber."
    },
    {
        code: "parent",
        display: "Parent",
        system: "http://terminology.hl7.org/CodeSystem/subscriber-relationship",
        description: "The beneficiary is a parent of the subscriber."
    },
    {
        code: "other",
        display: "Other",
        system: "http://terminology.hl7.org/CodeSystem/subscriber-relationship",
        description: "The beneficiary has some other relationship to the subscriber."
    }
];
// UI-specific mappings for Select components
COVERAGE_TYPE_OPTIONS.map((type)=>({
        value: type.code,
        label: type.display
    }));
COVERAGE_STATUS_OPTIONS.map((status)=>({
        value: status.code,
        label: status.display
    }));
COVERAGE_RELATIONSHIP_OPTIONS.map((relationship)=>({
        value: relationship.code,
        label: relationship.display
    }));

/**
 * Legacy Patient Engagement Status (V1)
 * @deprecated Use PATIENT_ENGAGEMENT_STATUS_OPTIONS instead
 * Maintained for backward compatibility only
 */ const LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM = "http://terminology.hl7.org/CodeSystem/patient-engagement";
const LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS = [
    {
        code: "initial-referral",
        display: "Initial Referral",
        system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v1",
        deprecated: true
    },
    {
        code: "engaged",
        display: "Engaged",
        system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v1",
        deprecated: false
    },
    {
        code: "activated",
        display: "Activated",
        system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v1",
        deprecated: false
    },
    {
        code: "unsure",
        display: "Unsure",
        system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v1",
        deprecated: true
    },
    {
        code: "declined",
        display: "Declined Services",
        system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v1",
        deprecated: false
    },
    {
        code: "unable-to-reach",
        display: "Unable to Reach",
        system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v1",
        deprecated: true
    }
];
/**
 * Patient Engagement Status (V2 - Current)
 * Enhanced engagement status codes following FHIR best practices
 */ const PATIENT_ENGAGEMENT_STATUS_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/patient-engagement-v2";
const PATIENT_ENGAGEMENT_STATUS_OPTIONS = [
    {
        code: "referred",
        display: "Referred",
        system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v2",
        description: "Initial state when ServiceRequest created"
    },
    {
        code: "engaged",
        display: "Engaged",
        system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v2",
        description: "Consented to services, completed intake"
    },
    {
        code: "activated",
        display: "Activated",
        system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v2",
        description: "Patient activated in system (backwards compatibility)"
    },
    {
        code: "graduated",
        display: "Graduated",
        system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v2",
        description: "Successfully completed program"
    },
    {
        code: "unreachable",
        display: "Unreachable",
        system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v2",
        description: "Unable to contact after multiple attempts"
    },
    {
        code: "declined",
        display: "Declined",
        system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v2",
        description: "Explicitly declined services"
    },
    {
        code: "discharged",
        display: "Discharged",
        system: PATIENT_ENGAGEMENT_STATUS_SYSTEM,
        version: "v2",
        description: "Administratively discharged from program"
    }
];
/**
 * All Patient Engagement Status Options (V1 + V2)
 * For display/editing existing patient statuses
 */ [
    ...LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS,
    ...PATIENT_ENGAGEMENT_STATUS_OPTIONS
];
/**
 * UI-ready select options for engagement status
 */ PATIENT_ENGAGEMENT_STATUS_OPTIONS.map((status)=>({
        value: status.code,
        label: status.display,
        description: status.description
    }));
(()=>{
    // Deduplicate by preferring V2 entries over V1 when codes match
    const seenCodes = new Set();
    const result = [];
    // Add V2 entries first (preferred)
    for (const status of PATIENT_ENGAGEMENT_STATUS_OPTIONS){
        if (!seenCodes.has(status.code)) {
            seenCodes.add(status.code);
            result.push({
                value: status.code,
                label: status.display,
                deprecated: false,
                version: status.version
            });
        }
    }
    // Add V1 entries that don't exist in V2
    for (const status of LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS){
        if (!seenCodes.has(status.code)) {
            seenCodes.add(status.code);
            result.push({
                value: status.code,
                label: status.display,
                deprecated: "deprecated" in status ? status.deprecated : false,
                version: status.version
            });
        }
    }
    return result;
})();

/**
 * State codes for which Connections currently authors and queries
 * coverage areas. Adding a new state is a one-line change here.
 * @agentic @benchmark/core
 */ const SUPPORTED_COVERAGE_AREA_STATE_CODES = [
    "MA",
    "CA"
];
new Set(SUPPORTED_COVERAGE_AREA_STATE_CODES);

/**
 * FHIR-Compliant Constants for Encounter Domain
 *
 * This file contains all constants used in Encounter forms, utils, and related components.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 *
 * ARCHITECTURE NOTE:
 * These constants replace the JSON ValueSets in packages/medplum/src/data/valuesets/.
 * While the JSON approach offers better FHIR compliance and versioning support,
 * the TypeScript constants provide immediate developer experience benefits:
 * - Type safety and IntelliSense
 * - Direct UI mappings for Select components
 * - Helper functions for display lookups
 *
 * VERSIONING STRATEGY:
 * - LEGACY_ENCOUNTER_TYPE_OPTIONS: Deprecated v1 types (for backwards compatibility)
 * - ENCOUNTER_TYPE_OPTIONS: Current v2 types (for new forms)
 * - ALL_ENCOUNTER_TYPE_OPTIONS: Combined for display/lookup functions
 *
 * Future consideration: Migrate to versioned JSON ValueSet system for better
 * FHIR server integration and centralized management.
 */ // =============================================================================
// LEGACY ENCOUNTER TYPES (v1) - DEPRECATED
// =============================================================================
// These are maintained for backwards compatibility with existing encounters.
// DO NOT use these in new forms - use ENCOUNTER_TYPE_OPTIONS instead.
const CLINICAL_INTAKE_ENCOUNTER_TYPE_CODE = "clinical-intake-interaction";
const COCM_INTAKE_ENCOUNTER_TYPE_CODE = "cocm-intake-interaction";
/** Wellness-track follow-up code. Mirrors the clinical (90791) intake track. */ const CLINICAL_FOLLOW_UP_ENCOUNTER_TYPE_CODE = "clinical-follow-up-interaction";
/** CoCM-track follow-up code. Minutes roll into the month-end CoCM packet. */ const COCM_FOLLOW_UP_ENCOUNTER_TYPE_CODE = "cocm-follow-up-interaction";
/**
 * No-bill intake code. Written when the Schedule Intake billing flag is
 * "No bill"; parity with the billed intake codes so unbilled intakes are a
 * first-class (non-billable) intake type rather than an untagged encounter.
 */ const UNBILLED_INTAKE_ENCOUNTER_TYPE_CODE = "unbilled-intake-interaction";
/**
 * No-bill follow-up code. Mirrors {@link UNBILLED_INTAKE_ENCOUNTER_TYPE_CODE}
 * for follow-up encounters whose originating intake was unbilled.
 */ const UNBILLED_FOLLOW_UP_ENCOUNTER_TYPE_CODE = "unbilled-follow-up-interaction";
/**
 * @deprecated Use ENCOUNTER_TYPE_OPTIONS for new forms.
 * This constant is maintained only for backwards compatibility with existing encounters.
 */ const LEGACY_ENCOUNTER_TYPE_OPTIONS = [
    {
        code: "outreach",
        display: "Outreach",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v1",
        deprecated: true,
        deprecationReason: "Outreach interactions are now handled by Communication resources"
    },
    {
        code: "initial",
        display: "Initial Assessment",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v1",
        deprecated: true,
        deprecationReason: "Replaced by more specific interaction types in v2"
    },
    {
        code: "check-in-evaluation",
        display: "Check-In/Evaluation",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v1",
        deprecated: true,
        deprecationReason: "Replaced by more specific interaction types in v2"
    },
    {
        code: "follow-up",
        display: "Follow-Up Assessment",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v1",
        deprecated: true,
        deprecationReason: "Replaced by more specific interaction types in v2"
    }
];
// =============================================================================
// CURRENT ENCOUNTER TYPES (v2) - ACTIVE
// =============================================================================
// These are the current encounter types for use in new forms and encounters.
const ENCOUNTER_TYPE_OPTIONS = [
    {
        code: "general-interaction",
        display: "General Interaction",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "General visit for information, coordination, or support"
    },
    {
        code: CLINICAL_INTAKE_ENCOUNTER_TYPE_CODE,
        display: "Clinical Intake Interaction",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "Initial clinical intake visit (90791) for assessment and planning"
    },
    {
        code: COCM_INTAKE_ENCOUNTER_TYPE_CODE,
        display: "CoCM Intake Interaction",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "Initial Collaborative Care visit (99492) with expanded clinical history and goal setting"
    },
    {
        code: CLINICAL_FOLLOW_UP_ENCOUNTER_TYPE_CODE,
        display: "Clinical Follow-Up Interaction",
        uiLabel: "Followup",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "Follow-up visit with an enrolled member. The billing track is confirmed on the Details step."
    },
    {
        code: "case-management-interaction",
        display: "Case Management Interaction",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "Case management visit for coordination and support"
    },
    {
        code: COCM_FOLLOW_UP_ENCOUNTER_TYPE_CODE,
        display: "CoCM Follow-Up Interaction",
        uiLabel: "CoCM Followup",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "Follow-up Collaborative Care visit with ongoing care coordination, goal progress tracking, and monitoring"
    },
    {
        code: "discharge-interaction",
        display: "Discharge Interaction",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "Final member interaction documenting discharge reason, follow-up plan, and care transition"
    },
    {
        code: UNBILLED_INTAKE_ENCOUNTER_TYPE_CODE,
        display: "Unbilled Intake Interaction",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "Initial intake visit documented but not billed (schedule-intake billing flag = No bill)"
    },
    {
        code: UNBILLED_FOLLOW_UP_ENCOUNTER_TYPE_CODE,
        display: "Unbilled Follow-Up Interaction",
        uiLabel: "Unbilled Followup",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-type",
        version: "v2",
        description: "Follow-up visit for a member whose intake was unbilled; documented but not billed"
    }
];
// =============================================================================
// COMBINED OPTIONS FOR BACKWARDS COMPATIBILITY
// =============================================================================
// This includes both legacy and current options for display/lookup functions
const ALL_ENCOUNTER_TYPE_OPTIONS = [
    ...LEGACY_ENCOUNTER_TYPE_OPTIONS,
    ...ENCOUNTER_TYPE_OPTIONS
];
const ENCOUNTER_METHOD_OPTIONS = [
    {
        code: "phone",
        display: "Phone",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-method",
        description: "Voice conversation with patient"
    },
    {
        code: "text",
        display: "Text",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-method",
        description: "SMS-based communication"
    },
    {
        code: "email",
        display: "Email",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-method",
        description: "Email-based communication"
    },
    {
        code: "emr-secure-message",
        display: "Secure Message",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-method",
        description: "Secure messaging through an EMR"
    },
    {
        code: "video",
        display: "Video Chat",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-method",
        description: "Video-based telehealth session"
    },
    {
        code: "in-person",
        display: "In-Person",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-method",
        description: "Face-to-face meeting"
    }
];
// Encounter direction options
const ENCOUNTER_DIRECTION_OPTIONS = [
    {
        code: "outbound",
        display: "Outbound",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-direction",
        description: "We initiated this interaction"
    },
    {
        code: "inbound",
        display: "Inbound",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-direction",
        description: "Member initiated this interaction"
    }
];
// Encounter success status options
/**
 * @deprecated This constant is deprecated by Communication resource support.
 * This constant is maintained only for backwards compatibility with existing encounters.
 */ const ENCOUNTER_SUCCESS_OPTIONS = [
    {
        code: "pending",
        display: "Pending",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-outcome"
    },
    {
        code: "successful",
        display: "Successful",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-outcome"
    },
    {
        code: "unsuccessful",
        display: "Unsuccessful",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-outcome"
    }
];
// Encounter billable status options
const ENCOUNTER_BILLABLE_OPTIONS = [
    {
        code: "billable",
        display: "Billable",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-billing",
        description: "This interaction meets the criteria for billing"
    },
    {
        code: "non-billable",
        display: "Non-Billable",
        system: "http://www.benchmarkhealth.com/fhir/ValueSet/encounter-billing",
        description: "This interaction does not meet the criteria for billing"
    }
];
/**
 * @deprecated Import PATIENT_ENGAGEMENT_STATUS_OPTIONS (V2) or LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS (V1) directly from patient.ts instead
 * This re-export is removed to avoid export conflicts in the domain constants index.
 *
 * For V1 statuses: import { LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS } from "./patient"
 * For V2 statuses: import { PATIENT_ENGAGEMENT_STATUS_OPTIONS } from "./patient"
 */ // =============================================================================
// UI-SPECIFIC MAPPINGS FOR SELECT COMPONENTS
// =============================================================================
/**
 * The label a clinician reads for an encounter type. `display` is the FHIR
 * `meta.tag.display` written on save and the join key for dbt's
 * `encounter_category`, so it is frozen; `uiLabel` lets a surface read
 * differently without touching stored data or analytics (D18/D21).
 */ const getEncounterTypeUiLabel = (type)=>type.uiLabel ?? type.display;
// Current (non-deprecated) types offered in new-form pickers. Excludes the
// unbilled tracks: those codes are derived from the Schedule Intake billing
// flag (intake) or the member's care plan / originating intake (follow-up) and
// are never chosen directly from this picker, so they must not appear as
// selectable options. Use ALL_ENCOUNTER_TYPE_SELECT_OPTIONS for
// display/editing of existing encounters (which must still resolve the
// unbilled labels).
ENCOUNTER_TYPE_OPTIONS.filter((type)=>type.code !== UNBILLED_INTAKE_ENCOUNTER_TYPE_CODE && type.code !== UNBILLED_FOLLOW_UP_ENCOUNTER_TYPE_CODE).map((type)=>({
        value: type.code,
        label: getEncounterTypeUiLabel(type),
        description: type.description
    }));
// All types (including legacy) for display/editing existing encounters
ALL_ENCOUNTER_TYPE_OPTIONS.map((type)=>({
        value: type.code,
        label: getEncounterTypeUiLabel(type),
        deprecated: "deprecated" in type ? type.deprecated : false,
        version: "version" in type ? type.version : undefined
    }));
// Standard select options (backward compatible)
ENCOUNTER_METHOD_OPTIONS.map((method)=>({
        value: method.code,
        label: method.display
    }));
// Enhanced options with descriptions for CardSelect component
ENCOUNTER_METHOD_OPTIONS.map((method)=>({
        value: method.code,
        label: method.display,
        description: method.description
    }));
ENCOUNTER_DIRECTION_OPTIONS.map((direction)=>({
        value: direction.code,
        label: direction.display,
        description: direction.description
    }));
ENCOUNTER_BILLABLE_OPTIONS.map((billable)=>({
        value: billable.code,
        label: billable.display,
        description: billable.description
    }));
ENCOUNTER_DIRECTION_OPTIONS.map((direction)=>({
        value: direction.code,
        label: direction.display
    }));
ENCOUNTER_SUCCESS_OPTIONS.map((success)=>({
        value: success.code,
        label: success.display
    }));
ENCOUNTER_BILLABLE_OPTIONS.map((billable)=>({
        value: billable.code,
        label: billable.display
    }));

/**
 * All legacy (v1) encounter codes. Stable ordering matches
 * `LEGACY_ENCOUNTER_TYPE_OPTIONS`.
 * @agentic @benchmark/core
 */ LEGACY_ENCOUNTER_TYPE_OPTIONS.map((t)=>t.code);

/**
 * Flag Domain Constants (FHIR R4)
 *
 * Defines controlled value sets for Flag.category and Flag.code.
 * - category: HL7-defined categories for flags
 * - code: Curated SNOMED CT clinical findings relevant to safety/behavioral risk flags
 */ // Flag.category uses HL7 flag-category codes
// Flag.code is a SNOMED CT clinical finding; constrain to relevant risk/safety findings
const FLAG_CODE_SYSTEM = "http://snomed.info/sct";
// Organization-specific codes for operational/policy flags
const FLAG_LOCAL_CODE_SYSTEM = "https://benchmark.health/fhir/CodeSystem/flag-code";
const FLAG_CODE_OPTIONS = [
    // Suicide risk and self-harm
    {
        code: "161901003",
        display: "Self-Harm",
        system: FLAG_CODE_SYSTEM,
        category: "safety",
        description: "History of self-harming behavior"
    },
    {
        code: "225444004",
        display: "Suicidality",
        system: FLAG_CODE_SYSTEM,
        category: "safety",
        description: "History of suicidal ideation or behavior"
    },
    {
        code: "275939002",
        display: "Suicide Attempt(s)",
        system: FLAG_CODE_SYSTEM,
        category: "safety",
        description: "History of suicide attempts"
    },
    // Violence/aggression risk
    {
        code: "248062006",
        display: "Aggression",
        system: FLAG_CODE_SYSTEM,
        category: "safety",
        description: "History of aggression"
    },
    {
        code: "110483000",
        display: "Violence",
        system: FLAG_CODE_SYSTEM,
        category: "safety",
        description: "History of violence"
    },
    // Operational/policy flags (local codes)
    {
        code: "clinician-contact-only",
        display: "Clinician Contact Only",
        system: FLAG_LOCAL_CODE_SYSTEM,
        category: "contact",
        description: "Member may only be contacted by a clinician"
    },
    {
        code: "cocm-enrolled",
        display: "Enrolled in COCM",
        system: FLAG_LOCAL_CODE_SYSTEM,
        category: "admin",
        description: "Member is enrolled in COCM"
    },
    {
        code: "patient-under-18",
        display: "Minor (Under 18)",
        system: FLAG_LOCAL_CODE_SYSTEM,
        category: "admin",
        description: "Member is a minor"
    },
    {
        code: "patient-paused",
        display: "Paused",
        system: FLAG_LOCAL_CODE_SYSTEM,
        category: "admin",
        description: "Member is temporarily paused"
    }
];
// UI-ready options for CardSelect with descriptions
FLAG_CODE_OPTIONS.map((o)=>({
        value: o.code,
        label: o.display,
        description: o.description
    }));

/**
 * Characteristic Visit Mode options (service-characteristic-visit-mode CodeSystem)
 * Comprehensive service delivery channels including modern text/app-based modalities
 */ const SERVICE_CHARACTERISTIC_VISIT_MODE_OPTIONS = [
    // Synchronous in-person
    {
        value: "in-person",
        label: "In-Person"
    },
    // Combinations
    {
        value: "hybrid",
        label: "Hybrid"
    },
    // Synchronous remote
    {
        value: "telehealth-video",
        label: "Telehealth (Video)"
    },
    {
        value: "telehealth-phone",
        label: "Telehealth (Phone)"
    },
    // Text-based
    {
        value: "text-sms",
        label: "Text/SMS"
    },
    {
        value: "chat",
        label: "Web Chat"
    },
    // Asynchronous
    {
        value: "async-messaging",
        label: "Asynchronous Messaging"
    },
    // Digital/self-guided
    {
        value: "digital-self-guided",
        label: "Digital Self-Guided"
    }
];

/**
 * FHIR-Compliant Constants for Insurance Plan Domain
 *
 * This file contains all constants used for InsurancePlan resources,
 * including plan type classification and network type classification.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 */ // =============================================================================
// INSURANCE PLAN TYPE CONSTANTS
// =============================================================================
/**
 * Insurance Plan Type CodeSystem URL
 * Used in InsurancePlan.type for plan classification
 */ const INSURANCE_PLAN_TYPE_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/insurance-plan-type";
/**
 * Insurance Plan Type Options (alphabetical by code)
 * Defines the classification system for insurance plan types:
 * - commercial: Standard commercial plans
 * - commercial-epo: Commercial EPO plans
 * - commercial-hmo: Commercial HMO plans
 * - commercial-ppo: Commercial PPO plans
 * - d-snp: Dual Eligible Special Needs Plans
 * - dual-eligible: Integrated dual-eligible plans (Medicare/Medicaid)
 * - eap: Employee Assistance Program plans
 * - fep: Federal Employees Program plans
 * - fep-ppo: Federal Employees Program PPO plans
 * - marketplace: ACA Marketplace plans
 * - masshealth-aco: MassHealth ACO plans
 * - masshealth-mco: MassHealth MCO plans
 * - medicaid: Medicaid plans
 * - medicare-advantage: Medicare Advantage/Replacement plans
 * - medicare-advantage-hmo: Medicare Advantage HMO plans
 * - medicare-advantage-ppo: Medicare Advantage PPO plans
 * - medicare-supplement: Medicare Supplement (Medigap) plans
 * - tricare: Military TRICARE plans
 * - tricare-hmo: TRICARE HMO plans
 * - tricare-ppo: TRICARE PPO plans
 * - workers-comp: Workers' Compensation plans
 */ const INSURANCE_PLAN_TYPE_OPTIONS = [
    {
        code: "commercial",
        display: "Commercial",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Standard commercial insurance plans"
    },
    {
        code: "commercial-epo",
        display: "Commercial EPO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Commercial Exclusive Provider Organization plans"
    },
    {
        code: "commercial-hmo",
        display: "Commercial HMO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Commercial Health Maintenance Organization plans"
    },
    {
        code: "commercial-ppo",
        display: "Commercial PPO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Commercial Preferred Provider Organization plans"
    },
    {
        code: "d-snp",
        display: "D-SNP",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Dual Eligible Special Needs Plan"
    },
    {
        code: "dual-eligible",
        display: "Dual Eligible (Medicare/Medicaid)",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Integrated dual-eligible plans for Medicare and Medicaid beneficiaries"
    },
    {
        code: "eap",
        display: "Employee Assistance Program",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Employee Assistance Program plans"
    },
    {
        code: "fep",
        display: "FEP",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Federal Employees Program plans"
    },
    {
        code: "fep-ppo",
        display: "FEP PPO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Federal Employees Program PPO plans"
    },
    {
        code: "marketplace",
        display: "ACA Marketplace",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "ACA Marketplace plans"
    },
    {
        code: "masshealth-aco",
        display: "MassHealth ACO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "MassHealth Accountable Care Organization plans"
    },
    {
        code: "masshealth-mco",
        display: "MassHealth MCO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "MassHealth Managed Care Organization plans"
    },
    {
        code: "medicaid",
        display: "Medicaid",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Medicaid plans"
    },
    {
        code: "medicare-advantage",
        display: "Medicare Advantage",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Medicare Advantage/Replacement plans"
    },
    {
        code: "medicare-advantage-hmo",
        display: "Medicare Advantage HMO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Medicare Advantage Health Maintenance Organization plans"
    },
    {
        code: "medicare-advantage-ppo",
        display: "Medicare Advantage PPO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Medicare Advantage Preferred Provider Organization plans"
    },
    {
        code: "medicare-supplement",
        display: "Medicare Supplement",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Medicare Supplement (Medigap) plans"
    },
    {
        code: "tricare",
        display: "TRICARE",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Military TRICARE plans"
    },
    {
        code: "tricare-hmo",
        display: "TRICARE HMO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Military TRICARE HMO plans"
    },
    {
        code: "tricare-ppo",
        display: "TRICARE PPO",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Military TRICARE PPO plans"
    },
    {
        code: "workers-comp",
        display: "Workers' Compensation",
        system: INSURANCE_PLAN_TYPE_SYSTEM,
        description: "Workers' Compensation plans"
    }
];
/**
 * UI-ready select options for insurance plan type
 */ INSURANCE_PLAN_TYPE_OPTIONS.map((type)=>({
        value: type.code,
        label: type.display
    }));
// =============================================================================
// NETWORK TYPE CONSTANTS
// =============================================================================
/**
 * Network Type CodeSystem URL
 * Used in InsurancePlan.network for network classification
 */ const NETWORK_TYPE_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/insurance-network-type";
/**
 * Network Type Options
 * Defines the classification system for insurance network types:
 * - hmo: Health Maintenance Organization
 * - ppo: Preferred Provider Organization
 * - epo: Exclusive Provider Organization
 * - pos: Point of Service
 * - hdhp: High Deductible Health Plan
 */ const NETWORK_TYPE_OPTIONS = [
    {
        code: "hmo",
        display: "HMO",
        system: NETWORK_TYPE_SYSTEM,
        description: "Health Maintenance Organization"
    },
    {
        code: "ppo",
        display: "PPO",
        system: NETWORK_TYPE_SYSTEM,
        description: "Preferred Provider Organization"
    },
    {
        code: "epo",
        display: "EPO",
        system: NETWORK_TYPE_SYSTEM,
        description: "Exclusive Provider Organization"
    },
    {
        code: "pos",
        display: "POS",
        system: NETWORK_TYPE_SYSTEM,
        description: "Point of Service"
    },
    {
        code: "hdhp",
        display: "HDHP",
        system: NETWORK_TYPE_SYSTEM,
        description: "High Deductible Health Plan"
    }
];
/**
 * UI-ready select options for network type
 */ NETWORK_TYPE_OPTIONS.map((type)=>({
        value: type.code,
        label: type.display
    }));

/**
 * CodeSystem URL for the selected billing-flag value (eligibility + reality prefill).
 * @agentic @benchmark/core
 */ const INTAKE_BILLING_FLAG_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/intake-billing-flag";
/**
 * Billing-flag options shown as cards on the Schedule Intake step.
 * @agentic @benchmark/core
 */ const INTAKE_BILLING_FLAG_OPTIONS = [
    {
        code: "cocm",
        display: "CoCM-eligible",
        system: INTAKE_BILLING_FLAG_SYSTEM,
        description: "Patient is eligible for CoCM (99492)."
    },
    {
        code: "wellness",
        display: "Wellness-eligible",
        system: INTAKE_BILLING_FLAG_SYSTEM,
        description: "Patient is eligible for Wellness exam (90791)."
    },
    {
        code: "no-bill",
        display: "No Bill",
        system: INTAKE_BILLING_FLAG_SYSTEM,
        description: "Patient is not eligible for billing."
    }
];
/**
 * UI-ready select/card options for the intake billing flag.
 * @agentic @benchmark/core
 */ INTAKE_BILLING_FLAG_OPTIONS.map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description
    }));

/**
 * FHIR-Compliant Constants for Organization Domain
 *
 * This file contains all constants used for Organization resources,
 * including business partnership tier classification and organization type classification.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 */ // =============================================================================
// ORGANIZATION TIER CONSTANTS
// =============================================================================
/**
 * Organization Tier CodeSystem URL
 * Used in meta.tag for Organization resources to track business partnership tiers
 */ const ORGANIZATION_TIER_SYSTEM = "https://benchmarkhealth.com/fhir/CodeSystem/organization-tier";
/**
 * Organization Tier Options
 * Defines the three-tier classification system for business partnerships:
 * - Tier 1: QuIN Member Provider
 * - Tier 2: QuIN Candidate or Preferred Provider
 * - Tier 3: Non-QuIN Provider
 */ const ORGANIZATION_TIER_OPTIONS = [
    {
        code: "tier-1",
        display: "Tier 1",
        system: ORGANIZATION_TIER_SYSTEM,
        description: "QuIN Member Provider"
    },
    {
        code: "tier-2",
        display: "Tier 2",
        system: ORGANIZATION_TIER_SYSTEM,
        description: "QuIN Candidate or Preferred Provider"
    },
    {
        code: "tier-3",
        display: "Tier 3",
        system: ORGANIZATION_TIER_SYSTEM,
        description: "Non-QuIN Provider"
    }
];
/**
 * UI-ready select options for organization tier
 */ ORGANIZATION_TIER_OPTIONS.map((tier)=>({
        value: tier.code,
        label: tier.display,
        description: tier.description
    }));
// =============================================================================
// ORGANIZATION TYPE CONSTANTS
// =============================================================================
/**
 * FHIR R4 Standard Organization Type CodeSystem URL
 * Used in Organization.type for standard FHIR organization type classification
 */ const ORGANIZATION_TYPE_SYSTEM = "http://terminology.hl7.org/CodeSystem/organization-type";
/**
 * Custom Organization Type CodeSystem URL
 * Used in Organization.type for Benchmark-specific organization types
 */ const CUSTOM_ORGANIZATION_TYPE_SYSTEM = "https://benchmarkhealth.com/fhir/CodeSystem/organization-type";
/**
 * FHIR Standard Organization Type Options
 * Standard FHIR R4 organization type codes from HL7 terminology
 * Commented out codes are not used in the current application
 */ const FHIR_ORGANIZATION_TYPE_OPTIONS = [
    {
        code: "prov",
        display: "Healthcare Provider",
        system: ORGANIZATION_TYPE_SYSTEM
    },
    // {
    //   code: "dept",
    //   display: "Hospital Department",
    //   system: ORGANIZATION_TYPE_SYSTEM,
    // },
    // {
    //   code: "team",
    //   display: "Organizational Team",
    //   system: ORGANIZATION_TYPE_SYSTEM,
    // },
    // {
    //   code: "govt",
    //   display: "Government",
    //   system: ORGANIZATION_TYPE_SYSTEM,
    // },
    {
        code: "ins",
        display: "Insurance Company",
        system: ORGANIZATION_TYPE_SYSTEM
    },
    {
        code: "pay",
        display: "Payer",
        system: ORGANIZATION_TYPE_SYSTEM
    },
    // {
    //   code: "edu",
    //   display: "Educational Institute",
    //   system: ORGANIZATION_TYPE_SYSTEM,
    // },
    // {
    //   code: "reli",
    //   display: "Religious Institution",
    //   system: ORGANIZATION_TYPE_SYSTEM,
    // },
    // {
    //   code: "crs",
    //   display: "Clinical Research Sponsor",
    //   system: ORGANIZATION_TYPE_SYSTEM,
    // },
    {
        code: "cg",
        display: "Community Group",
        system: ORGANIZATION_TYPE_SYSTEM
    }
];
/**
 * Custom Organization Type Options
 * Benchmark-specific organization types not in the FHIR standard
 */ const CUSTOM_ORGANIZATION_TYPE_OPTIONS = [
    {
        code: "ehr",
        display: "Electronic Health Record Provider",
        system: CUSTOM_ORGANIZATION_TYPE_SYSTEM
    },
    {
        code: "referring-practice",
        display: "Referring Practice",
        system: CUSTOM_ORGANIZATION_TYPE_SYSTEM
    },
    {
        code: "private-practice",
        display: "Private Practice",
        system: CUSTOM_ORGANIZATION_TYPE_SYSTEM
    }
];
/**
 * Combined Organization Type Options
 * All organization types (FHIR standard + custom) for use in forms and UI
 */ const ORGANIZATION_TYPE_OPTIONS = [
    ...FHIR_ORGANIZATION_TYPE_OPTIONS,
    ...CUSTOM_ORGANIZATION_TYPE_OPTIONS
];
/**
 * Service Provider Type Options
 * Healthcare service provider organization types for UI distinction
 * Contains: Healthcare Provider, Community Group, and Private Practice
 */ [
    FHIR_ORGANIZATION_TYPE_OPTIONS.find((t)=>t.code === "prov"),
    FHIR_ORGANIZATION_TYPE_OPTIONS.find((t)=>t.code === "cg"),
    CUSTOM_ORGANIZATION_TYPE_OPTIONS.find((t)=>t.code === "private-practice")
];
/**
 * Insurance Provider Type Options
 * Insurance and payer organization types for UI distinction
 * Contains: Insurance Company and Payer
 */ [
    FHIR_ORGANIZATION_TYPE_OPTIONS.find((t)=>t.code === "ins"),
    FHIR_ORGANIZATION_TYPE_OPTIONS.find((t)=>t.code === "pay")
];
/**
 * UI-ready select options for organization type
 */ ORGANIZATION_TYPE_OPTIONS.map((type)=>({
        value: type.code,
        label: type.display
    }));

/**
 * PlanDefinition Type System
 * Custom system for plan definition templates
 */ const PLAN_DEFINITION_TYPE_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/plan-type";
/**
 * PlanDefinition Type Options
 * Defines available plan definition templates
 */ const PLAN_DEFINITION_TYPE_OPTIONS = [
    {
        code: "intake-protocol",
        display: "Intake Protocol",
        system: PLAN_DEFINITION_TYPE_SYSTEM,
        description: "Standard intake protocol for new patients",
        canonical: "PlanDefinition/intake-protocol"
    },
    {
        code: "triage-protocol",
        display: "Triage Protocol",
        system: PLAN_DEFINITION_TYPE_SYSTEM,
        description: "Standard triage protocol for ongoing care coordination",
        canonical: "PlanDefinition/triage-protocol"
    },
    {
        code: "cocm-protocol",
        display: "CoCM Protocol",
        system: PLAN_DEFINITION_TYPE_SYSTEM,
        description: "Standard Collaborative Care Model protocol",
        canonical: "PlanDefinition/cocm-protocol"
    }
];
/**
 * PlanDefinition Resources
 * FHIR PlanDefinition resources that define care protocols
 */ /**
 * Intake Protocol PlanDefinition
 *
 * Defines the standard intake workflow for new members.
 * Activities: Care Team Assignment → Initial Contact → Consent → Intake Assessment → Care Plan Selection
 * Uses ActivityDefinition constants as the single source of truth.
 */ ({
    action: getActivityDefinitionsByProtocol("intake").map((activity, index)=>{
        const action = {
            id: activity.code,
            title: activity.display,
            description: activity.description,
            code: [
                {
                    coding: [
                        {
                            system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
                            code: activity.code,
                            display: getActivityDefinitionDisplay(activity.code)
                        }
                    ],
                    text: activity.description
                }
            ]
        };
        // Add relationship chain: each activity comes after the previous one
        if (index > 0) {
            const previousActivity = getActivityDefinitionsByProtocol("intake")[index - 1];
            if (previousActivity) {
                action.relatedAction = [
                    {
                        actionId: previousActivity.code,
                        relationship: "after"
                    }
                ];
            }
        }
        return action;
    })
});
/**
 * Triage Protocol PlanDefinition
 *
 * Defines the standard triage workflow for members requiring additional care coordination.
 * Activities: Resource Offering → Resource Acceptance → Appointment Scheduling → Appointment Completion → Needs Evaluation
 * Uses ActivityDefinition constants as the single source of truth.
 */ ({
    action: getActivityDefinitionsByProtocol("triage").map((activity, index)=>{
        const action = {
            id: activity.code,
            title: activity.display,
            description: activity.description,
            code: [
                {
                    coding: [
                        {
                            system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
                            code: activity.code,
                            display: getActivityDefinitionDisplay(activity.code)
                        }
                    ],
                    text: activity.description
                }
            ]
        };
        // Add relationship chain: each activity comes after the previous one
        if (index > 0) {
            const previousActivity = getActivityDefinitionsByProtocol("triage")[index - 1];
            if (previousActivity) {
                action.relatedAction = [
                    {
                        actionId: previousActivity.code,
                        relationship: "after"
                    }
                ];
            }
        }
        return action;
    })
});
/**
 * CoCM Protocol PlanDefinition
 *
 * Defines the standard Collaborative Care Model workflow.
 * Sequential activities must happen in order; flexible activities can occur in any order and may repeat.
 * Uses ActivityDefinition constants as the single source of truth.
 */ ({
    action: getActivityDefinitionsByProtocol("cocm").map((activity, index)=>{
        const action = {
            id: activity.code,
            title: activity.display,
            description: activity.description,
            code: [
                {
                    coding: [
                        {
                            system: ACTIVITY_DEFINITION_TYPE_SYSTEM,
                            code: activity.code,
                            display: getActivityDefinitionDisplay(activity.code)
                        }
                    ],
                    text: activity.description
                }
            ]
        };
        // Add relationship chain: sequential activities come after the previous one
        // Flexible activities don't have strict ordering requirements
        const cocmActivities = getActivityDefinitionsByProtocol("cocm");
        if (index > 0) {
            const previousActivity = cocmActivities[index - 1];
            if (previousActivity) {
                action.relatedAction = [
                    {
                        actionId: previousActivity.code,
                        relationship: "after"
                    }
                ];
            }
        }
        return action;
    })
});
/**
 * UI-ready select options
 */ PLAN_DEFINITION_TYPE_OPTIONS.map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description
    }));

/**
 * CodeSystem URL for the selected operational program value.
 * @agentic @benchmark/core
 */ const OPERATIONAL_PROGRAM_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/operational-program";
/**
 * Operational program options: the programs a patient can be enrolled into after intake.
 * @agentic @benchmark/core
 */ const OPERATIONAL_PROGRAM_OPTIONS = [
    {
        code: "triage",
        display: "Triage",
        system: OPERATIONAL_PROGRAM_SYSTEM,
        description: "Benchmark Care Triage & Navigation"
    },
    {
        code: "cocm",
        display: "CoCM",
        system: OPERATIONAL_PROGRAM_SYSTEM,
        description: "Benchmark Collaborative Care Model"
    }
];
/**
 * UI-ready select options for operational programs.
 * @agentic @benchmark/core
 */ OPERATIONAL_PROGRAM_OPTIONS.map((option)=>({
        value: option.code,
        label: option.display,
        description: option.description
    }));
/**
 * CodeSystem URL for program selection reason codes.
 * @agentic @benchmark/core
 */ const PROGRAM_SELECTION_REASON_SYSTEM = "http://benchmarkhealth.com/fhir/CodeSystem/program-selection-reason";
/**
 * Reason code options for justifying program selection.
 * @agentic @benchmark/core
 */ const PROGRAM_SELECTION_REASON_OPTIONS = [
    {
        code: "clinical-need-acuity",
        display: "Clinical Need or Acuity",
        system: PROGRAM_SELECTION_REASON_SYSTEM
    },
    {
        code: "insurance-cost",
        display: "Insurance or Cost",
        system: PROGRAM_SELECTION_REASON_SYSTEM
    },
    {
        code: "patient-preference",
        display: "Patient Preference",
        system: PROGRAM_SELECTION_REASON_SYSTEM
    },
    {
        code: "pcp-recommendation",
        display: "PCP Recommendation",
        system: PROGRAM_SELECTION_REASON_SYSTEM
    }
];
/**
 * UI-ready select options for program selection reasons.
 * @agentic @benchmark/core
 */ PROGRAM_SELECTION_REASON_OPTIONS.map((option)=>({
        value: option.code,
        label: option.display
    }));

/**
 * FHIR-Compliant Constants for Practitioner Domain
 *
 * This file contains all constants used in Practitioner forms, utils, and related components.
 * All codes follow FHIR R4 value sets and include proper system/code/display structures.
 */ // FHIR R4-compliant specialty options from Practice Setting Code Value Set
// http://hl7.org/fhir/ValueSet/c80-practice-codes
// Based on SNOMED CT International edition
const PRACTITIONER_SPECIALTY_OPTIONS = [
    {
        code: "394576009",
        display: "Accident & emergency",
        system: "http://snomed.info/sct"
    },
    {
        code: "28351000087106",
        display: "Addiction specialty",
        system: "http://snomed.info/sct"
    },
    {
        code: "408467006",
        display: "Adult mental illness",
        system: "http://snomed.info/sct"
    },
    {
        code: "394577000",
        display: "Anesthetics",
        system: "http://snomed.info/sct"
    },
    {
        code: "394579002",
        display: "Cardiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394588006",
        display: "Child and adolescent psychiatry",
        system: "http://snomed.info/sct"
    },
    {
        code: "394803006",
        display: "Clinical hematology",
        system: "http://snomed.info/sct"
    },
    {
        code: "1255918004",
        display: "Clinical psychology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408454008",
        display: "Clinical microbiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408478003",
        display: "Critical care medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394582007",
        display: "Dermatology",
        system: "http://snomed.info/sct"
    },
    {
        code: "1255916000",
        display: "Educational psychology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394583002",
        display: "Endocrinology",
        system: "http://snomed.info/sct"
    },
    {
        code: "419772000",
        display: "Family practice",
        system: "http://snomed.info/sct"
    },
    {
        code: "394584008",
        display: "Gastroenterology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408444009",
        display: "General dental practice",
        system: "http://snomed.info/sct"
    },
    {
        code: "394802001",
        display: "General medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394915009",
        display: "General pathology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394814009",
        display: "General practice",
        system: "http://snomed.info/sct"
    },
    {
        code: "394811001",
        display: "Geriatric medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394586005",
        display: "Gynecology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394597005",
        display: "Histopathology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394807007",
        display: "Infectious diseases",
        system: "http://snomed.info/sct"
    },
    {
        code: "419192003",
        display: "Internal medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "408468001",
        display: "Learning disability - specialty",
        system: "http://snomed.info/sct"
    },
    {
        code: "394593009",
        display: "Medical oncology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394733009",
        display: "Medical specialty - Other",
        system: "http://snomed.info/sct"
    },
    {
        code: "394589003",
        display: "Nephrology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394591006",
        display: "Neurology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394649004",
        display: "Nuclear medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394585009",
        display: "Obstetrics and gynecology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408470005",
        display: "Obstetrics",
        system: "http://snomed.info/sct"
    },
    {
        code: "394821009",
        display: "Occupational medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394594003",
        display: "Ophthalmology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394605001",
        display: "Oral surgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "394608004",
        display: "Orthodontics",
        system: "http://snomed.info/sct"
    },
    {
        code: "394882004",
        display: "Pain management",
        system: "http://snomed.info/sct"
    },
    {
        code: "394806003",
        display: "Palliative medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "408459003",
        display: "Pediatric cardiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394607009",
        display: "Pediatric dentistry",
        system: "http://snomed.info/sct"
    },
    {
        code: "419610006",
        display: "Pediatric endocrinology",
        system: "http://snomed.info/sct"
    },
    {
        code: "418058008",
        display: "Pediatric gastroenterology",
        system: "http://snomed.info/sct"
    },
    {
        code: "419365004",
        display: "Pediatric nephrology",
        system: "http://snomed.info/sct"
    },
    {
        code: "418002000",
        display: "Pediatric oncology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394539006",
        display: "Pediatric surgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "409968004",
        display: "Preventive medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394587001",
        display: "Psychiatry",
        system: "http://snomed.info/sct"
    },
    {
        code: "394817002",
        display: "Forensic psychiatry",
        system: "http://snomed.info/sct"
    },
    {
        code: "1255881004",
        display: "Mental health nursing",
        system: "http://snomed.info/sct"
    },
    {
        code: "721961006",
        display: "Psychosomatic medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394913002",
        display: "Psychotherapy",
        system: "http://snomed.info/sct"
    },
    {
        code: "722162001",
        display: "Psychology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408440000",
        display: "Public health medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "419815003",
        display: "Radiation oncology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394914008",
        display: "Radiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408455009",
        display: "Radiology - Interventional radiology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394602003",
        display: "Rehabilitation",
        system: "http://snomed.info/sct"
    },
    {
        code: "394810000",
        display: "Rheumatology",
        system: "http://snomed.info/sct"
    },
    {
        code: "408466002",
        display: "Surgery - Cardiac surgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "394609007",
        display: "Surgery - General",
        system: "http://snomed.info/sct"
    },
    {
        code: "394610002",
        display: "Surgery - Neurosurgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "394611003",
        display: "Surgery - Plastic surgery",
        system: "http://snomed.info/sct"
    },
    {
        code: "394801008",
        display: "Surgery - Trauma and orthopedics",
        system: "http://snomed.info/sct"
    },
    {
        code: "408463005",
        display: "Surgery - Vascular",
        system: "http://snomed.info/sct"
    },
    {
        code: "1251536003",
        display: "Sport medicine/psychology",
        system: "http://snomed.info/sct"
    },
    {
        code: "394732004",
        display: "Surgical specialty - Other",
        system: "http://snomed.info/sct"
    },
    {
        code: "394590007",
        display: "Thoracic medicine",
        system: "http://snomed.info/sct"
    },
    {
        code: "394612005",
        display: "Urology",
        system: "http://snomed.info/sct"
    }
];
// UI-specific mappings for Select components
PRACTITIONER_SPECIALTY_OPTIONS.map((specialty)=>({
        value: specialty.code,
        label: specialty.display
    }));

/** HL7 v2 Table 0360 — Degree/License/Certificate. */ const PRACTITIONER_QUALIFICATION_HL7_SYSTEM = `${HL7_TERMINOLOGY_BASE}/CodeSystem/v2-0360`;
/** Benchmark-owned CodeSystem for credentials HL7 v2-0360 does not define. */ const PRACTITIONER_QUALIFICATION_BENCHMARK_SYSTEM = `${ORGANIZATION_BASE}/CodeSystem/practitioner-qualification`;
/**
 * The complete v2-0360 value set (HL7 Terminology v7.3.0), with the published
 * casing — `PHD` not `PhD`, `RPH` not `RPh`, but `PharmD` genuinely mixed.
 */ const HL7_PRACTITIONER_QUALIFICATION_OPTIONS = [
    {
        code: "PN",
        display: "Advanced Practice Nurse"
    },
    {
        code: "AAS",
        display: "Associate of Applied Science"
    },
    {
        code: "AA",
        display: "Associate of Arts"
    },
    {
        code: "ABA",
        display: "Associate of Business Administration"
    },
    {
        code: "AE",
        display: "Associate of Engineering"
    },
    {
        code: "AS",
        display: "Associate of Science"
    },
    {
        code: "BA",
        display: "Bachelor of Arts"
    },
    {
        code: "BBA",
        display: "Bachelor of Business Administration"
    },
    {
        code: "BE",
        display: "Bachelor or Engineering"
    },
    {
        code: "BFA",
        display: "Bachelor of Fine Arts"
    },
    {
        code: "BN",
        display: "Bachelor of Nursing"
    },
    {
        code: "BS",
        display: "Bachelor of Science"
    },
    {
        code: "BSL",
        display: "Bachelor of Science - Law"
    },
    {
        code: "BSN",
        display: "Bachelor on Science - Nursing"
    },
    {
        code: "BT",
        display: "Bachelor of Theology"
    },
    {
        code: "CER",
        display: "Certificate"
    },
    {
        code: "CANP",
        display: "Certified Adult Nurse Practitioner"
    },
    {
        code: "CMA",
        display: "Certified Medical Assistant"
    },
    {
        code: "CNP",
        display: "Certified Nurse Practitioner"
    },
    {
        code: "CNM",
        display: "Certified Nurse Midwife"
    },
    {
        code: "CRN",
        display: "Certified Registered Nurse"
    },
    {
        code: "CNS",
        display: "Certified Nurse Specialist"
    },
    {
        code: "CPNP",
        display: "Certified Pediatric Nurse Practitioner"
    },
    {
        code: "CTR",
        display: "Certified Tumor Registrar"
    },
    {
        code: "DIP",
        display: "Diploma"
    },
    {
        code: "DBA",
        display: "Doctor of Business Administration"
    },
    {
        code: "DED",
        display: "Doctor of Education"
    },
    {
        code: "PharmD",
        display: "Doctor of Pharmacy"
    },
    {
        code: "PHE",
        display: "Doctor of Engineering"
    },
    {
        code: "PHD",
        display: "Doctor of Philosophy"
    },
    {
        code: "PHS",
        display: "Doctor of Science"
    },
    {
        code: "MD",
        display: "Doctor of Medicine"
    },
    {
        code: "DO",
        display: "Doctor of Osteopathy"
    },
    {
        code: "EMT",
        display: "Emergency Medical Technician"
    },
    {
        code: "EMTP",
        display: "Emergency Medical Technician - Paramedic"
    },
    {
        code: "FPNP",
        display: "Family Practice Nurse Practitioner"
    },
    {
        code: "HS",
        display: "High School Graduate"
    },
    {
        code: "JD",
        display: "Juris Doctor"
    },
    {
        code: "MA",
        display: "Master of Arts"
    },
    {
        code: "MBA",
        display: "Master of Business Administration"
    },
    {
        code: "MCE",
        display: "Master of Civil Engineering"
    },
    {
        code: "MDI",
        display: "Master of Divinity"
    },
    {
        code: "MED",
        display: "Master of Education"
    },
    {
        code: "MEE",
        display: "Master of Electrical Engineering"
    },
    {
        code: "ME",
        display: "Master of Engineering"
    },
    {
        code: "MFA",
        display: "Master of Fine Arts"
    },
    {
        code: "MME",
        display: "Master of Mechanical Engineering"
    },
    {
        code: "MS",
        display: "Master of Science"
    },
    {
        code: "MSL",
        display: "Master of Science - Law"
    },
    {
        code: "MSN",
        display: "Master of Science - Nursing"
    },
    {
        code: "MTH",
        display: "Master of Theology"
    },
    {
        code: "MDA",
        display: "Medical Assistant"
    },
    {
        code: "MT",
        display: "Medical Technician"
    },
    {
        code: "NG",
        display: "Non-Graduate"
    },
    {
        code: "NP",
        display: "Nurse Practitioner"
    },
    {
        code: "PA",
        display: "Physician Assistant"
    },
    {
        code: "RMA",
        display: "Registered Medical Assistant"
    },
    {
        code: "RN",
        display: "Registered Nurse"
    },
    {
        code: "RPH",
        display: "Registered Pharmacist"
    },
    {
        code: "SEC",
        display: "Secretarial Certificate"
    },
    {
        code: "TS",
        display: "Trade School Graduate"
    }
];
/**
 * Credentials Benchmark clinicians hold that v2-0360 does not define.
 *
 * Provenance: behavioral-health licenses referenced across this codebase
 * (`LCSW` in the CoCM billing packet and MCP directory; `PsyD`, `LMHC`,
 * `LICSW`, `LPC`, `LADC`, `MSW` in the practice-name credential parser) plus
 * the advanced-practice and referring-provider credentials the
 * `referring-practice` CLI has always accepted (`DNP`, `FNP`, `PNP`, `CRNA`,
 * `MBBS`, `DPM`, `DC`, `OD`, `DDS`, `DMD`), and the state-variant counseling
 * and psychiatric-nursing licenses that accompany them.
 */ const BENCHMARK_PRACTITIONER_QUALIFICATION_OPTIONS = [
    {
        code: "LCSW",
        display: "Licensed Clinical Social Worker"
    },
    {
        code: "LICSW",
        display: "Licensed Independent Clinical Social Worker"
    },
    {
        code: "LMSW",
        display: "Licensed Master Social Worker"
    },
    {
        code: "LSW",
        display: "Licensed Social Worker"
    },
    {
        code: "MSW",
        display: "Master of Social Work"
    },
    {
        code: "LPC",
        display: "Licensed Professional Counselor"
    },
    {
        code: "LPCC",
        display: "Licensed Professional Clinical Counselor"
    },
    {
        code: "LCPC",
        display: "Licensed Clinical Professional Counselor"
    },
    {
        code: "LMHC",
        display: "Licensed Mental Health Counselor"
    },
    {
        code: "LMFT",
        display: "Licensed Marriage and Family Therapist"
    },
    {
        code: "LADC",
        display: "Licensed Alcohol and Drug Counselor"
    },
    {
        code: "CADC",
        display: "Certified Alcohol and Drug Counselor"
    },
    {
        code: "PsyD",
        display: "Doctor of Psychology"
    },
    {
        code: "PMHNP",
        display: "Psychiatric-Mental Health Nurse Practitioner"
    },
    {
        code: "APRN",
        display: "Advanced Practice Registered Nurse"
    },
    {
        code: "DNP",
        display: "Doctor of Nursing Practice"
    },
    {
        code: "FNP",
        display: "Family Nurse Practitioner"
    },
    {
        code: "PNP",
        display: "Pediatric Nurse Practitioner"
    },
    {
        code: "CRNA",
        display: "Certified Registered Nurse Anesthetist"
    },
    {
        code: "MBBS",
        display: "Bachelor of Medicine, Bachelor of Surgery"
    },
    {
        code: "DPM",
        display: "Doctor of Podiatric Medicine"
    },
    {
        code: "DC",
        display: "Doctor of Chiropractic"
    },
    {
        code: "OD",
        display: "Doctor of Optometry"
    },
    {
        code: "DDS",
        display: "Doctor of Dental Surgery"
    },
    {
        code: "DMD",
        display: "Doctor of Dental Medicine"
    }
];
/** Every qualification concept, HL7 first, each tagged with its own system. */ const PRACTITIONER_QUALIFICATION_OPTIONS = [
    ...HL7_PRACTITIONER_QUALIFICATION_OPTIONS.map((option)=>({
            ...option,
            system: PRACTITIONER_QUALIFICATION_HL7_SYSTEM
        })),
    ...BENCHMARK_PRACTITIONER_QUALIFICATION_OPTIONS.map((option)=>({
            ...option,
            system: PRACTITIONER_QUALIFICATION_BENCHMARK_SYSTEM
        }))
];
/**
 * Case-folded lookup. Built once; the accompanying test asserts the two systems
 * never contribute the same folded key, so a lookup is never ambiguous.
 */ new Map(PRACTITIONER_QUALIFICATION_OPTIONS.map((option)=>[
        option.code.toUpperCase(),
        option
    ]));

// Business role codes use HL7 practitioner-role CodeSystem with our organization-specific codes
const PRACTITIONER_ROLE_CODE_SYSTEM = `${HL7_TERMINOLOGY_BASE}/CodeSystem/practitioner-role`;
const PRACTITIONER_ROLE_CODE_OPTIONS = [
    {
        code: "clinical-director",
        display: "Clinical Director",
        system: PRACTITIONER_ROLE_CODE_SYSTEM,
        description: "Clinical leadership responsible for care program oversight"
    },
    {
        code: "lead-advocate",
        display: "Lead Advocate",
        system: PRACTITIONER_ROLE_CODE_SYSTEM,
        description: "Senior LCSW responsible for licensed member care"
    },
    {
        code: "care-manager",
        display: "Care Manager",
        system: PRACTITIONER_ROLE_CODE_SYSTEM,
        description: "Care coordination and ongoing member management"
    },
    {
        code: "intake-coordinator",
        display: "Intake Coordinator",
        system: PRACTITIONER_ROLE_CODE_SYSTEM,
        description: "Responsible for scheduling and completing member intake"
    },
    {
        code: "special-operations-director",
        display: "Special Ops Director",
        system: PRACTITIONER_ROLE_CODE_SYSTEM,
        description: "Operations leadership for non-licensed member programs"
    },
    {
        code: "special-operations-associate",
        display: "Special Ops Associate",
        system: PRACTITIONER_ROLE_CODE_SYSTEM,
        description: "Operations Specialist responsible for non-licensed member outreach and follow-up"
    },
    {
        code: "consulting-psychiatrist",
        display: "Consulting Psychiatrist",
        system: PRACTITIONER_ROLE_CODE_SYSTEM,
        description: "Psychiatric consultation for CoCM and complex cases"
    }
];
new Set(PRACTITIONER_ROLE_CODE_OPTIONS.map((o)=>o.code));
// UI-ready options for Select/CardSelect components
PRACTITIONER_ROLE_CODE_OPTIONS.map((o)=>({
        value: o.code,
        label: o.display,
        description: o.description
    }));
PRACTITIONER_ROLE_CODE_OPTIONS.map((o)=>({
        value: o.code,
        label: o.display,
        description: o.description
    }));
/**
 * Case-folded lookup keyed by both the code and the display label, so operator
 * input can be either (`Clinical Director` or `clinical-director`).
 */ new Map(PRACTITIONER_ROLE_CODE_OPTIONS.flatMap((option)=>[
        [
            foldRoleInput(option.code),
            option
        ],
        [
            foldRoleInput(option.display),
            option
        ]
    ]));
function foldRoleInput(input) {
    return input.trim().toLowerCase().replace(/[\s_]+/g, "-");
}

/**
 * Common answer value sets for Likert-style questions
 */ const LIKERT_FREQUENCY_OPTIONS = [
    {
        value: 0,
        coding: {
            code: "LA6568-5",
            display: "Not at all"
        }
    },
    {
        value: 1,
        coding: {
            code: "LA6569-3",
            display: "Several days"
        }
    },
    {
        value: 2,
        coding: {
            code: "LA6570-1",
            display: "More than half the days"
        }
    },
    {
        value: 3,
        coding: {
            code: "LA6571-9",
            display: "Nearly every day"
        }
    }
];
 // Note: Validation type moved to questionnaire.ts to avoid circular dependency

// PHQ-4 Questions (first 2 from PHQ-9, first 2 from GAD-7)
[
    {
        linkId: "phq4-1",
        text: "Little interest or pleasure in doing things",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq4-2",
        text: "Feeling down, depressed, or hopeless",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq4-3",
        text: "Feeling nervous, anxious or on edge",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq4-4",
        text: "Not being able to stop or control worrying",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    }
];
// PHQ-9 Questions
[
    {
        linkId: "phq9-1",
        text: "Little interest or pleasure in doing things",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9-2",
        text: "Feeling down, depressed, or hopeless",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9-3",
        text: "Trouble falling or staying asleep, or sleeping too much",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9-4",
        text: "Feeling tired or having little energy",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9-5",
        text: "Poor appetite or overeating",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9-6",
        text: "Feeling bad about yourself - or that you are a failure or have let yourself or your family down",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9-7",
        text: "Trouble concentrating on things, such as reading the newspaper or watching television",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9-8",
        text: "Moving or speaking so slowly that other people could have noticed? Or the opposite - being so fidgety or restless that you have been moving around a lot more than usual",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9-9",
        text: "Thoughts that you would be better off dead, or of hurting yourself in some way",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    // Functional impairment item (not scored in PHQ-9 total)
    // TODO(#989): Add LOINC item coding for PHQ-9 items, including impairment, to align with FHIR best practices
    {
        linkId: "phq9-10",
        text: "If you checked off any problems, how difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?",
        type: "choice",
        required: false,
        scorable: false,
        options: [
            {
                value: 0,
                display: "Not difficult at all"
            },
            {
                value: 1,
                display: "Somewhat difficult"
            },
            {
                value: 2,
                display: "Very difficult"
            },
            {
                value: 3,
                display: "Extremely difficult"
            }
        ]
    }
];
// PHQ-9A Questions (Adolescent version, ages 12-18)
// Same core questions as PHQ-9, with adolescent-appropriate language
[
    {
        linkId: "phq9a-1",
        text: "Little interest or pleasure in doing things",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9a-2",
        text: "Feeling down, depressed, irritable, or hopeless",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9a-3",
        text: "Trouble falling asleep, staying asleep, or sleeping too much",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9a-4",
        text: "Feeling tired or having little energy",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9a-5",
        text: "Poor appetite, weight loss, or overeating",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9a-6",
        text: "Feeling bad about yourself - or feeling that you are a failure, or that you have let yourself or your family down",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9a-7",
        text: "Trouble concentrating on things like school work, reading, or watching TV",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9a-8",
        text: "Moving or speaking so slowly that other people could have noticed? Or the opposite - being so fidgety or restless that you were moving around a lot more than usual",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "phq9a-9",
        text: "Thoughts that you would be better off dead, or of hurting yourself in some way",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    // Functional impairment item (not scored in PHQ-9A total)
    {
        linkId: "phq9a-10",
        text: "If you checked off any problems, how difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?",
        type: "choice",
        required: false,
        scorable: false,
        options: [
            {
                value: 0,
                display: "Not difficult at all"
            },
            {
                value: 1,
                display: "Somewhat difficult"
            },
            {
                value: 2,
                display: "Very difficult"
            },
            {
                value: 3,
                display: "Extremely difficult"
            }
        ]
    }
];
// GAD-7 Questions
[
    {
        linkId: "gad7-1",
        text: "Feeling nervous, anxious or on edge",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "gad7-2",
        text: "Not being able to stop or control worrying",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "gad7-3",
        text: "Worrying too much about different things",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "gad7-4",
        text: "Trouble relaxing",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "gad7-5",
        text: "Being so restless that it is hard to sit still",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "gad7-6",
        text: "Becoming easily annoyed or irritable",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "gad7-7",
        text: "Feeling afraid as if something awful might happen",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    // Functional impairment item (not scored in GAD-7 total)
    // TODO(#989): Add LOINC item coding for GAD-7 items, including impairment, to align with FHIR best practices
    {
        linkId: "gad7-8",
        text: "If you checked off any problems, how difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?",
        type: "choice",
        required: false,
        scorable: false,
        options: [
            {
                value: 0,
                display: "Not difficult at all"
            },
            {
                value: 1,
                display: "Somewhat difficult"
            },
            {
                value: 2,
                display: "Very difficult"
            },
            {
                value: 3,
                display: "Extremely difficult"
            }
        ]
    }
];
/**
 * GAD-2 anxiety short check (D17). Its two items are, verbatim, the first two
 * GAD-7 items; the instrument is scored 0–6 on its own scale and recorded under
 * the GAD-2 name — never as a GAD-7 severity. A positive GAD-2 (published cut
 * ≥3) opens GAD-7 as a separate instrument in the adaptive flow.
 */ [
    {
        linkId: "gad2-1",
        text: "Feeling nervous, anxious or on edge",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    },
    {
        linkId: "gad2-2",
        text: "Not being able to stop or control worrying",
        type: "choice",
        scorable: true,
        answerOptions: LIKERT_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                code: opt.coding.code,
                display: opt.coding.display
            }))
    }
];
// PSC-17 Answer Options (3-point scale: Never, Sometimes, Often)
const PSC_17_FREQUENCY_OPTIONS = [
    {
        value: 0,
        coding: {
            code: "LA6270-8",
            display: "Never"
        }
    },
    {
        value: 1,
        coding: {
            code: "LA10082-8",
            display: "Sometimes"
        }
    },
    {
        value: 2,
        coding: {
            code: "LA10044-8",
            display: "Often"
        }
    }
];
// PSC-17 Questions (Pediatric Symptom Checklist-17)
// Parent-report screening for psychosocial dysfunction in children ages 4-18
// Subscales: Attention (1,2,3,4,7), Internalizing (5,6,9,10,11), Externalizing (8,12,13,14,15,16,17)
[
    // Attention subscale items: 1, 2, 3, 4, 7
    {
        linkId: "psc17-1",
        text: "Fidgety, unable to sit still",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-2",
        text: "Acts as if driven by a motor",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-3",
        text: "Daydreams too much",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-4",
        text: "Distracted easily",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    // Internalizing subscale items: 5, 6, 9, 10, 11
    {
        linkId: "psc17-5",
        text: "Feels sad, unhappy",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-6",
        text: "Feels hopeless",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    // Attention subscale item: 7
    {
        linkId: "psc17-7",
        text: "Has trouble concentrating",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    // Externalizing subscale items: 8, 12, 13, 14, 15, 16, 17
    {
        linkId: "psc17-8",
        text: "Fights with other children",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    // Internalizing subscale items: 9, 10, 11
    {
        linkId: "psc17-9",
        text: "Is down on him or herself",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-10",
        text: "Blames self for problems",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-11",
        text: "Seems to be having less fun",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    // Externalizing subscale items: 12, 13, 14, 15, 16, 17
    {
        linkId: "psc17-12",
        text: "Does not listen to rules",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-13",
        text: "Does not understand other people's feelings",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-14",
        text: "Teases others",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-15",
        text: "Blames others for his or her troubles",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-16",
        text: "Takes things that do not belong to him or her",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    },
    {
        linkId: "psc17-17",
        text: "Refuses to share",
        type: "choice",
        scorable: true,
        required: true,
        options: PSC_17_FREQUENCY_OPTIONS.map((opt)=>({
                value: opt.value,
                display: opt.coding.display
            }))
    }
];

const SERVICE_MODE_SELECT_OPTION_VALUES = [
    "in-person",
    "telehealth-video",
    "telehealth-phone",
    "text-sms",
    "hybrid"
];
/**
 * Referral mode select options with shared display labels.
 * @agentic @benchmark/core
 */ const REFERRAL_MODE_OPTIONS = [
    {
        value: "in-person",
        label: "In-Person Preferred"
    },
    {
        value: "telehealth-video",
        label: "Telehealth Preferred"
    },
    {
        value: "hybrid",
        label: "Flexible"
    }
];
/**
 * Service mode select options used when choosing a concrete referral delivery mode.
 * @agentic @benchmark/core
 */ const SERVICE_MODE_SELECT_OPTIONS = SERVICE_MODE_SELECT_OPTION_VALUES.map((value)=>({
        value,
        label: SERVICE_CHARACTERISTIC_VISIT_MODE_OPTIONS.find((option)=>option.value === value)?.label ?? value
    }));
new Map(REFERRAL_MODE_OPTIONS.map((option)=>[
        option.value,
        option.label
    ]));
new Map(SERVICE_MODE_SELECT_OPTIONS.map((option)=>[
        option.value,
        option.label
    ]));

/**
 * Canonical system URL for Benchmark Health task types.
 * Use this constant instead of hardcoding the URL in your code.
 */ const TASK_TYPE_SYSTEM = "http://www.benchmarkhealth.com/fhir/ValueSet/task-type";
/**
 * Predefined Task types for healthcare workflows.
 * Using custom system for business-specific task categories.
 *
 * OCPE Phase 5: Several standalone task types are deprecated and replaced by
 * Task-backed CarePlan activities (v2 taxonomy). Deprecated types are excluded
 * from the task creation UI via `isDeprecatedStandaloneTaskType`, but remain
 * here for display/lookup of existing tasks in production.
 */ const TASK_TYPE_OPTIONS = [
    {
        /** @deprecated Replaced by intake plan activities: schedule-intake, complete-intake, finalize-care-plan-enrollment */ code: "new-member-intake",
        display: "Complete New Member Intake",
        description: "Finish new member intake with initial assessment",
        instructions: "Either complete 90791 intake for the individual or document that the individual has accepted Benchmark Health's services.",
        system: TASK_TYPE_SYSTEM,
        defaultPriority: "routine",
        estimatedDuration: 30,
        isRecurring: false,
        cadenceWeeks: []
    },
    {
        /** @deprecated Replaced by triage plan activity: offer-services */ code: "offer-member-resources",
        display: "Offer Resources to Member",
        description: "Find appropriate healthcare services and facilitate connection for member",
        instructions: "Find free or clinical resource options and share them with the patient. Document the interaction or communication.",
        system: TASK_TYPE_SYSTEM,
        defaultPriority: "routine",
        estimatedDuration: 20,
        isRecurring: false,
        cadenceWeeks: []
    },
    {
        /** @deprecated Replaced by triage plan activity: schedule-services */ code: "schedule-member-appointment",
        display: "Schedule Appointment for Member",
        description: "Schedule an appointment for the member to receive care",
        instructions: "Schedule or confirm a member's appointment. Log the appointment in connections.",
        system: TASK_TYPE_SYSTEM,
        defaultPriority: "urgent",
        estimatedDuration: 20,
        isRecurring: false,
        cadenceWeeks: []
    },
    {
        /** @deprecated Replaced by plan activity: check-in-on-member (intake as-needed) */ code: "member-check-in",
        display: "Check In on Member",
        description: "General check-in on member to assess progress or needs",
        instructions: "General check in with member. Log the interaction.",
        system: TASK_TYPE_SYSTEM,
        defaultPriority: "routine",
        estimatedDuration: 15,
        isRecurring: false,
        cadenceWeeks: []
    },
    {
        /** @deprecated NPS surveys are now handled by automation, no longer a plan activity */ code: "send-nps-survey",
        display: "Send NPS Survey",
        description: "Send Net Promoter Score survey to assess member satisfaction with Benchmark",
        instructions: "Send Member NPS survey and log the communication.",
        system: TASK_TYPE_SYSTEM,
        defaultPriority: "routine",
        estimatedDuration: 5,
        isRecurring: false,
        cadenceWeeks: []
    },
    {
        /** @deprecated Replaced by triage plan activity: follow-up-on-services */ code: "post-care-followup",
        display: "Complete Post-Care Followup",
        description: "Check-in with member after appointment completion to assess progress or needs",
        instructions: "Check in with the member after their appointment. Log the interaction and update the status of the appointment.",
        system: TASK_TYPE_SYSTEM,
        defaultPriority: "routine",
        estimatedDuration: 20,
        isRecurring: false,
        cadenceWeeks: []
    },
    {
        /** @deprecated Replaced by triage plan activity: complete-cadenced-followup */ code: "cadenced-followup",
        display: "Complete Cadenced Followup",
        description: "Check-in at 6 week intervals to assess progress or needs",
        instructions: "Send member 6 week outcome questionnaire and log the communication. Log results in an encounter if the member responds.",
        system: TASK_TYPE_SYSTEM,
        defaultPriority: "routine",
        estimatedDuration: 15,
        isRecurring: true,
        cadenceWeeks: [
            6,
            12,
            18
        ]
    }
];
const CUSTOM_TASK_TYPE = {
    display: "Custom Task"};
// Define groupings so multiple underlying task codes can share one tab.
// These groups are used for the TasksPage segmented filter control.
// Each group includes both legacy standalone codes (task-type system) and
// v2 plan-backed activity codes (care-plan-activity system) so the filter
// works for both old standalone tasks and new Task-backed CarePlan activities.
const TASK_TYPE_GROUPS = [
    {
        id: "intake",
        label: "Intake",
        codes: [
            "new-member-intake",
            "schedule-intake",
            "complete-intake",
            "finalize-care-plan-enrollment",
            "check-in-on-member"
        ]
    },
    {
        id: "triage",
        label: "Triage",
        codes: [
            "offer-member-resources",
            "schedule-member-appointment",
            "connect-member-care",
            "member-check-in",
            "post-care-followup",
            "cadenced-followup",
            "select-services",
            "offer-services",
            "schedule-services",
            "follow-up-on-services",
            "approve-selected-services",
            "complete-cadenced-followup"
        ]
    },
    {
        id: "cocm",
        label: "CoCM",
        codes: [
            // Note: `enroll-in-mirah` (v2 cocm sequential, retired with the
            // Mirah partnership wind-down) is intentionally absent; legacy task
            // displays still resolve via taxonomy-mapping for backwards
            // compatibility.
            "build-cocm-care-plan",
            "follow-up-on-cocm",
            "consult-psych",
            "consult-pcp",
            "discharge-member"
        ]
    },
    {
        id: "other",
        label: "Other",
        codes: [
            "custom-task",
            "send-nps-survey",
            "review-new-referral",
            "potential-duplicate-review",
            "manual-review-fax",
            "review-unmatched-fax"
        ]
    }
];
/**
 * FHIR R4 Task status options
 * http://hl7.org/fhir/task-status
 */ const TASK_STATUS_OPTIONS = [
    {
        code: "draft",
        display: "Draft",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "requested",
        display: "Requested",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "received",
        display: "Received",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "accepted",
        display: "Accepted",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "rejected",
        display: "Rejected",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "ready",
        display: "Ready",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "cancelled",
        display: "Cancelled",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "in-progress",
        display: "In Progress",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "on-hold",
        display: "On Hold",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "failed",
        display: "Failed",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "completed",
        display: "Completed",
        system: "http://hl7.org/fhir/task-status"
    },
    {
        code: "entered-in-error",
        display: "Entered in Error",
        system: "http://hl7.org/fhir/task-status"
    }
];
/**
 * FHIR R4 Task priority options
 * http://hl7.org/fhir/request-priority
 */ const TASK_PRIORITY_OPTIONS = [
    {
        code: "routine",
        display: "Routine",
        system: "http://hl7.org/fhir/request-priority"
    },
    {
        code: "urgent",
        display: "Urgent",
        system: "http://hl7.org/fhir/request-priority"
    },
    {
        code: "asap",
        display: "ASAP",
        system: "http://hl7.org/fhir/request-priority"
    },
    {
        code: "stat",
        display: "STAT",
        system: "http://hl7.org/fhir/request-priority"
    }
];
/**
 * Task intent options for workflow context
 * http://hl7.org/fhir/task-intent
 */ const TASK_INTENT_OPTIONS = [
    {
        code: "proposal",
        display: "Proposal",
        system: "http://hl7.org/fhir/task-intent"
    },
    {
        code: "plan",
        display: "Plan",
        system: "http://hl7.org/fhir/task-intent"
    },
    {
        code: "order",
        display: "Order",
        system: "http://hl7.org/fhir/task-intent"
    },
    {
        code: "original-order",
        display: "Original Order",
        system: "http://hl7.org/fhir/task-intent"
    }
];
// UI-specific mappings for Select components
// OCPE Phase 5: Exclude deprecated standalone task types from creation UI
TASK_TYPE_OPTIONS.filter((type)=>!isDeprecatedStandaloneTaskType(type.code)).map((type)=>({
        value: type.code,
        label: type.display,
        description: type.description,
        isRecurring: type.isRecurring || false
    }));
TASK_TYPE_GROUPS.map((group)=>({
        value: group.id,
        label: group.label,
        codes: group.codes
    }));
/**
 * Set of all known v2 plan-backed activity codes (CARE_PLAN_ACTIVITY_SYSTEM).
 * @agentic @benchmark/core
 */ const V2_ACTIVITY_CODE_SET = new Set(getAllCarePlanActivityOptions().map((a)=>a.code));
/**
 * Set of all known standalone task type codes (TASK_TYPE_SYSTEM).
 * @agentic @benchmark/core
 */ new Set(TASK_TYPE_OPTIONS.map((t)=>t.code));
/** Codes excluded from the "Other" filter group (deprecated / automated). @agentic connections */ const OTHER_GROUP_EXCLUDED_CODES = new Set([
    "send-nps-survey"
]);
/** Short display overrides for filter dropdown labels. @agentic connections */ const FILTER_DISPLAY_OVERRIDES = {
    "custom-task": CUSTOM_TASK_TYPE.display,
    "review-new-referral": "Fax | New Referral",
    "potential-duplicate-review": "Fax | Potential Duplicate",
    "manual-review-fax": "Fax | Manual Review",
    "review-unmatched-fax": "Fax | Unmatched"
};
/** Resolve display name for a code in the filter dropdown. @agentic connections */ const getFilterActivityDisplay = (code)=>FILTER_DISPLAY_OVERRIDES[code] ?? resolveActivityDisplay(code);
/**
 * Displayable activities per task type group for the filter dropdown.
 * For intake/triage/cocm: only v2 activity codes are shown as individual items.
 * For "other": all codes are shown. Legacy codes are included implicitly
 * via full-group selection when resolving FHIR queries.
 * @agentic connections
 */ const TASK_TYPE_FILTER_OPTIONS = TASK_TYPE_GROUPS.map((group)=>({
        id: group.id,
        label: group.label,
        activities: group.codes.filter((code)=>{
            if (group.id === "other") return !OTHER_GROUP_EXCLUDED_CODES.has(code);
            return V2_ACTIVITY_CODE_SET.has(code);
        }).map((code)=>({
                code,
                display: getFilterActivityDisplay(code)
            }))
    }));
/** Set of all individually-selectable activity codes in the filter. @agentic connections */ new Set(TASK_TYPE_FILTER_OPTIONS.flatMap((g)=>g.activities.map((a)=>a.code)));
/** Group ID lookup for fast deserialize. @agentic connections */ new Map(TASK_TYPE_FILTER_OPTIONS.map((g)=>[
        g.id,
        g
    ]));
TASK_STATUS_OPTIONS.map((status)=>({
        value: status.code,
        label: status.display
    }));
TASK_PRIORITY_OPTIONS.map((priority)=>({
        value: priority.code,
        label: priority.display
    }));
TASK_INTENT_OPTIONS.map((intent)=>({
        value: intent.code,
        label: intent.display
    }));

/**
 * Select the billing ladder for the month per D13 + D14 + D18.
 * @agentic @benchmark/core
 */ const selectLadder = (input)=>{
    if (!input.hasEligibleAssessment) return NO_ASSESSMENT;
    // Concern 3: 99492 is billable at most once per episode. Once a prior month
    // billed it, force the subsequent (99493-based) ladder so it can never
    // recur — independent of the initial-month recompute.
    if (input.block99492) return SUBSEQUENT_WITH_ASSESSMENT;
    // D13 cascade: a G2214 initial month locks every later month into the
    // subsequent-month ladder (never 99492).
    if (input.episodeFirstBillWasG2214 && !input.isInitialMonth) {
        return SUBSEQUENT_WITH_ASSESSMENT;
    }
    return input.isInitialMonth ? INITIAL_WITH_ASSESSMENT : SUBSEQUENT_WITH_ASSESSMENT;
};
/**
 * Normalize raw input minutes: clamp negatives to 0, floor fractional
 * values. Every ladder rule + gap calculation operates on the
 * normalized value so the resolver stays internally consistent.
 *
 * Non-finite input (NaN / ±Infinity, e.g. from a corrupted upstream
 * `valueQuantity`) fails CLOSED to 0 → no bill. Without this guard,
 * `NaN` comparisons are all false, so `findRung` skips every bounded
 * rung and lands on the open-ended TOP rung — silently billing the
 * maximal code package for garbage input.
 * @agentic @benchmark/core
 */ const normalizeMinutes = (totalMinutes)=>Number.isFinite(totalMinutes) ? Math.max(0, Math.floor(totalMinutes)) : 0;
/**
 * Locate the rung whose minute window covers `minutes`. The caller is
 * responsible for passing a normalized integer (see `normalizeMinutes`).
 * @agentic @benchmark/core
 */ const findRung = (ladder, minutes)=>ladder.find((rung)=>{
        if (minutes < rung.minMinutes) return false;
        if (rung.maxMinutes === null) return true;
        return minutes <= rung.maxMinutes;
    });
/**
 * Compute the gap (in whole minutes) from `minutes` to the next rung's
 * lower bound. Returns `undefined` when there is no next rung (top of
 * ladder) or when the current rung's `codes` is already the highest
 * non-empty value.
 *
 * Per D15 the widget shows "minutes to next code" — so we always
 * compute relative to the next non-empty rung, even if the current
 * rung itself is non-empty (i.e. "minutes until the next more
 * lucrative package").
 *
 * The caller is responsible for passing a normalized integer; the
 * resolver normalizes once at entry so the gap stays a whole number.
 * @agentic @benchmark/core
 */ const computeMinutesToNext = (ladder, minutes, currentRung)=>{
    if (!currentRung) return undefined;
    const currentIndex = ladder.indexOf(currentRung);
    if (currentIndex === -1) return undefined;
    const nextRung = ladder[currentIndex + 1];
    if (!nextRung) return undefined;
    // Already inside the topmost open-ended rung → no next bound to chase.
    if (nextRung.maxMinutes === null && nextRung.codes.length === 0) {
        return undefined;
    }
    const gap = nextRung.minMinutes - minutes;
    return gap > 0 ? gap : undefined;
};
/**
 * Resolve the billing code package for a CoCM month. Pure function.
 *
 * Contract per the brief:
 *  - Returns empty `codes` when no rule fires (< 16 min, or < 16 min
 *    with no assessment).
 *  - Returns the package (codes + display) per D15.
 *  - Returns `minutesToNextCode` (whole minutes) when there is a
 *    higher-value rung reachable from the current `totalMinutes`.
 *
 * Defensive normalization (negative / fractional minutes are clamped
 * to `Math.max(0, Math.floor(...))`) is applied once at the top so
 * every downstream calculation operates on consistent integer minutes.
 * @agentic @benchmark/core
 */ const resolveBillingCode = (input)=>{
    const ladder = selectLadder(input);
    const minutes = normalizeMinutes(input.totalMinutes);
    const rung = findRung(ladder, minutes);
    if (!rung) {
        // Should never happen — every ladder covers [0, ∞).
        return {
            codes: [],
            packageDisplay: "",
            minutesToNextCode: undefined
        };
    }
    return {
        codes: [
            ...rung.codes
        ],
        packageDisplay: rung.packageDisplay,
        minutesToNextCode: computeMinutesToNext(ladder, minutes, rung)
    };
};

/** Page size used for the activity-log drawer queries. @agentic @benchmark/core */ const CARE_TIME_PAGE_SIZE = 50;
/** Default status filter applied when soft-deleted entries are excluded. */ const ACTIVE_STATUS_TOKEN = "final,amended,corrected,preliminary";
/**
 * Build a tuple-array search payload for a care-time Observation query.
 *
 * Tuples (rather than `Record<string, string>`) are required because
 * date-range queries emit two `date` parameters (`ge…` and `le…`) — a
 * record with the same key twice would collapse to a single entry, and
 * the previous "join with `&date=`" workaround caused URL-encoding to
 * percent-escape the literal `&` and `=`. Mirrors the pattern in
 * `task.service.ts:buildDueTodayTaskSearchParams`.
 * @agentic @benchmark/core
 */ const buildCareTimeSearchTuples = (patientId, options = {})=>{
    const tuples = [
        [
            "patient",
            patientId
        ],
        [
            "category",
            CARE_TIME_CATEGORY_TOKEN
        ],
        [
            "code",
            `${CARE_TIME_CODE_SYSTEM}|${CARE_TIME_OBSERVATION_CODE_VALUE}`
        ],
        [
            "_sort",
            "-date"
        ],
        [
            "_count",
            String(options.count ?? CARE_TIME_PAGE_SIZE)
        ],
        [
            "_total",
            "accurate"
        ]
    ];
    if (!options.includeSoftDeleted) {
        // FHIR allows comma-OR within a single search parameter. Exclude
        // `entered-in-error` by allowlisting the statuses we count.
        tuples.push([
            "status",
            ACTIVE_STATUS_TOKEN
        ]);
    }
    if (options.startDate) {
        tuples.push([
            "date",
            `ge${options.startDate}`
        ]);
    }
    if (options.endDate) {
        tuples.push([
            "date",
            `le${options.endDate}`
        ]);
    }
    if (options.offset !== undefined) {
        tuples.push([
            "_offset",
            String(options.offset)
        ]);
    }
    return tuples;
};
/**
 * Search care-time Observations for a patient, optionally constrained to
 * a date range. Returns the full Observation list (caller paginates).
 * @agentic @benchmark/core
 */ const searchCareTimeByPatient = async (medplum, patientId, options = {})=>{
    const tuples = buildCareTimeSearchTuples(patientId, options);
    // `MedplumClient.search` accepts `URLSearchParams`; constructing it
    // from the tuples preserves duplicate `date` keys under URL encoding
    // so the FHIR server receives `?date=ge…&date=le…` (AND filter).
    const params = new URLSearchParams(tuples);
    const bundle = await medplum.search("Observation", params);
    return bundle.entry?.map((e)=>e.resource).filter(Boolean) ?? [];
};

const encounterHasTag$1 = (encounter, system, code)=>(encounter.meta?.tag ?? []).some((tag)=>tag.system === system && tag.code === code);
const isFinishedWithType = (encounter, typeCode)=>encounter.status === "finished" && encounterHasTag$1(encounter, VALUE_SET.ENCOUNTER_TYPE, typeCode);
/**
 * A finished Encounter carrying any v2 intake-type tag (any billing track).
 *
 * Matches by tag PRESENCE — true when *any* `ENCOUNTER_TYPE` tag is an intake
 * code — not by inspecting only the first type tag. Every other signal in this
 * file already matches by presence (`encounterHasTag`); an encounter can carry
 * more than one type tag, so a first-tag check could report `hasCompletedIntake`
 * = false for an encounter that `hasBillableCoCMIntake` = true reports as an
 * intake, wrongly blocking all CoCM billing. Read-side billing signals must
 * match by presence (see `reconcileIntakeChargeItem.action.ts`).
 */ const isFinishedIntake = (encounter)=>encounter.status === "finished" && (encounter.meta?.tag ?? []).some((tag)=>tag.system === VALUE_SET.ENCOUNTER_TYPE && isIntakeEncounterType(tag.code));
/**
 * Fetch both encounter-based billing signals in a single search.
 *
 * Mirrors the logic previously inlined in Connections'
 * `useEncounterEligibility` so the two never drift. A "billable 90791"
 * is a finished clinical-intake Encounter carrying both the
 * clinical-intake type tag and the `billable` tag.
 * @agentic @benchmark/core
 */ const getPatientEncounterBillingSignals = async (medplum, patientId)=>{
    // Every signal here requires a FINISHED encounter, and the intake (which
    // gates billing, concern 6) is typically the OLDEST finished encounter — so a
    // newest-N slice would miss it and wrongly block a long-tenured patient. Scope
    // the search to finished encounters and take a full page so an old intake is
    // never truncated away.
    const encounters = await searchResources(medplum, "Encounter", {
        subject: `Patient/${patientId}`,
        status: "finished",
        _sort: "-date",
        _count: "1000"
    });
    const list = (encounters.entry ?? []).map((e)=>e.resource).filter(Boolean);
    const hasBillable90791 = list.some((e)=>isFinishedWithType(e, CLINICAL_INTAKE_ENCOUNTER_TYPE_CODE$1) && encounterHasTag$1(e, VALUE_SET.ENCOUNTER_BILLABLE, "billable"));
    const hasBillableCoCMIntake = list.some((e)=>isFinishedWithType(e, COCM_INTAKE_ENCOUNTER_TYPE_CODE$1));
    const hasCompletedIntake = list.some(isFinishedIntake);
    const cocmIntakeStarts = list.filter((e)=>isFinishedWithType(e, COCM_INTAKE_ENCOUNTER_TYPE_CODE$1)).map((e)=>e.period?.start).filter((s)=>Boolean(s)).sort();
    return {
        hasBillable90791,
        hasBillableCoCMIntake,
        hasCompletedIntake,
        cocmIntakeStarts
    };
};

/** Page size for the ChargeItem history scan. */ const CHARGE_ITEM_PAGE_SIZE$1 = 100;
/** Hard page cap so a looping/broken server response can't spin forever. */ const CHARGE_ITEM_MAX_PAGES$1 = 100;
const EMPTY_FACTS = {
    anyCodeBilledBefore: false,
    billed99492Before: false,
    earliestBilledWasG2214: false
};
/**
 * Codes that define a billed month for classification. 99494 is only ever an
 * add-on to a base code, so it is not billing-defining on its own; a stray
 * 99494 must not by itself mark a month as billed.
 */ const BILLING_DEFINING_CODES = new Set([
    "G2214",
    "99492",
    "99493"
]);
/**
 * Parse a CoCM ChargeItem identifier of the EXACT shape
 * `{patientId}|{YYYY-MM}|{code}|{n}`, validating the patient id, month format,
 * a billing-defining code, and a numeric occurrence index. Anything else
 * (missing/extra segments, foreign patient id, non-CoCM code, add-on 99494) is
 * ignored so a malformed or add-on line-item can't corrupt the history facts.
 */ const parseChargeItemIdentifier = (chargeItem, patientId)=>{
    const value = chargeItem.identifier?.find((i)=>i.system === COCM_CHARGE_ITEM_IDENTIFIER_SYSTEM)?.value;
    if (!value) return undefined;
    const parts = value.split("|");
    if (parts.length !== 4) return undefined;
    const [pid, month, code, index] = parts;
    if (pid !== patientId) return undefined;
    if (!/^\d{4}-\d{2}$/.test(month)) return undefined;
    if (!BILLING_DEFINING_CODES.has(code)) return undefined;
    if (!/^\d+$/.test(index)) return undefined;
    return {
        month,
        code
    };
};
/**
 * Read the patient's authoritative CoCM billing history for months strictly
 * before `currentMonth` (`YYYY-MM`). Scans persisted ChargeItems (reopened
 * months delete their ChargeItems, so a reopened month is correctly treated as
 * not-yet-billed). Returns {@link EMPTY_FACTS} when the patient has no prior
 * billed months.
 * @agentic @benchmark/core
 */ const getPriorBilledCodeHistory = async (medplum, patientId, currentMonth)=>{
    const codesByMonth = new Map();
    for(let page = 0; page < CHARGE_ITEM_MAX_PAGES$1; page++){
        const bundle = await searchResources(medplum, "ChargeItem", {
            subject: `Patient/${patientId}`,
            _count: String(CHARGE_ITEM_PAGE_SIZE$1),
            _offset: String(page * CHARGE_ITEM_PAGE_SIZE$1),
            // Stable ordering so offset paging can't skip or repeat a row.
            _sort: "_id"
        });
        const items = (bundle.entry ?? []).map((e)=>e.resource).filter(Boolean);
        for (const item of items){
            const parsed = parseChargeItemIdentifier(item, patientId);
            if (!parsed) continue;
            // Lexicographic compare is correct for `YYYY-MM`. Strictly prior only.
            if (parsed.month >= currentMonth) continue;
            const set = codesByMonth.get(parsed.month) ?? new Set();
            set.add(parsed.code);
            codesByMonth.set(parsed.month, set);
        }
        if (items.length < CHARGE_ITEM_PAGE_SIZE$1) break;
    }
    if (codesByMonth.size === 0) return EMPTY_FACTS;
    let anyCodeBilledBefore = false;
    let billed99492Before = false;
    for (const codes of codesByMonth.values()){
        if (codes.size > 0) anyCodeBilledBefore = true;
        if (codes.has("99492")) billed99492Before = true;
    }
    // Earliest prior billed month: was it G2214-only (no base code)?
    const earliestMonth = [
        ...codesByMonth.keys()
    ].sort()[0];
    const earliestCodes = codesByMonth.get(earliestMonth) ?? new Set();
    const earliestBilledWasG2214 = earliestCodes.has("G2214") && !earliestCodes.has("99492") && !earliestCodes.has("99493");
    return {
        anyCodeBilledBefore,
        billed99492Before,
        earliestBilledWasG2214
    };
};

const isInCategory = (plan, category)=>(plan.category ?? []).some((c)=>(c.coding ?? []).some((coding)=>coding.code === category));
/**
 * Inclusive lower bound (ms) of the assessment-freshness window for a billing
 * month: the START of the calendar day that is 6 weeks (42 days) before the
 * month's last day. Day-granular so an assessment dated exactly 6 weeks before
 * month-end still counts — e.g. an assessment logged 5/19 is valid for June
 * (last day 6/30), because 5/19 is exactly six weeks before 6/30. The window is
 * anchored to the LAST DAY of the billing month, never the first day of the next
 * billing period. Instant math (monthEnd − 42d against an end-of-day monthEnd)
 * dropped the boundary day; day-flooring fixes it.
 * @agentic @benchmark/core
 */ const assessmentFreshnessLowerBoundMs = (monthEndMs)=>{
    const d = new Date(monthEndMs);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - COCM_ASSESSMENT_FRESHNESS_WEEKS * 7);
};
const isFreshAssessment = (observation, monthEnd)=>{
    const effective = observation.effectiveDateTime ?? observation.effectivePeriod?.start;
    if (!effective) return false;
    const effectiveMs = Date.parse(effective);
    const monthEndMs = Date.parse(monthEnd);
    if (!Number.isFinite(effectiveMs) || !Number.isFinite(monthEndMs)) {
        return false;
    }
    return effectiveMs >= assessmentFreshnessLowerBoundMs(monthEndMs) && effectiveMs <= monthEndMs;
};
const monthKey = (iso)=>{
    const ms = Date.parse(iso);
    if (!Number.isFinite(ms)) return undefined;
    const d = new Date(ms);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};
const observationEffectiveMonthKey = (observation)=>{
    const effective = observation.effectiveDateTime ?? observation.effectivePeriod?.start;
    return effective ? monthKey(effective) : undefined;
};
/**
 * Minutes carried by a care-time entry. Non-finite values (a corrupted
 * `valueQuantity.value`) are treated as 0 so one bad entry can't poison the
 * month's summed totals into `NaN` (which would propagate to attested
 * minutes and the resolver input).
 */ const observationMinutes = (observation)=>{
    const value = observation.valueQuantity?.value;
    return typeof value === "number" && Number.isFinite(value) ? value : 0;
};
const isFinalStatus = (observation)=>observation.status !== "entered-in-error" && observation.status !== "cancelled";
/**
 * A care-time entry that counts toward CoCM billing: a final-status entry
 * that is NOT explicitly flagged non-billable. Used by the billable-minutes
 * sum and episode classification so a manually excluded entry never bills.
 * @agentic @benchmark/core
 */ const isBillableEntry = (observation)=>isFinalStatus(observation) && !isCareTimeNonBillable(observation);
const consentDateFromExtension = (carePlan)=>{
    for (const ext of carePlan.extension ?? []){
        if (ext.url === COCM_CONSENT_DATE_EXTENSION_URL) {
            return ext.valueDate ?? ext.valueDateTime;
        }
    }
    return undefined;
};
/**
 * Reconcile CoCM consent from the Patient resource (edit-form "verified"
 * flag + optional date) and the CoCM CarePlan's enrollment-time consent
 * extension into a single consent fact. Every consumer (eligibility, the
 * "Consented" chip, the CoCM registry, and the end-of-month billing report)
 * routes through this helper so they never disagree.
 *
 * Precedence: `consentObtained` is true when EITHER source signals consent
 * (the patient `verified` flag, or a consent date on either side).
 * `consentDate` prefers the Patient-level date and falls back to the CarePlan
 * enrollment consent date. Because enrollment now writes the consent date back
 * to the Patient (`buildCoCMEnrollmentBundle`), both sources agree for every
 * new enrollment regardless of precedence; the fallback only matters for
 * legacy records where consent was captured on just one side.
 * @agentic @benchmark/core
 */ const resolveCoCMConsent = (patient, carePlan)=>{
    const fromPatient = patient ? getCoCMConsentFromPatient(patient) : undefined;
    const fromPlan = carePlan ? consentDateFromExtension(carePlan) : undefined;
    // Consent is obtained when any source signals it: the patient verified flag,
    // a patient-level consent date, or the CarePlan enrollment consent date. This
    // keeps `consentObtained` consistent with `consentDate` — a populated date is
    // never reported as "not obtained".
    const consentObtained = Boolean(fromPatient?.verified) || Boolean(fromPatient?.date) || Boolean(fromPlan);
    const consentDate = fromPatient?.date ?? fromPlan;
    return {
        consentDate,
        consentObtained
    };
};
/**
 * Sum care-time Observation minutes filtered by:
 *  - status not entered-in-error / cancelled
 *  - effective date is within the calendar month window
 *
 * The `basedOn` reference is intentionally NOT consulted — per
 * A1/A3 (2026-05-19 plan) every minute counts when the caller has
 * already confirmed the patient is in CoCM.
 * @agentic @benchmark/core
 */ const sumMinutesInMonth = (observations, monthStart, monthEnd)=>{
    const startMs = Date.parse(monthStart);
    const endMs = Date.parse(monthEnd);
    let total = 0;
    for (const obs of observations){
        if (!isFinalStatus(obs)) continue;
        const effective = obs.effectiveDateTime ?? obs.effectivePeriod?.start;
        if (!effective) continue;
        const effectiveMs = Date.parse(effective);
        if (!Number.isFinite(effectiveMs)) continue;
        if (effectiveMs < startMs || effectiveMs > endMs) continue;
        total += observationMinutes(obs);
    }
    return total;
};
/**
 * Sum care-time minutes for entries within the calendar month that are
 * explicitly flagged non-billable (and not soft-deleted). Mirrors
 * {@link sumMinutesInMonth}'s window/status filtering so the returned value is
 * the non-billable slice of `totalMinutes`.
 * @agentic @benchmark/core
 */ const sumNonBillableMinutesInMonth = (observations, monthStart, monthEnd)=>{
    const startMs = Date.parse(monthStart);
    const endMs = Date.parse(monthEnd);
    let total = 0;
    for (const obs of observations){
        if (!isFinalStatus(obs)) continue;
        if (!isCareTimeNonBillable(obs)) continue;
        const effective = obs.effectiveDateTime ?? obs.effectivePeriod?.start;
        if (!effective) continue;
        const effectiveMs = Date.parse(effective);
        if (!Number.isFinite(effectiveMs)) continue;
        if (effectiveMs < startMs || effectiveMs > endMs) continue;
        total += observationMinutes(obs);
    }
    return total;
};
/** Inclusive calendar-month bounds (UTC) for a `YYYY-MM` key. */ const monthBoundsForKey = (key)=>{
    const [yearStr, monthStr] = key.split("-");
    const year = Number(yearStr);
    const monthIndex = Number(monthStr) - 1;
    if (!Number.isFinite(year) || !Number.isFinite(monthIndex)) return undefined;
    const startMs = Date.UTC(year, monthIndex, 1, 0, 0, 0, 0);
    const endMs = Date.UTC(year, monthIndex + 1, 0, 23, 59, 59, 999);
    return {
        startMs,
        endMs
    };
};
const episodeStartMs = (plan)=>{
    // Fall back to `created` when `period.start` is missing (legacy plans),
    // matching dbt's COALESCE(period_start, created) so both engines agree on
    // episode overlap.
    const start = plan.period?.start ?? plan.created;
    return start ? Date.parse(start) : Number.NaN;
};
/** Episode end in ms, or +Infinity for an open (still-enrolled) episode. */ const episodeEndMs = (plan)=>plan.period?.end ? Date.parse(plan.period.end) : Number.POSITIVE_INFINITY;
/**
 * Back-date each CoCM episode's start to the CoCM-intake encounter that began
 * THAT episode, returning copies with the corrected `period.start` so every
 * window consumer that reads the start through {@link episodeStartMs} sees it.
 *
 * The CoCM CarePlan is often created a day or two AFTER the actual CoCM intake
 * (its `period.start` defaults to the documentation date), so intake-month
 * care-time logged before it would otherwise be mis-classified as pre-enrollment
 * and dropped. This is the mechanism dbt already uses (`cocm_intake_start`).
 *
 * Association rule (brief IB2): for each episode (processed in raw-start order),
 * pick the LATEST — i.e. closest — finished CoCM-intake `t` such that
 *   `monthStartMs <= t <= rawStart` and `t > previousEpisodeRawStart`,
 * and back-date only when `t < rawStart`. The three bounds make this robust:
 *  - `t <= rawStart` and "latest": associates to THIS enrollment's intake, not a
 *    stray mid-episode one — closest wins.
 *  - `t >= monthStartMs`: keeps back-dating WITHIN the current billing month
 *    (cross-month is deferred, IB3) and ignores suspiciously old prior-month
 *    intakes that must not widen this episode.
 *  - `t > previousEpisodeRawStart`: a re-enrollment with no new intake of its own
 *    is never back-dated to an earlier episode's intake (the wrong-CPT guard).
 * @agentic @benchmark/core
 */ const buildBillingEpisodes = (episodes, cocmIntakeStartsMs, monthStartMs, monthEndMs)=>{
    const intakes = cocmIntakeStartsMs.filter((t)=>Number.isFinite(t)).sort((a, b)=>a - b);
    const sorted = [
        ...episodes
    ].filter((p)=>Number.isFinite(episodeStartMs(p))).sort((a, b)=>episodeStartMs(a) - episodeStartMs(b));
    const effectiveStartByPlan = new Map();
    let prevRawStart = Number.NEGATIVE_INFINITY;
    for (const plan of sorted){
        const rawStart = episodeStartMs(plan);
        // Only an episode whose raw start is at/before the month end is eligible for
        // back-dating. A FUTURE CarePlan (raw start after this month) must never be
        // pulled into the current month by a current-month intake — that would leak
        // a later enrollment's window into billing/classification (and cross-month
        // is deferred, IB3). An already-started episode (raw start before the month)
        // finds no match anyway, since `t >= monthStartMs` forces `t > rawStart`.
        if (rawStart <= monthEndMs) {
            // Latest intake in [monthStart, rawStart] and after the previous episode.
            let match;
            for (const t of intakes){
                if (t >= monthStartMs && t <= rawStart && t > prevRawStart) match = t;
            }
            if (match !== undefined && match < rawStart) {
                effectiveStartByPlan.set(plan, match);
            }
        }
        prevRawStart = rawStart;
    }
    return episodes.map((plan)=>{
        const eff = effectiveStartByPlan.get(plan);
        if (eff === undefined) return plan;
        return {
            ...plan,
            period: {
                ...plan.period,
                start: new Date(eff).toISOString()
            }
        };
    });
};
/**
 * Whether a CoCM episode overlaps the month by PERIOD alone. Billing
 * eligibility is intentionally `CarePlan.status`-independent (concerns 4 & 5):
 * a revoked plan still bills minutes before its `period.end`, and a paused
 * (`on-hold`) plan — which never sets `period.end` — still bills every in-month
 * minute, including those logged during the pause. Do not add a status filter
 * here; excluding revoked/paused plans would silently drop billable minutes.
 * @agentic @benchmark/core
 */ const episodeOverlapsMonth = (plan, monthStartMs, monthEndMs)=>{
    const start = episodeStartMs(plan);
    const end = episodeEndMs(plan);
    if (!Number.isFinite(start)) return false;
    return start <= monthEndMs && end >= monthStartMs;
};
/**
 * Build the set of billable millisecond intervals within a month (D1/D2):
 *  - one interval per overlapping episode, clamped to the month
 *  - plus the pre-enrollment span [monthStart, earliestEnrollment) when the
 *    patient enrolled mid-month AND has no billed 90791 intake (D1 default);
 *    the span is dropped when a billed 90791 exists (D1 carve-out).
 * Post-de-enrollment minutes and inter-episode gaps are excluded by
 * construction (they fall in no interval).
 * @agentic @benchmark/core
 */ const computeBillableIntervalsForMonth = (overlappingEpisodes, monthStartMs, monthEndMs, patientHasBillable90791)=>{
    if (overlappingEpisodes.length === 0) return [];
    const intervals = [];
    let earliestStart = Number.POSITIVE_INFINITY;
    for (const plan of overlappingEpisodes){
        const start = Math.max(episodeStartMs(plan), monthStartMs);
        const end = Math.min(episodeEndMs(plan), monthEndMs);
        if (Number.isFinite(start) && start <= end) {
            intervals.push({
                startMs: start,
                endMs: end
            });
            earliestStart = Math.min(earliestStart, start);
        }
    }
    // Pre-enrollment span, only when enrolled mid-month and no billed 90791.
    if (!patientHasBillable90791 && Number.isFinite(earliestStart) && earliestStart > monthStartMs) {
        intervals.push({
            startMs: monthStartMs,
            endMs: earliestStart
        });
    }
    return intervals;
};
const isInAnyInterval = (ms, intervals)=>intervals.some(({ startMs, endMs })=>ms >= startMs && ms <= endMs);
/** Sum care-time minutes whose effective instant falls in a billable interval. */ const sumMinutesInIntervals = (observations, intervals)=>{
    if (intervals.length === 0) return 0;
    let total = 0;
    for (const obs of observations){
        if (!isBillableEntry(obs)) continue;
        const effective = obs.effectiveDateTime ?? obs.effectivePeriod?.start;
        if (!effective) continue;
        const ms = Date.parse(effective);
        if (!Number.isFinite(ms)) continue;
        if (isInAnyInterval(ms, intervals)) total += observationMinutes(obs);
    }
    return total;
};
/** Billable minutes for an arbitrary month under the V2 window rules. */ const billableMinutesForMonthKey = (key, observations, episodes, patientHasBillable90791)=>{
    const bounds = monthBoundsForKey(key);
    if (!bounds) return 0;
    const overlapping = episodes.filter((p)=>episodeOverlapsMonth(p, bounds.startMs, bounds.endMs));
    if (overlapping.length === 0) return 0;
    const intervals = computeBillableIntervalsForMonth(overlapping, bounds.startMs, bounds.endMs, patientHasBillable90791);
    return sumMinutesInIntervals(observations, intervals);
};
/**
 * Whether any of `assessments` is fresh relative to the last day of the
 * `YYYY-MM` month key — used to classify a historical month with ITS OWN
 * assessment freshness rather than the current month's (concern 3). A
 * 99492/99493 bill requires a fresh assessment in that month's window; a month
 * with none can only ever have billed G2214.
 * @agentic @benchmark/core
 */ const hasFreshAssessmentForMonthKey = (assessments, monthKey)=>{
    const bounds = monthBoundsForKey(monthKey);
    if (!bounds) return false;
    const monthEndIso = new Date(bounds.endMs).toISOString();
    return assessments.some((a)=>isFreshAssessment(a, monthEndIso));
};
/**
 * D14 + D13 fallback classification under the V2 window: walk each `YYYY-MM`
 * present in the episode's care-time, compute windowed billable minutes, and
 * find the first month that produces a code. Each candidate month is classified
 * with its OWN assessment freshness (concern 3), so the result is deterministic
 * and independent of the current month's assessment status.
 *
 * This is the FALLBACK: the authoritative initial/subsequent + cascade signal
 * comes from persisted billing history (`getPriorBilledCodeHistory`); this walk
 * only covers months with no persisted packet.
 * @agentic @benchmark/core
 */ const classifyEpisodeMonthsWindowed = (observations, episodes, patientHasBillable90791, currentMonth, assessments)=>{
    const monthKeys = new Set();
    for (const obs of observations){
        if (!isFinalStatus(obs)) continue;
        const key = observationEffectiveMonthKey(obs);
        if (key) monthKeys.add(key);
    }
    const sortedKeys = [
        ...monthKeys
    ].sort();
    let initialMonth;
    let initialMonthBilledG2214 = false;
    for (const key of sortedKeys){
        const minutes = billableMinutesForMonthKey(key, observations, episodes, patientHasBillable90791);
        const tentative = resolveBillingCode({
            totalMinutes: minutes,
            isInitialMonth: true,
            episodeFirstBillWasG2214: false,
            hasEligibleAssessment: hasFreshAssessmentForMonthKey(assessments, key)
        });
        if (tentative.codes.length > 0) {
            initialMonth = key;
            initialMonthBilledG2214 = tentative.codes[0] === "G2214";
            break;
        }
    }
    return {
        isInitialMonth: initialMonth === undefined || initialMonth === currentMonth,
        episodeFirstBillWasG2214: initialMonthBilledG2214
    };
};
/** Pick the episode that represents the month for id/period/consent display. */ const pickCurrentEpisode = (overlappingEpisodes)=>{
    if (overlappingEpisodes.length === 0) return undefined;
    const active = overlappingEpisodes.find((p)=>p.status === "active");
    if (active) return active;
    return [
        ...overlappingEpisodes
    ].sort((a, b)=>episodeStartMs(b) - episodeStartMs(a))[0];
};
/**
 * All of a patient's CoCM CarePlans, any status — used to detect episodes
 * that overlapped a month even if since de-enrolled, and to find the
 * earliest episode start for full-history enumeration (billing tab).
 * Filters to the CoCM category server-side so a patient with many non-CoCM
 * plans can't push an older CoCM episode off the page; the client-side guard
 * stays as defense.
 * @agentic @benchmark/core
 */ const fetchAllCoCMEpisodes = async (medplum, patientId)=>{
    const bundle = await searchResources(medplum, "CarePlan", {
        subject: `Patient/${patientId}`,
        category: "cocm",
        _count: "100",
        _sort: "-_lastUpdated"
    });
    return (bundle.entry ?? []).map((e)=>e.resource).filter(Boolean).filter((p)=>isInCategory(p, "cocm"));
};
/**
 * Compute the CoCM eligibility summary for a patient + month (D1/D2 windowed).
 *
 * Pure orchestration: fetches FHIR resources, then invokes the pure
 * resolver. No domain events emitted (this is a read path). Billable minutes
 * are windowed to overlapping CoCM episodes (pre-enrollment same-month minutes
 * included unless a billed 90791 exists; post-de-enrollment minutes excluded).
 * @agentic @benchmark/core
 */ const computeCoCMEligibility = async (medplum, input)=>{
    const { patientId, monthStart, monthEnd } = input;
    const monthStartMs = Date.parse(monthStart);
    const monthEndMs = Date.parse(monthEnd);
    // 1. All CoCM CarePlans for the patient (any status), to detect episodes
    // that overlapped the month even if since de-enrolled.
    const cocmEpisodes = await fetchAllCoCMEpisodes(medplum, patientId);
    const overlapping = cocmEpisodes.filter((p)=>episodeOverlapsMonth(p, monthStartMs, monthEndMs));
    const hasCoCMInMonth = overlapping.length > 0;
    // 2. Encounter-derived billing signals: the billed-90791 signal for the D1
    // pre-enrollment carve-out, the completed-intake gate (concern 6), AND the
    // CoCM-intake starts used to back-date episode starts below. One Encounter
    // search covers all three; skipped when not enrolled in the month.
    const encounterSignals = hasCoCMInMonth ? await getPatientEncounterBillingSignals(medplum, patientId) : {
        hasBillable90791: false,
        hasCompletedIntake: false,
        cocmIntakeStarts: []
    };
    const patientHasBillable90791 = encounterSignals.hasBillable90791;
    const hasCompletedIntake = encounterSignals.hasCompletedIntake;
    // Back-date each episode's start to the CoCM-intake encounter that began it,
    // WITHIN the current billing month (brief IB2/IB3). The CoCM CarePlan's
    // `period.start` often lags the actual intake by a day or two, so intake-month
    // care-time logged before it would otherwise be mis-classified as
    // pre-enrollment and dropped. The back-dated episodes drive the billable-minute
    // window and episode classification; `hasCoCMInMonth`, month-overlap, and the
    // display fields keep the raw CarePlan (cross-month back-dating is deferred, so
    // raw and back-dated episodes overlap the month identically).
    const cocmIntakeStartsMs = encounterSignals.cocmIntakeStarts.map((s)=>Date.parse(s)).filter((n)=>Number.isFinite(n));
    const billingEpisodes = buildBillingEpisodes(cocmEpisodes, cocmIntakeStartsMs, monthStartMs, monthEndMs);
    const billingOverlapping = billingEpisodes.filter((p)=>episodeOverlapsMonth(p, monthStartMs, monthEndMs));
    // 3. Care-time from the earliest episode start (for episode classification)
    // through month end.
    const earliestEpisodeStartMs = cocmEpisodes.reduce((min, p)=>{
        const s = episodeStartMs(p);
        return Number.isFinite(s) ? Math.min(min, s) : min;
    }, Number.POSITIVE_INFINITY);
    const fetchStart = Number.isFinite(earliestEpisodeStartMs) && earliestEpisodeStartMs < monthStartMs ? new Date(earliestEpisodeStartMs).toISOString() : monthStart;
    const allObservations = await searchCareTimeByPatient(medplum, patientId, {
        startDate: fetchStart,
        endDate: monthEnd,
        count: 1000
    });
    // 4. Eligible assessments. Fetch back to the earliest episode month's window
    // (not just the current month's) so historical months can be classified with
    // THEIR OWN freshness (concern 3). The current-month freshness lower bound is
    // day-granular so the exact-6-weeks boundary day is included (concern 2).
    const sixWeeksMs = COCM_ASSESSMENT_FRESHNESS_WEEKS * 7 * 24 * 60 * 60 * 1000;
    const currentMonthLowerBoundMs = assessmentFreshnessLowerBoundMs(monthEndMs);
    const historyLowerBoundMs = (Number.isFinite(earliestEpisodeStartMs) ? earliestEpisodeStartMs : monthStartMs) - sixWeeksMs;
    const assessmentSearchStart = new Date(Math.min(currentMonthLowerBoundMs, historyLowerBoundMs)).toISOString();
    const assessmentBundle = await searchResources(medplum, "Observation", {
        patient: patientId,
        code: COCM_ELIGIBLE_ASSESSMENT_CODES_TOKEN,
        status: "final",
        date: `ge${assessmentSearchStart}`,
        _sort: "-date",
        _count: "100"
    });
    // Full eligible-assessment set over the fetched span (date-desc). Per-month
    // freshness is applied by the classifier; the current month uses its own.
    const allAssessments = (assessmentBundle.entry ?? []).map((e)=>e.resource).filter(Boolean);
    const currentMonthAssessments = allAssessments.filter((a)=>isFreshAssessment(a, monthEnd));
    const hasEligibleAssessment = currentMonthAssessments.length > 0;
    const latestAssessmentDate = currentMonthAssessments[0]?.effectiveDateTime ?? currentMonthAssessments[0]?.effectivePeriod?.start;
    // 5. Total minutes — all care-time in the calendar month (unchanged meaning).
    const totalMinutes = sumMinutesInMonth(allObservations, monthStart, monthEnd);
    // 5b. Non-billable slice of the calendar month (explicit exclusions).
    const nonBillableMinutes = sumNonBillableMinutesInMonth(allObservations, monthStart, monthEnd);
    // 6. Billable minutes — windowed sum, only when enrolled during the month.
    const billableMinutes = hasCoCMInMonth ? sumMinutesInIntervals(allObservations, computeBillableIntervalsForMonth(billingOverlapping, monthStartMs, monthEndMs, patientHasBillable90791)) : 0;
    // 7. Episode classification (D13/D14). Authoritative prior-billing history
    // (persisted ChargeItems) wins; the live recompute is the fallback for months
    // with no persisted packet (concerns 1 & 3).
    const currentMonthKey = monthKey(monthStart) ?? "";
    const priorFacts = hasCoCMInMonth ? await getPriorBilledCodeHistory(medplum, patientId, currentMonthKey) : {
        anyCodeBilledBefore: false,
        billed99492Before: false,
        earliestBilledWasG2214: false
    };
    const fallback = hasCoCMInMonth ? classifyEpisodeMonthsWindowed(allObservations, billingEpisodes, patientHasBillable90791, currentMonthKey, allAssessments) : {
        isInitialMonth: true,
        episodeFirstBillWasG2214: false
    };
    // Any authoritatively-billed prior month means this is not the initial month.
    const isInitialMonth = !priorFacts.anyCodeBilledBefore && fallback.isInitialMonth;
    // Cascade flag: prefer the authoritative earliest-billed-month signal.
    const episodeFirstBillWasG2214 = priorFacts.anyCodeBilledBefore ? priorFacts.earliestBilledWasG2214 : fallback.episodeFirstBillWasG2214;
    // 8. Pure resolver. `block99492` (authoritative) guarantees 99492 never
    // recurs, independent of the initial-month recompute.
    const resolved = resolveBillingCode({
        totalMinutes: billableMinutes,
        isInitialMonth,
        episodeFirstBillWasG2214,
        hasEligibleAssessment,
        block99492: priorFacts.billed99492Before
    });
    // 9. Intake gate (concern 6): no completed intake ⇒ no billable codes,
    // regardless of minutes. Minutes stay surfaced so the UI can explain why.
    const billingResult = hasCoCMInMonth && !hasCompletedIntake ? {
        codes: [],
        packageDisplay: "",
        minutesToNextCode: undefined
    } : resolved;
    // Display uses the raw overlapping plans so the surfaced enrollment period is
    // the CarePlan's own (back-dating is billing-only, within-month).
    const currentEpisode = pickCurrentEpisode(overlapping);
    const { consentDate, consentObtained } = resolveCoCMConsent(input.patient, currentEpisode);
    return {
        totalMinutes,
        billableMinutes,
        nonBillableMinutes,
        billingResult,
        hasCoCMCarePlan: hasCoCMInMonth,
        cocmCarePlanId: currentEpisode?.id,
        cocmPeriodStart: currentEpisode?.period?.start,
        cocmPeriodEnd: currentEpisode?.period?.end,
        consentDate,
        consentObtained,
        hasEligibleAssessment,
        latestAssessmentDate,
        isInitialMonth,
        episodeFirstBillWasG2214,
        hasCompletedIntake
    };
};

/** Inclusive UTC month bounds for a `YYYY-MM` key. */ const monthBounds = (billingMonth)=>{
    const [year, month] = billingMonth.split("-").map(Number);
    return {
        start: new Date(Date.UTC(year, month - 1, 1, 0, 0, 0, 0)).toISOString(),
        end: new Date(Date.UTC(year, month, 0, 23, 59, 59, 999)).toISOString()
    };
};
/**
 * Compute the authoritative, versioned billing-code packet for a patient-month.
 *
 * Always uses the V2 billing window (the authoritative computation, per the
 * close-month CLI) rather than the flag-gated display path — the packet is the
 * source of truth, not an on-read estimate.
 * @agentic @benchmark/core
 */ const computeCoCMBillingPacket = async (medplum, input)=>{
    const { patientId, billingMonth } = input;
    const { start, end } = monthBounds(billingMonth);
    const summary = await computeCoCMEligibility(medplum, {
        patientId,
        monthStart: start,
        monthEnd: end,
        patient: input.patient
    });
    return buildBillingCodePacket({
        patientId,
        billingMonth,
        summary,
        attribution: input.attribution,
        generatedAt: input.generatedAt
    });
};
/**
 * Best-effort performer attribution for a PractitionerRole: the Practitioner's
 * display name (so persisted references carry a `display`, per our reference
 * best practice) and their license/credential (D22 — metadata only; never gates
 * code selection). Resolves the performer PractitionerRole → Practitioner in one
 * read pair and returns both. Fields are `undefined` when not recorded; the
 * fuller credential capture is the D26 fast-follow.
 * @agentic @benchmark/core
 */ const getPractitionerRoleAttribution = async (medplum, practitionerRoleId)=>{
    const role = await medplum.readResource("PractitionerRole", practitionerRoleId);
    const practitionerRef = role.practitioner?.reference;
    const practitionerId = practitionerRef?.split("/")[1];
    if (!practitionerId) {
        // Fall back to any display carried on the role's practitioner reference.
        return {
            display: role.practitioner?.display
        };
    }
    const practitioner = await medplum.readResource("Practitioner", practitionerId);
    const qualification = practitioner.qualification?.[0]?.code;
    return {
        display: getPractitionerDisplayName(practitioner) || role.practitioner?.display,
        license: qualification?.text ?? qualification?.coding?.find((c)=>c.display)?.display ?? qualification?.coding?.[0]?.code
    };
};
/**
 * Find the persisted attested billing packet (`Composition`) for a
 * patient-month, if one exists. Used by the billing tab to show a locked
 * month's frozen packet.
 * @agentic @benchmark/core
 */ const findCoCMBillingPacket = async (medplum, patientId, billingMonth)=>{
    const bundle = await searchResources(medplum, "Composition", {
        identifier: buildCoCMBillingPacketIdentifierToken(patientId, billingMonth),
        _count: "1"
    });
    return bundle.entry?.[0]?.resource ?? undefined;
};
/** Page size for the reconciliation ChargeItem scan. */ const CHARGE_ITEM_PAGE_SIZE = 100;
/** Hard cap on pages so a broken/looping server response can't spin forever. */ const CHARGE_ITEM_MAX_PAGES = 100;
/** The COCM identifier value carried by a ChargeItem, if any. */ const cocmChargeItemIdentifierValue = (chargeItem)=>chargeItem.identifier?.find((i)=>i.system === COCM_CHARGE_ITEM_IDENTIFIER_SYSTEM)?.value;
/**
 * Find every persisted CoCM `ChargeItem` line-item for a patient-month. Used by
 * the submit/reopen reconciliation to prune stale line-items (a re-submit with
 * fewer codes, or a reopen) so `SUM`-based downstream billing can never
 * double-count an orphaned occurrence. Scoped by the shared
 * `{patientId}|{YYYY-MM}|` identifier prefix.
 * @agentic @benchmark/core
 */ const findCoCMChargeItemsForMonth = async (medplum, patientId, billingMonth)=>{
    const prefix = cocmChargeItemMonthPrefix(patientId, billingMonth);
    const matches = [];
    for(let page = 0; page < CHARGE_ITEM_MAX_PAGES; page++){
        const bundle = await searchResources(medplum, "ChargeItem", {
            subject: `Patient/${patientId}`,
            _count: String(CHARGE_ITEM_PAGE_SIZE),
            _offset: String(page * CHARGE_ITEM_PAGE_SIZE),
            // Stable ordering so offset paging can't skip or repeat a row.
            _sort: "_id"
        });
        const items = (bundle.entry ?? []).map((e)=>e.resource).filter(Boolean);
        for (const ci of items){
            const value = cocmChargeItemIdentifierValue(ci);
            if (value !== undefined && value.startsWith(prefix)) matches.push(ci);
        }
        if (items.length < CHARGE_ITEM_PAGE_SIZE) break;
    }
    return matches;
};

const bundleToObservations = (bundle)=>(bundle.entry ?? []).map((e)=>e.resource).filter(Boolean);
/**
 * Find the single billing summary for a patient + month, if one exists.
 * @agentic @benchmark/core
 */ const findCoCMBillingSummary = async (medplum, patientId, billingMonth)=>{
    const bundle = await searchResources(medplum, "Observation", {
        identifier: buildCoCMBillingSummaryIdentifierToken(patientId, billingMonth),
        _count: "1"
    });
    return bundleToObservations(bundle)[0];
};
/**
 * Pick the display that belongs to `performerRoleId` from the names already at
 * hand. A display is only ever valid for the role it was resolved for, so a
 * candidate for a different role is discarded rather than reused — otherwise a
 * month whose performer changes hands would label the new role with the
 * previous care manager's name.
 * @agentic @benchmark/core
 */ const performerDisplayForRole = (performerRoleId, candidates)=>performerRoleId ? candidates.find((c)=>c.roleId === performerRoleId && c.display)?.display : undefined;
/**
 * Resolve the display names the summary Observation's `subject` and
 * `performer` references should carry (reference best practice) — reading only
 * what the caller couldn't supply, in parallel. Best-effort: a missing or
 * unreadable resource yields `undefined` rather than failing the write.
 *
 * Costs one `Patient` read plus, for the performer, the `PractitionerRole` →
 * `Practitioner` read pair — so an entirely unknown attribution is three reads.
 * Callers that already hold the names (the billing modal) pay none.
 * @agentic @benchmark/core
 */ const resolveCoCMBillingSummaryAttribution = async (medplum, input)=>{
    const [patientDisplay, performerDisplay] = await Promise.all([
        input.patientDisplay ?? readResource(medplum, "Patient", input.patientId).then(getPatientReferenceDisplay).catch(()=>undefined),
        input.performerDisplay ?? (input.performerRoleId ? getPractitionerRoleAttribution(medplum, input.performerRoleId).then((a)=>a.display).catch(()=>undefined) : undefined)
    ]);
    return {
        patientDisplay,
        performerDisplay
    };
};

/**
 * Reopen a closed month, appending an audited reopen entry.
 * @agentic @benchmark/core
 */ const reopenCoCMBillingSummary = async (medplum, input)=>{
    const { patientId, billingMonth, reason, performerRoleId } = input;
    const existing = await findCoCMBillingSummary(medplum, patientId, billingMonth);
    if (!existing) {
        throw new Error("No billing summary exists for this patient-month");
    }
    const view = cocmBillingSummaryToView(existing);
    if (!view.closed) {
        throw new Error("Billing summary is not closed");
    }
    // Resolve who reopened so the audited `by` reference carries a `display`
    // (reference best practice). Best-effort; only when a role is known.
    const byDisplay = performerRoleId ? await getPractitionerRoleAttribution(medplum, performerRoleId).then((a)=>a.display).catch(()=>undefined) : undefined;
    const reopenHistory = [
        ...view.reopenHistory,
        {
            at: new Date().toISOString(),
            reason,
            by: performerRoleId,
            byDisplay
        }
    ];
    // Reopening rebuilds the resource, so the month keeps the performer who
    // recorded it. Reopening is not authorship: a record that was never
    // attributed stays unattributed rather than being stamped with the reopening
    // role, who is already audited in the reopen entry above. Any display the
    // record never had is backfilled here.
    const { patientDisplay, performerDisplay } = await resolveCoCMBillingSummaryAttribution(medplum, {
        patientId,
        patientDisplay: view.patientDisplay,
        performerRoleId: view.performerRoleId,
        // The reopening role's display is already resolved above; reuse it
        // instead of reading the same PractitionerRole twice.
        performerDisplay: performerDisplayForRole(view.performerRoleId, [
            {
                roleId: view.performerRoleId,
                display: view.performerDisplay
            },
            {
                roleId: performerRoleId,
                display: byDisplay
            }
        ])
    });
    const observation = buildCoCMBillingSummary({
        snapshot: {
            patientId,
            patientDisplay,
            billingMonth,
            attestedMinutes: view.attestedMinutes,
            clinicalSummary: view.clinicalSummary,
            contributingObservationIds: view.contributingObservationIds,
            performerRoleId: view.performerRoleId,
            performerDisplay
        },
        // Reopening un-submits the month so it returns to an editable state
        // awaiting re-submission; the audit that it *was* submitted lives in the
        // reopen entry (who/when/why) and the amended packet Composition.
        submission: {
            submitted: false
        },
        lockStatus: "reopened",
        reopenHistory,
        id: existing.id,
        issued: existing.issued
    });
    const updated = await updateResource(medplum, observation);
    if (!updated.id) throw new Error("Updated billing summary is missing an id");
    const event = {
        observationId: updated.id,
        observation: updated,
        patientId,
        billingMonth
    };
    globalEventEmitter.publish(DomainEventNames.CoCMBillingSummaryUpdated, event);
    return updated;
};

const roleReference = (roleId, display)=>roleId ? {
        reference: `PractitionerRole/${roleId}`,
        ...display ? {
            display
        } : {}
    } : undefined;
/**
 * Build the atomic transaction entries for an attested billing packet.
 * @agentic @benchmark/core
 */ const buildCoCMBillingPacketBundle = (input)=>{
    const { packet, summaryObservation } = input;
    const newUuid = input.uuid ?? (()=>`urn:uuid:${crypto.randomUUID()}`);
    const attestedAt = input.attestedAt ?? new Date().toISOString();
    const patientRef = {
        reference: `Patient/${packet.patientId}`,
        ...input.patientDisplay ? {
            display: input.patientDisplay
        } : {}
    };
    const performer = roleReference(input.performerRoleId, input.performerDisplay);
    const [year, month] = packet.billingMonth.split("-").map(Number);
    const occurrencePeriod = {
        start: new Date(Date.UTC(year, month - 1, 1, 0, 0, 0, 0)).toISOString(),
        end: new Date(Date.UTC(year, month, 0, 23, 59, 59, 999)).toISOString()
    };
    const summaryFullUrl = newUuid();
    const entries = [
        {
            resource: summaryObservation,
            method: "PUT",
            url: input.summaryConditionalUrl,
            fullUrl: summaryFullUrl
        }
    ];
    // One ChargeItem per billed code occurrence (99494×2 → two line-items).
    const chargeItemRefs = [];
    packet.codes.forEach((code, index)=>{
        const identifierValue = buildCoCMChargeItemIdentifierValue(packet.patientId, packet.billingMonth, code, index);
        const chargeItem = {
            resourceType: "ChargeItem",
            identifier: [
                {
                    system: COCM_CHARGE_ITEM_IDENTIFIER_SYSTEM,
                    value: identifierValue
                }
            ],
            status: "billable",
            code: {
                coding: [
                    {
                        system: billingCodeSystem(code),
                        code
                    }
                ],
                text: code
            },
            subject: patientRef,
            occurrencePeriod,
            quantity: {
                value: 1
            },
            ...performer ? {
                performer: [
                    {
                        actor: performer
                    }
                ]
            } : {},
            supportingInformation: [
                {
                    reference: summaryFullUrl
                }
            ],
            extension: [
                {
                    url: COCM_BILLING_PACKET_VERSION_EXTENSION_URL,
                    extension: [
                        {
                            url: "packetVersion",
                            valueInteger: packet.packetVersion
                        },
                        {
                            url: "rulesVersion",
                            valueString: packet.rulesVersion
                        },
                        {
                            url: "careUnits",
                            valueDecimal: CARE_UNITS_BY_CODE[code] ?? 0
                        }
                    ]
                }
            ]
        };
        const fullUrl = newUuid();
        chargeItemRefs.push({
            reference: fullUrl
        });
        entries.push({
            resource: chargeItem,
            method: "PUT",
            url: `ChargeItem?identifier=${COCM_CHARGE_ITEM_IDENTIFIER_SYSTEM}|${identifierValue}`,
            fullUrl
        });
    });
    const sections = [
        {
            title: "Billed Codes",
            text: {
                status: "generated",
                div: `<div xmlns="http://www.w3.org/1999/xhtml">${packet.packageDisplay || "No bill"} (${packet.careUnits} care units, rules ${packet.rulesVersion})</div>`
            },
            ...chargeItemRefs.length > 0 ? {
                entry: chargeItemRefs
            } : {}
        },
        {
            title: "Attested Minutes",
            text: {
                status: "generated",
                div: `<div xmlns="http://www.w3.org/1999/xhtml">${packet.totalMinutes} attested minutes (${packet.billableMinutes} billable)</div>`
            },
            entry: [
                {
                    reference: summaryFullUrl
                }
            ]
        }
    ];
    if (summaryObservation.note?.[0]?.text) {
        sections.push({
            title: "Clinical Summary",
            text: {
                status: "generated",
                div: `<div xmlns="http://www.w3.org/1999/xhtml">${summaryObservation.note[0].text}</div>`
            }
        });
    }
    const composition = {
        resourceType: "Composition",
        identifier: {
            system: COCM_BILLING_PACKET_IDENTIFIER_SYSTEM,
            value: buildCoCMBillingPacketIdentifierValue(packet.patientId, packet.billingMonth)
        },
        status: COCM_BILLING_PACKET_STATUS.closed,
        type: {
            ...COCM_BILLING_PACKET_TYPE
        },
        category: [
            {
                ...COCM_BILLING_PACKET_CATEGORY
            }
        ],
        subject: patientRef,
        date: attestedAt,
        title: `CoCM Billing Packet ${packet.billingMonth}`,
        // `author` is required (1..*); the submitting care manager is the author.
        // Empty only in the defensive no-role path (the modal always supplies one).
        author: performer ? [
            performer
        ] : [],
        ...performer ? {
            attester: [
                {
                    mode: "legal",
                    time: attestedAt,
                    party: performer
                }
            ]
        } : {},
        // Full packet snapshot (D19): the complete criteria — assessments used,
        // enrollment window, attribution, episode flags, versions, timestamp — so
        // the packet is reconstructible from this one resource for audit.
        extension: [
            {
                url: COCM_BILLING_PACKET_SNAPSHOT_EXTENSION_URL,
                valueString: JSON.stringify(packet)
            }
        ],
        section: sections
    };
    entries.push({
        resource: composition,
        method: "PUT",
        url: `Composition?identifier=${COCM_BILLING_PACKET_IDENTIFIER_SYSTEM}|${buildCoCMBillingPacketIdentifierValue(packet.patientId, packet.billingMonth)}`,
        fullUrl: newUuid()
    });
    return entries;
};

/**
 * Execute a FHIR Bundle transaction with atomic guarantees
 *
 * This function creates a Bundle with type "transaction" which ensures that
 * all operations either succeed together or fail together. If any single
 * operation fails, the entire transaction is rolled back and no resources
 * are persisted.
 *
 * @param medplum - MedplumClient instance
 * @param entries - Array of transaction entries with resources and optional HTTP methods
 * @returns Promise resolving to the Bundle result containing all created/updated resources
 * @throws Error if the transaction fails (with OperationOutcome details if available)
 *
 * @example
 * ```typescript
 * const entries: TransactionEntry[] = [
 *   { resource: encounter, method: 'POST' },
 *   { resource: observation, method: 'POST' },
 *   { resource: clinicalImpression, method: 'POST' }
 * ];
 *
 * try {
 *   const result = await executeTransaction(medplum, entries);
 *   // All resources created successfully
 * } catch (error) {
 *   // Transaction failed - no resources were created
 * }
 * ```
 * @agentic @benchmark/core
 */ async function executeTransaction(medplum, entries) {
    if (entries.length === 0) {
        return {
            resourceType: "Bundle",
            type: "transaction-response",
            entry: []
        };
    }
    const bundle = {
        resourceType: "Bundle",
        type: "transaction",
        entry: entries.map((entry)=>({
                fullUrl: entry.fullUrl,
                request: {
                    method: entry.method || "POST",
                    url: entry.url || entry.resource.resourceType
                },
                resource: entry.resource
            }))
    };
    return await executeBatchWithCacheInvalidation(medplum, bundle);
}
/**
 * Extract resources from a Bundle transaction result
 *
 * @param bundle - Bundle result from executeTransaction
 * @param resourceType - Optional filter to extract only resources of a specific type
 * @returns Array of resources from the Bundle entries
 *
 * @example
 * ```typescript
 * const result = await executeTransaction(medplum, entries);
 *
 * // Get all resources
 * const allResources = extractResourcesFromTransactionResult(result);
 *
 * // Get only Observations
 * const observations = extractResourcesFromTransactionResult(result, 'Observation');
 * ```
 * @agentic @benchmark/core
 */ function extractResourcesFromTransactionResult(bundle, resourceType) {
    const entries = bundle.entry || [];
    return entries.map((entry)=>entry.resource).filter((resource)=>!!resource).filter((resource)=>!resourceType || resource.resourceType === resourceType);
}
/**
 * Extract a single resource from a Bundle transaction result by resource type
 *
 * Useful when you know the transaction should return exactly one resource of a given type.
 * Throws an error if no resource is found or multiple resources of the type exist.
 *
 * @param bundle - Bundle result from executeTransaction
 * @param resourceType - Resource type to extract
 * @returns The single resource of the specified type
 * @throws Error if zero or multiple resources of the type are found
 *
 * @example
 * ```typescript
 * const result = await executeTransaction(medplum, entries);
 * const encounter = extractSingleResourceFromTransactionResult(result, 'Encounter');
 * ```
 * @agentic @benchmark/core
 */ function extractSingleResourceFromTransactionResult(bundle, resourceType) {
    const resources = extractResourcesFromTransactionResult(bundle, resourceType);
    if (resources.length === 0) {
        throw new Error(`No ${resourceType} resource found in transaction result`);
    }
    if (resources.length > 1) {
        throw new Error(`Expected 1 ${resourceType} resource but found ${resources.length}`);
    }
    return resources[0];
}

/**
 * DELETE `TransactionEntry`s for a patient-month's persisted ChargeItems whose
 * identifier is not in `keepIdentifierValues`. An empty keep-set deletes them
 * all (reopen). Keeps `SUM`-based downstream billing free of orphan line-items.
 */ const staleChargeItemDeletes = (existing, keepIdentifierValues)=>existing.filter((ci)=>{
        if (!ci.id) return false;
        const value = ci.identifier?.find((i)=>i.system === COCM_CHARGE_ITEM_IDENTIFIER_SYSTEM)?.value;
        return value === undefined || !keepIdentifierValues.has(value);
    }).map((ci)=>({
            resource: ci,
            method: "DELETE",
            url: `ChargeItem/${ci.id}`
        }));
/**
 * Reject a submitted packet whose billing-determining values diverge from the
 * server-recomputed authoritative packet (#1226). Only the NAMES of the
 * diverging fields are surfaced — never the values — because a patient-month's
 * codes/minutes are PHI-class clinical billing data and this message can reach
 * logs/telemetry (the sanitizer would not redact bare CPT codes or minute
 * counts). `careUnits` is derived from `codes` but is checked explicitly so a
 * divergence is reported precisely.
 */ const assertPacketMatchesAuthoritative = (submitted, authoritative)=>{
    const divergingFields = [];
    const sameCodes = submitted.codes.length === authoritative.codes.length && submitted.codes.every((code, index)=>code === authoritative.codes[index]);
    if (!sameCodes) divergingFields.push("codes");
    if (submitted.careUnits !== authoritative.careUnits) {
        divergingFields.push("careUnits");
    }
    if (submitted.billableMinutes !== authoritative.billableMinutes) {
        divergingFields.push("billableMinutes");
    }
    if (submitted.totalMinutes !== authoritative.totalMinutes) {
        divergingFields.push("totalMinutes");
    }
    if (divergingFields.length > 0) {
        throw new Error(`Submitted billing packet does not match the server-recomputed packet and cannot be frozen (diverging: ${divergingFields.join(", ")}). Regenerate the codes and resubmit.`);
    }
};
/**
 * Submit + lock the attested packet for a patient-month.
 * @agentic @benchmark/core
 */ const submitCoCMBillingPacket = async (medplum, input)=>{
    const { patientId, billingMonth, packet } = input;
    if (!patientId) throw new Error("patientId is required");
    if (!billingMonth) throw new Error("billingMonth is required");
    // Integrity: never freeze a packet whose identity doesn't match the target.
    // Guards against a mismatched/tampered client-supplied packet being persisted
    // for the wrong patient or month.
    if (packet.patientId !== patientId || packet.billingMonth !== billingMonth) {
        throw new Error(`Packet identity (${packet.patientId}/${packet.billingMonth}) does not match submission target (${patientId}/${billingMonth})`);
    }
    const existingSummary = await findCoCMBillingSummary(medplum, patientId, billingMonth);
    const existingView = existingSummary ? cocmBillingSummaryToView(existingSummary) : undefined;
    // D7 integrity: a locked month must be reopened before re-submitting.
    if (existingView?.closed) {
        throw new Error("Billing month is already locked — reopen it before re-submitting (D7)");
    }
    // Submission must be attributed: the packet Composition's `author` is required
    // (1..*) and the attested packet needs a legal attester. Fail closed rather
    // than write a Composition with an empty `author` / no attester.
    if (!input.performerRoleId) {
        throw new Error("performerRoleId is required to submit a billing packet (submission must be attributed)");
    }
    // Value integrity (#1226): never freeze a client-computed packet without
    // re-deriving it server-side. The stepped modal computes the packet via the
    // authoritative engine (`POST /api/v1/billing-codes` → computeCoCMBillingPacket)
    // and submits it through the user's Medplum client, so a tampered in-memory
    // packet could otherwise freeze inflated/incorrect CPT/HCPCS codes as the
    // attested FHIR billing record (which then feeds dbt billing reporting).
    // Recompute from the patient's actual care-time and reject any submit whose
    // billing-determining values diverge — a tamper OR a packet that went stale
    // after the month's care-time changed. The recompute is deterministic w.r.t.
    // FHIR state, so a legitimate same-state submit always matches.
    const authoritativePacket = await computeCoCMBillingPacket(medplum, {
        patientId,
        billingMonth
    });
    assertPacketMatchesAuthoritative(packet, authoritativePacket);
    // Persist the server-recomputed determinative values (defense in depth: never
    // trust the client's codes/units/minutes even after the equality check),
    // preserving only the client's original generation stamp. Attribution is
    // layered on below.
    const verifiedPacket = {
        ...authoritativePacket,
        generatedAt: packet.generatedAt
    };
    const submittedAt = new Date().toISOString();
    // Resolve performer attribution once (one PractitionerRole → Practitioner read
    // pair): the display name so persisted references carry a `display` (our
    // reference best practice), and the license for the packet snapshot (D22 —
    // metadata only, never gates code selection). Best-effort: a failure/absence
    // leaves fields undefined rather than blocking submission (fuller credential
    // capture is the D26 fast-follow).
    const { display: performerDisplay, license } = input.performerRoleId ? await getPractitionerRoleAttribution(medplum, input.performerRoleId).catch(()=>({
            display: undefined,
            license: undefined
        })) : {
        display: undefined,
        license: undefined
    };
    const attributedPacket = input.performerRoleId ? {
        ...verifiedPacket,
        attribution: {
            ...verifiedPacket.attribution,
            performerRoleId: input.performerRoleId,
            ...license ? {
                license
            } : {}
        }
    } : verifiedPacket;
    // Submitting attests to the month; it does not re-author it. The summary
    // keeps the performer who recorded it, and only a month that was never
    // captured takes the submitter as its performer. Who submitted is separately
    // audited in the `submittedBy` extension and the packet Composition's
    // author/attester, so nothing about the submission is lost.
    const recordedByRoleId = existingView?.performerRoleId ?? input.performerRoleId;
    // The packet's Patient references must carry a display; fall back to a read
    // when neither the caller nor the existing summary supplied one.
    const { patientDisplay, performerDisplay: recordedByDisplay } = await resolveCoCMBillingSummaryAttribution(medplum, {
        patientId,
        patientDisplay: input.patientDisplay ?? existingView?.patientDisplay,
        performerRoleId: recordedByRoleId,
        performerDisplay: performerDisplayForRole(recordedByRoleId, [
            {
                roleId: existingView?.performerRoleId,
                display: existingView?.performerDisplay
            },
            {
                roleId: input.performerRoleId,
                display: performerDisplay
            }
        ])
    });
    const summaryObservation = buildCoCMBillingSummary({
        snapshot: {
            patientId,
            patientDisplay,
            billingMonth,
            attestedMinutes: input.attestedMinutes ?? verifiedPacket.billableMinutes,
            clinicalSummary: input.clinicalSummary ?? existingView?.clinicalSummary,
            contributingObservationIds: input.contributingObservationIds ?? existingView?.contributingObservationIds,
            performerRoleId: recordedByRoleId,
            performerDisplay: recordedByDisplay
        },
        submission: {
            submitted: true,
            submittedBy: input.performerRoleId,
            submittedByDisplay: performerDisplay,
            submittedAt
        },
        lockStatus: "closed",
        reopenHistory: existingView?.reopenHistory,
        id: existingSummary?.id,
        issued: existingSummary?.issued
    });
    const entries = buildCoCMBillingPacketBundle({
        packet: attributedPacket,
        summaryObservation,
        summaryConditionalUrl: `Observation?identifier=${buildCoCMBillingSummaryIdentifierToken(patientId, billingMonth)}`,
        performerRoleId: input.performerRoleId,
        performerDisplay,
        patientDisplay,
        attestedAt: submittedAt
    });
    // Reconcile line-items: a re-submit that produces FEWER codes than a prior
    // submit would otherwise leave orphan `billable` ChargeItems (PUT is keyed by
    // `{code}|{index}`, so dropped occurrences are never overwritten), and
    // `SUM`-based downstream billing would double-count them. Delete, in the same
    // atomic transaction, any existing CoCM ChargeItem for the month not in the
    // new packet's identifier set.
    const keepIdentifierValues = new Set(attributedPacket.codes.map((code, index)=>buildCoCMChargeItemIdentifierValue(patientId, billingMonth, code, index)));
    const existingChargeItems = await findCoCMChargeItemsForMonth(medplum, patientId, billingMonth);
    entries.push(...staleChargeItemDeletes(existingChargeItems, keepIdentifierValues));
    const result = await executeTransaction(medplum, entries);
    const composition = extractSingleResourceFromTransactionResult(result, "Composition");
    const observation = extractSingleResourceFromTransactionResult(result, "Observation");
    if (composition.id) {
        const event = {
            compositionId: composition.id,
            composition,
            patientId,
            billingMonth
        };
        globalEventEmitter.publish(DomainEventNames.CoCMBillingPacketSubmitted, event);
    }
    // Also announce the summary Observation change so the billing tab's
    // `useCoCMBillingSummary*` hooks (which listen for CoCMBillingSummary* events)
    // refetch and reflect the now-locked month without a manual refresh.
    if (observation.id) {
        const summaryEvent = {
            observationId: observation.id,
            observation,
            patientId,
            billingMonth
        };
        globalEventEmitter.publish(DomainEventNames.CoCMBillingSummaryUpdated, summaryEvent);
    }
    return {
        composition,
        observation
    };
};
/**
 * Reopen a locked packet Composition for an audited correction (D7). Flips the
 * Composition status to `amended`; the summary Observation reopen is handled by
 * the existing `reopenCoCMBillingSummary` action at the call site (facade).
 * @agentic @benchmark/core
 */ const reopenCoCMBillingPacket = async (medplum, input)=>{
    const { patientId, billingMonth } = input;
    const composition = await findCoCMBillingPacket(medplum, patientId, billingMonth);
    if (!composition?.id) return undefined;
    // Reopening a month must leave NO `billable` line-items behind, so a reopen
    // that is never re-submitted cannot be billed by a `SUM` over ChargeItems
    // (re-submit rebuilds them from the new packet). Delete them even when the
    // Composition is already `amended` — a legacy packet reopened before this
    // reconciliation shipped can still carry orphan line-items, and skipping
    // cleanup on the already-amended path would leave them forever.
    const alreadyReopened = composition.status === COCM_BILLING_PACKET_STATUS.reopened;
    const existingChargeItems = await findCoCMChargeItemsForMonth(medplum, patientId, billingMonth);
    const chargeItemDeletes = staleChargeItemDeletes(existingChargeItems, new Set());
    // Nothing to do: already reopened and no lingering line-items.
    if (alreadyReopened && chargeItemDeletes.length === 0) {
        return composition;
    }
    // Flip the Composition to `amended` (unless already) AND delete its
    // ChargeItems in one atomic transaction (D7).
    const reopenEntries = [
        ...alreadyReopened ? [] : [
            {
                resource: {
                    ...composition,
                    status: COCM_BILLING_PACKET_STATUS.reopened
                },
                method: "PUT",
                url: `Composition/${composition.id}`
            }
        ],
        ...chargeItemDeletes
    ];
    await executeTransaction(medplum, reopenEntries);
    // The already-reopened path only deleted line-items, so the Composition is
    // unchanged; otherwise the transaction amended it.
    const updated = alreadyReopened ? composition : {
        ...composition,
        status: COCM_BILLING_PACKET_STATUS.reopened
    };
    // Publish the reopen event only when this call actually amended the packet,
    // so an idempotent cleanup pass on an already-reopened month doesn't re-fire.
    if (!alreadyReopened && updated.id) {
        const event = {
            compositionId: updated.id,
            composition: updated,
            patientId,
            billingMonth
        };
        globalEventEmitter.publish(DomainEventNames.CoCMBillingPacketReopened, event);
    }
    return updated;
};

/**
 * Retire a Library document.
 * @throws Error if the actor/library is invalid or the document is already retired
 * @agentic @benchmark/core
 */ async function retireLibrary(medplum, input) {
    const { library, actor } = input;
    if (!actor?.name) {
        throw new Error("A signed-in user is required");
    }
    if (!library.id) {
        throw new Error("Library id is required to retire");
    }
    if (library.status === "retired") {
        throw new Error("Document is already retired");
    }
    const updated = buildLibraryResource({
        action: "retire",
        actor,
        existing: library
    });
    const result = await updateResource$1(medplum, updated);
    if (!result.id) {
        throw new Error("Missing required ID for event");
    }
    const event = {
        libraryId: result.id,
        library: result
    };
    globalEventEmitter$1.publish(DomainEventNames$1.LibraryRetired, event);
    return result;
}

/**
 * Record a review on a Library document.
 * @throws Error if the actor/library is invalid or the document is retired
 * @agentic @benchmark/core
 */ async function reviewLibrary(medplum, input) {
    const { library, actor } = input;
    if (!actor?.name) {
        throw new Error("A signed-in reviewer is required");
    }
    if (!library.id) {
        throw new Error("Library id is required to review");
    }
    if (library.status === "retired") {
        throw new Error("A retired document cannot be reviewed");
    }
    const updated = buildLibraryResource({
        action: "review",
        actor,
        existing: library
    });
    const result = await updateResource$1(medplum, updated);
    if (!result.id) {
        throw new Error("Missing required ID for event");
    }
    const event = {
        libraryId: result.id,
        library: result
    };
    globalEventEmitter$1.publish(DomainEventNames$1.LibraryReviewed, event);
    return result;
}

/**
 * Build the Wellness-intake 90791 `ChargeItem`.
 * @agentic @benchmark/core
 */ function buildIntakeChargeItem(input) {
    const { encounterId, patientId, patientDisplay, performerRoleId, performerDisplay, occurrenceDateTime, existingId } = input;
    const chargeItem = {
        resourceType: "ChargeItem",
        status: "billable",
        identifier: [
            {
                system: INTAKE_CHARGE_ITEM_IDENTIFIER_SYSTEM,
                value: encounterId
            }
        ],
        code: {
            coding: [
                {
                    system: CODE_SYSTEM$1.CPT,
                    code: INTAKE_CHARGE_CPT_90791
                }
            ],
            text: INTAKE_CHARGE_CPT_90791
        },
        subject: {
            reference: `Patient/${patientId}`,
            ...patientDisplay ? {
                display: patientDisplay
            } : {}
        },
        context: {
            reference: `Encounter/${encounterId}`
        },
        quantity: {
            value: 1
        },
        occurrenceDateTime: occurrenceDateTime ?? new Date().toISOString(),
        ...performerRoleId ? {
            performer: [
                {
                    actor: {
                        reference: `PractitionerRole/${performerRoleId}`,
                        ...performerDisplay ? {
                            display: performerDisplay
                        } : {}
                    }
                }
            ]
        } : {}
    };
    if (existingId) {
        chargeItem.id = existingId;
    }
    return chargeItem;
}

const encounterHasTag = (encounter, system, code)=>(encounter.meta?.tag ?? []).some((tag)=>tag.system === system && tag.code === code);
/** True when any ENCOUNTER_TYPE tag is an intake code (not only the first). */ const hasIntakeEncounterType = (encounter)=>(encounter.meta?.tag ?? []).some((tag)=>tag.system === VALUE_SET.ENCOUNTER_TYPE && isIntakeEncounterType(tag.code));
/**
 * Whether the saved encounter is a Wellness (90791) intake that should carry a
 * billable ChargeItem. Deliberately mirrors the authoritative billing signal in
 * `getPatientEncounterBillingSignals` **exactly** — finished + a clinical-intake
 * type tag + a `billable` tag, each matched via `encounterHasTag` (presence of a
 * matching tag, not the first tag) — so the persisted charge and the read-side
 * eligibility signal can never disagree, even on an encounter that carries more
 * than one type tag. A clinical intake the clinician marked **non-billable** on
 * the Details step — or one not yet `finished` — is not a billable charge and
 * must fall through to the reversal (delete) path.
 */ const isBillableWellnessIntake = (encounter)=>encounter.status === "finished" && encounterHasTag(encounter, VALUE_SET.ENCOUNTER_TYPE, CLINICAL_INTAKE_ENCOUNTER_TYPE_CODE$1) && encounterHasTag(encounter, VALUE_SET.ENCOUNTER_BILLABLE, "billable");
/**
 * Upsert or remove the intake ChargeItem to match the saved encounter's billing
 * reality. No-op for non-intake encounters.
 * @agentic @benchmark/core
 */ async function reconcileIntakeChargeItem(medplum, { encounter, patient, practitionerRole }) {
    const encounterId = encounter.id;
    if (!encounterId || !patient.id) return;
    const identifier = `${INTAKE_CHARGE_ITEM_IDENTIFIER_SYSTEM}|${encounterId}`;
    // A finished, billable Wellness (90791) intake must carry a ChargeItem. This is
    // evaluated FIRST and by tag presence (not the first type tag), so it mirrors
    // the read-side billing signal (getPatientEncounterBillingSignals) exactly,
    // independent of tag ordering. A clinical intake marked non-billable (or not
    // yet finished) is not billable and falls through to the reversal path.
    if (isBillableWellnessIntake(encounter)) {
        const chargeItem = buildIntakeChargeItem({
            encounterId,
            patientId: patient.id,
            patientDisplay: getPatientDisplayName(patient),
            performerRoleId: practitionerRole?.id,
            occurrenceDateTime: encounter.period?.start
        });
        // Server-side conditional upsert by the encounter-scoped identifier: creates
        // once, updates in place on re-save — no client race window, no duplicates.
        await medplum.upsertResource(chargeItem, {
            identifier
        });
        return;
    }
    // Not a billable Wellness intake. Only intake encounters (CoCM / Unbilled /
    // non-billable or unfinished Wellness) need reversal; a non-intake encounter
    // has nothing to reconcile. Match by tag presence (any type tag), same as the
    // create path — first-tag-only would miss multi-tag shapes and leave stale
    // ChargeItems behind after a create that keyed off a later intake tag.
    if (!hasIntakeEncounterType(encounter)) return;
    // Remove any prior Wellness ChargeItem(s) for this encounter (the billing
    // reality changed off a billable Wellness on edit). Delete every match
    // defensively.
    const stale = await medplum.searchResources("ChargeItem", {
        identifier,
        _count: 100
    });
    for (const charge of stale){
        if (charge.id) {
            await medplum.deleteResource("ChargeItem", charge.id);
        }
    }
}

/**
 * Extracts the linked care plan ID from Task.basedOn references.
 * @agentic @benchmark/core
 */ const getLinkedCarePlanId = (task)=>task.basedOn?.map((reference)=>reference.reference).find((reference)=>Boolean(reference?.startsWith("CarePlan/")))?.replace("CarePlan/", "");
/**
 * Returns Task reference variants used by care plan activity links.
 * @agentic @benchmark/core
 */ const getTaskReferenceVariants = (taskId)=>[
        `Task/${taskId}`,
        taskId
    ];
/**
 * Syncs legacy care plan activity scheduling when detail blocks are present.
 * Task-backed activities (reference-only) intentionally remain unchanged.
 * @agentic @benchmark/core
 */ const syncLegacyActivitySchedule = (carePlan, taskId, dueDateIso)=>{
    if (!carePlan.activity?.length) {
        return undefined;
    }
    const referenceVariants = getTaskReferenceVariants(taskId);
    let didChange = false;
    const updatedActivities = carePlan.activity.map((activity)=>{
        const activityReference = activity.reference?.reference;
        const isLinkedActivity = activityReference ? referenceVariants.includes(activityReference) : false;
        if (!isLinkedActivity || !activity.detail) {
            return activity;
        }
        if (activity.detail.scheduledString === dueDateIso) {
            return activity;
        }
        didChange = true;
        return {
            ...activity,
            detail: {
                ...activity.detail,
                scheduledString: dueDateIso
            }
        };
    });
    if (!didChange) {
        return undefined;
    }
    return {
        ...carePlan,
        activity: updatedActivities
    };
};
/**
 * Snoozes a task by updating FHIR Task due date and syncing linked care plan
 * legacy activity scheduling when present.
 * @agentic @benchmark/core
 */ async function snoozeTask(medplum, input) {
    const { task, dueDateIso } = input;
    if (!task.id) {
        throw new Error("Task ID is required to snooze task");
    }
    const updatedTask = await updateResource$1(medplum, {
        ...task,
        id: task.id,
        restriction: {
            ...task.restriction,
            period: {
                ...task.restriction?.period,
                end: dueDateIso
            }
        },
        lastModified: new Date().toISOString()
    });
    const carePlanId = getLinkedCarePlanId(task);
    if (!carePlanId) {
        return {
            task: updatedTask
        };
    }
    const carePlan = await readResource$1(medplum, "CarePlan", carePlanId);
    const syncedCarePlan = syncLegacyActivitySchedule(carePlan, task.id, dueDateIso);
    if (!syncedCarePlan || !syncedCarePlan.id) {
        return {
            task: updatedTask
        };
    }
    const updatedCarePlan = await updateResource$1(medplum, {
        ...syncedCarePlan,
        id: syncedCarePlan.id
    });
    return {
        task: updatedTask,
        carePlan: updatedCarePlan
    };
}

const extractPatientIdFromSubject = (observation)=>{
    const ref = observation.subject?.reference;
    if (!ref || !ref.startsWith("Patient/")) return undefined;
    return ref.slice("Patient/".length) || undefined;
};
/**
 * Soft-delete a care-time Observation (status → entered-in-error).
 * @agentic @benchmark/core
 */ const softDeleteCareTime = async (medplum, input)=>{
    if (!input.observationId) {
        throw new Error("observationId is required");
    }
    const existing = input.observation ?? await readResource(medplum, "Observation", input.observationId);
    if (!existing.id) {
        throw new Error(`Care-time Observation ${input.observationId} could not be loaded`);
    }
    const updated = {
        ...existing,
        id: existing.id,
        status: "entered-in-error"
    };
    const result = await updateResource(medplum, updated);
    const patientId = input.patientId ?? extractPatientIdFromSubject(result) ?? "";
    const event = {
        observationId: result.id ?? input.observationId,
        observation: result,
        patientId
    };
    globalEventEmitter.publish(DomainEventNames.CareTimeDeleted, event);
    return {
        observation: result
    };
};

/**
 * Submit (create or update) a Program Selection Observation.
 *
 * Idempotent: if an observation already exists for this patient + intake care plan,
 * it is updated in place rather than creating a duplicate.
 *
 * @param medplum - MedplumClient instance
 * @param input - Program selection data
 * @returns The created or updated Observation and whether it was new
 * @agentic @benchmark/core
 */ async function submitProgramSelection(medplum, input) {
    const { patient, practitionerRole, intakeCarePlan, selectedProgram, reasonCodes, reasonText, selectedNeeds } = input;
    if (!patient.id) {
        throw new Error("Patient must have an id for program selection");
    }
    if (!practitionerRole.id) {
        throw new Error("PractitionerRole must have an id for program selection");
    }
    if (!intakeCarePlan.id) {
        throw new Error("Intake CarePlan must have an id for program selection");
    }
    const performerDisplay = practitionerRole.practitioner?.display || "Unknown Practitioner";
    const existing = await getProgramSelectionObservation(medplum, patient.id, intakeCarePlan.id);
    const builderInput = {
        patientRef: patient.id,
        patientDisplay: getPatientDisplayName$1(patient),
        performerRef: practitionerRole.id,
        performerDisplay,
        selectedProgram,
        reasonCodes,
        reasonText,
        selectedNeeds,
        intakeCarePlanId: intakeCarePlan.id,
        existingId: existing?.id
    };
    const observationResource = buildProgramSelectionObservation(builderInput);
    let result;
    let isNew;
    if (existing?.id) {
        result = await updateResource$1(medplum, observationResource);
        isNew = false;
        if (!result.id) throw new Error("Missing observation ID after update");
        const event = {
            observationId: result.id,
            observation: result,
            patientId: patient.id
        };
        globalEventEmitter$1.publish(DomainEventNames$1.ObservationUpdated, event);
    } else {
        result = await createResource$1(medplum, observationResource);
        isNew = true;
        if (!result.id) throw new Error("Missing observation ID after create");
        const event = {
            observationId: result.id,
            observation: result,
            patientId: patient.id
        };
        globalEventEmitter$1.publish(DomainEventNames$1.ObservationCreated, event);
    }
    return {
        observation: result,
        isNew
    };
}

/**
 * Sync minor flag for patient based on their current age
 *
 * Automatically manages the "patient-under-18" flag:
 * - Creates/activates the flag if patient is under 18
 * - Deactivates the flag if patient is 18 or older
 *
 * This is designed to be called during encounters to catch:
 * - New patients who need the flag
 * - Patients who have turned 18 since their last encounter
 *
 * @param medplum - MedplumClient instance
 * @param input - Patient data
 * @returns Result indicating what action was taken
 * @agentic @benchmark/core
 */ async function syncMinorFlag(medplum, input) {
    const { patient } = input;
    // Validate input
    if (!patient) {
        throw new Error("Patient is required");
    }
    if (!patient.id) {
        throw new Error("Patient must have an id");
    }
    const patientId = toPatientId(patient.id);
    // Check if patient has a birth date
    if (!patient.birthDate) {
        // No birth date - cannot determine age, skip flag management
        return {
            changed: false,
            flag: null,
            action: "none"
        };
    }
    // Calculate current age
    const age = calculateAge(patient.birthDate);
    if (age === null) {
        // Invalid birth date - cannot determine age, skip flag management
        return {
            changed: false,
            flag: null,
            action: "none"
        };
    }
    const isMinor = age < 18;
    // Check for existing minor flag
    const existingFlag = await findExistingMinorFlag(medplum, patientId);
    // Case 1: Patient is a minor and needs the flag
    if (isMinor) {
        // If flag exists and is already active, no change needed
        if (existingFlag && existingFlag.status === "active") {
            return {
                changed: false,
                flag: existingFlag,
                action: "none"
            };
        }
        // Build flag resource
        const flag = existingFlag ? {
            ...existingFlag,
            status: "active",
            subject: buildPatientReference(patientId, getPatientDisplayName$1(patient))
        } : {
            resourceType: "Flag",
            status: "active",
            code: {
                coding: [
                    {
                        system: FLAG_LOCAL_CODE_SYSTEM$1,
                        code: "patient-under-18",
                        display: "Minor (Under 18)"
                    }
                ],
                text: "Minor (Under 18)"
            },
            category: [
                {
                    coding: [
                        {
                            system: "http://terminology.hl7.org/CodeSystem/flag-category",
                            code: "admin",
                            display: "Administrative"
                        }
                    ],
                    text: "Administrative"
                }
            ],
            subject: buildPatientReference(patientId, getPatientDisplayName$1(patient))
        };
        // Create or update flag
        let result;
        let action;
        if (flag.id) {
            result = await updateResource$1(medplum, flag);
            action = "activated";
            // Emit update event
            const event = {
                flagId: result.id,
                flag: result,
                patientId: patient.id
            };
            globalEventEmitter$1.publish(DomainEventNames$1.FlagUpdated, event);
        } else {
            result = await createResource$1(medplum, flag);
            action = "created";
            // Emit create event
            const event = {
                flagId: result.id,
                flag: result,
                patientId: patient.id
            };
            globalEventEmitter$1.publish(DomainEventNames$1.FlagCreated, event);
        }
        return {
            changed: true,
            flag: result,
            action
        };
    }
    // Case 2: Patient is 18 or older - deactivate flag if it exists
    if (existingFlag && existingFlag.status === "active") {
        const deactivatedFlag = {
            ...existingFlag,
            id: existingFlag.id,
            status: "inactive"
        };
        const result = await updateResource$1(medplum, deactivatedFlag);
        // Emit update event
        const event = {
            flagId: result.id,
            flag: result,
            patientId: patient.id
        };
        globalEventEmitter$1.publish(DomainEventNames$1.FlagUpdated, event);
        return {
            changed: true,
            flag: result,
            action: "deactivated"
        };
    }
    // No change needed - patient is adult and no active flag exists
    return {
        changed: false,
        flag: existingFlag,
        action: "none"
    };
}

/**
 * Update a care plan for a patient
 *
 * Finds the active care plan for the patient and updates its status.
 * If no active care plan exists, throws an error.
 *
 * @param medplum - MedplumClient instance
 * @param input - Care plan update data
 * @returns Updated care plan
 * @throws Error if no active care plan found or update fails
 * @agentic @benchmark/core
 */ async function updateCarePlan(medplum, input) {
    // Fetch active care plans for the patient
    const activeCarePlans = await getActiveCarePlans(medplum, input.patientId);
    if (activeCarePlans.length === 0) {
        throw new Error(`No active care plan found for patient ${input.patientId}. Cannot update care plan status.`);
    }
    // Use the most recent active care plan
    const carePlan = activeCarePlans[0];
    if (!carePlan) {
        // This should never happen if length > 0, but TypeScript needs the check
        throw new Error(`No active care plan found for patient ${input.patientId}. Cannot update care plan status.`);
    }
    if (!carePlan.id) {
        throw new Error(`Care plan for patient ${input.patientId} is missing an ID. Cannot update.`);
    }
    // Update the care plan with the new status
    const updatedCarePlan = {
        ...carePlan,
        id: carePlan.id,
        status: input.status
    };
    return await updateResource$1(medplum, updatedCarePlan);
}

/**
 * Updates metadata (due date, owner) on a draft Task without changing status.
 * @agentic @benchmark/core
 */ async function updateDraftTaskMetadata(medplum, input) {
    const { taskId, targetDate, ownerRef, ownerDisplay } = input;
    const task = await readResource$1(medplum, "Task", taskId);
    if (task.status !== "draft") {
        throw new Error(`Cannot update metadata on a non-draft task (status: ${task.status})`);
    }
    const updates = {};
    if (targetDate !== undefined) {
        if (targetDate === "") {
            updates.restriction = {
                ...task.restriction,
                period: undefined
            };
        } else {
            updates.restriction = {
                ...task.restriction,
                period: {
                    end: targetDate
                }
            };
        }
    }
    if (ownerRef !== undefined) {
        if (ownerRef === "") {
            updates.owner = undefined;
        } else {
            updates.owner = {
                reference: ownerRef.startsWith("PractitionerRole/") ? ownerRef : `PractitionerRole/${ownerRef}`,
                display: ownerDisplay || "Unknown"
            };
        }
    }
    if (!task.id) {
        throw new Error("Task ID is required to update metadata");
    }
    return updateResource$1(medplum, {
        ...task,
        ...updates,
        id: task.id
    });
}

/**
 * Update an existing Goal resource
 *
 * Updates a Goal resource with new form data. Preserves the goal ID and
 * updates all provided fields.
 *
 * @param medplum - MedplumClient instance
 * @param input - Goal update data
 * @returns Updated Goal resource
 * @throws Error if validation or update fails
 * @agentic @benchmark/core
 */ async function updateGoal(medplum, input) {
    const { formData, existingGoal, patient, practitionerRole } = input;
    // Validate existing goal has ID
    if (!existingGoal.id) {
        throw new Error("Goal must have an id for update");
    }
    // Validate patient has active CoCM care plan
    // Goals can only be updated for patients enrolled in CoCM
    const cocmCarePlan = await getCarePlanByType(medplum, patient.id, "cocm");
    if (!cocmCarePlan) {
        throw new Error("Patient must be enrolled in Collaborative Care Management (CoCM) to update goals. Please enroll the patient in CoCM first.");
    }
    // Only allow goal updates if CoCM care plan is active
    if (cocmCarePlan.status !== "active") {
        throw new Error(`Patient's CoCM care plan is ${cocmCarePlan.status}. Goals can only be updated for patients with an active CoCM care plan.`);
    }
    const carePlan = cocmCarePlan;
    // Build updated Goal resource from form data
    const updatedGoal = goalFromForm(formData, patient, practitionerRole, carePlan, existingGoal);
    // Ensure ID is preserved
    updatedGoal.id = existingGoal.id;
    // Save to server
    const savedGoal = await updateResource$1(medplum, updatedGoal);
    // Emit event
    if (!savedGoal.id || !patient.id) {
        throw new Error("Missing required IDs for event");
    }
    // Calculate changes for event payload
    const changes = {};
    if (formData.category && existingGoal.category?.[0]?.coding?.[0]?.code !== formData.category) {
        changes.category = updatedGoal.category;
    }
    if (formData.description && existingGoal.description?.text !== formData.description) {
        changes.description = updatedGoal.description;
    }
    if (formData.lifecycleStatus && existingGoal.lifecycleStatus !== formData.lifecycleStatus) {
        changes.lifecycleStatus = updatedGoal.lifecycleStatus;
    }
    if (formData.achievementStatus) {
        changes.achievementStatus = updatedGoal.achievementStatus;
    }
    if (formData.startDate && existingGoal.startDate !== formData.startDate) {
        changes.startDate = updatedGoal.startDate;
    }
    if (formData.statusDate && existingGoal.statusDate !== formData.statusDate) {
        changes.statusDate = updatedGoal.statusDate;
    }
    const event = {
        goalId: savedGoal.id,
        goal: savedGoal,
        patientId: patient.id,
        changes
    };
    globalEventEmitter$1.publish(DomainEventNames$1.GoalUpdated, event);
    return savedGoal;
}

/**
 * Validate patient update input.
 * @agentic @benchmark/core
 */ function validateUpdateInput(input) {
    if (!input.existingPatient) {
        throw new Error("Existing patient is required for update");
    }
    if (!input.existingPatient.id) {
        throw new Error("Existing patient must have an id");
    }
}
/**
 * Update a patient
 *
 * @param medplum - MedplumClient instance
 * @param input - Patient update data
 * @returns Updated patient resource
 * @throws Error if validation or update fails
 * @agentic @benchmark/core
 */ async function updatePatient(medplum, input) {
    // Validate input
    validateUpdateInput(input);
    // Build updated patient resource
    const updatedPatient = patientUpdateFromForm(input.formData, input.existingPatient, input.authorReference);
    // Ensure id is present for update
    const patientWithId = updatedPatient;
    patientWithId.id = input.existingPatient.id;
    // Update resource
    const result = await updateResource$1(medplum, patientWithId);
    // Emit event
    if (!result.id) {
        throw new Error("Patient ID is required after update");
    }
    const event = {
        patientId: result.id,
        patient: result
    };
    globalEventEmitter$1.publish(DomainEventNames$1.PatientUpdated, event);
    return result;
}

/**
 * Update patient care team information
 *
 * Creates or updates the Benchmark Care Team with patient, LCSW, and/or Advocate as participants.
 *
 * @param medplum - MedplumClient instance
 * @param patient - Patient resource
 * @param formData - Patient form data containing care team information
 * @throws Error if operation fails
 * @agentic @benchmark/core
 */ async function updatePatientCareTeam(medplum, patient, formData) {
    if (!patient.id) {
        throw new Error("Patient must have an id");
    }
    // Get existing care team for this patient
    const existingCareTeams = await medplum.searchResources("CareTeam", {
        subject: `Patient/${patient.id}`
    });
    // Find the benchmark care team or create a new one
    const careTeam = existingCareTeams.find((ct)=>ct.category?.[0]?.coding?.[0]?.code === "LA28865-6" || ct.name === "Benchmark Care Team");
    // If no LCSW or Advocate is specified, and no existing care team, skip creation
    if (!formData["benchmark-lcsw"] && !formData["benchmark-advocate"] && !careTeam) {
        return;
    }
    // Build participant list
    const participants = [];
    // Always add the patient as a participant with their name as display
    const patientName = patient.name?.[0];
    const patientDisplayName = patientName ? `${patientName.given?.[0] || ""} ${patientName.family || ""}`.trim() : undefined;
    participants.push({
        role: [
            {
                coding: [
                    {
                        system: "http://snomed.info/sct",
                        code: "116154003",
                        display: "Patient"
                    }
                ]
            }
        ],
        member: {
            reference: `Patient/${patient.id}`,
            display: patientDisplayName
        }
    });
    // Add LCSW if specified
    if (formData["benchmark-lcsw"]) {
        participants.push({
            role: [
                {
                    coding: [
                        {
                            system: "http://snomed.info/sct",
                            code: "224598009",
                            display: "Licensed Clinical Social Worker"
                        }
                    ]
                }
            ],
            member: {
                reference: `PractitionerRole/${formData["benchmark-lcsw"]}`,
                display: formData["benchmark-lcsw-display"] || "LCSW"
            }
        });
    }
    // Add Advocate if specified
    if (formData["benchmark-advocate"]) {
        participants.push({
            role: [
                {
                    coding: [
                        {
                            system: "http://snomed.info/sct",
                            code: "429577009",
                            display: "Patient advocate"
                        }
                    ]
                }
            ],
            member: {
                reference: `PractitionerRole/${formData["benchmark-advocate"]}`,
                display: formData["benchmark-advocate-display"] || "Advocate"
            }
        });
    }
    const careTeamData = {
        resourceType: "CareTeam",
        status: "active",
        name: "Benchmark Care Team",
        subject: {
            reference: `Patient/${patient.id}`,
            display: patientDisplayName
        },
        category: [
            {
                coding: [
                    {
                        system: "http://loinc.org",
                        code: "LA28865-6",
                        display: "Longitudinal Care-Coordination"
                    }
                ]
            }
        ],
        participant: participants
    };
    if (careTeam?.id) {
        // Update existing care team
        await updateResource$1(medplum, {
            ...careTeamData,
            id: careTeam.id
        });
    } else {
        // Create new care team
        await createResource$1(medplum, careTeamData);
    }
}

/**
 * Update patient emergency contact information
 *
 * Creates, updates, or deletes the emergency contact RelatedPerson resource based on form data.
 *
 * @param medplum - MedplumClient instance
 * @param patient - Patient resource
 * @param formData - Patient form data containing emergency contact information
 * @throws Error if operation fails
 * @agentic @benchmark/core
 */ async function updatePatientEmergencyContact(medplum, patient, formData) {
    if (!patient.id) {
        throw new Error("Patient must have an id");
    }
    // Get existing emergency contact
    const existingRelatedPersons = await medplum.searchResources("RelatedPerson", {
        patient: `Patient/${patient.id}`
    });
    const emergencyContact = existingRelatedPersons.find((rp)=>rp.relationship?.[0]?.coding?.[0]?.code === "emergency");
    if (formData["emergency-contact-first-name"] || formData["emergency-contact-last-name"] || formData["emergency-contact-phone"]) {
        const contactData = {
            resourceType: "RelatedPerson",
            patient: {
                reference: `Patient/${patient.id}`,
                display: getPatientDisplayName$1(patient)
            },
            relationship: [
                {
                    coding: [
                        {
                            system: "http://terminology.hl7.org/CodeSystem/v2-0131",
                            code: "emergency",
                            display: "Emergency contact person"
                        }
                    ]
                }
            ],
            name: formData["emergency-contact-first-name"] || formData["emergency-contact-last-name"] ? [
                {
                    given: formData["emergency-contact-first-name"] ? [
                        formData["emergency-contact-first-name"]
                    ] : undefined,
                    family: formData["emergency-contact-last-name"]
                }
            ] : undefined,
            telecom: formData["emergency-contact-phone"] ? [
                {
                    system: "phone",
                    value: formData["emergency-contact-phone"]
                }
            ] : undefined
        };
        if (emergencyContact?.id) {
            // Merge onto the existing resource instead of overwriting it. A full PUT
            // of the form-only fields silently drops everything the form does not
            // manage (identifier, address, period, meta, email telecom, ...).
            const phone = formData["emergency-contact-phone"];
            const mergedTelecom = phone ? [
                {
                    system: "phone",
                    value: phone
                },
                ...emergencyContact.telecom?.filter((t)=>t.system !== "phone") ?? []
            ] : emergencyContact.telecom;
            await updateResource$1(medplum, {
                ...emergencyContact,
                id: emergencyContact.id,
                patient: contactData.patient,
                relationship: emergencyContact.relationship ?? contactData.relationship,
                // Preserve the existing name when the form did not edit it.
                name: contactData.name ?? emergencyContact.name,
                telecom: mergedTelecom
            });
        } else {
            // Create new emergency contact
            await createResource$1(medplum, contactData);
        }
    } else if (emergencyContact?.id) {
        // Remove emergency contact if no data provided
        await deleteResource(medplum, "RelatedPerson", emergencyContact.id);
    }
}

/**
 * Update patient engagement status tags separately from transaction
 *
 * Patient tags require a read-modify-write pattern which must be done
 * separately to avoid race conditions. This uses patch operations with
 * version checking for optimistic locking.
 *
 * @param medplum - Medplum client
 * @param patient - Patient resource
 * @param formData - Form data with patient tag updates (EncounterFormData, CommunicationFormData, etc.)
 * @throws Error if patch operation fails
 * @agentic @benchmark/core
 */ async function updatePatientEngagementStatusTags(medplum, patient, formData) {
    // Get the engagement status code (handle undefined, null, and empty string)
    const code = formData["patient-engagement-status"];
    // Only update if engagement status is provided and not empty
    if (code === undefined || code === null || code === "") {
        return;
    }
    // Get the current version of the patient to ensure we're working with the latest
    if (!patient.id) {
        throw new Error("Patient ID is required for tag updates");
    }
    const currentPatient = await medplum.readResource("Patient", patient.id);
    const currentTags = currentPatient.meta?.tag || [];
    // Filter out all engagement status tags (both V1 and V2 systems) before adding new one
    const filteredTags = currentTags.filter((tag)=>tag.system !== LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM$1 && tag.system !== PATIENT_ENGAGEMENT_STATUS_SYSTEM$1);
    // Find the matching option from V2 first (current codes), then V1 (legacy codes)
    // V2 codes can be stored under V1 system in production
    const v2Option = PATIENT_ENGAGEMENT_STATUS_OPTIONS$1.find((s)=>s.code === code);
    const v1Option = LEGACY_PATIENT_ENGAGEMENT_STATUS_OPTIONS$1.find((s)=>s.code === code);
    // Use V2 option if found, otherwise V1 option
    const option = v2Option || v1Option;
    if (!option) {
        // If no matching option found, don't update (shouldn't happen with valid form data)
        console.warn(`Invalid engagement status code: ${code}. Skipping tag update.`);
        return;
    }
    // Build new tag array with the engagement status tag
    // Ensure we always have at least one tag (the engagement status tag)
    // Use V1 system URL (production standard) even for V2 codes
    const engagementStatusTag = {
        system: LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM$1,
        code: option.code,
        display: option.display
    };
    const newTags = [
        ...filteredTags,
        engagementStatusTag
    ];
    // Ensure newTags is never empty (should always have at least the engagement status tag)
    if (newTags.length === 0) {
        console.error("Cannot update patient tags: newTags array is empty");
        return;
    }
    // Prepare patch operations with optimistic locking
    const ops = [];
    // Handle case where meta doesn't exist yet
    if (!currentPatient.meta) {
        // Create meta object with tags (no versionId test needed for new meta)
        ops.push({
            op: "add",
            path: "/meta",
            value: {
                tag: newTags
            }
        });
    } else {
        // Test versionId for optimistic locking (if it exists)
        if (currentPatient.meta.versionId) {
            ops.push({
                op: "test",
                path: "/meta/versionId",
                value: currentPatient.meta.versionId
            });
        }
        // Always use replace for /meta/tag - it works whether tag exists or not
        // If tag doesn't exist, replace will create it
        ops.push({
            op: "replace",
            path: "/meta/tag",
            value: newTags
        });
    }
    // Apply the patch
    await medplum.patchResource("Patient", patient.id, ops);
    // Cancel all open tasks if transitioning to terminating engagement status
    if (isTerminatingEngagementStatus(code)) {
        try {
            await cancelTasks(medplum, {
                patientId: patient.id,
                reason: `Patient engagement status changed to ${code}`
            });
        } catch (error) {
            // Log but don't fail - patient update succeeded
            console.error("Failed to cancel tasks after engagement status change:", error);
        }
    }
}
/**
 * Set the patient-level intake billing ELIGIBILITY tag.
 *
 * Read-modify-write with optimistic locking, mirroring the engagement-status
 * pattern. Replaces any existing eligibility tag. Chosen at schedule-intake and
 * read at complete-intake to prefill the clinician's billing-reality selection.
 *
 * @param medplum - Medplum client
 * @param patient - Patient resource
 * @param code - The eligibility code (`cocm` / `wellness` / `no-bill`)
 * @throws Error if the code is invalid or the patch operation fails
 * @agentic @benchmark/core
 */ async function updatePatientIntakeEligibilityTag(medplum, patient, code) {
    if (!patient.id) {
        throw new Error("Patient ID is required for tag updates");
    }
    const option = INTAKE_BILLING_FLAG_OPTIONS$1.find((o)=>o.code === code);
    if (!option) {
        throw new Error(`Invalid intake billing eligibility code: ${code}`);
    }
    const currentPatient = await medplum.readResource("Patient", patient.id);
    const currentTags = currentPatient.meta?.tag ?? [];
    const filteredTags = currentTags.filter((tag)=>tag.system !== PATIENT_INTAKE_ELIGIBILITY_SYSTEM);
    const newTags = [
        ...filteredTags,
        {
            system: PATIENT_INTAKE_ELIGIBILITY_SYSTEM,
            code: option.code,
            display: option.display
        }
    ];
    const ops = [];
    if (!currentPatient.meta) {
        ops.push({
            op: "add",
            path: "/meta",
            value: {
                tag: newTags
            }
        });
    } else {
        if (currentPatient.meta.versionId) {
            ops.push({
                op: "test",
                path: "/meta/versionId",
                value: currentPatient.meta.versionId
            });
        }
        ops.push({
            op: "replace",
            path: "/meta/tag",
            value: newTags
        });
    }
    await medplum.patchResource("Patient", patient.id, ops);
}
/**
 * Update patient engagement status to V2 code
 *
 * Handles both creating new V2 status tags and migrating from V1 to V2.
 * Automatically migrates V1 codes to V2 if a V1 code is detected.
 *
 * @param medplum - Medplum client
 * @param patient - Patient to update
 * @param newStatus - New V2 engagement status code
 * @param reason - Optional reason for status change
 * @returns Updated patient with V2 status
 * @agentic @benchmark/core
 */ async function updatePatientEngagementStatus(medplum, patient, newStatus, _reason) {
    const currentTags = patient.meta?.tag || [];
    // Filter out both V1 and V2 engagement status tags
    const filteredTags = currentTags.filter((tag)=>tag.system !== LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM$1 && tag.system !== PATIENT_ENGAGEMENT_STATUS_SYSTEM$1);
    // Find the new status option
    const statusOption = PATIENT_ENGAGEMENT_STATUS_OPTIONS$1.find((s)=>s.code === newStatus);
    if (!statusOption) {
        throw new Error(`Invalid engagement status code: ${newStatus}`);
    }
    // Add new V2 status tag
    const newTags = [
        ...filteredTags,
        {
            system: PATIENT_ENGAGEMENT_STATUS_SYSTEM$1,
            code: statusOption.code,
            display: statusOption.display
        }
    ];
    // Update patient with new tags
    const updatedPatient = await medplum.updateResource({
        ...patient,
        meta: {
            ...patient.meta || {},
            tag: newTags
        }
    });
    // Publish so engagement-status badges and patient detail views refresh
    // without a hand-wired refetch callback.
    if (updatedPatient.id) {
        globalEventEmitter$1.publish(DomainEventNames$1.PatientUpdated, {
            patientId: updatedPatient.id,
            patient: updatedPatient
        });
    }
    // Cancel all active CarePlans and open tasks if transitioning to terminating status
    if (patient.id && isTerminatingEngagementStatus(newStatus)) {
        const reason = `Patient engagement status changed to ${newStatus}`;
        // Cancel active CarePlans first (this also cascades to their activity Tasks)
        try {
            const activeCarePlans = await medplum.searchResources("CarePlan", {
                subject: `Patient/${patient.id}`,
                status: "active"
            });
            for (const cp of activeCarePlans){
                if (!cp.id) continue;
                try {
                    await cancelCarePlan(medplum, {
                        carePlan: cp,
                        reason
                    });
                    // Cancel linked activity Tasks for Task-backed CarePlans
                    await cancelActivityTasks(medplum, cp.id, reason);
                } catch (cpError) {
                    console.error(`Failed to cancel CarePlan ${cp.id}:`, cpError);
                }
            }
        } catch (error) {
            console.error("Failed to cancel CarePlans after engagement status change:", error);
        }
        // Cancel remaining standalone tasks (not linked to CarePlans)
        try {
            await cancelTasks(medplum, {
                patientId: patient.id,
                reason
            });
        } catch (error) {
            console.error("Failed to cancel tasks after engagement status change:", error);
        }
    }
    return updatedPatient;
}
/**
 * Migrate patient engagement status from V1 to V2
 *
 * Automatically detects V1 status codes and migrates them to V2.
 * If patient already has V2 status, returns patient unchanged.
 *
 * @param medplum - Medplum client
 * @param patient - Patient to migrate
 * @returns Patient with V2 status
 * @agentic @benchmark/core
 */ async function migratePatientEngagementStatus(medplum, patient) {
    const currentTags = patient.meta?.tag || [];
    // Check if patient already has V2 status
    const hasV2Status = currentTags.some((tag)=>tag.system === PATIENT_ENGAGEMENT_STATUS_SYSTEM$1);
    if (hasV2Status) {
        // Already migrated
        return patient;
    }
    // Find V1 status tag
    const v1StatusTag = currentTags.find((tag)=>tag.system === LEGACY_PATIENT_ENGAGEMENT_STATUS_SYSTEM$1);
    if (!v1StatusTag?.code) {
        // No V1 status found, default to "referred"
        return updatePatientEngagementStatus(medplum, patient, "referred");
    }
    // Migrate V1 code to V2 code
    const v2Code = migratePatientEngagementStatusCode(v1StatusTag.code);
    return updatePatientEngagementStatus(medplum, patient, v2Code);
}

/**
 * PHI-safe error shaping for log statements.
 *
 * Never log a raw caught error object: Medplum/FHIR errors can carry request
 * context and resource fragments (patient-linked notes, identifiers,
 * demographics) that must not reach centralized logs. Pass errors through
 * {@link toLogSafeError} to keep only non-sensitive metadata.
 *
 * @agentic @benchmark/core
 */ /** Redacted error metadata safe to emit in logs. @agentic @benchmark/core */ /**
 * Reduce an unknown caught value to loggable, non-sensitive metadata.
 * @agentic @benchmark/core
 */ const toLogSafeError = (error)=>error instanceof Error ? {
        name: error.name,
        message: error.message
    } : {
        name: "Unknown",
        message: String(error)
    };

const participantDisplay = (appointment, typeCode)=>appointment.participant?.find((p)=>p.type?.some((t)=>t.coding?.some((c)=>c.code === typeCode)))?.actor?.display;
const qualifies = (appointment, now)=>{
    if (appointment.status !== "booked" || !appointment.start) {
        return false;
    }
    const start = new Date(appointment.start).getTime();
    return !Number.isNaN(start) && start > now.getTime();
};
const buildDescription = (appointment, timeZone)=>{
    const when = new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZoneName: "short",
        timeZone
    }).format(new Date(appointment.start));
    const service = participantDisplay(appointment, "SPRF");
    const provider = participantDisplay(appointment, "PPRF");
    return [
        `Appointment scheduled for ${when}`,
        service ? ` · ${service}` : "",
        provider ? ` with ${provider}` : ""
    ].join("");
};
/**
 * Notify the patient's Primary Advocate of a newly booked appointment.
 * Best-effort: never throws, so the appointment save is unaffected. Returns
 * the notice, or null when the appointment does not qualify, there is no
 * active tagged advocate, or delivery failed (logged without PHI).
 * @agentic @benchmark/core
 */ async function dispatchAppointmentScheduledNotification(medplum, input) {
    const { appointment, patientId } = input;
    if (!appointment.id || !qualifies(appointment, input.now ?? new Date())) {
        return null;
    }
    try {
        const patient = await readResource(medplum, "Patient", patientId);
        const owner = getPatientBenchmarkLCSW(patient);
        const roleId = owner?.reference?.startsWith("PractitionerRole/") ? owner.reference.slice("PractitionerRole/".length) : undefined;
        if (!owner || !roleId) {
            return null;
        }
        const role = await readResource(medplum, "PractitionerRole", roleId);
        if (role.active === false) {
            return null;
        }
        return await createNotificationTask(medplum, {
            code: NOTIFICATION_TYPE_DEFINITIONS.APPOINTMENT_SCHEDULED.code,
            patient,
            description: buildDescription(appointment, input.timeZone),
            focus: {
                reference: `Appointment/${appointment.id}`,
                display: "Appointment"
            },
            owner: {
                reference: owner.reference,
                display: owner.display
            }
        });
    } catch (error) {
        console.error("appointment-scheduled notification dispatch failed:", {
            appointmentId: appointment.id,
            error: toLogSafeError(error)
        });
        return null;
    }
}

/**
 * Create or update an appointment
 *
 * @param medplum - MedplumClient instance
 * @param input - Appointment creation/update data
 * @returns Created or updated appointment resource
 * @throws Error if validation or operation fails
 * @agentic @benchmark/core
 */ async function upsertAppointment(medplum, input) {
    const { formData, patient, referrer, existingAppointment } = input;
    // Validate patient has ID
    if (!patient.id) {
        throw new Error("Patient must have an id for appointment creation");
    }
    // Build appointment resource
    const appointment = appointmentFromForm(formData, patient, referrer, existingAppointment);
    // Create or update
    let result;
    if (existingAppointment?.id) {
        result = await updateResource$1(medplum, appointment);
        // Emit event
        if (!result.id || !patient.id) {
            throw new Error("Missing required IDs for event");
        }
        const event = {
            appointmentId: result.id,
            appointment: result,
            patientId: patient.id
        };
        globalEventEmitter$1.publish(DomainEventNames$1.AppointmentUpdated, event);
    } else {
        result = await createResource$1(medplum, appointment);
        // Emit event
        if (!result.id || !patient.id) {
            throw new Error("Missing required IDs for event");
        }
        const event = {
            appointmentId: result.id,
            appointment: result,
            patientId: patient.id
        };
        globalEventEmitter$1.publish(DomainEventNames$1.AppointmentCreated, event);
        await dispatchAppointmentScheduledNotification(medplum, {
            appointment: result,
            patientId: patient.id
        });
    }
    return result;
}

/**
 * Create or merge a CoCM monthly billing summary Observation.
 * @agentic @benchmark/core
 */ const upsertCoCMBillingSummary = async (medplum, input)=>{
    const { snapshot } = input;
    if (!snapshot.patientId) throw new Error("snapshot.patientId is required");
    if (!snapshot.billingMonth) {
        throw new Error("snapshot.billingMonth is required");
    }
    const existing = await findCoCMBillingSummary(medplum, snapshot.patientId, snapshot.billingMonth);
    const existingView = existing ? cocmBillingSummaryToView(existing) : undefined;
    // D7 integrity: a closed (`final`) month is frozen. Any edit — content or
    // lock — must go through the audited `reopenCoCMBillingSummary` first. This
    // guards every caller (facade / CLI / devtools), not just the UI which merely
    // hides the control. The CLI close path never hits this (it skips
    // already-closed months).
    if (existingView?.closed) {
        throw new Error("Billing summary is closed — reopen it before editing (D7)");
    }
    const submission = input.submission ?? {
        submitted: existingView?.submitted ?? false,
        submittedBy: existingView?.submittedBy,
        submittedAt: existingView?.submittedAt
    };
    const lockStatus = input.lockStatus ?? existingView?.lockStatus ?? "open";
    // The Observation is rebuilt from scratch each upsert, so any snapshot field
    // the caller omits would be dropped. Close-month/submit flows pass only the
    // attested minutes; preserve the previously-saved A&P, contributing entries,
    // patient display, and performer from the existing record so locking never
    // wipes them.
    // A month is authored once: the care manager who first recorded it stays its
    // performer, and a later editor does not take the credit (matching how
    // editing a care-time entry preserves its performer, and how submitting and
    // reopening leave authorship alone). Medplum's version history still records
    // who made each subsequent write.
    const performerRoleId = existingView?.performerRoleId ?? snapshot.performerRoleId;
    // Both references must carry a `display` (reference best practice) — resolve
    // whatever the caller and the existing record couldn't supply.
    const { patientDisplay, performerDisplay } = await resolveCoCMBillingSummaryAttribution(medplum, {
        patientId: snapshot.patientId,
        patientDisplay: snapshot.patientDisplay ?? existingView?.patientDisplay,
        performerRoleId,
        performerDisplay: performerDisplayForRole(performerRoleId, [
            {
                roleId: existingView?.performerRoleId,
                display: existingView?.performerDisplay
            },
            {
                roleId: snapshot.performerRoleId,
                display: snapshot.performerDisplay
            }
        ])
    });
    const mergedSnapshot = {
        ...snapshot,
        clinicalSummary: snapshot.clinicalSummary ?? existingView?.clinicalSummary,
        contributingObservationIds: snapshot.contributingObservationIds ?? existingView?.contributingObservationIds,
        patientDisplay,
        performerRoleId,
        performerDisplay
    };
    const observation = buildCoCMBillingSummary({
        snapshot: mergedSnapshot,
        submission,
        lockStatus,
        reopenHistory: existingView?.reopenHistory,
        id: existing?.id,
        issued: existing?.issued
    });
    if (existing?.id) {
        const updated = await updateResource(medplum, observation);
        if (!updated.id) throw new Error("Updated billing summary is missing an id");
        medplum.invalidateSearches("Observation");
        const event = {
            observationId: updated.id,
            observation: updated,
            patientId: snapshot.patientId,
            billingMonth: snapshot.billingMonth
        };
        globalEventEmitter.publish(DomainEventNames.CoCMBillingSummaryUpdated, event);
        return {
            observation: updated,
            isUpdate: true
        };
    }
    const created = await createResource(medplum, observation);
    if (!created.id) throw new Error("Created billing summary is missing an id");
    medplum.invalidateSearches("Observation");
    const event = {
        observationId: created.id,
        observation: created,
        patientId: snapshot.patientId,
        billingMonth: snapshot.billingMonth
    };
    globalEventEmitter.publish(DomainEventNames.CoCMBillingSummaryCreated, event);
    return {
        observation: created,
        isUpdate: false
    };
};

/**
 * Create, update, or delete a clinical impression
 *
 * @param medplum - MedplumClient instance
 * @param input - ClinicalImpression creation/update data
 * @returns Created or updated clinical impression, or undefined if deleted/no-op
 * @throws Error if validation or operation fails
 * @agentic @benchmark/core
 */ async function upsertClinicalImpression(medplum, input) {
    const { formData, patient, encounter, existingClinicalImpression, tempEncounterFullUrl } = input;
    // Transform form data to domain model
    const domainModel = clinicalImpressionFromForm(formData);
    // Business logic: Determine if we should delete
    // If value is empty and existing impression exists, delete it
    const shouldDelete = (!domainModel.value || !domainModel.value.valueString && !domainModel.value.valueCoding && !domainModel.value.valueUri) && !!existingClinicalImpression?.id;
    // If deleting, build minimal resource for DELETE operation
    if (shouldDelete && existingClinicalImpression?.id) {
        const entries = [
            {
                resource: {
                    resourceType: "ClinicalImpression",
                    id: existingClinicalImpression.id
                },
                method: "DELETE",
                url: `ClinicalImpression/${existingClinicalImpression.id}`
            }
        ];
        await executeTransaction$1(medplum, entries);
        if (patient.id) {
            const event = {
                clinicalImpressionId: existingClinicalImpression.id,
                patientId: patient.id
            };
            globalEventEmitter$1.publish(DomainEventNames$1.ClinicalImpressionDeleted, event);
        }
        return undefined;
    }
    // Build clinical impression resource (mechanical transformation)
    const clinicalImpression = buildClinicalImpression({
        patient,
        encounter,
        code: domainModel.code,
        valueType: domainModel.valueType,
        value: domainModel.value,
        existingClinicalImpressionId: existingClinicalImpression?.id,
        tempEncounterFullUrl,
        effectiveDate: domainModel.date
    });
    // If no clinical impression can be built (missing code or value), return undefined
    if (!clinicalImpression) {
        return undefined;
    }
    // Note: Condition creation is not applicable here since clinicalImpressionFromForm
    // always returns valueType: "summary" for form data. Condition creation logic
    // would be handled here if/when we support problem-type clinical impressions.
    // Build transaction entries
    const entries = [
        {
            resource: clinicalImpression,
            method: clinicalImpression.id ? "PUT" : "POST",
            url: clinicalImpression.id ? `ClinicalImpression/${clinicalImpression.id}` : "ClinicalImpression"
        }
    ];
    // Execute transaction
    const result = await executeTransaction$1(medplum, entries);
    // Extract and return the clinical impression
    const savedClinicalImpression = extractSingleResourceFromTransactionResult$1(result, "ClinicalImpression");
    if (!savedClinicalImpression?.id || !patient.id) {
        return savedClinicalImpression;
    }
    // Emit event
    if (existingClinicalImpression?.id) {
        const event = {
            clinicalImpressionId: savedClinicalImpression.id,
            clinicalImpression: savedClinicalImpression,
            patientId: patient.id
        };
        globalEventEmitter$1.publish(DomainEventNames$1.ClinicalImpressionUpdated, event);
    } else {
        const event = {
            clinicalImpressionId: savedClinicalImpression.id,
            clinicalImpression: savedClinicalImpression,
            patientId: patient.id
        };
        globalEventEmitter$1.publish(DomainEventNames$1.ClinicalImpressionCreated, event);
    }
    return savedClinicalImpression;
}

/**
 * Validate communication upsert input.
 * @agentic @benchmark/core
 */ function validateInput$8(input) {
    if (!input.patient) {
        throw new Error("Patient is required");
    }
    if (!input.practitioner) {
        throw new Error("Practitioner is required");
    }
}
/**
 * Create or update a communication
 *
 * @param medplum - MedplumClient instance
 * @param input - Communication creation/update data
 * @returns Created or updated communication resource
 * @throws Error if validation or operation fails
 * @remarks TODO(#983): Move to workflows - This action has complex causal effects:
 * Updates patient engagement status tags, fetches practitioner role,
 * builds/submits transaction bundle, emits domain events.
 * @agentic @benchmark/core
 */ async function upsertCommunication(medplum, input) {
    // Validate input
    validateInput$8(input);
    // Update patient engagement status tags separately if provided
    // Patient tags require a read-modify-write pattern which must be done
    // separately to avoid race conditions.
    if (input.formData["patient-engagement-status"] !== undefined) {
        try {
            await updatePatientEngagementStatusTags(medplum, input.patient, input.formData);
        } catch (error) {
            // Log error but don't fail the entire communication save
            // Patient tag updates are important but not critical to communication persistence
            console.error("Failed to update patient engagement status:", error instanceof Error ? error.message : String(error));
        }
    }
    // Validate patient has ID
    if (!input.patient.id) {
        throw new Error("Patient must have an id for communication creation");
    }
    // Get practitioner role if not provided
    let practitionerRole = input.practitionerRole;
    if (!practitionerRole && input.practitioner.id && input.benchmarkOrganizationId) {
        const foundRole = await findBenchmarkPractitionerRole(medplum, {
            reference: `Practitioner/${input.practitioner.id}`,
            benchmarkOrganizationId: input.benchmarkOrganizationId
        });
        if (foundRole) {
            practitionerRole = foundRole;
        }
    }
    // PractitionerRole is required for bundle building
    if (!practitionerRole) {
        throw new Error("PractitionerRole is required. Could not resolve role for practitioner.");
    }
    // Fetch existing active flags for duplicate prevention
    const existingActiveFlags = await fetchExistingActiveFlags(medplum, input.patient.id);
    // Build the transaction bundle
    const bundle = buildUpsertCommunicationBundle({
        formData: input.formData,
        patient: input.patient,
        practitioner: input.practitioner,
        practitionerRole,
        existingCommunication: input.existingCommunication,
        existingActiveFlags
    });
    // Submit the bundle
    const result = await submitBundle(medplum, bundle);
    // Extract communication from result
    const communication = extractSingleResourceFromTransactionResult$1(result, "Communication");
    // Validate IDs before emitting events
    if (!communication.id) {
        throw new Error("Communication ID is required after transaction");
    }
    if (!input.patient.id) {
        throw new Error("Patient ID is required for communication event");
    }
    // Publish domain event
    if (input.existingCommunication) {
        const event = {
            communicationId: communication.id,
            communication,
            patientId: input.patient.id
        };
        globalEventEmitter$1.publish(DomainEventNames$1.CommunicationUpdated, event);
    } else {
        const event = {
            communicationId: communication.id,
            communication,
            patientId: input.patient.id
        };
        globalEventEmitter$1.publish(DomainEventNames$1.CommunicationCreated, event);
    }
    // Post-save: Add plan activity if requested (standalone task deprecation Phase 3)
    const formData = input.formData;
    if (formData["followup-mode"] === "plan-activity" && formData["followup-activity-code"] && formData["followup-care-plan-id"]) {
        try {
            const carePlan = await medplum.readResource("CarePlan", formData["followup-care-plan-id"]);
            await addCarePlanActivity(medplum, {
                carePlan,
                activityCode: formData["followup-activity-code"]
            });
        } catch (error) {
            console.error("Failed to add plan activity follow-up:", error);
            input.onWarning?.(`Outreach logged, but follow-up activity could not be added: ${error instanceof Error ? error.message : "Unknown error"}`);
        }
    }
    return communication;
}

/**
 * Validate coverage upsert input.
 * @agentic @benchmark/core
 */ function validateInput$7(input) {
    if (!input.patient) {
        throw new Error("Patient is required");
    }
    if (!input.patient.id) {
        throw new Error("Patient must have an id");
    }
}
/**
 * Create or update a coverage
 *
 * @param medplum - MedplumClient instance
 * @param input - Coverage creation/update data
 * @returns Created or updated coverage resource
 * @throws Error if validation or operation fails
 * @agentic @benchmark/core
 */ async function upsertCoverage(medplum, input) {
    // Validate input
    validateInput$7(input);
    // Build coverage resource
    const coverage = coverageFromForm(input.formData, input.patient, input.existingCoverage);
    // Upsert resource (Medplum handles create vs update)
    if (!input.patient.id) {
        throw new Error("Patient must have an id for coverage upsert");
    }
    const beneficiaryRef = getReferenceString(input.patient);
    if (!beneficiaryRef) {
        throw new Error("Failed to get patient reference string");
    }
    const searchParams = {
        beneficiary: beneficiaryRef
    };
    if (coverage.payor && coverage.payor[0]) {
        const payorRef = getReferenceString(coverage.payor[0]);
        if (payorRef) {
            searchParams.payor = payorRef;
        }
    }
    const result = await medplum.upsertResource(coverage, searchParams);
    // Determine if this was a create or update based on whether existingCoverage had an id
    const isUpdate = !!input.existingCoverage?.id;
    // Emit event
    if (!result.id || !input.patient.id) {
        throw new Error("Missing required IDs for event");
    }
    if (isUpdate) {
        const event = {
            coverageId: result.id,
            coverage: result,
            patientId: input.patient.id
        };
        globalEventEmitter$1.publish(DomainEventNames$1.CoverageUpdated, event);
    } else {
        const event = {
            coverageId: result.id,
            coverage: result,
            patientId: input.patient.id
        };
        globalEventEmitter$1.publish(DomainEventNames$1.CoverageCreated, event);
    }
    return result;
}

const hasOwn = (value, key)=>Object.prototype.hasOwnProperty.call(value, key);
const carriesAssessmentProjection = (formData)=>hasAssessmentStatePayload(formData) || hasOwn(formData, "_questionnaireScoreConflictUrls") || hasOwn(formData, "_questionnaireDuplicateScoreUrls");
const sortedConflictMarkers = (value)=>[
        ...value ?? []
    ].sort();
/**
 * Upsert an encounter with all related resources
 *
 * This action orchestrates the complete encounter save operation:
 * 1. Fetches required data (existing flags, task owner, practitioner role)
 * 2. Builds a comprehensive transaction bundle
 * 3. Submits the bundle atomically
 * 4. Publishes domain events
 *
 * @param options - Configuration for upserting the encounter
 * @returns Created/updated encounter resource
 * @throws Error if validation or submission fails
 * @remarks TODO(#983): Move to workflows - This action has complex causal effects:
 * Updates patient engagement status tags, fetches additional data,
 * builds/submits transaction bundle, emits domain events.
 * @agentic @benchmark/core
 */ async function upsertEncounter(options) {
    const { medplum, formData, patient, practitioner, practitionerRole: providedPractitionerRole, existingEncounter, organization, originalFormData, onWarning, updatePatientTags = false, isDraft = false, draftFormState } = options;
    // Validate and reload questionnaire artifacts before any write side effect.
    // A stale/partial resource view must never be followed by patient-tag or goal
    // writes and then a failed encounter transaction.
    if (!patient.id) {
        throw new Error("Patient must have an id for encounter creation");
    }
    const existingQuestionnaireResponses = {};
    const existingScoreObservations = {};
    // Acknowledgement provenance is either preserved from freshly reloaded
    // resources or stamped at save by the action. Never accept client-supplied
    // author/time values for a new or changed acknowledgement.
    let bundleFormData = {
        ...formData,
        _suicideRiskAcknowledgedAt: undefined,
        _suicideRiskAcknowledgedBy: undefined
    };
    let authoritativeOriginalFormData = originalFormData;
    let persistedAcknowledgementAt;
    let persistedAcknowledgementBy;
    let authoritativeScoreConflictUrls = new Set();
    let authoritativeDuplicateScoreUrls = new Set();
    if (existingEncounter?.id) {
        const context = await loadEncounterFormDataContext(medplum, existingEncounter, patient, {
            throwOnError: true
        });
        const refreshedForm = encounterToForm(context);
        authoritativeScoreConflictUrls = new Set(refreshedForm._questionnaireScoreConflictUrls ?? []);
        authoritativeDuplicateScoreUrls = new Set(refreshedForm._questionnaireDuplicateScoreUrls ?? []);
        const submittedHasAssessmentProjection = carriesAssessmentProjection(formData);
        const submittedSuppliesConflictMarkers = hasOwn(formData, "_questionnaireScoreConflictUrls") || hasOwn(formData, "_questionnaireDuplicateScoreUrls");
        const submittedHasConflictMarkers = hasOwn(formData, "_questionnaireScoreConflictUrls") && Array.isArray(formData._questionnaireScoreConflictUrls) && hasOwn(formData, "_questionnaireDuplicateScoreUrls") && Array.isArray(formData._questionnaireDuplicateScoreUrls);
        const submittedConflictUrls = sortedConflictMarkers(formData._questionnaireScoreConflictUrls);
        const refreshedConflictUrls = [
            ...authoritativeScoreConflictUrls
        ].sort();
        const submittedDuplicateUrls = sortedConflictMarkers(formData._questionnaireDuplicateScoreUrls);
        const refreshedDuplicateUrls = [
            ...authoritativeDuplicateScoreUrls
        ].sort();
        const authoritativeHasConflictEvidence = authoritativeScoreConflictUrls.size > 0 || authoritativeDuplicateScoreUrls.size > 0;
        const submittedConflictMarkersInvalid = submittedSuppliesConflictMarkers ? !submittedHasConflictMarkers || JSON.stringify(submittedConflictUrls) !== JSON.stringify(refreshedConflictUrls) || JSON.stringify(submittedDuplicateUrls) !== JSON.stringify(refreshedDuplicateUrls) : authoritativeHasConflictEvidence;
        const originalSuppliesConflictMarkers = originalFormData !== undefined && (hasOwn(originalFormData, "_questionnaireScoreConflictUrls") || hasOwn(originalFormData, "_questionnaireDuplicateScoreUrls"));
        const originalConflictUrls = sortedConflictMarkers(originalFormData?._questionnaireScoreConflictUrls);
        const originalDuplicateUrls = sortedConflictMarkers(originalFormData?._questionnaireDuplicateScoreUrls);
        if (submittedHasAssessmentProjection && submittedConflictMarkersInvalid || originalSuppliesConflictMarkers && (JSON.stringify(originalConflictUrls) !== JSON.stringify(refreshedConflictUrls) || JSON.stringify(originalDuplicateUrls) !== JSON.stringify(refreshedDuplicateUrls)) || authoritativeDuplicateScoreUrls.size > 0 && submittedHasAssessmentProjection && getQuarantinedAssessmentStateFingerprint(formData, authoritativeDuplicateScoreUrls) !== getQuarantinedAssessmentStateFingerprint(refreshedForm, authoritativeDuplicateScoreUrls)) {
            throw new QuestionnairePersistenceError("A recorded assessment score requires review before assessments can be changed.");
        }
        bundleFormData = {
            ...bundleFormData,
            _questionnaireScoreConflictUrls: refreshedConflictUrls,
            _questionnaireDuplicateScoreUrls: refreshedDuplicateUrls
        };
        authoritativeOriginalFormData = refreshedForm;
        const refreshedSession = refreshedForm._adaptiveScreenerSession;
        if (submittedHasAssessmentProjection && refreshedSession) {
            const submittedSession = formData._adaptiveScreenerSession;
            const refreshedReasons = refreshedSession.reasons;
            const submittedReasons = submittedSession?.reasons;
            // Draft form state may legitimately retain predeclared reasons for
            // sections that have not produced a QR yet, so submitted reasons may be a
            // superset. Every reason already persisted on a QR must remain unchanged.
            const persistedReasonsPreserved = submittedReasons !== undefined && Object.entries(refreshedReasons).every(([url, reason])=>submittedReasons[url] === reason);
            if (!submittedSession || submittedSession.id !== refreshedSession.id || submittedSession.baseline !== refreshedSession.baseline || !persistedReasonsPreserved) {
                throw new QuestionnairePersistenceError("Adaptive screening provenance changed while this form was open. Refresh before saving.");
            }
        }
        persistedAcknowledgementAt = refreshedForm._suicideRiskAcknowledgedAt;
        persistedAcknowledgementBy = refreshedForm._suicideRiskAcknowledgedBy;
        const expectedPatientRef = `Patient/${patient.id}`;
        const expectedEncounterRef = `Encounter/${existingEncounter.id}`;
        const validateReferences = (resource)=>{
            if (resource.subject?.reference && resource.subject.reference !== expectedPatientRef) {
                throw new Error("A linked assessment no longer belongs to this patient. Refresh before saving.");
            }
            if (resource.encounter?.reference && resource.encounter.reference !== expectedEncounterRef) {
                throw new Error("A linked assessment no longer belongs to this encounter. Refresh before saving.");
            }
        };
        for (const [url, id] of Object.entries(refreshedForm._questionnaireResponseIds ?? {})){
            const matches = context.questionnaireResponses.filter((response)=>response.id === id);
            if (matches.length !== 1) {
                throw new Error("A recorded assessment changed while this form was open. Refresh before saving.");
            }
            const response = matches[0];
            if (refreshedForm._questionnaireResponseIds?.[url] !== id) {
                throw new Error("A recorded assessment changed while this form was open. Refresh before saving.");
            }
            if (response.status === "entered-in-error" || response.status === "stopped") {
                throw new Error("A recorded assessment is no longer active. Refresh before saving.");
            }
            validateReferences(response);
            existingQuestionnaireResponses[url] = response;
        }
        for (const [url, id] of Object.entries(refreshedForm._observationIds ?? {})){
            const matches = context.observations.filter((observation)=>observation.id === id);
            if (matches.length !== 1) {
                throw new Error("A recorded assessment score changed while this form was open. Refresh before saving.");
            }
            const observation = matches[0];
            if (refreshedForm._observationIds?.[url] !== id) {
                throw new Error("A recorded assessment score changed while this form was open. Refresh before saving.");
            }
            if (observation.status === "entered-in-error" || observation.status === "cancelled") {
                throw new Error("A recorded assessment score is no longer active. Refresh before saving.");
            }
            validateReferences(observation);
            existingScoreObservations[url] = observation;
        }
    }
    if (!existingEncounter?.id && ((formData._questionnaireScoreConflictUrls?.length ?? 0) > 0 || (formData._questionnaireDuplicateScoreUrls?.length ?? 0) > 0)) {
        throw new QuestionnairePersistenceError("A recorded assessment score requires review before assessments can be changed.");
    }
    assertAdaptiveScreenerFinalization(formData, isDraft, authoritativeDuplicateScoreUrls, authoritativeScoreConflictUrls);
    // Update patient tags separately if requested (skip for drafts — engagement status shouldn't change)
    if (updatePatientTags && !isDraft) {
        try {
            await updatePatientEngagementStatusTags(medplum, patient, formData);
        } catch (error) {
            // Log error but don't fail the entire encounter save
            // Patient tag updates are important but not critical to encounter persistence
            console.error("Failed to update patient engagement status:", error instanceof Error ? error.message : String(error));
        // Optionally: could throw here if engagement status updates are critical
        }
    }
    // Resolve practitioner role if not provided
    let practitionerRole = providedPractitionerRole;
    if (!practitionerRole) {
        if (!practitioner.id) {
            throw new Error("Practitioner ID is required when practitionerRole is not provided");
        }
        if (!options.benchmarkOrganizationId) {
            throw new Error("benchmarkOrganizationId is required when practitionerRole is not provided");
        }
        practitionerRole = await fetchBenchmarkPractitionerRole(medplum, practitioner.id, {
            benchmarkOrganizationId: options.benchmarkOrganizationId
        });
    }
    const actingPractitionerReference = practitionerRole.id ? `PractitionerRole/${practitionerRole.id}` : practitioner.id ? `Practitioner/${practitioner.id}` : undefined;
    const acknowledgementIsActive = formData._suicideRiskAcknowledged === true && formData._suicideRiskAcknowledgedAt !== undefined;
    const acknowledgementMatchesPersisted = acknowledgementIsActive && formData._suicideRiskAcknowledgedAt === persistedAcknowledgementAt;
    const authoritativeAcknowledgementAt = acknowledgementIsActive ? acknowledgementMatchesPersisted ? persistedAcknowledgementAt : new Date().toISOString() : undefined;
    const authoritativeAcknowledgementBy = acknowledgementIsActive ? acknowledgementMatchesPersisted ? persistedAcknowledgementBy : actingPractitionerReference : undefined;
    bundleFormData = {
        ...bundleFormData,
        _suicideRiskAcknowledgedAt: authoritativeAcknowledgementAt,
        _suicideRiskAcknowledgedBy: authoritativeAcknowledgementBy
    };
    const draftHasAcknowledgementProvenance = draftFormState !== undefined && (Object.prototype.hasOwnProperty.call(draftFormState, "_suicideRiskAcknowledgedAt") || Object.prototype.hasOwnProperty.call(draftFormState, "_suicideRiskAcknowledgedBy"));
    const bundleDraftFormState = draftFormState && (authoritativeAcknowledgementAt !== undefined || authoritativeAcknowledgementBy !== undefined || draftHasAcknowledgementProvenance) ? {
        ...draftFormState,
        _suicideRiskAcknowledgedAt: authoritativeAcknowledgementAt,
        _suicideRiskAcknowledgedBy: authoritativeAcknowledgementBy
    } : draftFormState;
    // Fetch existing active flags for duplicate prevention
    const existingActiveFlags = await fetchExistingActiveFlags(medplum, patient.id);
    // Create goals if goal creation data is present (skip for drafts — deferred to finalization)
    if (!isDraft && formData["goal-creation"] && formData["goal-creation"].length > 0) {
        try {
            // Create each goal with baseline observation
            for (const goalData of formData["goal-creation"]){
                if (!goalData.category || !goalData.description) {
                    onWarning?.("Skipping goal creation: category and description are required");
                    continue;
                }
                const goalFormData = {
                    category: goalData.category,
                    description: goalData.description,
                    notes: goalData.notes,
                    lifecycleStatus: "active",
                    startDate: localCalendarDateTodayISO()
                };
                await createGoal(medplum, {
                    formData: goalFormData,
                    patient,
                    practitionerRole
                });
            }
        } catch (error) {
            onWarning?.(`Failed to create goals: ${error instanceof Error ? error.message : String(error)}`);
        // Continue with encounter creation even if goal creation fails
        }
    }
    // Fetch goals if goal progress data is present (CoCM follow-up)
    let goals = [];
    if (formData["goal-progress"] && formData["goal-progress"].length > 0) {
        try {
            const goalIds = formData["goal-progress"].map((gp)=>gp.goalId);
            // Fetch all goals referenced in progress updates
            const fetchedGoals = await Promise.all(goalIds.map((goalId)=>medplum.readResource("Goal", goalId)));
            goals = fetchedGoals;
        } catch (error) {
            onWarning?.(`Failed to fetch goals for progress tracking: ${error instanceof Error ? error.message : String(error)}`);
        // Continue without goals - builder will skip progress observations
        }
    }
    // Load scribe composition if prefill was used (for status update)
    let scribeComposition;
    if (formData._scribeCompositionId) {
        try {
            scribeComposition = await medplum.readResource("Composition", formData._scribeCompositionId);
        } catch (error) {
            console.warn("Could not load scribe composition for status update:", error);
        // Continue without composition status update - not critical to encounter save
        }
    }
    // Reconcile intake-call care time with the Dialpad webhook tee. Both the
    // tee (on call ingest) and this encounter save can produce care-time for the
    // same call; linking our entry to the call Communication — and reusing the
    // tee entry's id when it already exists — collapses them into one entry so
    // the call minutes are not double-counted in totals / the activity log.
    let bundleOriginalFormData = authoritativeOriginalFormData;
    if (!isDraft && scribeComposition && typeof formData["care-time-call-minutes"] === "number" && formData["care-time-call-minutes"] > 0) {
        try {
            const link = await resolveIntakeCallCareTimeLink(medplum, scribeComposition);
            if (link.communicationId) {
                bundleFormData = {
                    ...bundleFormData,
                    _careTimeCallCommunicationId: link.communicationId
                };
            }
            const alreadyLinked = authoritativeOriginalFormData?._careTimeObservationIds?.["intake-call"];
            if (link.existingObservationId && !alreadyLinked) {
                bundleOriginalFormData = {
                    ...authoritativeOriginalFormData ?? {},
                    _careTimeObservationIds: {
                        ...authoritativeOriginalFormData?._careTimeObservationIds ?? {},
                        "intake-call": link.existingObservationId
                    }
                };
            }
        } catch (error) {
            console.warn("Failed to reconcile intake-call care time with Dialpad tee:", error instanceof Error ? error.message : String(error));
        }
    }
    // Build the transaction bundle
    const bundle = buildUpsertEncounterBundle({
        formData: bundleFormData,
        patient,
        practitioner,
        practitionerRole,
        organization,
        existingEncounter,
        originalFormData: bundleOriginalFormData,
        existingQuestionnaireResponses,
        existingScoreObservations,
        existingActiveFlags,
        goals,
        scribeComposition,
        onWarning,
        isDraft,
        draftFormState: bundleDraftFormState,
        benchmarkOrganizationId: options.benchmarkOrganizationId
    });
    // Submit the bundle
    const result = await submitBundle(medplum, bundle);
    // Extract encounter from result
    const encounter = extractSingleResourceFromTransactionResult$1(result, "Encounter");
    // Validate IDs before emitting events
    if (!encounter.id) {
        throw new Error("Encounter ID is required after transaction");
    }
    if (!patient.id) {
        throw new Error("Patient ID is required for encounter event");
    }
    // Publish domain event
    if (existingEncounter) {
        const event = {
            encounterId: encounter.id,
            encounter,
            patientId: patient.id
        };
        globalEventEmitter$1.publish(DomainEventNames$1.EncounterUpdated, event);
    } else {
        const event = {
            encounterId: encounter.id,
            encounter,
            patientId: patient.id
        };
        globalEventEmitter$1.publish(DomainEventNames$1.EncounterCreated, event);
    }
    // Encounter-administered questionnaires and their score observations are saved
    // in this same transaction, but only Encounter events were published — so
    // views keyed on QuestionnaireResponse/Observation events (e.g. the patient
    // Assessments tab) did not auto-refresh after an in-encounter save. Publish
    // those events too, keyed by patient, so every consumer view refetches. The
    // refetch hook debounces and filters by patient, so the extra events collapse
    // into a single refresh.
    const savedQuestionnaireResponses = extractResourcesFromTransactionResult$1(result, "QuestionnaireResponse");
    const savedObservations = extractResourcesFromTransactionResult$1(result, "Observation");
    for (const questionnaireResponse of savedQuestionnaireResponses){
        if (!questionnaireResponse.id) continue;
        const event = {
            questionnaireResponseId: questionnaireResponse.id,
            questionnaireResponse,
            patientId: patient.id,
            observations: savedObservations
        };
        globalEventEmitter$1.publish(DomainEventNames$1.QuestionnaireResponseUpdated, event);
    }
    for (const observation of savedObservations){
        if (!observation.id) continue;
        const event = {
            observationId: observation.id,
            observation,
            patientId: patient.id
        };
        globalEventEmitter$1.publish(DomainEventNames$1.ObservationUpdated, event);
    }
    // Sync minor flag (skip for drafts — side effect deferred to finalization)
    if (!isDraft) {
        try {
            await syncMinorFlag(medplum, {
                patient
            });
        } catch (error) {
            console.error("Failed to sync minor flag:", error instanceof Error ? error.message : String(error));
        }
    }
    // Post-save: Add plan activity if requested (skip for drafts)
    if (!isDraft && formData["followup-mode"] === "plan-activity" && formData["followup-activity-code"] && formData["followup-care-plan-id"]) {
        try {
            const carePlan = await medplum.readResource("CarePlan", formData["followup-care-plan-id"]);
            await addCarePlanActivity(medplum, {
                carePlan,
                activityCode: formData["followup-activity-code"]
            });
        } catch (error) {
            console.error("Failed to add plan activity follow-up:", error);
            onWarning?.(`Encounter saved, but follow-up activity could not be added: ${error instanceof Error ? error.message : "Unknown error"}`);
        }
    }
    return encounter;
}
/**
 * Upsert encounter with patient tag updates
 *
 * Convenience function that calls upsertEncounter with updatePatientTags enabled.
 * This provides the complete encounter save experience:
 * - Updates patient engagement status tags separately (if needed)
 * - Executes the main encounter transaction atomically
 *
 * @param options - Configuration for upserting the encounter
 * @returns Created/updated encounter resource
 * @throws Error if validation or submission fails
 * @agentic @benchmark/core
 */ async function upsertEncounterWithPatientTags(options) {
    return upsertEncounter({
        ...options,
        updatePatientTags: true
    });
}

/**
 * Validate flag upsert input.
 * @agentic @benchmark/core
 */ function validateInput$6(input) {
    if (!input.patient) {
        throw new Error("Patient is required");
    }
    if (!input.patient.id) {
        throw new Error("Patient must have an id");
    }
}
/**
 * Check if a duplicate active flag exists for the same patient and code.
 * @agentic @benchmark/core
 */ async function checkForDuplicateFlag(medplum, patient, code) {
    const coding = getFlagCodingByCode(code);
    const targetToken = coding.system ? `${coding.system}|${code}` : code;
    // Search for active flags for this patient
    const existingActiveFlags = await medplum.searchResources("Flag", {
        subject: `Patient/${patient.id}`,
        status: "active"
    });
    // Check if any existing flag has the same code
    return existingActiveFlags.some((f)=>{
        const codings = f.code?.coding || [];
        return codings.some((c)=>{
            const token = c.system ? `${c.system}|${c.code}` : c.code;
            return token === targetToken;
        });
    });
}
/**
 * Create or update a flag
 *
 * @param medplum - MedplumClient instance
 * @param input - Flag creation/update data
 * @returns Created or updated flag resource
 * @throws Error if validation, duplicate check, or operation fails
 * @agentic @benchmark/core
 */ async function upsertFlag(medplum, input) {
    // Validate input
    validateInput$6(input);
    // Get practitioner role if practitioner provided but role not
    let practitionerRole = input.practitionerRole;
    if (input.practitioner && !practitionerRole && input.benchmarkOrganizationId) {
        try {
            practitionerRole = await findBenchmarkPractitionerRole(medplum, {
                reference: `Practitioner/${input.practitioner.id}`,
                benchmarkOrganizationId: input.benchmarkOrganizationId
            });
        } catch  {
        // Fallback to using practitioner directly (handled in builder)
        }
    }
    // Build flag resource
    const flag = flagFromForm(input.formData, input.patient, input.practitioner, practitionerRole, input.existingFlag);
    // Check for duplicate active flags on create (not on update)
    if (!flag.id && input.formData.code) {
        const prospectiveStatus = flag.status || "active";
        if (prospectiveStatus === "active") {
            const hasDuplicate = await checkForDuplicateFlag(medplum, input.patient, input.formData.code);
            if (hasDuplicate) {
                throw new Error("This member already has this active flag.");
            }
        }
    }
    // Create or update
    let result;
    if (flag.id) {
        result = await updateResource$1(medplum, flag);
        // Emit event
        if (!result.id || !input.patient.id) {
            throw new Error("Missing required IDs for event");
        }
        const event = {
            flagId: result.id,
            flag: result,
            patientId: input.patient.id
        };
        globalEventEmitter$1.publish(DomainEventNames$1.FlagUpdated, event);
    } else {
        result = await createResource$1(medplum, flag);
        // Emit event
        if (!result.id || !input.patient.id) {
            throw new Error("Missing required IDs for event");
        }
        const event = {
            flagId: result.id,
            flag: result,
            patientId: input.patient.id
        };
        globalEventEmitter$1.publish(DomainEventNames$1.FlagCreated, event);
    }
    return result;
}

/**
 * Validate healthcare service upsert input.
 * @agentic @benchmark/core
 */ function validateInput$5(input) {
    const isUpdate = !!input.existingHealthcareService?.id;
    if (isUpdate && !input.formData.type) {
        throw new Error("Service type is required for updates");
    }
    if (!isUpdate) {
        const hasTypes = input.formData.types && input.formData.types.length > 0 || !!input.formData.type;
        if (!hasTypes) {
            throw new Error("At least one service type is required");
        }
    }
}
/**
 * Create or update a healthcare service
 *
 * @param medplum - MedplumClient instance
 * @param input - HealthcareService creation/update data
 * @returns Created or updated healthcare service resources
 * @throws Error if validation or operation fails
 * @agentic @benchmark/core
 */ async function upsertHealthcareService(medplum, input) {
    validateInput$5(input);
    const isUpdate = !!input.existingHealthcareService?.id;
    // Resolve canonical state-coverage Location resources before building
    // the bundle; the builder requires every coverageAreas[i].locationId
    // to be populated. No-op when the form carries no coverage areas.
    const enrichedFormData = await enrichCoverageAreasWithLocations(medplum, input.formData);
    // Build transaction bundle
    const { bundle, newLocationTempUrls, newOrganizationTempUrl } = buildUpsertHealthcareServiceBundle({
        formData: enrichedFormData,
        existingHealthcareService: input.existingHealthcareService
    });
    // Submit bundle atomically
    const result = await submitBundle(medplum, bundle);
    // Extract all HealthcareServices from result
    const healthcareServices = extractResourcesFromTransactionResult$1(result, "HealthcareService");
    if (healthcareServices.length === 0) {
        throw new Error("No HealthcareService resources created");
    }
    // Emit Location events (same as before)
    if (newLocationTempUrls.size > 0) {
        const createdLocations = extractResourcesFromTransactionResult$1(result, "Location");
        createdLocations.forEach((location)=>{
            if (location.id) {
                const event = {
                    locationId: location.id,
                    location,
                    healthcareServiceId: healthcareServices[0]?.id
                };
                globalEventEmitter$1.publish(DomainEventNames$1.LocationCreated, event);
            }
        });
    }
    // Emit Organization event (same as before)
    if (newOrganizationTempUrl) {
        const createdOrganizations = extractResourcesFromTransactionResult$1(result, "Organization");
        const createdOrganization = createdOrganizations.find((org)=>org.id);
        if (createdOrganization?.id) {
            const event = {
                organizationId: createdOrganization.id,
                organization: createdOrganization,
                healthcareServiceId: healthcareServices[0]?.id
            };
            globalEventEmitter$1.publish(DomainEventNames$1.OrganizationCreated, event);
        }
    }
    // Emit HealthcareService events for EACH created/updated service
    healthcareServices.forEach((healthcareService)=>{
        if (!healthcareService.id) return;
        if (isUpdate) {
            const event = {
                healthcareServiceId: healthcareService.id,
                healthcareService
            };
            globalEventEmitter$1.publish(DomainEventNames$1.HealthcareServiceUpdated, event);
        } else {
            const event = {
                healthcareServiceId: healthcareService.id,
                healthcareService
            };
            globalEventEmitter$1.publish(DomainEventNames$1.HealthcareServiceCreated, event);
        }
    });
    return {
        healthcareServices,
        isUpdate,
        createdCount: healthcareServices.length
    };
}

/**
 * Validates Library upsert input: a signed-in author, a non-empty title and
 * body, and a body within the size cap.
 * @agentic @benchmark/core
 */ function validateInput$4(input) {
    if (!input.actor?.name) {
        throw new Error("A signed-in author is required to save a document");
    }
    if (!input.formData.title.trim()) {
        throw new Error("Title is required");
    }
    if (!input.formData.markdownBody.trim()) {
        throw new Error("Content is required");
    }
    const bytes = getMarkdownByteLength(input.formData.markdownBody);
    if (bytes > MAX_LIBRARY_MARKDOWN_BYTES) {
        throw new Error(`Content exceeds the maximum size of ${MAX_LIBRARY_MARKDOWN_BYTES} bytes`);
    }
}
/**
 * Create or edit a Library document.
 * @throws Error if validation or the Medplum operation fails
 * @agentic @benchmark/core
 */ async function upsertLibrary(medplum, input) {
    validateInput$4(input);
    const library = libraryFromForm(input.formData, input.actor, input.existingLibrary);
    if (library.id) {
        const result = await updateResource$1(medplum, library);
        if (!result.id) {
            throw new Error("Missing required ID for event");
        }
        const event = {
            libraryId: result.id,
            library: result
        };
        globalEventEmitter$1.publish(DomainEventNames$1.LibraryUpdated, event);
        return result;
    }
    const result = await createResource$1(medplum, library);
    if (!result.id) {
        throw new Error("Missing required ID for event");
    }
    const event = {
        libraryId: result.id,
        library: result
    };
    globalEventEmitter$1.publish(DomainEventNames$1.LibraryCreated, event);
    return result;
}

/**
 * Validate observation upsert input.
 * @agentic @benchmark/core
 */ function validateInput$3(input) {
    if (!input.patient) {
        throw new Error("Patient is required");
    }
    if (!input.patient.id) {
        throw new Error("Patient must have an id");
    }
    if (!input.practitionerRole) {
        throw new Error("PractitionerRole is required");
    }
    if (!input.practitionerRole.id) {
        throw new Error("PractitionerRole must have an id");
    }
}
/**
 * Create or update an observation
 *
 * @param medplum - MedplumClient instance
 * @param input - Observation creation/update data
 * @returns Created or updated observation resource
 * @throws Error if validation or operation fails
 * @agentic @benchmark/core
 */ async function upsertObservation(medplum, input) {
    // Validate input
    validateInput$3(input);
    // Build observation resource
    const observation = observationFromForm(input.formData, input.patient, input.practitionerRole, input.existingObservation);
    // Create or update
    let result;
    if (observation.id) {
        result = await updateResource$1(medplum, observation);
        // Emit event
        if (!result.id || !input.patient.id) {
            throw new Error("Missing required IDs for event");
        }
        const event = {
            observationId: result.id,
            observation: result,
            patientId: input.patient.id
        };
        globalEventEmitter$1.publish(DomainEventNames$1.ObservationUpdated, event);
    } else {
        result = await createResource$1(medplum, observation);
        // Emit event
        if (!result.id || !input.patient.id) {
            throw new Error("Missing required IDs for event");
        }
        const event = {
            observationId: result.id,
            observation: result,
            patientId: input.patient.id
        };
        globalEventEmitter$1.publish(DomainEventNames$1.ObservationCreated, event);
    }
    return result;
}

/**
 * Local form-data diff helper. Inlined from `apps/connections`'s
 * `@benchmark/ui`-tagged `utils/fhir/form.ts` to avoid pulling a UI utility
 * into core; the apps file remains the canonical version for UI consumers.
 */ function getGenericFormDataDiff(original, updated, fieldsToCompare) {
    const changedFields = [];
    for (const field of fieldsToCompare){
        if (JSON.stringify(original[field]) !== JSON.stringify(updated[field])) {
            changedFields.push(field);
        }
    }
    return {
        hasChanges: changedFields.length > 0,
        changedFields
    };
}
/**
 * Validate questionnaire upsert input.
 * @agentic @benchmark/core
 */ function validateInput$2(input) {
    if (!input.formData.status) {
        throw new Error("Questionnaire status is required");
    }
}
/**
 * Compare two form data objects and return what has changed.
 * Uses generic diff utility with questionnaire-specific fields.
 * @agentic @benchmark/core
 */ function getFormDataDiff(original, updated) {
    const questionnaireFields = [
        "url",
        "identifier",
        "version",
        "name",
        "title",
        "derivedFrom",
        "status",
        "experimental",
        "subjectType",
        "date",
        "publisher",
        "contact",
        "description",
        "useContext",
        "jurisdiction",
        "purpose",
        "copyright",
        "approvalDate",
        "lastReviewDate",
        "effectivePeriod",
        "code",
        "item"
    ];
    const diff = getGenericFormDataDiff(original, updated, questionnaireFields);
    return {
        questionnaireChanged: diff.hasChanges
    };
}
/**
 * Create or update a questionnaire
 *
 * @param medplum - MedplumClient instance
 * @param input - Questionnaire creation/update data
 * @returns Created or updated questionnaire resource
 * @throws Error if validation or operation fails
 * @agentic @benchmark/core
 */ async function upsertQuestionnaire(medplum, input) {
    // Validate input
    validateInput$2(input);
    // If editing, check if anything has changed
    const isEditing = !!input.existingQuestionnaire && !!input.originalFormData;
    if (isEditing) {
        const diff = getFormDataDiff(input.originalFormData, input.formData);
        // If nothing changed, return existing questionnaire without API call
        if (!diff.questionnaireChanged) {
            return input.existingQuestionnaire;
        }
    }
    // Build questionnaire resource
    const questionnaire = questionnaireFromForm(input.formData, input.existingQuestionnaire);
    // Create or update
    let result;
    if (questionnaire.id) {
        result = await updateResource$1(medplum, questionnaire);
        // Emit event
        if (!result.id) {
            throw new Error("Missing required ID for event");
        }
        const event = {
            questionnaireId: result.id,
            questionnaire: result
        };
        globalEventEmitter$1.publish(DomainEventNames$1.QuestionnaireUpdated, event);
    } else {
        result = await createResource$1(medplum, questionnaire);
        // Emit event
        if (!result.id) {
            throw new Error("Missing required ID for event");
        }
        const event = {
            questionnaireId: result.id,
            questionnaire: result
        };
        globalEventEmitter$1.publish(DomainEventNames$1.QuestionnaireCreated, event);
    }
    return result;
}

/**
 * Validate questionnaire response upsert input.
 * @agentic @benchmark/core
 */ function validateInput$1(input) {
    if (!input.patient) {
        throw new Error("Patient is required");
    }
    if (!input.patient.id) {
        throw new Error("Patient must have an id");
    }
    if (!input.practitioner) {
        throw new Error("Practitioner is required");
    }
    if (!input.practitionerRole) {
        throw new Error("PractitionerRole is required");
    }
    if (!input.practitionerRole.id) {
        throw new Error("PractitionerRole must have an id");
    }
}
/**
 * Create or update a questionnaire response with associated observations
 *
 * This action handles creating/updating a QuestionnaireResponse and optionally
 * creating Observation resources for scoring. All resources are created/updated
 * in a single atomic transaction.
 *
 * @param medplum - MedplumClient instance
 * @param input - QuestionnaireResponse creation/update data
 * @returns Created or updated questionnaire response and observations
 * @throws Error if validation or operation fails
 * @agentic @benchmark/core
 */ async function upsertQuestionnaireResponse(medplum, input) {
    // Validate input
    validateInput$1(input);
    // Build questionnaire response
    const response = questionnaireResponseFromForm(input.formData, input.patient, input.practitioner, input.practitionerRole, input.encounter, input.existingResponse, input.tempEncounterFullUrl);
    // Build observations based on score if this is a completed standardized questionnaire
    const observations = [];
    if (input.formData.status === "completed" && input.formData.questionnaire && input.formData.item) {
        const flattenedAnswers = flattenResponseItems(input.formData.item);
        const completion = evaluateQuestionnaireCompletion(input.formData.questionnaire, flattenedAnswers, {
            declaredComplete: input.formData.status === "completed"
        });
        if (!completion.supported || !completion.complete) {
            throw new Error("QuestionnaireResponse cannot be completed because required answers are missing or unsupported.");
        }
        // Find the questionnaire configuration
        const questionnaireConfig = Object.values(STANDARD_QUESTIONNAIRES).find((config)=>config.url === input.formData.questionnaire);
        if (questionnaireConfig && questionnaireConfig.totalScoreLoincCode && questionnaireConfig.scoring.linkIds.length > 0) {
            // Extract responses for scoring
            const allResponses = flattenedAnswers.map((answer)=>({
                    linkId: answer.linkId,
                    value: answer.value
                }));
            // Filter out non-scorable responses (e.g., functional impairment questions)
            const scoringResponses = filterScorableResponses(allResponses, input.formData.questionnaire);
            // Calculate the score
            const scoreResult = calculateQuestionnaireScore(scoringResponses, questionnaireConfig.scoring);
            // Build the observation
            const observation = buildObservationFromScore({
                score: scoreResult.score,
                questionnaireConfig,
                scoring: questionnaireConfig.scoring,
                patient: input.patient,
                encounter: input.encounter,
                practitionerRole: input.practitionerRole,
                interpretation: scoreResult.interpretation,
                riskLevel: scoreResult.riskLevel
            });
            observations.push(observation);
        }
    }
    // Build transaction entries
    const entries = [
        {
            resource: response,
            method: response.id ? "PUT" : "POST",
            url: response.id ? `QuestionnaireResponse/${response.id}` : "QuestionnaireResponse"
        },
        ...observations.map((obs)=>({
                resource: obs,
                method: obs.id ? "PUT" : "POST",
                url: obs.id ? `Observation/${obs.id}` : "Observation"
            }))
    ];
    // Execute transaction
    const result = await executeTransaction$1(medplum, entries);
    // Extract results
    const savedResponse = extractSingleResourceFromTransactionResult$1(result, "QuestionnaireResponse");
    const savedObservations = extractResourcesFromTransactionResult$1(result, "Observation");
    // Emit event
    if (!savedResponse.id || !input.patient.id) {
        throw new Error("Missing required IDs for event");
    }
    if (response.id) {
        const event = {
            questionnaireResponseId: savedResponse.id,
            questionnaireResponse: savedResponse,
            patientId: input.patient.id,
            observations: savedObservations
        };
        globalEventEmitter$1.publish(DomainEventNames$1.QuestionnaireResponseUpdated, event);
    } else {
        const event = {
            questionnaireResponseId: savedResponse.id,
            questionnaireResponse: savedResponse,
            patientId: input.patient.id,
            observations: savedObservations
        };
        globalEventEmitter$1.publish(DomainEventNames$1.QuestionnaireResponseCreated, event);
    }
    return {
        response: savedResponse,
        observations: savedObservations
    };
}

/**
 * Validate subscriber upsert input.
 * @agentic @benchmark/core
 */ function validateInput(input) {
    if (!input.patient) {
        throw new Error("Patient is required");
    }
    if (!input.patient.id) {
        throw new Error("Patient must have an id");
    }
    if (!input.subscriberFirstName || !input.subscriberLastName) {
        throw new Error("Subscriber first name and last name are required");
    }
}
/**
 * Create or update a subscriber (RelatedPerson) resource for insurance coverage
 *
 * @param medplum - MedplumClient instance
 * @param input - Subscriber creation/update data
 * @returns Reference string for the subscriber (e.g., "RelatedPerson/123")
 * @throws Error if validation or operation fails
 * @agentic @benchmark/core
 */ async function upsertSubscriber(medplum, input) {
    // Validate input
    validateInput(input);
    // Check if we should update an existing RelatedPerson
    if (input.existingSubscriberRef) {
        try {
            const existingSubscriber = await medplum.readReference({
                reference: input.existingSubscriberRef
            });
            if (existingSubscriber.resourceType === "RelatedPerson") {
                // Merge onto the existing resource: only overwrite fields the form
                // actually supplies. Blindly setting birthDate/telecom from optional
                // inputs wipes the prior DOB and any non-phone telecom (e.g. email).
                const mergedTelecom = input.subscriberPhone ? [
                    {
                        system: "phone",
                        value: input.subscriberPhone
                    },
                    ...existingSubscriber.telecom?.filter((t)=>t.system !== "phone") ?? []
                ] : existingSubscriber.telecom;
                const updatedSubscriber = {
                    ...existingSubscriber,
                    id: existingSubscriber.id,
                    name: [
                        {
                            given: [
                                input.subscriberFirstName
                            ],
                            family: input.subscriberLastName
                        }
                    ],
                    birthDate: input.subscriberDob ?? existingSubscriber.birthDate,
                    telecom: mergedTelecom
                };
                const result = await updateResource$1(medplum, updatedSubscriber);
                return `RelatedPerson/${result.id}`;
            }
        } catch (error) {
            console.error("Error updating existing subscriber:", error);
        // Fall through to create new subscriber
        }
    }
    // Create new RelatedPerson subscriber
    const subscriberData = {
        resourceType: "RelatedPerson",
        patient: {
            reference: `Patient/${input.patient.id}`,
            display: getPatientDisplayName$1(input.patient)
        },
        relationship: [
            {
                coding: [
                    {
                        system: "http://terminology.hl7.org/CodeSystem/subscriber-relationship",
                        code: input.relationship || "unknown",
                        display: input.relationship || "Unknown"
                    }
                ]
            }
        ],
        name: [
            {
                given: [
                    input.subscriberFirstName
                ],
                family: input.subscriberLastName
            }
        ],
        birthDate: input.subscriberDob,
        telecom: input.subscriberPhone ? [
            {
                system: "phone",
                value: input.subscriberPhone
            }
        ] : undefined
    };
    const result = await createResource$1(medplum, subscriberData);
    if (!result.id) {
        throw new Error("Subscriber ID is required after creation");
    }
    return `RelatedPerson/${result.id}`;
}

/**
 * Create or update a task
 *
 * @param medplum - MedplumClient instance
 * @param input - Task creation/update data
 * @returns Created or updated task resource
 * @throws Error if validation or operation fails
 * @agentic @benchmark/core
 */ async function upsertTask(medplum, input) {
    const { formData, patient: providedPatient, existingTask } = input;
    // Determine patient - either from provided patient or fetch from form data
    let patient = providedPatient;
    if (!patient && formData["patient-id"]) {
        patient = await readResource$1(medplum, "Patient", formData["patient-id"]);
    }
    if (!patient) {
        throw new Error("Patient is required for task creation");
    }
    // Build task resource
    const task = taskFromForm(formData, patient, existingTask);
    // Validate patient has ID
    if (!patient.id) {
        throw new Error("Patient must have an id for task creation");
    }
    // Create or update
    let result;
    if (existingTask?.id) {
        result = await updateResource$1(medplum, task);
        // Emit event
        if (!result.id || !patient.id) {
            throw new Error("Missing required IDs for event");
        }
        const event = {
            taskId: result.id,
            task: result,
            patientId: patient.id
        };
        globalEventEmitter$1.publish(DomainEventNames$1.TaskUpdated, event);
    } else {
        result = await createResource$1(medplum, task);
        // Emit event
        if (!result.id || !patient.id) {
            throw new Error("Missing required IDs for event");
        }
        const event = {
            taskId: result.id,
            task: result,
            patientId: patient.id
        };
        globalEventEmitter$1.publish(DomainEventNames$1.TaskCreated, event);
    }
    return result;
}

export { APPROVE_SELECTED_SERVICES_CODE, activateCarePlan, activateCoCMFlag, addCarePlanActivity, adjustTaskDatesForPause, archiveTask, batchUpdateHealthcareServices, buildCancelledCarePlan, buildCompletedCarePlan, cancelActivityTasks, cancelCarePlan, cancelTasks, claimTask, clearServiceApprovalNotificationForTask, closeGoal, completeCarePlan, createCarePlan, createGoal, createGoalProgress, createNotificationTask, createPatient, createPatientReferralServiceRequest, createPauseFlag, createPractitioner, createServiceApprovalNotificationTask, deactivateCoCMFlag, deactivatePauseFlag, endorseLibrary, holdCarePlan, logActivityProgress, logAvailabilityUpdate, markNotificationTaskRead, migratePatientEngagementStatus, reassignServiceApprovalNotificationForTask, reassignTask, recalculateTaskDatesAfterUnpause, reconcileCarePlanReadyState, reconcileIntakeChargeItem, reopenCoCMBillingPacket, reopenCoCMBillingSummary, reorderCarePlanActivities, resumeCarePlan, retireLibrary, reviewLibrary, saveTaskDraft, snoozeTask, softDeleteCareTime, submitCoCMBillingPacket, submitProgramSelection, syncMinorFlag, updateCarePlan, updateCarePlanActivity, updateDraftTaskMetadata, updateGoal, updatePatient, updatePatientCareTeam, updatePatientEmergencyContact, updatePatientEngagementStatus, updatePatientEngagementStatusTags, updatePatientIntakeEligibilityTag, upsertAppointment, upsertCareTime, upsertClinicalImpression, upsertCoCMBillingSummary, upsertCommunication, upsertCoverage, upsertEncounter, upsertEncounterWithPatientTags, upsertFlag, upsertHealthcareService, upsertLibrary, upsertObservation, upsertQuestionnaire, upsertQuestionnaireResponse, upsertSubscriber, upsertTask };
